import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";
import * as nativeApp from "./nativeApp";
import STAGES_DATA from "./stages.json";
import guideContentModule from "./guideContent";
import { suggestEmail } from "./emailSuggest";
const { GUIDE_SECTIONS } = guideContentModule;

// ── Error Boundary ────────────────────────────────────────────────────
// Catches any uncaught React render errors and shows a friendly recovery
// screen instead of a blank white page. Must be a class component —
// React doesn't support error boundaries as function components.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("[GRTP Error Boundary]", error, info.componentStack);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "Unknown error";
    return (
      <div style={{ fontFamily: "-apple-system,'Segoe UI',system-ui,sans-serif", background: "#f0f4f8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 32, maxWidth: 480, width: "100%", textAlign: "center", border: "1px solid #e2eaf2" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 10 }}>Something went wrong</div>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16, lineHeight: 1.6 }}>An unexpected error occurred. Your data is safe — please refresh the page to continue. If the problem persists, contact <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#1a4a2e" }}>{SUPPORT_EMAIL}</a>.</p>
          <div style={{ background: "#f8fafb", border: "1px solid #e2eaf2", borderRadius: 8, padding: "10px 14px", marginBottom: 20, textAlign: "left" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>Error detail</div>
            <div style={{ fontSize: 12, color: "#374151", fontFamily: "monospace", wordBreak: "break-all" }}>{msg}</div>
          </div>
          <button onClick={() => window.location.reload()} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", marginRight: 12 }}>Refresh page</button>
          <button onClick={() => this.setState({ hasError: false, error: null })} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Try to recover without refreshing</button>
        </div>
      </div>
    );
  }
}

// The stage definitions are frontend/src/stages.json, an exact copy of
// backend/stages.json: staff on the dashboard and parents reading their
// emails must see the same instructions, and two hand-maintained copies
// here and in protocol.js had drifted apart. The backend's
// test_stage_definitions.js fails if the copies ever differ. `id` is 0-4,
// matching the backend's current_stage column; labels follow the RFU's
// HEADCASE numbering (Stage 1, 2/3, 4, 5, 6).
const STAGES = STAGES_DATA;
// A band's number as the guidance names it: "1", "2/3", "4", "5", "6".
// Not its position in the list: Stages 2 and 3 run together as one band,
// so position + 1 read "4" beside "Stage 5" for every band after it.
const stageNumber = (s) => String(s?.label || "").replace(/^Stage\s*/i, "");

const SYMPTOMS = [
  "Headache", "Pressure in head", "Neck pain", "Dizziness", "Balance problems",
  "Nausea / vomiting", "Blurred or double vision",
  "Sensitivity to light", "Sensitivity to noise", "Feeling slowed down",
  "Feeling in a fog", "Don't feel right", "Confusion", "Difficulty concentrating",
  "Memory problems", "Fatigue", "Drowsiness", "Sleep disturbance",
  "Irritability / emotional changes", "Sadness", "Nervous or anxious",
  "Loss of consciousness (even briefly)", "Seizure activity",
];

// Loose but sane email format check — matches the equivalent check on the
// backend (server.js, isPlausibleEmail). Kept as a single named constant
// here so there's only one place to update if the rule ever changes.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getDaysSinceIncident(incidentDate) {
  // Always calculate "today" using the school's timezone (UK), matching
  // the identical fix on the backend (protocol.js) — without this, the
  // same incident date could show a different "Day X" depending on which
  // timezone the viewer's device happens to be set to.
  const [year, month, day] = incidentDate.split("-").map(Number);
  const incident = new Date(year, month - 1, day);

  const todayInUK = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Europe/London" })
  );
  const today = new Date(todayInUK.getFullYear(), todayInUK.getMonth(), todayInUK.getDate());

  // The day of the injury is Day 0, as in the guidance (see protocol.js).
  return Math.max(0, Math.floor((today - incident) / 86400000));
}

// Mirrors protocol.js. The day a record is AT: live while running,
// frozen at the day it ended once a protocol is cleared or removed early.
// Without this the counter kept climbing after a protocol stopped, so a
// record removed on Day 4 read "Day 5 of 21" the following morning.
function protocolDay(player) {
  const endedAt =
    player.status === "removed_early" ? player.removed_at :
    player.status === "cleared" ? player.cleared_at : null;
  if (!endedAt) return getDaysSinceIncident(player.incident_date);
  const [y, m, d] = player.incident_date.split("-").map(Number);
  const incident = new Date(y, m - 1, d);
  const ref = new Date(endedAt);
  if (Number.isNaN(ref.getTime())) return getDaysSinceIncident(player.incident_date);
  const end = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  return Math.max(0, Math.floor((end - incident) / 86400000));
}

function protocolHasEnded(player) {
  return player.status === "removed_early" || player.status === "cleared";
}

// Where people write to us. Two addresses because they carry different
// obligations: a data-rights request (access, erasure, objection) has a
// statutory one-month clock, and routing it to its own address keeps it
// from being buried among support email. Both deliver to the same
// mailbox; the split is about recognising what arrived.
const SUPPORT_EMAIL = "support@grtp.co.uk";
const PRIVACY_EMAIL = "privacy@grtp.co.uk";

const INJURY_ACTIVITY = { match: "Match", training: "Training", other: "Other" };
const INJURY_CARRIED_ON = { yes: "Yes, carried on", no: "No, stopped straight away", came_off_later: "Played on, came off later" };
const INJURY_ADVICE = { seek_medical: "Seek medical attention", rest: "Rest", none: "None needed" };
const INJURY_REFERRAL = {
  none: "Not referred", parent: "Handed over to parent or guardian", gp: "GP",
  ae_self: "A&E, made their own way", ambulance: "Ambulance called", admitted: "Admitted to hospital",
};
// Why a record is kept until the date it is kept until. The backend
// stores these as keys; a club reading its own record should not be
// shown "age_25_from_date_of_birth". The age_21 pair are the older basis
// these were recalculated from, and still appear on records not yet
// touched by that migration.
const RETENTION_BASIS = {
  age_25_from_date_of_birth: "Until their 25th birthday, from the date of birth held",
  age_25: "Until their 25th birthday, estimated from their age grade",
  age_25_assumed_minor: "Until their 25th birthday, assuming they were a minor",
  age_21_from_date_of_birth: "Until their 21st birthday, from the date of birth held",
  age_21: "Until their 21st birthday, estimated from their age grade",
  minimum_7_years: "Seven years from the incident, the minimum for an adult",
};
const label = (map, v) => (v ? (map[v] || v) : null);

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// Mirrors the backend's protocol.js exactly (see scheduler.js, which is
// the actual source of truth for a real player's current_stage). This
// copy is only ever used for the live day/stage preview when picking an
// incident date on a brand-new log — never for computing an existing
// player's displayed stage — but keeping the signature identical avoids
// the two silently drifting apart again if this ever gets reused.
function getCorrectStage(incidentDate, stage5Earliest = null) {
  const day = getDaysSinceIncident(incidentDate);
  // Days 0–1, 2–7, 8–14, 15–20, then 21 onwards: each stage's last day,
  // read from the shared stage data, exactly as protocol.js does.
  let stageByDay = STAGES.length - 1;
  for (let i = 0; i < STAGES.length; i++) {
    if (STAGES[i].dayEnd !== null && day <= STAGES[i].dayEnd) { stageByDay = i; break; }
  }

  if (stage5Earliest && stageByDay >= 3) {
    const earliest = new Date(stage5Earliest);
    const now = new Date();
    if (now < earliest) return 2;
  }

  return stageByDay;
}

// A copy of the stage update that staff can forward by hand — to a parent
// whose email bounced, for instance.
//
// It is deliberately the version anyone may receive. It used to include
// the symptoms recorded, address itself to every contact on the record
// including additional contacts who had not yet agreed to anything, and
// claim it had already been sent to all of them. None of that was true of
// the real emails: symptoms and incident notes go to the parent only, and
// an additional contact is sent nothing until they confirm. A copy button
// on a version that broke both promises invited staff to break them too.
function generateEmailDraft(player, organisationName) {
  const orgLabel = organisationName || "your school/club";
  const stage = STAGES[player.current_stage];
  const day = protocolDay(player);
  const nextStage = stage.id < 4 ? STAGES[stage.id + 1] : null;

  const isJustLogged = player.stageHistory && player.stageHistory.length === 1;
  const intro = player.paused
    ? `${player.name} has reported a return of concussion symptoms. They have been placed into a 48-hour rest period and will resume the protocol from their previous stage.`
    : player.status === "cleared"
    ? `${player.name} has reached the end of the Graduated Return to Play protocol's recommended 21-day schedule. This is NOT an automatic medical clearance — the decision to return to play must still be made by a qualified medical professional or your school/club's designated lead.`
    : isJustLogged
    ? `${player.name} has been placed on the Graduated Return to Play (GRTP) protocol due to a suspected concussion.`
    : `This is a GRTP status update for ${player.name}.`;

  return `Subject: GRTP Update — ${player.name} — ${stage.label}: ${stage.name}

Dear Parent/Guardian and Coaching Staff,

${intro}

CURRENT STATUS
--------------
Player:        ${player.name}
Age Grade:    ${player.age_grade || "—"}
Position:      ${player.position || "—"}
Incident Date: ${formatDate(player.incident_date)}
Protocol Day:  Day ${day}
Current Stage: ${stage.label} — ${stage.name}

GUIDANCE AT THIS STAGE
-----------------------
${stage.activities.map(a => `  - ${a}`).join("\n")}

${stage.notes}
${nextStage ? `\nNEXT STAGE\n----------\n${player.name} is expected to progress to ${nextStage.label} (${nextStage.name}) on Day ${nextStage.dayStart} of the protocol.\n` : ""}
IMPORTANT
---------
If ${player.name} experiences any return of concussion symptoms at any stage, they must stop all activity immediately and inform a member of staff.

For more information on concussion and the Graduated Return to Play process, please see the RFU's HEADCASE programme:
https://www.englandrugby.com/run/player-welfare/headcase

If you have any concerns, please contact ${orgLabel} directly.

Yours sincerely,
${orgLabel}`;
}

// ── Sub-components ──────────────────────────────────────────────

// ── Sidebar + AppShell ─────────────────────────────────────────
// Replaces the old top-bar Header. The AppShell wraps every authenticated
// view with a left sidebar on desktop and a slide-in drawer on mobile.
// Individual views render their content inside <AppShell> rather than
// placing <Header> at the top — the shell handles nav, the view handles
// content.

function Sidebar({ view, setView, orgName, orgLogoDataUrl, isOrgAdmin, pendingRegCount,
                   onLogout, onOpenRoster, onOpenStaff, onOpenActivity, onOpenSecurity,
                   onOpenRegistrations, onOpenAnalytics, onOpenMessages, messagesUnreadCount,
                   onOpenDataProtection, sidebarOpen, setSidebarOpen }) {
  function nav(v, handler) {
    if (handler) handler();
    else setView(v);
    setSidebarOpen(false);
  }

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={"grtp-mobile-overlay" + (sidebarOpen ? " open" : "")}
        onClick={() => setSidebarOpen(false)}
      />
      <aside className={"grtp-sidebar" + (sidebarOpen ? " open" : "")}>
        {/* Logo */}
        <div style={{ padding: "18px 18px 14px", borderBottom: "1px solid #e2eaf2" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LogoMark size={36} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#1a4a2e", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                GRTP
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#1a4a2e", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
                Tracker
              </div>
            </div>
          </div>
        </div>

        {/* Org name */}
        <div style={{ padding: "10px 18px 10px", borderBottom: "1px solid #e2eaf2", display: "flex", alignItems: "center", gap: 10 }}>
          {orgLogoDataUrl && (
            <img src={orgLogoDataUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "contain", flexShrink: 0, border: "1px solid #e2eaf2" }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "#7a95ad", marginBottom: 2 }}>Organisation</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3d5166", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orgName || "GRTP Tracker"}</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ padding: "10px 10px", flex: 1 }}>
          <button className={"grtp-nav-item" + (view === "dashboard" ? " active" : "")} onClick={() => nav("dashboard")}>
            <span className="grtp-nav-icon">⬛</span> Dashboard
          </button>
          <button className={"grtp-nav-item" + (view === "roster" ? " active" : "")} onClick={() => nav("roster", onOpenRoster)}>
            <span className="grtp-nav-icon">📋</span> Roster
          </button>
          {/* Not admin-only. The person who needs the accident book is
              usually the one who ran onto the pitch with the kit, and
              making them ask an admin for it is how a register stops
              being kept. */}
          <button className={"grtp-nav-item" + (view === "first_aid_log" ? " active" : "")} onClick={() => nav("first_aid_log")}>
            <span className="grtp-nav-icon">🩹</span> First Aid Log
          </button>
          <button className={"grtp-nav-item" + (view === "messages" ? " active" : "")} onClick={() => nav("messages", onOpenMessages)}>
            <span className="grtp-nav-icon">💬</span> Messages
            {messagesUnreadCount > 0 && <span className="grtp-nav-badge" style={{ background: "#c0272d" }}>{messagesUnreadCount}</span>}
          </button>

          {isOrgAdmin && (
            <button
              className={"grtp-nav-item" + (view === "registrations" ? " active" : "")}
              onClick={() => nav("registrations", onOpenRegistrations)}
            >
              <span className="grtp-nav-icon">📥</span> Registrations
              {pendingRegCount > 0 && <span className="grtp-nav-badge">{pendingRegCount}</span>}
            </button>
          )}
          {isOrgAdmin && (
            <button className={"grtp-nav-item" + (view === "staff" ? " active" : "")} onClick={() => nav("staff", onOpenStaff)}>
              <span className="grtp-nav-icon">👥</span> Staff
            </button>
          )}
          {isOrgAdmin && (
            <button className={"grtp-nav-item" + (view === "activity" ? " active" : "")} onClick={() => nav("activity", onOpenActivity)}>
              <span className="grtp-nav-icon">📜</span> Activity
            </button>
          )}
          {isOrgAdmin && (
            <button className={"grtp-nav-item" + (view === "analytics" ? " active" : "")} onClick={() => nav("analytics", onOpenAnalytics)}>
              <span className="grtp-nav-icon">📊</span> Analytics
            </button>
          )}
          {isOrgAdmin && (
            <button className={"grtp-nav-item" + (view === "dataprotection" ? " active" : "")} onClick={() => nav("dataprotection", onOpenDataProtection)}>
              <span className="grtp-nav-icon">⚖️</span> Data Protection
            </button>
          )}
          <button className={"grtp-nav-item" + (view === "security" ? " active" : "")} onClick={() => nav("security", onOpenSecurity)}>
            <span className="grtp-nav-icon">🔐</span> Security
          </button>
          {isOrgAdmin && (
            <button className={"grtp-nav-item" + (view === "branding" ? " active" : "")} onClick={() => nav("branding")}>
              <span className="grtp-nav-icon">🎨</span> Branding
            </button>
          )}
          <button className={"grtp-nav-item" + (view === "help" ? " active" : "")} onClick={() => nav("help")}>
            <span className="grtp-nav-icon">📖</span> Help
          </button>
          <SupportButton
            pageContext="Dashboard"
            renderTrigger={(openModal) => (
              <button className="grtp-nav-item" onClick={() => { setSidebarOpen(false); openModal(); }}>
                <span className="grtp-nav-icon">🛟</span> Support
              </button>
            )}
          />
        </nav>

        {/* Footer */}
        <div style={{ padding: "12px 10px", borderTop: "1px solid #e2eaf2" }}>
          <button
            onClick={onLogout}
            className="grtp-nav-item"
            style={{ color: "#7a95ad" }}
          >
            <span className="grtp-nav-icon">→</span> Sign out
          </button>
        </div>
      </aside>
    </>
  );
}

// The phone app's offer to turn notifications on, and the code that keeps
// them working once they are. Only on an iPhone that stays signed in
// (notifications go to that phone's record, so there must be one), and
// only until the person answers: iOS asks once, and a "Not now" here is
// remembered so the offer doesn't follow them round every screen.
let lastPushTokenSent = null;
function NotificationsOffer({ onOpenView }) {
  const [offer, setOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const deviceId = nativeApp.isNativeApp() ? nativeApp.savedDeviceId() : null;

  const sendToken = useCallback((token) => {
    const id = nativeApp.savedDeviceId();
    const key = id + ":" + token;
    if (!id || key === lastPushTokenSent) return; // every screen mounts this; once is enough
    lastPushTokenSent = key;
    api.setAppPushToken(id, token).catch(() => { lastPushTokenSent = null; }); // tried again on the next screen
  }, []);

  useEffect(() => {
    nativeApp.onNotificationOpen(onOpenView);
    return () => nativeApp.onNotificationOpen(null);
  }, [onOpenView]);

  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    nativeApp.notificationPermission().then((state) => {
      if (cancelled) return;
      if (state === "granted") nativeApp.startNotifications({ onToken: sendToken });
      else if (state === "prompt" && !nativeApp.notificationsOfferDeclined()) setOffer(true);
    });
    return () => { cancelled = true; };
  }, [deviceId, sendToken]);

  if (!offer) return null;

  async function turnOn() {
    setBusy(true);
    await nativeApp.startNotifications({ ask: true, onToken: sendToken });
    setBusy(false);
    setOffer(false);
  }
  function notNow() {
    nativeApp.declineNotificationsOffer();
    setOffer(false);
  }

  return (
    <div style={{ margin: "12px 16px 0", padding: "14px 16px", background: "#eef6f1", border: "1px solid #cfe4d7", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>Turn on notifications?</div>
      <div style={{ fontSize: 13, color: "#3d5166", lineHeight: 1.5, marginBottom: 10 }}>
        Find out when a parent messages you, or when a player you're named on moves stage.
        Notifications never show a player's name.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={turnOn} disabled={busy}
          style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          {busy ? "Turning on..." : "Turn on"}
        </button>
        <button type="button" onClick={notNow} disabled={busy}
          style={{ background: "none", color: "#3d5166", border: "1px solid #cfd9e3", borderRadius: 8, padding: "9px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          Not now
        </button>
      </div>
    </div>
  );
}

function AppShell({ children, view, setView, orgName, isOrgAdmin,
                    pendingRegCount, onLogout, onOpenRoster, onOpenStaff,
                    onOpenActivity, onOpenSecurity, onOpenRegistrations,
                    onOpenAnalytics, onOpenMessages, messagesUnreadCount,
                    onOpenDataProtection, pageTitle, pageActions }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Read directly from the api layer rather than threading as props
  // through every AppShell call site (there are close to a dozen) —
  // these values only ever change via the Branding settings screen,
  // which reloads the page after saving, so a fresh read here on mount
  // is always enough; no live-reactivity needed.
  const orgBrandColor = api.orgBrandColor();
  const orgLogoDataUrl = api.orgLogoDataUrl();
  // A tapped notification opens the screen it is about, the same way the
  // menu would.
  const openFromNotification = useCallback((target) => {
    if (target === "messages") {
      if (onOpenMessages) onOpenMessages();
      else setView("messages");
    } else {
      setView("dashboard");
    }
  }, [onOpenMessages, setView]);
  // Android's back button closes the side menu first, if it is open.
  useEffect(() => {
    if (!sidebarOpen) return undefined;
    nativeApp.onBackButton(() => { setSidebarOpen(false); return true; }, "overlay");
    return () => nativeApp.onBackButton(null, "overlay");
  }, [sidebarOpen]);
  return (
    <div className="grtp-shell" style={{ "--org-accent": orgBrandColor || "#1a4a2e" }}>
      <GlobalStyles />
      <Sidebar
        view={view} setView={setView} orgName={orgName} orgLogoDataUrl={orgLogoDataUrl}
        isOrgAdmin={isOrgAdmin} pendingRegCount={pendingRegCount}
        onLogout={onLogout} onOpenRoster={onOpenRoster}
        onOpenStaff={onOpenStaff} onOpenActivity={onOpenActivity}
        onOpenSecurity={onOpenSecurity} onOpenRegistrations={onOpenRegistrations}
        onOpenAnalytics={onOpenAnalytics} onOpenMessages={onOpenMessages} messagesUnreadCount={messagesUnreadCount}
        onOpenDataProtection={onOpenDataProtection}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
      />
      <div className="grtp-main">
        {/* Mobile top bar */}
        <div className="grtp-mobile-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            <LogoMark size={26} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {orgName || "GRTP Tracker"}
            </span>
          </div>
          {/* The glyph is the whole label, so without aria-label a screen
              reader announces the character itself — and on a phone this
              is the only way to reach the navigation at all. */}
          <button
            onClick={() => setSidebarOpen(v => !v)}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            aria-expanded={sidebarOpen}
            style={{ background: "none", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 6, padding: "6px 10px", fontSize: 16, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
          >
            <span aria-hidden="true">☰</span>
          </button>
        </div>
        {/* On a phone the page's own actions get their own row rather than
            competing with the menu button for a 52px bar. They used to sit
            inside it, and because they never shrink ("+ Log New Concussion"
            is a wide button that must not wrap), they pushed the menu clean
            off the right-hand edge — on the dashboard, which is where every
            session starts, the navigation could not be opened at all. */}
        {pageActions && (
          <div className="grtp-mobile-actions">{pageActions}</div>
        )}
        {nativeApp.isNativeApp() && <NotificationsOffer onOpenView={openFromNotification} />}
        {/* Desktop topbar */}
        <div className="grtp-topbar">
          <div style={{ fontWeight: 700, fontSize: 16, color: "#0d1a2a", letterSpacing: "-0.02em" }}>{pageTitle || "Dashboard"}</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {pageActions}
          </div>
        </div>
        <div className="grtp-content">
          {children}
        </div>
      </div>
    </div>
  );
}

// Legacy Header kept as a thin shim for public/auth screens that still
// use it (login, signup, parent tracking, legal pages etc.).
function Header({ onBack, onLogout, onManageStaff, onManageRoster, onManageSecurity, onManageActivity, orgName }) {
  return (
    <div style={{ background: "#1a4a2e", padding: "0 16px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto", display: "flex", alignItems: "center", gap: 8, minHeight: 58, padding: "8px 0", flexWrap: "wrap" }}>
        {onBack && (
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 20, padding: "0 4px 0 0", marginRight: 2, flexShrink: 0 }}>←</button>
        )}
        <LogoMark size={28} />
        <div style={{ minWidth: 0, overflow: "hidden" }}>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{orgName || "GRTP Tracker"}</div>
          <div style={{ color: "#c9991a", fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Graduated Return to Activity & Sport</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexShrink: 0 }}>
          {onLogout && (
            <button onClick={onLogout} style={{ background: "none", border: "1px solid #ffffff44", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 6, whiteSpace: "nowrap" }}>
              Log Out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ color, children }) {
  return (
    <span style={{ background: color + "22", color, border: `1px solid ${color}44`, borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
      {children}
    </span>
  );
}

// Whether the family has confirmed reading the first email about a
// concussion, or an injury shared with them (backend/acknowledgements.js).
// ack: { sharedAt, acknowledgedAt } or null when there is no family to ask.
// Waiting turns to overdue after a day: the cue to phone them. Only a
// live record warns; a finished one just shows the tick if it was given,
// so records from before this existed don't all turn red.
const ACK_OVERDUE_HOURS = 24;
function familyAckState(ack, { live = true } = {}) {
  if (!ack || !ack.sharedAt) return null;
  if (ack.acknowledgedAt) return "done";
  if (!live) return null;
  const hours = (Date.now() - new Date(ack.sharedAt).getTime()) / 3600000;
  return hours >= ACK_OVERDUE_HOURS ? "overdue" : "waiting";
}
function formatAckTime(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}
// What an admin can tell a parent when turning down a registration: a
// fixed list, matching REGISTRATION_REJECTION_REASONS on the server, which
// writes the sentence the parent actually reads. The admin's own note is
// never sent.
const PARENT_REJECTION_REASONS = [
  { value: "none",         short: "No reason given",        hint: "They're asked to contact you." },
  { value: "duplicate",    short: "Already registered",     hint: "They're told there's nothing more to do." },
  { value: "not_our_club", short: "Not one of our players", hint: "They're asked to check they used the right school or club's link." },
  { value: "incomplete",   short: "Details missing",        hint: "They're asked to fill the form in again, completing every section." },
  { value: "incorrect",    short: "Details incorrect",      hint: "They're asked to check their details and fill the form in again." },
];

// How each roster contact is named when a parent asks to change it.
const CONTACT_FIELD_LABELS = {
  parent_name: "Parent's name",
  parent_email: "Parent's email",
  school_coach_name: "School coach",
  school_coach_email: "School coach's email",
  school_coach_2_name: "Second school coach",
  school_coach_2_email: "Second school coach's email",
  club_coach_name: "Club coach",
  club_coach_email: "Club coach's email",
  club_coach_2_name: "Second club coach",
  club_coach_2_email: "Second club coach's email",
  other_contact_1_name: "Other contact",
  other_contact_1_email: "Other contact's email",
  other_contact_2_name: "Second other contact",
  other_contact_2_email: "Second other contact's email",
};

function AckBadge({ ack, live = true, who = "Parent" }) {
  const state = familyAckState(ack, { live });
  if (!state) return null;
  const look = {
    done:    { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", text: `✓ ${who} confirmed ${formatAckTime(ack.acknowledgedAt)}` },
    waiting: { color: "#92400e", bg: "#fef9ec", border: "#f0d88a", text: `Awaiting ${who.toLowerCase()} confirmation` },
    overdue: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca", text: `${who} not confirmed after 24h` },
  }[state];
  return (
    <span
      title={state === "overdue" ? "Not confirmed after a day: worth a phone call to check the family has seen it." : undefined}
      // Wraps rather than running off the edge on the narrowest phones,
      // where it sits indented under the player's name.
      style={{ display: "inline-block", maxWidth: "100%", boxSizing: "border-box", whiteSpace: "normal", lineHeight: 1.35, fontSize: 11.5, fontWeight: 600, color: look.color, background: look.bg, border: `1px solid ${look.border}`, borderRadius: 4, padding: "2px 7px" }}>
      {look.text}
    </span>
  );
}

// The family's side of the same thing, on the tracking link, the parent
// portal and the injury record. Unconfirmed, it is the first thing on the
// page (the reason the email asked them to open it); once confirmed it
// shows when, so they can see it was recorded. onConfirm returns the
// server's answer: { acknowledgedAt }.
function FamilyConfirmCard({ acknowledgedAt, onConfirm, prompt, buttonLabel = "I have read this" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmedAt, setConfirmedAt] = useState(acknowledgedAt || null);
  useEffect(() => { if (acknowledgedAt) setConfirmedAt(acknowledgedAt); }, [acknowledgedAt]);

  async function confirm() {
    setBusy(true);
    setError("");
    try {
      const r = await onConfirm();
      setConfirmedAt((r && r.acknowledgedAt) || new Date().toISOString());
    } catch (e) {
      setError(e.message || "That didn't go through. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (confirmedAt) {
    const d = new Date(confirmedAt);
    return (
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 18, color: "#15803d", fontWeight: 800 }}>✓</div>
        <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.5 }}>
          You confirmed you had read this on {d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at {d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}.
        </div>
      </div>
    );
  }
  return (
    <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#78350f", marginBottom: 6 }}>Please confirm you've read this</div>
      <p style={{ fontSize: 13, color: "#78350f", margin: "0 0 12px", lineHeight: 1.6 }}>{prompt}</p>
      <button
        onClick={confirm}
        disabled={busy}
        style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "12px 0", fontSize: 14, fontWeight: 700, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}
      >
        {busy ? "Recording..." : buttonLabel}
      </button>
      {error && <div role="alert" style={{ marginTop: 8, fontSize: 12.5, color: "#b91c1c" }}>{error}</div>}
    </div>
  );
}

// ── Message photo attachments ─────────────────────────────────────────
const MAX_MESSAGE_IMAGES = 4;
const MAX_MESSAGE_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MESSAGE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

// Reads a FileList picked for a message composer into data URLs the
// backend accepts directly. Enforces the same count/size/type limits the
// server enforces, so a rejection shows up immediately rather than after
// a round trip — the server still re-checks everything itself.
function readMessageImageFiles(fileList, currentCount) {
  const allFiles = Array.from(fileList);
  const room = Math.max(0, MAX_MESSAGE_IMAGES - currentCount);
  const files = allFiles.slice(0, room);
  if (files.length === 0) {
    return Promise.resolve({ images: [], error: `You can attach up to ${MAX_MESSAGE_IMAGES} photos per message.` });
  }
  let error = allFiles.length > files.length ? `Only the first ${room} photo${room === 1 ? "" : "s"} were added — ${MAX_MESSAGE_IMAGES} max per message.` : null;
  const results = [];
  let remaining = files.length;
  return new Promise((resolve) => {
    files.forEach(file => {
      if (!ALLOWED_MESSAGE_IMAGE_TYPES.includes(file.type)) {
        error = "Only JPEG, PNG, WebP or GIF images are supported.";
        if (--remaining === 0) resolve({ images: results, error });
        return;
      }
      if (file.size > MAX_MESSAGE_IMAGE_BYTES) {
        error = `Each photo must be under ${MAX_MESSAGE_IMAGE_BYTES / (1024 * 1024)}MB.`;
        if (--remaining === 0) resolve({ images: results, error });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        results.push({ dataUrl: reader.result });
        if (--remaining === 0) resolve({ images: results, error });
      };
      reader.onerror = () => {
        if (--remaining === 0) resolve({ images: results, error });
      };
      reader.readAsDataURL(file);
    });
  });
}

// Staff-side attachment thumbnail. Attachments are served behind the same
// x-auth-token header used by every other staff request, which a plain
// <img src> can't send — so this resolves the image via an authenticated
// fetch and displays the resulting blob URL instead.
function MessageAttachmentThumb({ attachmentId, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    api.fetchMessageAttachmentUrl(attachmentId).then(u => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      objectUrl = u;
      setUrl(u);
    }).catch(() => {});
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachmentId]);
  if (!url) {
    return <div style={{ width: 72, height: 72, borderRadius: 8, background: "#f3f4f6", flexShrink: 0 }} />;
  }
  return (
    <img
      src={url} alt="Attachment" onClick={() => onOpen(url)}
      style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid #e2eaf2", cursor: "pointer", flexShrink: 0 }}
    />
  );
}

function Notif({ msg, type, onDismiss }) {
  return (
    <div onClick={onDismiss} style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      background: type === "error" ? "#c0272d" : type === "warn" ? "#c9991a" : "#1a4a2e",
      color: "#fff", padding: "12px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600,
      boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 1000, maxWidth: 440, textAlign: "center", cursor: "pointer",
    }}>{msg}</div>
  );
}

function Sect({ title, subtitle, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ marginBottom: 14, borderBottom: "1px solid #f3f4f6", paddingBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#1a4a2e", letterSpacing: 0.8, textTransform: "uppercase" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4, textTransform: "none", letterSpacing: "normal", fontWeight: 400 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function TwoCol({ children }) {
  return <div className="grtp-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}

// The page-level actions ("+ Log an Injury", "+ Log New Concussion").
//
// These render into TWO places: the white desktop topbar, and the mobile
// action row. They used to be styled for the dark green mobile bar --
// white text on a transparent background with a barely-there white border
// -- which meant that on desktop "+ Log an Injury" was white text on a
// white topbar and could not be seen at all, on the dashboard and on the
// First Aid Log alike. Defined once here, for a light surface, so the two
// surfaces cannot drift apart again.
// Which text colour actually reads on a given background.
//
// The registration cards colour their Copy button from the card's own
// accent, and hardcoded white text on it. That is fine on the dark green
// and slate cards and wrong on the amber one, where white measured 2.61
// against the 4.5 a control's label needs. Choosing by luminance means a
// new accent — or an organisation's own brand colour, which these buttons
// also take — cannot quietly reintroduce it.
function readableOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return "#fff";
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return (1.05 / (L + 0.05)) >= ((L + 0.05) / 0.05) ? "#fff" : "#1b1400";
}

const ACTION_BTN_BASE = {
  borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};
const ACTION_BTN_PRIMARY = {
  ...ACTION_BTN_BASE,
  background: "var(--org-accent, #1a4a2e)", color: "#fff",
  border: "2px solid var(--org-accent, #1a4a2e)",
};
const ACTION_BTN_SECONDARY = {
  ...ACTION_BTN_BASE,
  background: "#fff", color: "var(--org-accent, #1a4a2e)",
  border: "2px solid var(--org-accent, #1a4a2e)",
};

// The GRTP Tracker mark: a rugby ball inside a progress ring coloured
// red → amber → green, the same colours used for the protocol stages
// throughout the app. Replaces the plain 🏉 emoji everywhere it was used
// as a brand mark. Renders as real SVG so it stays crisp at any size,
// from a small header icon up to the homepage lockup.
function LogoMark({ size = 32 }) {
  // The October 2026 GRTP Tracker mark, drawn to the measured proportions
  // of the logo artwork (also the app icon): a blue outer ring, a red,
  // green and yellow ring in three segments, and a rugby ball tilted 36°.
  // The same drawing is the favicon in public/index.html.
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ flexShrink: 0 }} aria-hidden="true">
      <circle cx="24" cy="24" r="23.6" fill="#011F12"/>
      <circle cx="24" cy="24" r="21.87" fill="none" stroke="#009AFE" strokeWidth="3.46"/>
      <g fill="none" strokeWidth="5.35">
        <path d="M25.22 8.47A15.58 15.58 0 0 1 37.82 31.19" stroke="#4BC936"/>
        <path d="M36.99 32.60A15.58 15.58 0 0 1 10.86 32.37" stroke="#FEC504"/>
        <path d="M10.06 30.95A15.58 15.58 0 0 1 23.59 8.43" stroke="#FC242D"/>
      </g>
      <g transform="rotate(-36 24 24)">
        <ellipse cx="24" cy="24" rx="10.23" ry="5.82" fill="#FEFDFE"/>
        <g stroke="#011F12" strokeLinecap="round">
          <line x1="17.6" y1="24" x2="30.4" y2="24" strokeWidth="1.15"/>
          <line x1="19.8" y1="22.1" x2="19.8" y2="25.9" strokeWidth="1.05"/>
          <line x1="22.6" y1="22.1" x2="22.6" y2="25.9" strokeWidth="1.05"/>
          <line x1="25.4" y1="22.1" x2="25.4" y2="25.9" strokeWidth="1.05"/>
          <line x1="28.2" y1="22.1" x2="28.2" y2="25.9" strokeWidth="1.05"/>
        </g>
      </g>
    </svg>
  );
}

// A member of staff's own switch for GRTP news. Service updates (new
// features, maintenance, policy changes) go to every member of staff
// regardless; this is only the optional news, which is marketing and so
// needs their say-so.
function NewsPreferencePanel({ showNotif }) {
  const [subscribed, setSubscribed] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAccountNews()
      .then(d => setSubscribed(d.subscribed))
      .catch(() => setSubscribed(null));
  }, []);

  async function toggle() {
    if (saving || subscribed === null) return;
    setSaving(true);
    try {
      const d = await api.setAccountNews(!subscribed);
      setSubscribed(d.subscribed);
      showNotif(d.subscribed ? "You'll get GRTP Tracker news." : "You won't get GRTP Tracker news.");
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  if (subscribed === null) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: "1 1 240px" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>GRTP Tracker news</div>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
            Occasional emails about new features and tips for using GRTP Tracker. Emails about changes to the service you use come either way.
          </p>
        </div>
        <button type="button" role="switch" aria-checked={subscribed} onClick={toggle} disabled={saving}
          style={{ background: subscribed ? "#1a4a2e" : "#fff", color: subscribed ? "#fff" : "#374151", border: `1px solid ${subscribed ? "#1a4a2e" : "#d1d5db"}`, borderRadius: 99, padding: "7px 16px", fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1, fontFamily: "inherit" }}>
          {subscribed ? "● On" : "○ Off"}
        </button>
      </div>
    </div>
  );
}

// Phones signed in to the GRTP Tracker app. Everyone sees their own; an
// admin sees the whole organisation's and can sign any of them out, which
// is what to do the moment a coach's phone is lost or stolen. Shown on
// the website too, since that is where an admin would go to do it.
function AppDevicesPanel({ isOrgAdmin, showNotif }) {
  const [devices, setDevices] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Signing a phone out is asked about first: it takes effect at once,
  // and a stray tap would sign a coach out in the middle of a session.
  const [confirmDevice, setConfirmDevice] = useState(null);

  const load = useCallback(async () => {
    try {
      setDevices(await (isOrgAdmin ? api.getOrgAppDevices() : api.getAppDevicesMine()));
    } catch {
      // MFA not yet set up blocks the admin view; fall back to their own.
      try { setDevices(await api.getAppDevicesMine()); } catch { setDevices([]); }
    }
  }, [isOrgAdmin]);
  useEffect(() => { load(); }, [load]);

  async function signOut(d) {
    setBusyId(d.id);
    try {
      if (d.userEmail !== undefined) await api.revokeOrgAppDevice(d.id);
      else await api.revokeOwnAppDevice(d.id);
      showNotif("That phone has been signed out of the app.");
      load();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  const live = (devices || []).filter((d) => d.active);
  // Nothing to say on the website until somebody actually uses the app:
  // an empty "phones signed in" panel would only raise the question
  // "what app?". Inside the app it always shows.
  if (!nativeApp.isNativeApp() && (devices === null || live.length === 0)) return null;
  const platformName = (p) => (p === "ios" ? "iPhone" : p === "android" ? "Android" : "Phone");
  const when = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginTop: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 6 }}>
        {isOrgAdmin ? "Phones signed in to the app" : "Your phones signed in to the app"}
      </div>
      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
        {isOrgAdmin
          ? "Staff who use the GRTP Tracker phone app can stay signed in with Face ID or a fingerprint. If a phone is lost or stolen, sign it out here and it stops working straight away."
          : "If you lose a phone you use for GRTP Tracker, sign it out here, or ask an admin at your organisation to."}
      </p>
      {devices === null ? (
        <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
      ) : live.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 13 }}>No phones are signed in.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {live.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, border: "1px solid #eef2f6", borderRadius: 8, padding: "10px 12px", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                  {d.label || platformName(d.platform)}{d.userEmail ? ` · ${d.userEmail}` : ""}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {platformName(d.platform)} · last used {when(d.lastUsedAt)}
                </div>
              </div>
              <button type="button" onClick={() => setConfirmDevice(d)} disabled={busyId === d.id}
                style={{ background: "#fff", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", opacity: busyId === d.id ? 0.6 : 1 }}>
                {busyId === d.id ? "Signing out..." : "Sign out"}
              </button>
            </div>
          ))}
        </div>
      )}
      {confirmDevice && (
        <ConfirmModal
          title="Sign this phone out?"
          message={`${confirmDevice.label || platformName(confirmDevice.platform)}${confirmDevice.userEmail ? ` (${confirmDevice.userEmail})` : ""} will stop working with GRTP Tracker straight away. To use the app on it again, they'll need to sign in with their password. Do this if the phone is lost, stolen or no longer used.`}
          confirmLabel="Sign phone out"
          danger
          onConfirm={async () => {
            const d = confirmDevice;
            setConfirmDevice(null);
            await signOut(d);
          }}
          onCancel={() => setConfirmDevice(null)}
        />
      )}
    </div>
  );
}

// A single, self-contained support/bug-report widget — manages its own
// open/closed state, so it can be dropped onto any screen (logged in or
// not) without that screen needing to know anything about it beyond
// rendering <SupportButton />.
function SupportButton({ pageContext, renderTrigger }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  function handleClose() {
    setOpen(false);
    setMessage("");
    setCategory("bug");
    setSent(false);
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.sendSupportRequest(category, message.trim(), pageContext);
      setSent(true);
    } catch (e) {
      setError(e.message || `Something went wrong sending this. Please try again, or email ${SUPPORT_EMAIL} directly.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setOpen(true))
      ) : (
        <button
          onClick={() => setOpen(true)}
          style={{ background: "none", border: "1px solid #ffffff44", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 6, whiteSpace: "nowrap" }}
        >
          🛟 Support
        </button>
      )}

      {/* Drawn on <body>, not where the button is. The button usually sits
          in the slide-out menu, and on a phone that menu is moved with a
          CSS transform, which turns position:fixed into "fixed to the
          menu": the form was squeezed into the menu's 224px strip and slid
          off-screen with it. */}
      {open && createPortal(
        <div className="grtp-sheet-backdrop" style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3000 }}>
          <div className="grtp-sheet" style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", maxHeight: "85vh", overflowY: "auto", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" }}>
            {sent ? (
              <>
                <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 10 }}>Thanks — that's been sent</div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, marginBottom: 20 }}>
                  We'll get back to you as soon as we can. If it's urgent, you can also email <strong>{SUPPORT_EMAIL}</strong> directly.
                </p>
                <button onClick={handleClose} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Close
                </button>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 4 }}>Contact Support</div>
                <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 18 }}>
                  Found a bug, or just have a question? Let us know below.
                </p>

                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={() => setCategory("bug")}
                    style={{ flex: 1, background: category === "bug" ? "#1a4a2e" : "#fff", color: category === "bug" ? "#fff" : "#374151", border: "1px solid " + (category === "bug" ? "#1a4a2e" : "#d1d5db"), borderRadius: 6, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    🐞 Report a Bug
                  </button>
                  <button
                    type="button"
                    onClick={() => setCategory("support")}
                    style={{ flex: 1, background: category === "support" ? "#1a4a2e" : "#fff", color: category === "support" ? "#fff" : "#374151", border: "1px solid " + (category === "support" ? "#1a4a2e" : "#d1d5db"), borderRadius: 6, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                  >
                    💬 General Question
                  </button>
                </div>

                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  autoFocus
                  rows={5}
                  placeholder={category === "bug" ? "What happened? What were you trying to do, and what went wrong?" : "What can we help with?"}
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", marginBottom: 12 }}
                />

                {error && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
                    {error}
                  </div>
                )}

                <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>
                  If you're logged in, we'll automatically include your account and organisation so we can help faster.
                </p>

                <div style={{ display: "flex", gap: 10 }}>
                  <button type="submit" disabled={submitting || !message.trim()} style={{ flex: 1, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting || !message.trim() ? 0.6 : 1 }}>
                    {submitting ? "Sending..." : "Send"}
                  </button>
                  <button type="button" onClick={handleClose} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function GlobalStyles() {
  return (
    <style>{`
      @import url('/fonts/app-fonts.css');
      *, *::before, *::after { box-sizing: border-box; }
        body, #root {
          font-family: 'Plus Jakarta Sans', -apple-system, system-ui, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        .grtp-shell { display: flex; min-height: 100vh; background: #f0f4f8; }
        .grtp-sidebar {
          width: 224px; flex-shrink: 0; background: #fff;
          border-right: 1px solid #e2eaf2; display: flex;
          flex-direction: column; position: sticky; top: 0;
          height: 100vh; overflow-y: auto; z-index: 200;
        }
        .grtp-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        .grtp-topbar {
          background: #fff; border-bottom: 1px solid #e2eaf2;
          padding: 0 24px; height: 52px; display: flex;
          align-items: center; gap: 12px;
          position: sticky; top: 0; z-index: 100; flex-shrink: 0;
        }
        .grtp-content { padding: 24px; flex: 1; }
        .grtp-nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 12px; border-radius: 8px; font-size: 13.5px;
          font-weight: 500; color: #3d5166; cursor: pointer;
          border: none; background: none; width: 100%; text-align: left;
          font-family: inherit; margin-bottom: 1px;
          transition: background 0.12s, color 0.12s;
        }
        .grtp-nav-item:hover { background: #f7fafc; color: #0d1a2a; }
        .grtp-nav-item.active { background: #e6f7f1; color: var(--org-accent, #1a4a2e); font-weight: 600; }
        .grtp-nav-icon { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; opacity: 0.65; }
        .grtp-nav-item.active .grtp-nav-icon { opacity: 1; }
        .grtp-nav-badge {
          margin-left: auto; background: var(--org-accent, #1a4a2e); color: white;
          font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 10px;
        }
        .grtp-mobile-bar {
          display: none; background: var(--org-accent, #1a4a2e); padding: 0 16px;
          height: 52px; align-items: center; justify-content: space-between;
          position: sticky; top: 0; z-index: 300; flex-shrink: 0;
        }
        .grtp-mobile-overlay {
          display: none; position: fixed; top: 0; right: 0; bottom: 0; left: 0;
          background: rgba(0,0,0,0.45); z-index: 250;
        }
        .grtp-mobile-actions { display: none; }
        /* iOS gives date and time inputs their own intrinsic width and
           centres their text, ignoring the width:100% every other field in
           the form honours — so on a phone the Date and Time boxes sat
           visibly wider than every control beneath them. */
        input[type="date"], input[type="time"] {
          -webkit-appearance: none; appearance: none;
          width: 100%; min-width: 0; box-sizing: border-box; text-align: left;
        }
        @media (max-width: 768px) {
          .grtp-shell { flex-direction: column; }
          /* Pinned to the top and bottom of what is actually visible,
             not 100vh: on an iPhone 100vh is taller than the screen, and
             with an admin's full menu Sign out, the last item, sat below
             the bottom edge where it could not be scrolled to. The extra
             bottom padding keeps it clear of the home bar. */
          .grtp-sidebar {
            position: fixed; top: 0; left: 0; bottom: 0;
            transform: translateX(-100%);
            transition: transform 0.22s ease;
            z-index: 400; height: auto;
            overscroll-behavior: contain;
            padding-bottom: env(safe-area-inset-bottom, 0px);
          }
          /* Open, the menu and its shade sit above the top bar and the
             page's action buttons, which otherwise covered the top of the
             menu and stayed tappable through the shade. Tapping the shade
             closes it. */
          .grtp-mobile-overlay { z-index: 390; }
          .grtp-sidebar.open { transform: translateX(0); }
          .grtp-mobile-bar { display: flex; }
          /* Clear of the notch, the rounded corners and the home bar in
             the phone app, which draws to the screen's edges. These are
             all 0 in a browser, so the website is unchanged. The top bar
             grows upwards behind the status bar, in its own colour. */
          .grtp-mobile-bar { box-sizing: content-box; padding-top: env(safe-area-inset-top, 0px); }
          .grtp-mobile-actions { top: calc(52px + env(safe-area-inset-top, 0px)) !important; }
          .grtp-sidebar { border-top: env(safe-area-inset-top, 0px) solid #1a4a2e; }
          .grtp-content { padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)) !important; }
          /* Light, matching the desktop topbar, because the buttons it
             holds are the same buttons that bar renders — one set of
             styles has to read correctly on both. */
          .grtp-mobile-actions {
            display: flex; gap: 8px; padding: 10px 16px;
            background: #fff; border-bottom: 1px solid #e2eaf2;
            position: sticky; top: 52px; z-index: 299;
          }
          /* Share the width evenly and let the labels shrink, so two
             actions fit a narrow screen instead of one running off it. */
          .grtp-mobile-actions > div { display: flex; gap: 8px; width: 100%; }
          .grtp-mobile-actions button { flex: 1 1 0; min-width: 0; }
          /* On the narrowest phones "+ Log New Concussion" no longer fits
             on one line; wrapping beats losing the end of the word. */
          @media (max-width: 370px) {
            .grtp-mobile-actions button { white-space: normal !important; line-height: 1.25; }
          }
          .grtp-mobile-overlay.open { display: block; }
          .grtp-content { padding: 16px; }
          .grtp-topbar { display: none; }
          .grtp-two-col { grid-template-columns: 1fr !important; }
          .grtp-symptom-grid { grid-template-columns: 1fr !important; }
          .grtp-stats-grid { grid-template-columns: 1fr 1fr !important; }
          /* Analytics: four (and three) figures across don't fit a phone.
             Two across; the odd one out in the row of three takes the
             full width rather than sitting alone beside a gap. */
          /* Pop-ups sit near the top on a phone, so the keyboard opening
             underneath doesn't cover what is being typed. */
          .grtp-sheet-backdrop { align-items: flex-start !important; padding: calc(env(safe-area-inset-top, 0px) + 32px) 12px 12px !important; }
          .grtp-sheet { padding: 20px 18px !important; }
          .grtp-kpi { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .grtp-kpi-3 > :first-child { grid-column: 1 / -1; }
          .grtp-log-tags { flex-direction: column; align-items: flex-end !important; gap: 4px !important; }
          .grtp-stat { padding: 14px 14px !important; }
          .grtp-guide-layout { flex-direction: column !important; gap: 16px !important; }
          .grtp-guide-toc { width: 100% !important; position: static !important; }
          .grtp-guide-content { padding: 16px !important; }
        }
        @media (max-width: 480px) {
          input, select, textarea { font-size: 16px !important; }
        }
        @media (hover: none) and (pointer: coarse) {
          button { min-height: 40px; }
          input[type="checkbox"] { width: 20px !important; height: 20px !important; }
        }
      `}</style>
  );
}

// ── Calendar ─────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function dateKeyFromISO(iso) {
  // Returns a plain "YYYY-MM-DD" key from any ISO date/datetime string,
  // ignoring time and timezone — used to group events by calendar day.
  return iso ? iso.split("T")[0] : null;
}

function addDaysToDateKey(dateKey, daysToAdd) {
  // Adds whole days to a "YYYY-MM-DD" key using plain calendar arithmetic
  // (not a timezone-aware instant), consistent with getDaysSinceIncident.
  const [year, month, day] = dateKey.split("-").map(Number);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + daysToAdd);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function buildCalendarEvents(players) {
  // Builds a map of "YYYY-MM-DD" -> array of { type, player } events.
  // type is one of: "logged", "cleared", "expected_cleared", "symptom_return"
  const events = {};

  function addEvent(dateKey, type, player) {
    if (!dateKey) return;
    if (!events[dateKey]) events[dateKey] = [];
    events[dateKey].push({ type, player });
  }

  players.forEach((p) => {
    const incidentKey = dateKeyFromISO(p.incident_date);
    addEvent(incidentKey, "logged", p);

    let alreadyClearedDateKey = null;
    (p.stageHistory || []).forEach((h) => {
      if (h.stage === 4 && !h.notes?.toLowerCase().includes("archived")) {
        const clearedKey = dateKeyFromISO(h.changed_at);
        addEvent(clearedKey, "cleared", p);
        alreadyClearedDateKey = clearedKey;
      }
      if (h.notes?.toLowerCase().includes("symptoms returned")) {
        addEvent(dateKeyFromISO(h.changed_at), "symptom_return", p);
      }
    });

    // For anyone still active (not yet cleared, not archived), show their
    // expected completion date — Day 21 from the incident date — even
    // though it's in the future. This is what makes upcoming completions
    // visible on the calendar, not just ones that have already happened.
    //
    // If the player is CURRENTLY paused (mid symptom-return rest), their
    // incident_date hasn't been shifted forward yet — that only happens
    // once the scheduler resolves the pause. So we add that pending 2-day
    // shift here too, otherwise the dot would show 2 days too early for
    // as long as the pause is still active.
    //
    // If stage5_earliest is set and falls AFTER the natural Day 21, the
    // expected completion moves to six days after stage5_earliest (they
    // spend Stage 5's full six days there before Stage 6).
    if (!alreadyClearedDateKey && !p.archived && p.status !== "removed_early" && incidentKey) {
      const pendingPauseShift = p.paused ? 2 : 0;
      // Day 21 is 21 days after the incident date: the injury is Day 0.
      let expectedKey = addDaysToDateKey(incidentKey, 21 + pendingPauseShift);

      // If stage5_earliest would push RTP past the natural Day 21, reflect that
      if (p.stage5_earliest) {
        const naturalDay21 = new Date(p.incident_date);
        naturalDay21.setDate(naturalDay21.getDate() + 21);
        const s5date = new Date(p.stage5_earliest);
        if (s5date > naturalDay21) {
          // Stage 6 earliest = stage5_earliest + Stage 5's length (Days
          // 15–20, six days), matching getCorrectStage in protocol.js,
          // which holds a player in Stage 5 for that long after the gate.
          // This used to add 7 and showed completion a day late.
          const stage5Days = STAGES[3].dayEnd - STAGES[3].dayStart + 1;
          const s6date = new Date(s5date);
          s6date.setDate(s6date.getDate() + stage5Days);
          expectedKey = dateKeyFromISO(s6date.toISOString());
          // Also mark stage5_earliest on the calendar
          addEvent(dateKeyFromISO(p.stage5_earliest), "stage5_gate", p);
        }
      }
      addEvent(expectedKey, "expected_cleared", p);
    }
  });

  return events;
}

const EVENT_STYLES = {
  logged:           { color: "#c0272d", label: "Concussion logged" },
  cleared:          { color: "#1a4a2e", label: "GRAS programme completed" },
  expected_cleared: { color: "#7ec98f", label: "Expected GRAS completion" },
  symptom_return:   { color: "#c9991a", label: "Symptoms returned" },
  stage5_gate:      { color: "#3b5bdb", label: "Stage 5 (Full Contact) earliest" },
};

function GRTPCalendar({ players, onSelectPlayer }) {
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const events = buildCalendarEvents(players);

  const { year, month } = viewDate;
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastOfMonth.getDate();
  // getDay() is 0=Sunday..6=Saturday; convert to a Monday-first index (0=Mon..6=Sun)
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7;

  // Anchored to the school's timezone (UK), matching the identical fix in
  // getDaysSinceIncident — otherwise "today" here could disagree with what
  // the rest of the app considers today, depending on the viewer's device.
  const todayInUK = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" }));
  const todayKey = `${todayInUK.getFullYear()}-${String(todayInUK.getMonth() + 1).padStart(2, "0")}-${String(todayInUK.getDate()).padStart(2, "0")}`;

  function goToMonth(delta) {
    setSelectedDay(null);
    setViewDate((v) => {
      let newMonth = v.month + delta;
      let newYear = v.year;
      if (newMonth < 0) { newMonth = 11; newYear -= 1; }
      if (newMonth > 11) { newMonth = 0; newYear += 1; }
      return { year: newYear, month: newMonth };
    });
  }

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedEvents = selectedDay
    ? events[`${year}-${String(month + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`] || []
    : [];

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button onClick={() => goToMonth(-1)} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#374151" }}>‹</button>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e" }}>{MONTH_NAMES[month]} {year}</div>
        <button onClick={() => goToMonth(1)} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: "#374151" }}>›</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {WEEKDAY_LABELS.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af", padding: "2px 0" }}>{d}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayEvents = events[dateKey] || [];
          const isToday = dateKey === todayKey;
          const isSelected = selectedDay === d;
          const uniqueTypes = [...new Set(dayEvents.map((e) => e.type))];

          return (
            <button
              key={i}
              onClick={() => dayEvents.length && setSelectedDay(isSelected ? null : d)}
              style={{
                background: isSelected ? "#1a4a2e11" : "transparent",
                border: isToday ? "1.5px solid #1a4a2e" : "1px solid transparent",
                borderRadius: 6,
                padding: "6px 0 8px",
                cursor: dayEvents.length ? "pointer" : "default",
                fontFamily: "inherit",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                minHeight: 38,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: isToday ? 800 : 500, color: isToday ? "#1a4a2e" : "#374151" }}>{d}</span>
              {uniqueTypes.length > 0 && (
                <span style={{ display: "flex", gap: 2 }}>
                  {uniqueTypes.map((t) => (
                    <span key={t} style={{ width: 6, height: 6, borderRadius: "50%", background: EVENT_STYLES[t].color, display: "inline-block" }} />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 14, marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6", flexWrap: "wrap" }}>
        {Object.entries(EVENT_STYLES).map(([key, s]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#6b7280" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.color, display: "inline-block" }} />
            {s.label}
          </div>
        ))}
      </div>

      {selectedDay && selectedEvents.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
            {MONTH_NAMES[month]} {selectedDay}, {year}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {selectedEvents.map((e, i) => (
              <button
                key={i}
                onClick={() => onSelectPlayer(e.player.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6,
                  padding: "8px 12px", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: EVENT_STYLES[e.type].color, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: "#374151" }}>
                  <strong>{e.player.name}</strong> — {EVENT_STYLES[e.type].label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// An email box that offers "Did you mean …@gmail.com?" once the person
// leaves it, for the slips that made parents' addresses bounce (gmial.com,
// hotmail.co.ukk). Only after they leave the box: mid-typing, "hotmail.co"
// is on its way to ".uk", not a mistake.
function EmailInput({ value, onChange, style, placeholder, autoComplete = "email", ariaLabel }) {
  const [left, setLeft] = useState(false);
  const suggestion = left ? suggestEmail(value) : null;
  return (
    <>
      <input
        type="email"
        value={value}
        onChange={e => { setLeft(false); onChange(e.target.value); }}
        onBlur={() => setLeft(true)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        style={style}
      />
      {suggestion && (
        <div role="status" style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #f0d88a", borderRadius: 6, padding: "6px 10px", margin: "4px 0 2px", lineHeight: 1.5, overflowWrap: "anywhere" }}>
          Did you mean <strong>{suggestion}</strong>?{" "}
          <button type="button" onClick={() => { onChange(suggestion); setLeft(false); }}
            style={{ background: "none", border: "none", padding: 0, color: "#1a4a2e", fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
            Yes, use that
          </button>
        </div>
      )}
    </>
  );
}

function ParentRegistrationPage({ token }) {
  const [screen, setScreen] = useState("landing"); // landing | form | thankyou
  const [orgName, setOrgName] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittedPlayerName, setSubmittedPlayerName] = useState("");
  const [parentEmailConfirm, setParentEmailConfirm] = useState("");
  const [form, setForm] = useState({
    playerName: "", ageGrade: "", position: "", dateOfBirth: "",
    parentName: "", parentEmail: "",
    schoolCoachName: "", schoolCoachEmail: "",
    schoolCoach2Name: "", schoolCoach2Email: "",
    clubCoachName: "", clubCoachEmail: "",
    clubCoach2Name: "", clubCoach2Email: "",
    otherContact1Name: "", otherContact1Email: "",
    otherContact2Name: "", otherContact2Email: "",
    consentGiven: false,
    alsoPlaysFor: false,
    linkedOrgs: [], // array of { id, name, org_type }
  });
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [addingOrgType, setAddingOrgType] = useState("");

  useEffect(() => {
    let mounted = true;
    api.getRegistrationOrgInfo(token)
      .then(d => { if (mounted) setOrgName(d.organisationName); })
      .catch(e => { if (mounted) setLoadError(e.message); });
    return () => { mounted = false; };
  }, [token]);

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  async function handleSubmit() {
    if (!form.playerName.trim()) return setSubmitError("Please enter the player's name.");
    if (!form.parentName.trim()) return setSubmitError("Please enter your name.");
    if (!form.parentEmail.trim()) return setSubmitError("Please enter your email address.");
    if (form.parentEmail.trim().toLowerCase() !== parentEmailConfirm.trim().toLowerCase()) {
      return setSubmitError("The two email addresses you typed don't match. Please check them both.");
    }
    if (!form.dateOfBirth) return setSubmitError("Please enter the player's date of birth.");
    if (!form.consentGiven) return setSubmitError("Please tick the box to confirm you've read how your child's information is used.");
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await api.submitParentRegistration(token, { ...form, linkedOrgIds: form.linkedOrgs.map(o => o.id) });
      if (mountedRef.current) {
        setSubmittedPlayerName(result.playerName || form.playerName);
        setScreen("thankyou");
      }
    } catch (e) {
      if (mountedRef.current) setSubmitError(e.message || "Something went wrong. Please try again.");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  const inp = {
    width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db",
    borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "inherit",
    marginBottom: 2,
  };

  const containerStyle = {
    fontFamily: "'Inter', system-ui, sans-serif",
    background: "#f9fafb", minHeight: "100vh", paddingBottom: 60,
  };
  const headerStyle = { background: "#1a4a2e", padding: "0 16px" };
  const headerInner = {
    maxWidth: 640, margin: "0 auto", display: "flex",
    alignItems: "center", gap: 14, height: 58,
  };
  const cardStyle = {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
    padding: 24, marginBottom: 16,
  };

  if (loadError) {
    return (
      <div style={containerStyle}>
        <GlobalStyles />
        <div style={headerStyle}><div style={headerInner}><LogoMark size={32} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>GRTP Tracker</div></div></div>
        <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
          <div style={{ fontWeight: 700, color: "#c0272d", fontSize: 16, marginBottom: 8 }}>Link Not Found</div>
          <p style={{ color: "#6b7280", fontSize: 14 }}>{loadError}</p>
          <p style={{ color: "#9ca3af", fontSize: 13 }}>Please check the link you were sent and try again, or contact your school or club directly.</p>
        </div>
      </div>
    );
  }

  if (screen === "thankyou") {
    return (
      <div style={containerStyle}>
        <GlobalStyles />
        <div style={headerStyle}><div style={headerInner}>
          <LogoMark size={28} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName || "GRTP Tracker"}</div>
            <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Player Registration</div>
          </div>
        </div></div>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 16px" }}>
          <div style={{ ...cardStyle, textAlign: "center", borderColor: "#bbf7d0", background: "#f0fdf4" }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 12px" }}>
              Thank you for registering {submittedPlayerName}
            </h2>
            <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.7, margin: "0 0 20px" }}>
              {submittedPlayerName}'s registration has been received by {orgName || "the school or club"} and will be reviewed shortly. You don't need to do anything else right now.
            </p>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 12 }}>About GRTP Tracker</div>
            <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: "0 0 12px" }}>
              GRTP Tracker helps {orgName || "schools and clubs"} manage England Rugby's Graduated Return to Activity and Sport (GRAS) programme after a suspected concussion: a staged return taking at least 21 days before match play, unless a doctor is overseeing the player's recovery.
            </p>
            <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: 0 }}>
              We hope the system is rarely needed for {submittedPlayerName}, but if it is, you'll receive regular updates at each stage of their recovery — and you'll be able to track their progress in real time via a secure link we'll send to your email address.
            </p>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 12 }}>Your privacy</div>
            <ul style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
              <li>Your data is stored securely in the EU (Amsterdam) and never sold to third parties.</li>
              <li>It will only be used to look after {submittedPlayerName} after a suspected concussion or injury, and to keep you informed at each stage.</li>
              <li>Anyone else you listed will be emailed to ask if they're happy to receive updates. Nothing is sent to them until they agree.</li>
              <li>Records are kept until {submittedPlayerName}'s 25th birthday, or seven years after any incident, whichever is later — then deleted automatically.</li>
              <li>You can ask for a copy, ask for corrections, or object to how the data is used at any time, by contacting {orgName || "the school or club"}.</li>
            </ul>
            <p style={{ fontSize: 13, color: "#9ca3af", margin: "12px 0 0", lineHeight: 1.6 }}>
              GRTP Tracker is operated by Sports Welfare Systems Ltd (Company No. 17341126). <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Privacy Policy</a> · <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Terms of Service</a>. ICO registration number ZC200130. Companies House No. 17341126.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "form") {
    return (
      <div style={containerStyle}>
        <GlobalStyles />
        <div style={headerStyle}><div style={headerInner}>
          <LogoMark size={28} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName || "GRTP Tracker"}</div>
            <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Player Registration</div>
          </div>
        </div></div>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "24px 16px" }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a4a2e", margin: "0 0 4px" }}>Register a Player</h2>
          <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 20px" }}>All fields marked * are required.</p>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 14 }}>Player details</div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Player name *</label>
            <input value={form.playerName} onChange={e => setForm(f => ({ ...f, playerName: e.target.value }))} placeholder="e.g. Tom Davies" style={inp} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Age grade</label>
                <select value={form.ageGrade} onChange={e => setForm(f => ({ ...f, ageGrade: e.target.value }))} style={{ ...inp, marginBottom: 0 }}>
                  <option value="">Select...</option>
                  {/* No "Adult": this link is for parents registering a
                      child; adult players register themselves on their own link. */}
                  {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19"].map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Position</label>
                <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="e.g. Flanker" style={{ ...inp, marginBottom: 0 }} />
              </div>
            </div>
            {/* Required, and the reason is given. Asking a parent for a
                child's date of birth without saying why is exactly the
                kind of unexplained collection that makes people uneasy —
                and here there is a good reason worth stating. */}
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Date of birth *</label>
              <input
                type="date"
                value={form.dateOfBirth}
                onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))}
                max={new Date().toISOString().split("T")[0]}
                style={{ ...inp, marginBottom: 2 }}
              />
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                Used to be certain {orgName || "your school or club"} has the right child if another club or school is
                also tracking them — two players sharing a name is common enough to matter — and to know exactly when
                their record should be deleted.
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 14 }}>Your details (parent / guardian)</div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Your full name *</label>
            <input value={form.parentName} onChange={e => setForm(f => ({ ...f, parentName: e.target.value }))} placeholder="e.g. Sarah Davies" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Your email address *</label>
            <EmailInput value={form.parentEmail} onChange={v => setForm(f => ({ ...f, parentEmail: v }))} placeholder="sarah@example.com" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Type your email address again *</label>
            {/* A second typing catches the slip in the name part that no
                check can (jon@ for john@). Not autofilled, or it would
                just copy the first. */}
            <input type="email" value={parentEmailConfirm} onChange={e => setParentEmailConfirm(e.target.value)} autoComplete="off" style={inp} />
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0", lineHeight: 1.5 }}>
              GRTP notifications about your child's recovery will be sent to this address, so please check it carefully.
            </p>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>Others to keep informed (optional)</div>
            {/* Only this organisation's own coaches are written to straight
                away; everyone else is asked first (nominated-contacts.js
                gatedSlotsForOrg). The form doesn't know which kind of
                organisation it belongs to, so it says it in those terms. */}
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.6 }}>
              {orgName || "Your school or club"}'s own coaches will be kept up to date. Anyone else you add, such as a coach at
              another club, school or academy, or a family member, will be emailed to ask if they're happy to receive updates.
              Nothing is sent to them until they agree, and we'll let you know if someone hasn't.
            </p>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>School coach name</label>
            <input value={form.schoolCoachName} onChange={e => setForm(f => ({ ...f, schoolCoachName: e.target.value }))} placeholder="e.g. Mr. Smith" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>School coach email</label>
            <EmailInput value={form.schoolCoachEmail} onChange={v => setForm(f => ({ ...f, schoolCoachEmail: v }))} placeholder="coach@school.co.uk" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Second school coach name (optional)</label>
            <input value={form.schoolCoach2Name} onChange={e => setForm(f => ({ ...f, schoolCoach2Name: e.target.value }))} placeholder="e.g. Ms. Jones" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Second school coach email</label>
            <EmailInput value={form.schoolCoach2Email} onChange={v => setForm(f => ({ ...f, schoolCoach2Email: v }))} placeholder="assistant@school.co.uk" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Club coach name</label>
            <input value={form.clubCoachName} onChange={e => setForm(f => ({ ...f, clubCoachName: e.target.value }))} placeholder="e.g. Dave Wilson" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Club coach email</label>
            <EmailInput value={form.clubCoachEmail} onChange={v => setForm(f => ({ ...f, clubCoachEmail: v }))} placeholder="coach@club.co.uk" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Second club coach name (optional)</label>
            <input value={form.clubCoach2Name} onChange={e => setForm(f => ({ ...f, clubCoach2Name: e.target.value }))} placeholder="e.g. Team manager" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Second club coach email</label>
            <EmailInput value={form.clubCoach2Email} onChange={v => setForm(f => ({ ...f, clubCoach2Email: v }))} placeholder="manager@club.co.uk" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Additional contact 1 name</label>
            <input value={form.otherContact1Name} onChange={e => setForm(f => ({ ...f, otherContact1Name: e.target.value }))} placeholder="e.g. Club welfare officer" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Additional contact 1 email</label>
            <EmailInput value={form.otherContact1Email} onChange={v => setForm(f => ({ ...f, otherContact1Email: v }))} style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Additional contact 2 name</label>
            <input value={form.otherContact2Name} onChange={e => setForm(f => ({ ...f, otherContact2Name: e.target.value }))} style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginTop: 8 }}>Additional contact 2 email</label>
            <EmailInput value={form.otherContact2Email} onChange={v => setForm(f => ({ ...f, otherContact2Email: v }))} style={inp} />
          </div>

          {/* Dual registration — does the player also belong to another org? */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 6 }}>Does your child also play for another club or school?</div>
            <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px" }}>
              If they do, naming them here means a suspected concussion recorded by either organisation is known to both — so
              nobody returns to play at one while still on a protocol at the other.
            </p>
            <p style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "9px 11px" }}>
              To set this up we will share your child's <strong>name, age grade and date of birth, and your contact
              details</strong> with the organisation you name, so they can match your child to their records or add them if
              they don't have them yet. Nothing else is shared, and no concussion details are shared until a link is in
              place and both organisations have confirmed it. You can withdraw this at any time from your parent portal.
            </p>
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {[["yes", "Yes"], ["no", "No"]].map(([val, label]) => (
                <button key={val} type="button"
                  onClick={() => { setForm(f => ({ ...f, alsoPlaysFor: val === "yes", linkedOrgs: [] })); setOrgSearch(""); setOrgResults([]); setAddingOrgType(""); }}
                  style={{ flex: 1, border: `2px solid ${form.alsoPlaysFor === (val === "yes") ? "#1a4a2e" : "#e5e7eb"}`, background: form.alsoPlaysFor === (val === "yes") ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "10px 0", fontSize: 14, fontWeight: form.alsoPlaysFor === (val === "yes") ? 700 : 500, color: form.alsoPlaysFor === (val === "yes") ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit" }}
                >
                  {label}
                </button>
              ))}
            </div>

            {form.alsoPlaysFor && (
              <div>
                {/* Already linked orgs */}
                {form.linkedOrgs.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {form.linkedOrgs.map(org => (
                      <div key={org.id} style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 8, padding: "9px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                        <div>
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1a4a2e" }}>✓ {org.name}</span>
                          <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8, textTransform: "capitalize" }}>{org.org_type}</span>
                        </div>
                        <button type="button"
                          onClick={() => setForm(f => ({ ...f, linkedOrgs: f.linkedOrgs.filter(o => o.id !== org.id) }))}
                          style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add another org picker */}
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                  {form.linkedOrgs.length === 0 ? "What type of organisation?" : "Add another organisation"}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  {[["school", "🏫 School"], ["club", "🏉 Club"], ["academy", "🌟 Academy"]].map(([val, label]) => (
                    <button key={val} type="button"
                      onClick={() => { setOrgSearch(""); setOrgResults([]); setAddingOrgType(val); }}
                      style={{ flex: 1, border: `2px solid ${addingOrgType === val ? "#1a4a2e" : "#e5e7eb"}`, background: addingOrgType === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "8px 4px", fontSize: 13, fontWeight: addingOrgType === val ? 700 : 500, color: addingOrgType === val ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {addingOrgType && (
                  <div>
                    <div style={{ position: "relative" }}>
                      <input
                        value={orgSearch}
                        onChange={async e => {
                          const q = e.target.value;
                          setOrgSearch(q);
                          if (q.trim().length < 2) { setOrgResults([]); return; }
                          setOrgSearching(true);
                          try {
                            const r = await api.searchOrgDirectory(addingOrgType, q);
                            const alreadyLinked = form.linkedOrgs.map(o => o.id);
                            if (mountedRef.current) setOrgResults((r.orgs || []).filter(o => !alreadyLinked.includes(o.id)));
                          } catch (_) { if (mountedRef.current) setOrgResults([]); }
                          finally { if (mountedRef.current) setOrgSearching(false); }
                        }}
                        placeholder={`Search for a ${addingOrgType}...`}
                        style={{ ...inp, paddingLeft: 36 }}
                      />
                      <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }}>🔍</span>
                    </div>
                    {orgSearching && <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>Searching...</div>}
                    {orgResults.length > 0 && (
                      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginTop: 6 }}>
                        {orgResults.map(org => (
                          <button key={org.id} type="button"
                            onClick={() => {
                              setForm(f => ({ ...f, linkedOrgs: [...f.linkedOrgs, { id: org.id, name: org.name, org_type: addingOrgType }] }));
                              setOrgSearch("");
                              setOrgResults([]);
                              setAddingOrgType("");
                            }}
                            style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: "#fff", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontSize: 13.5, fontFamily: "inherit", color: "#111827" }}
                          >
                            {org.name}
                            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{org.org_type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {orgSearch.trim().length >= 2 && !orgSearching && orgResults.length === 0 && (
                      <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>No results — the organisation may not be on GRTP Tracker yet.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ background: form.consentGiven ? "#f0fdf4" : "#fff", border: "2px solid " + (form.consentGiven ? "#86efac" : "#c0272d"), borderRadius: 10, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111827", marginBottom: 12 }}>How your child's information is used — please read carefully *</div>
            <p style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.7, margin: "0 0 12px" }}>
              Before you register, here is exactly what happens to your child's information:
            </p>
            <ul style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.8, margin: "0 0 14px 18px", padding: 0 }}>
              <li>Their name, date of birth, age grade, and any concussion symptoms are recorded in GRTP Tracker, operated by <strong>Sports Welfare Systems Ltd</strong> on behalf of {orgName || "the school or club"}, and stored securely in the EU. With your agreement at the time, any other injury treated at a session is recorded too</li>
              <li>The information is seen by <strong>{orgName || "the school or club"}</strong> and the coaching or welfare staff responsible for your child, to manage their safe return to rugby after a suspected concussion</li>
              <li>Automated email updates go to you at each stage of the return to play programme. Anyone else you've listed above receives a summary, but only once they've agreed to</li>
              <li>The record is kept until your child's <strong>25th birthday</strong>, or seven years after an incident, whichever is later — a claim about a childhood injury can be brought long after the injury itself, so the record protects your family as much as the club. It is then deleted automatically.</li>
            </ul>
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.65, margin: "0 0 12px" }}>
              {orgName || "The school or club"} keeps these records to meet its safeguarding duty of care, which is the legal basis for holding
              them. That means they are not deleted on request while that duty applies — but you keep every other right:
              you can ask for a copy, ask for corrections, object to how the information is used, or ask us to stop emailing
              any contact, at any time. Full detail is in our{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Privacy Policy</a>.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.consentGiven}
                onChange={e => setForm(f => ({ ...f, consentGiven: e.target.checked }))}
                style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
              />
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.6 }}>
                I am the parent or legal guardian of the player named above, and I have read and understood how their information will be used.
              </span>
            </label>
          </div>

          {submitError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 14, color: "#c0272d" }}>
              {submitError}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting || !form.consentGiven}
            style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: submitting || !form.consentGiven ? "default" : "pointer", opacity: submitting || !form.consentGiven ? 0.5 : 1, marginBottom: 12 }}
          >
            {submitting ? "Submitting..." : "Submit Registration"}
          </button>
          <button onClick={() => setScreen("landing")} style={{ width: "100%", background: "none", border: "1px solid #d1d5db", color: "#374151", borderRadius: 8, padding: "12px 0", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Back
          </button>
        </div>
      </div>
    );
  }

  // Screen 1 — landing / information page shown before the form
  return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}>
        <LogoMark size={28} />
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName || "GRTP Tracker"}</div>
          <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Player Registration</div>
        </div>
      </div></div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "28px 16px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 6px" }}>
          {orgName ? `${orgName} — Player Registration` : "Player Registration"}
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 24px", lineHeight: 1.6 }}>
          Please read the information below before completing the registration form for your child.
        </p>

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 10 }}>What is GRTP Tracker?</div>
          <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: "0 0 10px" }}>
            GRTP Tracker is a digital tool used by {orgName || "this school or club"} to manage England Rugby's <strong>Graduated Return to Activity and Sport (GRAS)</strong> programme after a suspected concussion: a staged return taking at least 21 days before match play, unless a doctor is overseeing the player's recovery.
          </p>
          <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, margin: 0 }}>
            If your child ever sustains a suspected concussion during training or a match, GRTP Tracker allows staff to log the incident and automatically keeps you informed at every stage of their recovery — via email notifications and a secure progress link sent directly to you.
          </p>
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 10 }}>What data do we collect?</div>
          <ul style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
            <li>Your child's name, date of birth, age grade, and playing position</li>
            <li>Your name and email address as parent or guardian</li>
            <li>Details of any suspected concussion — symptoms, date, and recovery progress</li>
            <li>With your agreement at the time, a record of any other injury treated at a session</li>
            <li>Names and email addresses of anyone you ask us to keep informed, and any other club or school you link</li>
          </ul>
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 10 }}>Who sees it?</div>
          <ul style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
            <li>You, as parent or guardian — via email notifications and a secure tracking link</li>
            <li>Authorised staff at {orgName || "the school or club"} — coaches, safeguarding leads, and PE staff</li>
            <li>Anyone else you ask us to keep informed, once they have agreed to receive updates</li>
            <li>Another club or school your child plays for, if you link them</li>
          </ul>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "12px 0 0", lineHeight: 1.5 }}>
            Your data is never sold, never used for marketing, and never shared beyond those listed above (and the companies that host and email it for us, under contract).
          </p>
        </div>

        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 10 }}>Your rights</div>
          <ul style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, margin: 0, paddingLeft: 20 }}>
            <li><strong>Stop updates</strong> to anyone you've added, or remove a link to another club or school, at any time</li>
            <li><strong>Ask for a copy</strong> of your child's records, or for anything wrong to be corrected</li>
            <li><strong>Object</strong> to how the information is used</li>
          </ul>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "12px 0 0", lineHeight: 1.5 }}>
            Concussion records are kept to meet {orgName || "the school or club"}'s safeguarding duty, so they can't be deleted early on request. The{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Privacy Policy</a> explains why, and how long they are kept.
          </p>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "12px 0 0", lineHeight: 1.5 }}>
            GRTP Tracker is operated by <strong>Sports Welfare Systems Ltd</strong> (Company No. 17341126). Data queries: <strong>{PRIVACY_EMAIL}</strong>. Registered with the ICO (ZC200130).
          </p>
        </div>

        <button
          onClick={() => setScreen("form")}
          style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "14px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}
        >
          Continue to Registration Form →
        </button>
        <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", lineHeight: 1.5, margin: 0 }}>
          Before you submit, you'll be asked to confirm you've read how your child's information is used.
        </p>
      </div>
    </div>
  );
}


// ── Legal pages ─────────────────────────────────────────────────────────
// Served from the React app at /privacy, /terms, /dpa so they're always
// in sync with the deployed version and need no separate homepage deploy.
const LEGAL_CONTENT = {
  privacy: { title: "Privacy Policy", version: "Version 3.12 · November 2026" },
  terms:   { title: "Terms of Service", version: "Version 2.2 · October 2026" },
  dpa:     { title: "Data Processing Agreement", version: "Version 3.3 · October 2026" },
  apd:     { title: "Appropriate Policy Document", version: "Version 1.3 · October 2026" },
};

const LEGAL_HTML = {
  privacy: `<p>This policy explains how personal data is handled when GRTP Tracker is used. GRTP Tracker is operated by Sports Welfare Systems Ltd, a company incorporated in England and Wales. It is written in plain English because most of the people it affects are parents, and parents deserve to understand exactly what happens with their child's information.</p>
<p><em>ICO registration number: ZC200130. Registered in England and Wales, Companies House No. 17341126. Registered office: 9 Whitfield Avenue, Paddington, Warrington WA1 3NF.</em></p>
<h2>1. Who does what with your data</h2>
<p>Two organisations are involved in handling a player's data, and the law gives them different roles:</p>
<p><strong>Your school or club is the data controller.</strong> They decide to use GRTP Tracker, they (or you, via the registration form) enter the data, and they are your first point of contact for questions, corrections, and objections.</p>
<p><strong>Sports Welfare Systems Ltd is the data processor.</strong> We store and process the data on the school or club's behalf, only on their instructions, and we never use it for any other purpose. We do not sell data, we do not use it for advertising, and we do not share it with anyone except as described in this policy.</p>
<p>For staff account data (staff names, email addresses, and login records, including the phones staff stay signed in on and the browsers and phones they have signed in from) and billing information, Sports Welfare Systems Ltd acts as a data controller.</p>
<h2>2. What data is collected</h2>
<p>About players (children):</p>
<ul>
<li><p>Name, age grade, team, and playing position</p></li>
<li><p>Date of birth. Asked for on the registration form because it is the only reliable way to be certain a school and a club are tracking the same child where two players share a name, and because it fixes exactly when the record falls due for deletion. It is used for nothing else. Players added before this was collected, or imported from a school or club's own records, may not have one; where it is absent the age grade is used instead.</p></li>
<li><p>Details of a suspected concussion: the incident date, a description of what happened, the context and mechanism of the injury, and reported symptoms</p></li>
<li><p>Progress through the Graduated Return to Play protocol, including stage dates and outcome (completed, or removed early with the reason recorded)</p></li>
</ul>
<p>About parents, guardians, and contacts (adults):</p>
<ul>
<li><p>Parent or guardian name and email address</p></li>
<li><p>School coach, club coach, and up to two additional contacts &mdash; names and email addresses</p></li>
<li><p>Records of when the parent was informed about how the data would be used, which version of that wording was shown, and &mdash; for the online registration form &mdash; the internet address (IP address) the form was submitted from</p></li>
<li><p>Whether, and when, the parent (or a player who is an adult) confirmed reading the first email about a concussion, and whether that was on their tracking link or in the parent portal; whether a reminder was sent; and whether notification emails were delivered (see section 6)</p></li>
</ul>
<p><strong>Messages and photographs.</strong> GRTP Tracker includes a private messaging area where a parent and the school or club's staff can exchange messages about their child's recovery. We store the text of those messages, who sent each one and when, and any photographs attached to them. Parents choose what to write and what to attach; because these messages concern a child's recovery, they may contain health information and images of a child. Message photographs are deleted automatically one year after they are sent; the messages themselves are kept for as long as the concussion records the conversation relates to, and are then deleted automatically (see section 7).</p>
<p><strong>Injuries and first aid.</strong> A school or club can also keep a first aid log: a record of injuries and of the first aid it gave, for its own players and for anyone else it treats &mdash; a visiting player or a spectator, for example. For each entry we store:</p>
<ul>
<li><p>The person's name, and their age grade and team where they are on the roster. For somebody who is not, their date of birth if it is given, or otherwise whether they are under 18, so that the record is kept for the right length of time</p></li>
<li><p>The date and time, the activity, the part of the body, what the injury looked like (chosen from a fixed list &mdash; for example a sprain, a suspected fracture, an asthma attack, or an allergic reaction), whether it involved contact, and a short description</p></li>
<li><p>Whether the person carried on, the first aid given, the advice given and whether it was accepted, and where they were referred on to &mdash; for example to a parent, a GP, A&amp;E, or an ambulance</p></li>
<li><p>Anything learned later that staff choose to add: whether the person was admitted to hospital, what the injury turned out to be, and the date they returned to play</p></li>
<li><p>Who agreed to the injury being recorded, when, and which version of the wording they agreed to; and, separately for each person it was sent to, when that was agreed</p></li>
<li><p>Where the record was sent to the injured person or their family, whether and when they confirmed reading it, and whether a reminder was sent</p></li>
</ul>
<p>The first aid log is a record of what happened and what was done. Unlike a concussion record it has no stages, and nothing in it changes on its own. Where an injury is to the head, staff are offered the option of starting the concussion protocol; it is never started automatically.</p>
<p>Suspected concussion information and injury records are health data. The law calls this special category data and gives it the strongest protection. We treat it accordingly.</p>
<p><strong>Staff who use the phone app.</strong> Staff can install the GRTP Tracker app on their phone and choose to stay signed in, unlocking it with Face ID or a fingerprint. For each phone set up this way we store whether it is an iPhone or an Android phone, the make and model the phone reports, when it was set up and last used, and, if it has been signed out, when and why. This lets staff and their administrator see which phones can open the account, and sign out one that is lost. The phone keeps a sign-in key in its own secure storage; we hold only a scrambled (hashed) copy of it that cannot be turned back into the key. Face ID and fingerprints are checked by the phone itself: we never receive or store a face, a fingerprint, or anything derived from them. The app does not use the phone&rsquo;s location, contacts, or advertising identifier.</p>
<p><strong>Notifications on the phone app.</strong> If a member of staff turns notifications on, we also store the address that phone is given for delivering them. On an iPhone, Apple Inc. delivers them; on an Android phone, Google LLC does, through its Firebase Cloud Messaging service. A notification only says that something has happened &mdash; for example that a parent has sent a message, or that a player has moved to a new stage &mdash; and never contains a player&rsquo;s name or team, the details of an injury, or the text of a message, so nothing Apple or Google handles identifies a player. Apple and Google are US companies, so the phone&rsquo;s notification address may be processed in the United States; each provides safeguards for such transfers in its terms. The address is removed when the phone is signed out, and deleted with the rest of that phone&rsquo;s record.</p>
<h2>3. Why the data is used, and the lawful basis</h2>
<p>Schools and clubs have a duty of care towards the children in their charge. Concussion records exist to discharge that duty. We do not rely on consent as the legal basis for holding them, and we want to be clear about why.</p>
<p>Consent, in data protection law, must be freely given and as easy to withdraw as it was to give. A parent whose child cannot safely play unless a record is kept is not making a free choice, and a record that vanished the moment consent was withdrawn would fail exactly when a child needed it most. Relying on consent would therefore be both legally fragile and unsafe.</p>
<p>Instead, the school or club relies on its safeguarding duty. That does not reduce your rights: you are still told exactly what is held, who sees it, and for how long, and you can still ask for a copy, ask for corrections, and object &mdash; see section 8.</p>
<table>
<tbody>
<tr class="odd">
<td><strong>Purpose</strong></td>
<td><strong>Lawful basis</strong></td>
</tr>
<tr class="even">
<td>Recording a suspected concussion and managing the player's return-to-play schedule</td>
<td>The controller's legitimate interests in meeting its duty of care and safeguarding obligations (UK GDPR Article 6(1)(f)), or its public task where the controller is a school (Article 6(1)(e)). For the health data itself: substantial public interest &mdash; safeguarding of children and of individuals at risk (Article 9(2)(g), and Schedule 1, Part 2, paragraph 18 of the Data Protection Act 2018). Our Appropriate Policy Document, required for that condition, is published alongside this policy.</td>
</tr>
<tr class="odd">
<td>Sending automated email updates to the parent or guardian, and to the school or club's own coaching and welfare staff responsible for the player</td>
<td>The same basis. Keeping the people responsible for a child informed is part of the safeguarding purpose, not separate from it.</td>
</tr>
<tr class="even">
<td>Sending updates to additional contacts who are neither the parent nor the school or club's own staff, and sharing a concussion alert with a second organisation where a player is registered with both (see section 4)</td>
<td>Consent of the parent or guardian (Articles 6(1)(a) and 9(2)(a)). These are genuinely optional, so they genuinely depend on consent, and can be stopped at any time on request. For an additional contact, the parent's nomination is not treated as sufficient on its own: we write to that person first, explaining who named them and what would be sent, and nothing about a concussion is sent to them unless they confirm. They can remove themselves at any time, in one click, without asking anyone.</td>
</tr>
<tr class="odd">
<td>Recording an injury, or the first aid given, in the first aid log</td>
<td>Consent of the parent or guardian where the injured person is a child, or of the person themselves where they are an adult (Articles 6(1)(a) and 9(2)(a)). Staff must confirm that this agreement has been given before an entry can be saved, and the confirmation is stored with the date, time, and version of the wording.</td>
</tr>
<tr class="even">
<td>Sending an injury record to anyone</td>
<td>A separate consent, given for each person it is sent to (Articles 6(1)(a) and 9(2)(a)). Agreeing to an injury being recorded is not agreeing to it being shared, and agreeing to it being sent to one person does not cover anyone added later. Contacts who receive concussion updates are asked separately before they are sent anything about other injuries, and anybody who has declined is never written to.</td>
</tr>
<tr class="odd">
<td>The private messaging area between a parent and staff</td>
<td>The same safeguarding basis as above. Messages are initiated by the people involved and are used only to support the player's recovery.</td>
</tr>
<tr class="even">
<td>Keeping concussion and injury records for a fixed period after an incident</td>
<td>The controller's safeguarding record-keeping obligations, and the establishment, exercise, or defence of legal claims (Article 9(2)(f)). This is why withdrawing consent to an injury record stops it being sent to anyone further, but does not delete it before the end of the period in section 7: it is evidence of what happened and what was done, for the same reason a concussion record is.</td>
</tr>
<tr class="odd">
<td>Operating staff accounts and billing</td>
<td>Performance of a contract with the school or club (Article 6(1)(b)).</td>
</tr>
<tr class="even">
<td>Emailing staff about the service they use: a new feature, the phone app, planned maintenance, or a change to this policy or our terms</td>
<td>Our legitimate interests in running the service and keeping the people who use it informed (Article 6(1)(f)). These emails never contain prices or offers. They go only to staff, never to parents or contacts.</td>
</tr>
<tr class="odd">
<td>Sending GRTP Tracker news (new features and tips) to anyone who asks for it</td>
<td>Consent (Article 6(1)(a)), given by ticking a box or turning news on under Security. Every news email has a one-click unsubscribe link, and staff can turn it off under Security at any time. We never add anyone to it from a school's or club's records.</td>
</tr>
</tbody>
</table>
<p>How parents are informed. GRTP Tracker supports two routes, and each one is recorded with a timestamp and the exact version of the wording that was shown:</p>
<ul>
<li><p>Parent registration form: the school or club shares a secure link; the parent completes the form themselves, reads a plain-English explanation of what is recorded, who sees it, what is sent, and how long it is kept, and confirms they have read and understood it. The date, time, wording version, and submitting IP address are stored.</p></li>
<li><p>Staff attestation: where a concussion is logged directly by staff, the staff member must confirm that the parent or guardian has been informed of those same four things before the record can be saved. The confirming staff member's email address, the date and time, and the wording version are stored on the record. Responsibility for the accuracy of that attestation rests with the school or club.</p></li>
</ul>
<h2>4. Who can see the data</h2>
<ul>
<li><p>Authorised staff at your school or club &mdash; the people with a coaching, PE, or safeguarding responsibility whom the organisation's administrator has invited.</p></li>
<li><p>You, as parent or guardian &mdash; via email updates, a private tracking link showing your child's live progress, and a private portal where you can message staff.</p></li>
<li><p>The contacts named on the player's record &mdash; school coach, club coach, and any additional contacts &mdash; who receive stage-by-stage email updates. These are deliberately narrower than the parent's: they say that a protocol is running, which stage it has reached, and what the player may and may not do, together with the signs that need urgent medical attention. They do <strong>not</strong> contain the symptoms recorded at the time, or the notes staff wrote about the incident. Those go only to the parent or guardian (or to an adult player themselves).</p></li>
</ul>
<p><strong>Injury records.</strong> Staff at your school or club can see its first aid log. Nobody outside it is told about an injury unless staff choose to tell them and the separate agreement described in section 3 has been given. When somebody is told, the email itself contains no name and nothing about the injury &mdash; only a link &mdash; and they must enter a code sent to their email address before the link shows anything. Everyone who opens it then sees the injured person's name and the date. Beyond that, what they see depends on who they are:</p>
<ul>
<li><p>The parent or guardian, or an adult who was injured: the full entry &mdash; what happened, what the injury looked like, the part of the body, the first aid given, and the advice given. Their link does not expire.</p></li>
<li><p>A contact who has agreed to receive welfare updates, or one of the school or club's own coaches: what the injury looked like and the part of the body, and nothing about treatment or advice.</p></li>
<li><p>An address typed in by staff that nobody has yet confirmed: nothing more than the name and date, and who to contact.</p></li>
</ul>
<p>Every link apart from the parent's stops working after 28 days. Where a player's school and club are linked (see below) and staff choose to share an injury with the other organisation, that organisation sees the name, the date, what the injury looked like, and the part of the body, in its own first aid log, and nothing more.</p>
<p><strong>Isolation between organisations.</strong> Each organisation's data is held separately, and staff at one school or club cannot browse, search, or open another organisation's records. There is one deliberate exception, described next.</p>
<p><strong>Dual registration.</strong> Many players are registered with both a school and a club. Where a parent has asked for those two organisations to be linked for their child, and both have approved the link, a concussion recorded by one organisation triggers an alert to the other. That alert tells the second organisation the player's name, that a concussion has been recorded, and which stage of the protocol they are at &mdash; so a child is not sent out to play at their club on Sunday after being concussed at school on Friday. It does not give the second organisation access to the first organisation's records. This linking is optional, relies on the parent's consent, is tied to a specific named child rather than to their name as text, and can be removed at any time on request.</p>
<p><strong>What a link means for you as a parent.</strong> The restriction above protects your child from the organisations; it does not apply to you. Once a link is approved, your parent portal shows what <em>each</em> linked organisation holds about your child &mdash; their protocol, their record of it, and your messages with their staff &mdash; from the single link you already have, rather than requiring one link per organisation. You choose which organisation you are writing to, and a message to one is not visible to the other. If the link is removed, or an organisation's subscription ends, that organisation drops out of your portal immediately.</p>
<h2>5. Who we share data with (our sub-processors)</h2>
<p>We use a small number of service providers to run GRTP Tracker. We have data processing terms in place with each of them.</p>
<table>
<tbody>
<tr class="odd">
<td><strong>Provider</strong></td>
<td><strong>What they do, and where</strong></td>
</tr>
<tr class="even">
<td>Railway Corporation<br />548 Market St PMB 68956, San Francisco, California 94104, USA</td>
<td>Hosts the application and the database. Our database and application are deployed in Railway's EU West region (Amsterdam, Netherlands), so player data is stored in the European Economic Area. Railway is a US company and its own operations, including support and system administration, take place in the United States. Railway's underlying infrastructure runs on Google Cloud Platform.</td>
</tr>
<tr class="odd">
<td>Plus Five Five, Inc., trading as Resend<br />2261 Market Street #5039, San Francisco, California 94114, USA</td>
<td>Delivers our notification emails. Processes recipient email addresses and message content in order to send them, and stores delivery logs in the United States.</td>
</tr>
<tr class="odd">
<td>Backblaze, Inc.<br />2261 Market Street STE 81006, San Francisco, California 94114, USA</td>
<td>Stores the off-site copy of our nightly backup, in Backblaze's data centre in Amsterdam, Netherlands. The copy is encrypted before it leaves our servers with a key Backblaze does not hold, so Backblaze cannot read it. Each copy is deleted automatically 30 days after it is made.</td>
</tr>
<tr class="even">
<td>Zoho Corporation Limited<br />Suite B, 3rd Floor, CBX II East, 414&ndash;432 Midsummer Boulevard, Milton Keynes MK9 2EA, United Kingdom</td>
<td>Hosts our own email, in Zoho's EU data centre, including the support and privacy addresses. It therefore holds whatever parents, schools, and clubs write to us, which can include information about a child.</td>
</tr>
</tbody>
</table>
<p><strong>International transfers.</strong> Player data, backups, and our email are all stored in the European Economic Area, which the UK recognises as providing an adequate level of protection. However, Railway, Resend, and Backblaze are US companies, and some processing &mdash; support access, system administration, and email delivery logs &mdash; takes place in the United States. For Railway and Resend, those transfers are made under safeguards recognised by UK law: the UK International Data Transfer Addendum to the EU Standard Contractual Clauses, and, where the provider is certified, the UK Extension to the EU&ndash;US Data Privacy Framework. Backblaze's data processing agreement incorporates Standard Contractual Clauses, and because backups are encrypted with a key Backblaze does not hold, nothing it could access is readable. Our email is provided under a data processing agreement with Zoho's UK company, and any access to it from outside the UK and the European Economic Area is governed by that agreement. An up-to-date list of sub-processors is available on request.</p>
<p><strong>Billing.</strong> Card payments from schools and clubs are handled by Stripe. Card details are entered directly with Stripe and never reach our systems. No player data is shared with Stripe.</p>
<p><strong>Backups.</strong> A snapshot of the database is taken nightly. A compressed copy is kept on the same server for 14 days for fast recovery, and a second copy is stored with Backblaze, in its European data centre in Amsterdam, so that records survive the loss of the server itself. The off-site copy is encrypted with AES-256-GCM before it leaves our servers and the key is held separately, so it cannot be read by anyone who obtains it &mdash; including Backblaze. Our servers can add a backup but cannot read or delete one, and each off-site copy is deleted automatically 30 days after it is made.</p>
<h2>6. Email delivery tracking</h2>
<p>Because these notifications carry safeguarding significance, the system records whether each notification email was sent, delivered, or bounced. This creates an auditable record that parents and contacts were actually sent the information &mdash; or flags to staff that a message failed so they can follow up another way. That information comes from the email system itself. We do not track whether you open an email or click a link in it. Where it matters that a family has seen something &mdash; the first email about a concussion, and an injury record sent to them &mdash; they are asked instead to open it and confirm they have read it. The date and time they confirmed, and where (their tracking link, the parent portal, or the injury record), are kept with the record, and the school or club can see them. If nothing is confirmed after a day, one reminder email is sent; no further reminders follow. Later updates, such as a move to the next stage, do not ask for confirmation.</p>
<h2>7. How long data is kept</h2>
<ul>
<li><p><strong>Concussion and GRTP records for a child: until the player's 25th birthday, or seven years from the date of the incident, whichever is later.</strong> This matches the period for which a health record about a child is ordinarily kept &mdash; seven years past the age of 18. The limitation period for a claim about a childhood injury does not begin until the individual turns 18, and where the effects of an injury only become apparent later it can begin later still, so the record is the family's evidence as much as the club's and destroying it earlier would harm both. Where a date of birth has been given the date is exact; otherwise the player's approximate age is worked out from their age grade, erring on the side of keeping the record longer. Records are then deleted automatically.</p></li>
<li><p>Concussion records for an adult player: seven years from the date of the incident, then deleted automatically.</p></li>
<li><p>Injury and first aid records: on exactly the same rule as concussion records &mdash; until a child's 25th birthday or seven years from the incident, whichever is later, and seven years for an adult. Where somebody not on the roster is recorded as under 18 but neither a date of birth nor an age grade is given, the record is kept as though they were among the youngest players, so that it is never deleted before a claim of theirs could be brought.</p></li>
<li><p>Messages between a parent and staff: kept for as long as the concussion records the conversation relates to &mdash; messages describing symptoms and recovery are part of the same evidential picture, so they last exactly as long and no longer. Where a conversation relates to no concussion record, it is deleted one year after the last message. Photographs attached to messages are deleted automatically one year after they are sent, or with the conversation if that is deleted sooner.</p></li>
<li><p>Roster entries with no incident: for the duration of the school or club's use of the Service, reviewed annually at age-grade advancement, with aged-out players archived and deletable by the organisation.</p></li>
<li><p>Records of what parents were told: kept for as long as the record they relate to, as evidence that the information was given.</p></li>
<li><p>Registration forms that the school or club does not accept, from a parent registering a child or a coach asking for staff access: deleted automatically 30 days after the decision. The parent is normally emailed to say the registration was not accepted. The school or club's activity log keeps a note that it was rejected.</p></li>
<li><p>Records of the browsers and phones a member of staff has signed in from: a random identifier the browser or app makes for itself (stored only in scrambled form), the kind of device, such as "Chrome on Windows", and the dates it was first and last used. No IP address or location is kept. They are used only to email the member of staff the first time their account is used somewhere new, so they can act if it wasn't them. Each record is deleted 12 months after the device was last used, or when the member of staff is removed.</p></li>
<li><p>Records of the phones staff stay signed in on: a phone that is not used for 30 days is signed out automatically, and its record is deleted 30 days after it is signed out or stops working, whichever comes first. If the school or club leaves the Service, these records are deleted with the rest of its data.</p></li>
<li><p>When a member of staff is removed from a school or club, the links they were emailed about its players as staff stop working, and their email address is kept so that they are not sent further updates or links as staff, even where it is still on a player's record. It stops being used if they rejoin, and is deleted with the rest of the organisation's data.</p></li>
<li><p>GRTP Tracker news: an email address stays on the list until its owner unsubscribes. After that it is kept only with a note that they unsubscribed, so that they are never sent news again by mistake.</p></li>
<li><p>If the school or club leaves the Service: their data is retained for 60 days so they can export it, then deleted from live systems. Copies held in backups are removed as those backups age out &mdash; within 14 days for the on-server copy and 30 days for the off-site copy &mdash; so all copies are gone within roughly 90 days of termination.</p></li>
</ul>
<p>Deletion is carried out automatically by the system, and each deletion is recorded in a log that notes what type of record was removed and when, but contains no personal data. Your school or club can see that log, and the dates on which records are due to be deleted, from within the Service.</p>
<h2>8. Your rights</h2>
<p>Under UK GDPR you have the right to:</p>
<ul>
<li><p><strong>Access</strong> the data held about you or your child (a subject access request). Your school or club can produce a complete copy from within the Service, covering the roster entry, every concussion record, every injury record and everyone it was sent to, messages, and requested changes.</p></li>
<li><p><strong>Have inaccurate data corrected.</strong></p></li>
<li><p><strong>Request erasure.</strong> Your school or club can erase a player's roster entry, messages, photographs, requested changes and registration record from within the Service. Concussion and injury records still within the retention period in section 7 are kept, because the law permits retention where it is necessary for the establishment, exercise, or defence of legal claims &mdash; and because that record is the evidence a family would need if a claim ever arose. You will be told exactly what was kept and when it will be deleted.</p></li>
<li><p><strong>Object</strong> to processing based on legitimate interests, and <strong>request restriction</strong> of processing in certain circumstances.</p></li>
<li><p><strong>Withdraw consent</strong> for the elements described in section 3 that rely on it &mdash; updates to additional contacts, dual-registration alerts, and injury records being recorded or shared &mdash; at any time. Section 3 explains what withdrawal does to an injury record already made. Withdrawal does not affect the lawfulness of processing before withdrawal.</p></li>
<li><p><strong>Receive a copy</strong> of the data in a portable format, where the right to data portability applies. It covers data held on the basis of consent or a contract, such as an injury record in the first aid log. Separately, your school or club can export everything it holds about a player from within the Service at any time.</p></li>
</ul>
<p>The quickest route for any of these is your school or club, as controller. You can also contact us directly at ${PRIVACY_EMAIL} and we will pass your request to the controller and assist with it. You also have the right to complain to the Information Commissioner's Office (ico.org.uk).</p>
<h2>9. Security</h2>
<ul>
<li><p>All data is encrypted in transit (HTTPS/TLS).</p></li>
<li><p>Passwords are stored only as salted PBKDF2-SHA512 hashes at 210,000 iterations, the current OWASP recommendation, never in a readable form. A password set before October 2026 is upgraded to this standard automatically the next time its owner signs in.</p></li>
<li><p>Multi-factor authentication is required before any administrator action can be carried out.</p></li>
<li><p>On the phone app, staying signed in is only offered after a full sign-in, and only the faces or fingerprints set up on that phone at the time can unlock it. The sign-in key changes every time the app is opened, and a key that turns up twice is treated as copied: the phone is signed out. A phone is also signed out by its owner, remotely by the school or club's administrator, by a password reset, and by removal from the account.</p></li>
<li><p>Everything a member of staff asks for is restricted to their own organisation, so one organisation's records cannot be returned to another. The only exception is the one described in section 4, where a parent has asked for their child's school and club to be linked.</p></li>
<li><p>Tracking, portal, and registration links use long random tokens that cannot be guessed, and are never included in data exports.</p></li>
<li><p>Off-site backups are encrypted with AES-256-GCM before leaving our servers, with the key held separately.</p></li>
<li><p>Access by our own personnel is limited to what is necessary to operate and support the Service.</p></li>
</ul>
<p>No system is perfectly secure. If a breach affecting personal data occurs, we will notify the affected school or club without undue delay so they can meet their obligations, and we will support any required notification to the ICO and affected individuals.</p>
<h2>10. Children's data</h2>
<p>GRTP Tracker exists to protect children, and processes children's data only for that purpose. Children do not use the Service themselves and no accounts are created for them. We apply the ICO's Age Appropriate Design Code principles: we collect the minimum data needed &mdash; we do not, for example, collect home addresses, telephone numbers, NHS numbers, or medical records, and the concussion record and first aid log hold only what staff saw, did, and were told &mdash; we never use children's data for marketing or profiling, we never sell it, and we do not make automated decisions about a child. Date of birth is asked for on the registration form, and used only to be certain of a child's identity where two organisations are linked and to work out exactly when their record should be deleted. It is not required for a player added by staff or imported from an existing roster, and nothing depends on having it. The stage schedule is a calendar, not a judgement: every decision about whether a child returns to play is made by people.</p>
<h2>11. Changes to this policy</h2>
<p>If we make material changes we will notify schools and clubs, who will inform parents where appropriate. The current version is always available at grtp.co.uk.</p>
<h2>12. Contact</h2>
<p>Sports Welfare Systems Ltd (trading as GRTP Tracker)</p>
<p>Email: ${PRIVACY_EMAIL} &middot; Web: grtp.co.uk</p>
<p><em>ICO registration number: ZC200130. Registered in England and Wales, Companies House No. 17341126. Registered office: 9 Whitfield Avenue, Paddington, Warrington WA1 3NF.</em></p>`,
  terms: `<p>These Terms of Service (&ldquo;Terms&rdquo;) govern access to and use of the GRTP Tracker service (the &ldquo;Service&rdquo;), operated by Sports Welfare Systems Ltd, a company incorporated in England and Wales (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;the Provider&rdquo;). By creating an organisation account, or by using the Service, the school, club, or other organisation on whose behalf the account is created (&ldquo;the Customer&rdquo;, &ldquo;you&rdquo;) agrees to be bound by these Terms.</p>
<h2>1. The Service</h2>
<p>1.1 GRTP Tracker is a software tool that helps schools and sports clubs record suspected concussions in youth rugby, manage the Graduated Return to Play (GRTP) protocol, keep a log of injuries and the first aid given, and keep parents, guardians, and coaching staff informed during a player's recovery.</p>
<p>1.2 The Service is an administrative and communication tool only. It does not provide medical advice, diagnosis, or treatment, and it does not make return-to-play decisions. All medical decisions, including whether a player is fit to progress through or return from the protocol, remain the sole responsibility of the Customer, its staff, appropriately qualified medical professionals, and the player's parents or guardians. The stage schedule presented by the Service is based on the RFU's HEADCASE guidance. We keep it under review against that guidance and will tell the Customer's administrators of any change to the schedule. Where the Customer's governing body or a medical professional sets a different or more cautious return, that takes precedence over the schedule in the Service.</p>
<p>1.3 We may improve, modify, or add features to the Service at any time. We will not materially reduce the core functionality of the Service during a paid subscription period without reasonable notice.</p>
<h2>2. Accounts and access</h2>
<p>2.1 The person who creates the organisation account confirms that they are authorised to bind the Customer to these Terms.</p>
<p>2.2 The Customer is responsible for maintaining the confidentiality of its users' login credentials, for ensuring multi-factor authentication remains enabled for administrator accounts, and for all activity that occurs under its accounts.</p>
<p>2.3 The Customer must ensure that only appropriate members of staff &mdash; those with a safeguarding, coaching, or welfare responsibility for the relevant players &mdash; are invited to the organisation's account.</p>
<p>2.4 We may suspend access to an account where we reasonably believe it has been compromised or is being used in breach of these Terms, and will notify the Customer promptly if we do so.</p>
<h2>3. Data protection and roles</h2>
<p>3.1 In respect of personal data entered into the Service about players, other people given first aid, parents, guardians, and contacts (&ldquo;Player Data&rdquo;), the Customer is the data controller and the Provider is the data processor, as those terms are defined in the UK General Data Protection Regulation and the Data Protection Act 2018 (&ldquo;UK GDPR&rdquo;).</p>
<p>3.2 The parties' respective obligations as controller and processor are set out in the Data Processing Agreement, which forms part of these Terms.</p>
<p>3.3 The Customer is responsible for establishing and maintaining a lawful basis for the Player Data it processes through the Service. The Service is built on the basis that the Customer relies on its safeguarding duty of care rather than on parental consent for the core concussion record, as described in the Privacy Policy, and provides tools to support the Customer's transparency obligations &mdash; a parent-facing registration form and a staff attestation step confirming the parent has been informed. Responsibility for the accuracy of that attestation, and for obtaining consent where the Service indicates consent is required (updates to contacts who are neither the parent nor the Customer's own staff, and dual-registration alerts), rests with the Customer. The first aid log is built on consent: the Customer is responsible for obtaining the agreement of the parent or guardian, or of an injured adult, before an injury is recorded, and a separate agreement before it is sent to anyone, and for the accuracy of its staff's confirmation that each was given.</p>
<p>3.4 In respect of account data about the Customer's own staff users (names, email addresses, login records) and billing information, the Provider acts as a data controller, as described in the Privacy Policy.</p>
<h2>4. Customer responsibilities</h2>
<ul>
<li><p>Enter Player Data accurately and keep contact details reasonably up to date.</p></li>
<li><p>Ensure a parent or guardian has been informed how their child's data will be used, as set out in the Privacy Policy, before entering a real player's data.</p></li>
<li><p>Select players from the roster rather than typing names freehand where the player is on the roster, so that records are correctly attributed and, where applicable, dual-registration alerts reach the right organisation.</p></li>
<li><p>Use the &ldquo;test entry&rdquo; facility, not real children's details, for training and experimentation.</p></li>
<li><p>Not share tracking links, portal links, or registration links beyond the intended recipients.</p></li>
<li><p>Comply with its own safeguarding obligations, governing body regulations, and applicable law. The Service supports these duties; it does not discharge them.</p></li>
<li><p>Not use the Service to store data beyond what it is designed for, including medical documents, or health data about adults other than adult players registered on the Service and people recorded in its first aid log.</p></li>
</ul>
<h2>5. Acceptable use</h2>
<p>5.1 The Customer must not: attempt to gain unauthorised access to any part of the Service or to another organisation's data; probe, scan, or test the vulnerability of the Service without our prior written consent; use the Service to send unsolicited communications; resell or provide access to the Service to third parties; or use the Service in any way that is unlawful or infringes the rights of any person.</p>
<h2>6. Fees and payment</h2>
<p>6.1 The Service is provided on a subscription basis at the fees published at grtp.co.uk or agreed in writing. Fees are exclusive of VAT unless stated otherwise.</p>
<p>6.2 Fees are payable annually in advance unless agreed otherwise. If fees remain unpaid 30 days after they fall due, we may suspend access to the Service after giving at least 14 days' written notice, and access will be restored promptly on payment.</p>
<p>6.3 We may change fees with effect from the Customer's next renewal, giving at least 60 days' notice before the renewal date.</p>
<h2>7. Availability and support</h2>
<p>7.1 We will use reasonable endeavours to make the Service available at all times, but we do not guarantee uninterrupted availability. Planned maintenance will, where practicable, be carried out outside UK school hours.</p>
<p>7.2 Support is provided by email during normal UK business hours. We aim to respond to support requests within two business days, and to reports of a fault preventing use of the Service within one business day.</p>
<p>7.3 The Service depends on third-party infrastructure and email delivery providers. We are not responsible for failures of the Customer's own systems, networks, or email services, nor for the recipient mail systems of parents and contacts.</p>
<h2>8. Intellectual property</h2>
<p>8.1 We own all intellectual property rights in the Service. The Customer is granted a non-exclusive, non-transferable licence to use the Service for its internal safeguarding and welfare purposes for the duration of its subscription.</p>
<p>8.2 The Customer retains all rights in Player Data. The Customer grants us a licence to process Player Data solely as necessary to provide the Service, as set out in the Data Processing Agreement.</p>
<h2>9. Data return and deletion on exit</h2>
<p>9.1 The Customer may at any time, from within the Service, export a complete copy of the data it holds &mdash; including its roster, concussion records, protocol history, first aid log, messages, records of what parents were told, and staff accounts &mdash; in a machine-readable format. Access tokens and credentials are excluded from exports by design.</p>
<p>9.2 On termination or expiry of the subscription, we will retain the Customer's data for 60 days, during which the Customer may export it. After that period we will delete the Customer's data from live systems, except where retention is required by law, and residual copies in backups will be deleted as those backups age out &mdash; within 14 days for the on-server copy and 30 days for the encrypted off-site copy.</p>
<p>9.3 Business continuity: in the event that the Provider permanently ceases to operate the Service, we will give the Customer at least 60 days' notice where reasonably possible and provide a full data export, so that safeguarding records can be preserved by the Customer.</p>
<h2>10. Warranties and disclaimers</h2>
<p>10.1 We warrant that the Service will be provided with reasonable skill and care.</p>
<p>10.2 Except as expressly set out in these Terms, all other warranties, conditions, and terms, whether express or implied, are excluded to the fullest extent permitted by law.</p>
<p>10.3 The Customer acknowledges that the Service is a record-keeping and notification tool: it does not verify the medical accuracy of the information entered, and outputs of the Service (including stage schedules, reports, and notifications) depend on the accuracy and timeliness of the data entered by the Customer's staff and by parents.</p>
<p>10.4 Guidance only &mdash; no reliance. The Service provides administrative guidance and record-keeping support only. The Customer acknowledges and agrees that it does not rely, and will not rely, on the Service for any medical judgement or for any decision about a player's fitness to train, progress through the protocol, or return to play, and that it will ensure such decisions are made by appropriately qualified persons in accordance with the applicable governing body guidance current at the time.</p>
<h2>11. Liability</h2>
<p>11.1 Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for any other liability that cannot be excluded or limited under the law of England and Wales.</p>
<p>11.2 Subject to clause 11.1, neither party shall be liable for any indirect or consequential loss, loss of profits, loss of revenue, or loss of anticipated savings arising under or in connection with these Terms.</p>
<p>11.3 Subject to clauses 11.1 and 11.2, the total aggregate liability of the Provider arising under or in connection with these Terms in any 12-month period shall not exceed the fees paid by the Customer for the Service in that period.</p>
<p>11.4 For the avoidance of doubt, the Provider is not liable for decisions made by the Customer, its staff, medical professionals, or parents about a player's participation in sport, nor for any injury arising from a player's return to play. Responsibility for return-to-play decisions rests entirely with those persons.</p>
<p>11.5 The Customer shall indemnify the Provider against losses, claims, and reasonable costs arising from third-party claims to the extent caused by: (a) any decision about a player's participation, progression, or return to play; (b) inaccurate, incomplete, or untimely data entered into the Service by or on behalf of the Customer; or (c) any attestation made by the Customer's staff, as to a parent having been informed or as to anyone having given consent, that was not accurate &mdash; except in each case to the extent the loss was caused by the Provider's breach of these Terms or negligence.</p>
<h2>12. Term, termination, and suspension</h2>
<p>12.1 These Terms apply from account creation and continue for the subscription period, renewing automatically unless either party gives notice of non-renewal before the renewal date. The Customer may give that notice at any time before the renewal date by email to ${SUPPORT_EMAIL} from one of its administrators, and we will acknowledge it in writing.</p>
<p>12.2 Either party may terminate immediately on written notice if the other commits a material breach which is not remedied within 30 days of notice, or becomes insolvent.</p>
<p>12.3 We may suspend or terminate the account of any Customer that uses the Service in a manner that creates a safeguarding risk, where doing so is reasonably necessary and proportionate to that risk. Before acting we will take reasonable steps to notify the Customer and, where the circumstances allow, give it an opportunity to put the matter right.</p>
<h2>13. Changes to these Terms</h2>
<p>13.1 We may update these Terms from time to time. Material changes will be notified to the Customer's administrator by email at least 30 days before they take effect. Continued use of the Service after the effective date constitutes acceptance. If the Customer does not accept a material change, it may terminate at the end of the notice period and receive a pro-rata refund of prepaid fees.</p>
<h2>14. General</h2>
<p>14.1 These Terms, together with the Data Processing Agreement and Privacy Policy, constitute the entire agreement between the parties in relation to the Service.</p>
<p>14.2 Neither party may assign these Terms without the other's consent, except that we may assign to a successor in business that agrees to be bound by them.</p>
<p>14.3 Neither party is liable for delay or failure caused by events beyond its reasonable control.</p>
<p>14.4 These Terms are governed by the law of England and Wales, and the courts of England and Wales have exclusive jurisdiction.</p>
<h2>15. Contact</h2>
<p>Sports Welfare Systems Ltd (trading as GRTP Tracker)</p>
<p>Email: ${SUPPORT_EMAIL} &middot; Web: grtp.co.uk</p>
<p><em>ICO registration number: ZC200130. Registered in England and Wales, Companies House No. 17341126. Registered office: 9 Whitfield Avenue, Paddington, Warrington WA1 3NF.</em></p>`,
  dpa: `<p>This Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of the Terms of Service between Sports Welfare Systems Ltd (&ldquo;the Processor&rdquo;) and the school, club, or other organisation using GRTP Tracker (&ldquo;the Controller&rdquo;). It sets out the parties' obligations under Article 28 of the UK GDPR in respect of the personal data described in the Annex (&ldquo;Controller Data&rdquo;).</p>
<h2>1. Roles and scope</h2>
<p>1.1 The Controller determines the purposes and means of processing Controller Data. The Processor processes Controller Data only on the Controller's behalf to provide the GRTP Tracker service.</p>
<p>1.2 The details of the processing are set out in the Annex to this DPA.</p>
<h2>2. Processor obligations</h2>
<p>The Processor shall:</p>
<ul>
<li><p>process Controller Data only on the Controller's documented instructions (which are, principally, to provide the Service as described in the Terms), unless required to do otherwise by law, in which case it will inform the Controller unless prohibited;</p></li>
<li><p>ensure that all persons authorised to process Controller Data are subject to obligations of confidentiality;</p></li>
<li><p>implement appropriate technical and organisational measures as described in clause 5;</p></li>
<li><p>assist the Controller, by appropriate technical and organisational measures and insofar as possible, in responding to data subject rights requests (access, rectification, erasure, restriction, portability, objection). The Service provides self-service tools for subject access, export, and erasure, so that in most cases the Controller can respond without needing to contact us;</p></li>
<li><p>immediately inform the Controller if, in the Processor's opinion, an instruction infringes the UK GDPR or other UK data protection law;</p></li>
<li><p>assist the Controller in meeting its obligations regarding security, breach notification, data protection impact assessments, and prior consultation with the Information Commissioner, taking into account the nature of the processing and the information available to the Processor;</p></li>
<li><p>at the Controller's choice, delete or return all Controller Data at the end of the provision of services, as set out in clause 7, unless UK law requires storage;</p></li>
<li><p>make available to the Controller all information necessary to demonstrate compliance with Article 28, and allow for and contribute to audits as set out in clause 8.</p></li>
</ul>
<h2>3. Controller obligations</h2>
<p>3.1 The Controller warrants that it has a lawful basis for the processing it instructs. The Service is built on the basis that the Controller relies on its safeguarding duty of care, together with the substantial public interest condition for safeguarding children and individuals at risk (UK GDPR Article 9(2)(g) and Schedule 1, Part 2, paragraph 18 of the Data Protection Act 2018), rather than on parental consent, for the core concussion record. The Controller is responsible for maintaining its own Appropriate Policy Document as required by Schedule 1, Part 4 of that Act; the Processor publishes its own alongside this DPA and makes it available as a reference.</p>
<p>3.2 The Controller is responsible for ensuring that parents and guardians are informed as described in the Privacy Policy, and for the accuracy of any attestation by its staff that a parent has been informed or has consented.</p>
<p>3.3 Where the Service indicates that consent is required &mdash; updates sent to contacts who are neither the parent nor the Controller's own staff, and dual-registration alerts shared with a second organisation &mdash; the Controller is responsible for obtaining and, where withdrawn, acting on that consent. The first aid log is built on consent in the same way: the Controller is responsible for obtaining the agreement of the parent or guardian, or of an injured adult, before an injury is recorded, and a separate agreement before it is disclosed to anyone.</p>
<p>3.4 The Controller shall ensure the accuracy of Controller Data it enters, keep contact details reasonably up to date, and limit account access to appropriate staff.</p>
<h2>4. Sub-processors</h2>
<p>4.1 The Controller gives general authorisation to the engagement of the sub-processors listed below. The Processor has data processing terms in place with each, imposing obligations materially equivalent to those in this DPA, and remains fully liable to the Controller for their performance.</p>
<table>
<tbody>
<tr class="odd">
<td><strong>Sub-processor</strong></td>
<td><strong>Purpose, location, and transfer safeguards</strong></td>
</tr>
<tr class="even">
<td>Railway Corporation<br />548 Market St PMB 68956, San Francisco, California 94104, USA</td>
<td>Application and database hosting, on infrastructure provided to Railway by Google Cloud Platform. Controller Data is stored in the European Economic Area, in Railway's EU West region (Amsterdam, Netherlands). Railway is incorporated in the United States, and its support access and system administration take place there. Those transfers are made under the EU Standard Contractual Clauses as amended by the UK International Data Transfer Addendum.</td>
</tr>
<tr class="odd">
<td>Plus Five Five, Inc., trading as Resend<br />2261 Market Street #5039, San Francisco, California 94114, USA</td>
<td>Transactional email delivery. Processes recipient email addresses and message content in order to deliver notifications, and stores delivery logs, in the United States. Transfers are made under the EU Standard Contractual Clauses as amended by the UK International Data Transfer Addendum, and Resend is certified under the EU&ndash;US Data Privacy Framework and its UK Extension.</td>
</tr>
<tr class="odd">
<td>Backblaze, Inc.<br />2261 Market Street STE 81006, San Francisco, California 94114, USA</td>
<td>Off-site backup storage, in Backblaze's data centre in Amsterdam, Netherlands. Each nightly backup is encrypted with AES-256-GCM before it leaves the Processor's servers, with a key Backblaze does not hold, and is deleted automatically 30 days after upload. The Processor's servers hold a key that can add backups but cannot read or delete them. Governed by Backblaze's UK Data Processing Addendum, which incorporates Standard Contractual Clauses.</td>
</tr>
<tr class="even">
<td>Zoho Corporation Limited<br />Suite B, 3rd Floor, CBX II East, 414&ndash;432 Midsummer Boulevard, Milton Keynes MK9 2EA, United Kingdom</td>
<td>The Processor's own email, hosted in Zoho's EU data centre, including the support and privacy addresses to which parents, schools, and clubs write. Holds Controller Data only where a correspondent includes it. Governed by the Processor's data processing agreement with Zoho Corporation Limited, signed 28 September 2026, which also covers any access from outside the UK and the European Economic Area.</td>
</tr>
</tbody>
</table>
<p>4.2 The Processor shall notify the Controller of any intended addition or replacement of a sub-processor as soon as reasonably practicable, and in any event within two working days of the Processor itself receiving notice from its own suppliers. The Controller may object on reasonable data protection grounds; if the objection cannot be resolved, the Controller may terminate the affected services and receive a pro-rata refund of prepaid fees.</p>
<p>4.3 The Controller acknowledges that the Processor's hosting supplier gives it ten days' notice of changes to its own sub-processors, and that the Processor therefore cannot undertake to give a longer period of notice than it receives.</p>
<h2>5. Security measures</h2>
<ul>
<li><p>Encryption of data in transit (TLS) on all connections;</p></li>
<li><p>Password storage as salted PBKDF2-SHA512 hashes at 210,000 iterations, with passwords set before October 2026 upgraded at the user's next sign-in; multi-factor authentication required before any administrative action;</p></li>
<li><p>Per-organisation data isolation: every request by the Controller's staff that reads or writes Controller Data is restricted to the Controller's own organisation, with the single documented exception of dual-registration links a parent has asked for;</p></li>
<li><p>Unguessable high-entropy tokens for all public links (parent tracking, parent portal, and registration), excluded by design from all data exports;</p></li>
<li><p>Off-site backups encrypted with AES-256-GCM before leaving the Processor's servers, with the encryption key held separately from the backup;</p></li>
<li><p>Automatic enforcement of retention periods, with each deletion recorded in a log that contains no personal data;</p></li>
<li><p>Input validation on public endpoints; signature verification on inbound payment webhooks;</p></li>
<li><p>Access to production systems restricted to authorised personnel of the Processor;</p></li>
<li><p>Audit logging of significant organisational actions, including staff changes, record exports, and erasures, visible to the Controller within the Service.</p></li>
</ul>
<h2>6. Personal data breach</h2>
<p>6.1 The Processor shall notify the Controller without undue delay, and in any event within <strong>24 hours</strong>, after the Processor becomes aware of a personal data breach affecting Controller Data. This period is set so that the Controller retains the greater part of its own 72-hour window under Article 33(1) of the UK GDPR.</p>
<p>6.2 The initial notification shall be made within that period on the basis of the information then available, and shall state what is known, what is not yet known, and when the Processor expects to know more. Where it is not possible to provide all of the information required by Article 33(3) at once, the Processor shall provide it in phases as it becomes available, without further undue delay, as permitted by Article 33(4). Incomplete information is not a reason to delay the initial notification.</p>
<p>6.3 The Processor shall provide the Controller with sufficient information to meet its own notification obligations to the Information Commissioner and to affected data subjects, shall cooperate with the Controller in its investigation, and shall take reasonable steps to contain and mitigate the breach and to prevent its recurrence.</p>
<p>6.4 Where a breach originates with a sub-processor, the 24-hour period runs from the point at which the Processor is itself notified. The Processor's contracts with its sub-processors require them to notify it of a personal data breach without undue delay.</p>
<p>6.5 Notification under this clause is made to the email addresses of every account administrator the Controller has in the Service. The Controller is responsible for keeping those accounts current, and for ensuring that at least one of them reaches a person able to act on a breach notification.</p>
<h2>7. Return and deletion</h2>
<p>7.1 The Controller may export Controller Data at any time via the Service, in a machine-readable format covering its roster, concussion records, protocol history, first aid log, messages, records of what parents were told, and staff accounts. On termination, the Processor will retain Controller Data for 60 days for export, then delete it from live systems, with backup copies deleted as those backups age out (14 days on-server, 30 days off-site), except where retention is required by law.</p>
<p>7.2 Retention periods for individual records within the Service are set out in the Privacy Policy and are enforced automatically.</p>
<h2>8. Audit</h2>
<p>8.1 The Processor will, on reasonable written notice and not more than once per year (except following a breach, or where a supervisory authority requires it), make available the information reasonably necessary to demonstrate compliance with this DPA, and allow for and contribute to audits, including inspections, conducted by the Controller or an auditor it mandates, in a manner that does not compromise the security or the data of other customers. Written responses and documentation shall be the primary audit mechanism; where they are not sufficient to demonstrate compliance, an inspection or technical audit will be made available. The Controller can additionally, at any time and without notice to us, inspect its own audit log, retention status, and deletion history from within the Service.</p>
<h2>9. International transfers</h2>
<p>9.1 Controller Data is stored in the European Economic Area. Most of the Processor's sub-processors are US companies, and some processing necessarily takes place in the United States, as described in clause 4. Those transfers are made under transfer mechanisms recognised by UK law, namely the EU Standard Contractual Clauses as amended by the UK International Data Transfer Addendum and, where the sub-processor is certified, the UK Extension to the EU&ndash;US Data Privacy Framework. The Processor will not transfer Controller Data to any other third country without first putting an appropriate safeguard in place and notifying the Controller in accordance with clause 4.2.</p>
<h2>10. Liability and term</h2>
<p>10.1 This DPA takes effect on acceptance of the Terms and continues for as long as the Processor processes Controller Data. Liability under this DPA is subject to the exclusions and cap in the Terms of Service, save where UK GDPR provides otherwise.</p>
<h2>11. Notices</h2>
<p>11.1 Notices to the Processor under this DPA &mdash; including objections under clause 4.2, audit requests under clause 8, and instructions on return or deletion under clause 7 &mdash; are sent to ${PRIVACY_EMAIL}. Notices to the Controller are sent to the email addresses of its administrator accounts in the Service, as described in clause 6.5.</p>
<h2>Annex &mdash; Details of processing</h2>
<table>
<tbody>
<tr class="odd">
<td><strong>Item</strong></td>
<td><strong>Description</strong></td>
</tr>
<tr class="even">
<td>Subject matter</td>
<td>Provision of the GRTP Tracker concussion protocol management and first aid log service</td>
</tr>
<tr class="odd">
<td>Duration</td>
<td>The subscription term plus the 60-day exit period</td>
</tr>
<tr class="even">
<td>Nature and purpose</td>
<td>Recording suspected concussions in youth and adult players; managing the Graduated Return to Play protocol; notifying parents and contacts; enabling private messaging between parents and staff about a player's recovery; alerting a second organisation where a player is dual-registered and the parent has consented; recording what parents were told and their acknowledgements; recording injuries and the first aid given, and, where separately agreed, telling named people about them; reporting</td>
</tr>
<tr class="odd">
<td>Categories of data subjects</td>
<td>Youth players (children); adult players; other people given first aid by the Controller who are not on its roster, such as visiting players and spectators; parents and guardians; coaching, PE, welfare, and other staff contacts; the Controller's staff users</td>
</tr>
<tr class="even">
<td>Categories of personal data</td>
<td>Player name, age grade, team, position; incident date, description, context and mechanism, and symptoms; protocol stage history and outcome; parent and contact names and email addresses; message content and attached photographs exchanged between parents and staff; records of what parents were told (timestamp, wording version, attesting staff email or submitter IP address); notification delivery events; acknowledgement timestamps; staff account and login data; for injury and first aid entries, the injured person's name, age grade, date of birth or whether they are under 18, the date, time and activity, the part of the body, what the injury looked like, a description, whether they carried on, the first aid given, the advice given and whether it was accepted, onward referral, and, where added later, hospital admission, confirmed diagnosis, and return date; the agreement to record each entry and, per recipient, the agreement to share it</td>
</tr>
<tr class="odd">
<td>Special category data</td>
<td>Health data of children and adults (suspected concussion records, reported symptoms, injury and first aid records including any hospital admission or diagnosis later recorded, and any health information contained in messages between parents and staff). Photographs attached to messages may also constitute health data where they show a player's injury.</td>
</tr>
<tr class="even">
<td>Location of processing</td>
<td>Stored in the European Economic Area (Amsterdam, Netherlands). Support access, system administration, and email delivery logs take place in the United States under the safeguards in clause 9.</td>
</tr>
</tbody>
</table>`,

  apd: `<p><strong>Schools and clubs: you need your own Appropriate Policy Document.</strong> The duty to hold one falls on each school or club that relies on a Schedule 1 condition, as controller of its own data. This document is Sports Welfare Systems Ltd's, as processor. It is published as a reference you may adopt or adapt, but it does not by itself meet your obligation.</p>
<p><strong>Why this document exists.</strong> Schedule 1, Part 4 of the Data Protection Act 2018 requires an appropriate policy document wherever special category or criminal offence data is processed in reliance on a Schedule 1 condition. It is published here, rather than kept internally, so that schools, clubs, parents, and the Information Commissioner can read it.</p>
<p>GRTP Tracker records suspected concussions in youth and adult rugby players so that a Graduated Return to Play protocol can be managed safely. That means processing health data about children, which is special category data under Article 9 of the UK GDPR.</p>
<p><strong>What this document does not cover.</strong> The Service also includes a first aid log, which records injuries and the first aid given. That processing rests on the explicit consent of the parent, guardian, or injured adult (Article 9(2)(a)), which is not a Schedule 1 condition, so it falls outside this document. It is described in the Privacy Policy.</p>
<p>This document explains the condition relied on for that processing, and the procedures that secure compliance with the data protection principles. It is written for the schools and clubs who use the Service and are the controllers of that data, and Sports Welfare Systems Ltd maintains it as the processor.</p>
<h2>1. The condition relied on</h2>
<p><strong>Schedule 1, Part 2, paragraph 18 of the Data Protection Act 2018 &mdash; safeguarding of children and of individuals at risk.</strong></p>
<p>The processing is necessary for the purposes of protecting an individual under 18 from neglect or physical, mental, or emotional harm, or protecting the physical, mental, or emotional wellbeing of such an individual. It is carried out without the consent of the data subject for one of the reasons that paragraph permits: seeking consent would prejudice the protective purpose, because a record that could be withdrawn at will would fail precisely when a concussed child most needed it, and because a parent whose child cannot safely play unless a record exists is not in a position to give freely-given consent.</p>
<p>The corresponding Article 9 condition is Article 9(2)(g), substantial public interest. The Article 6 basis is Article 6(1)(f), legitimate interests, or Article 6(1)(e), public task, where the controller is a school exercising a public function.</p>
<p>Where processing is <em>not</em> necessary for the safeguarding purpose &mdash; sending updates to contacts who are neither the parent nor the organisation's own staff, and sharing a concussion alert with a second organisation where a player is dual-registered &mdash; consent is relied on instead, and that consent can be withdrawn at any time without affecting the core record.</p>
<p>Records are also retained, after the protocol ends, in reliance on Article 9(2)(f) &mdash; necessary for the establishment, exercise, or defence of legal claims.</p>
<h2>2. Procedures for compliance with the data protection principles</h2>
<h3>Lawfulness, fairness, and transparency</h3>
<p>Parents are told, before or at the point their child's data is entered, exactly what is recorded, who sees it, what is sent, and how long it is kept. Two routes deliver this: a parent-facing registration form containing that explanation, and a mandatory staff attestation confirming the parent has been informed before a concussion record can be saved. Each is recorded with a timestamp and the version of the wording shown, so it is always possible to establish what a given parent was actually told. The full Privacy Policy is published and linked from every parent-facing page.</p>
<h3>Purpose limitation</h3>
<p>Data is used only to manage a player's return to play and to keep the people responsible for that player informed. It is not used for marketing, is not sold, is not shared with any party other than the sub-processors named in the Privacy Policy, and is not repurposed for research or analytics about identifiable individuals.</p>
<h3>Data minimisation</h3>
<p>Only the fields needed for the protocol are collected. Home addresses, telephone numbers, NHS numbers, clinician identities, and medical records are not collected at all; the concussion record holds only what staff saw, did, and were told. Date of birth is required on the parent registration form and nowhere else. It serves two purposes that cannot be met otherwise &mdash; establishing beyond doubt that two organisations linked for the same child are in fact tracking the same child, where names repeat, and fixing the retention date exactly instead of inferring it. It is not required when staff add a player directly or import an existing roster, and where it is absent age is derived approximately from the age grade, keeping the record longer rather than shorter. Free-text fields are limited to what staff need to describe an incident. Photographs are optional and parent-initiated.</p>
<h3>Accuracy</h3>
<p>Parents can request corrections to their child's details through a parent portal, and those requests are reviewed by the organisation's staff. Staff can correct records directly. Concussion records are attributed to a specific roster entry rather than matched by name, so that a record cannot be attached to the wrong child, and the interface warns staff when a record is not linked to a roster entry.</p>
<h3>Storage limitation</h3>
<p>Retention periods are set out in section 3 below and enforced automatically by the system rather than left to manual housekeeping. Each deletion is recorded in a log containing no personal data, which the controller can inspect at any time, together with a list of records due for deletion.</p>
<h3>Integrity and confidentiality</h3>
<p>Data is encrypted in transit; passwords are stored only as salted PBKDF2-SHA512 hashes; multi-factor authentication is required before any administrative action; every request by an organisation's staff is restricted to that organisation's own records, apart from the parent-requested links between a school and a club; public links use high-entropy tokens that are excluded from all exports; and off-site backups are encrypted with AES-256-GCM with the key held separately. Access by our own personnel is limited to what is necessary to operate and support the Service. Significant actions &mdash; staff changes, record exports, erasures &mdash; are written to an audit log the controller can read.</p>
<h3>Accountability</h3>
<p>Sports Welfare Systems Ltd is registered with the Information Commissioner's Office (registration ZC200130). We maintain a record of processing activities, a data protection impact assessment for this processing, and a personal data breach procedure. This document is reviewed at least annually and whenever the processing changes materially.</p>
<h2>3. Retention and erasure policy</h2>
<table>
<tbody>
<tr class="odd">
<td><strong>Record</strong></td>
<td><strong>Retention period</strong></td>
</tr>
<tr class="even">
<td>Concussion and protocol records for a player under 18</td>
<td>Until the player's 25th birthday, or seven years from the incident, whichever is later &mdash; seven years past the age of 18, matching the period for which a health record about a child is ordinarily kept. The limitation period for a personal injury claim arising in childhood does not run until the individual's 18th birthday, and can run from a later date of knowledge where the effects of an injury emerge over time, so a shorter period would destroy the evidence a family would need. Exact where a date of birth has been given; otherwise derived from the age grade, erring long.</td>
</tr>
<tr class="odd">
<td>Concussion and protocol records for an adult player</td>
<td>Seven years from the date of the incident.</td>
</tr>
<tr class="even">
<td>Messages between a parent and staff</td>
<td>For as long as the concussion records the conversation relates to, since messages describing symptoms and recovery form part of the same evidential picture. Where a conversation relates to no concussion record, one year after the last message.</td>
</tr>
<tr class="odd">
<td>Photographs attached to messages</td>
<td>One year from the date sent, then deleted automatically.</td>
</tr>
<tr class="even">
<td>Roster entries with no incident</td>
<td>For the duration of the organisation's use of the Service, reviewed annually at age-grade advancement; aged-out players archived and deletable.</td>
</tr>
<tr class="odd">
<td>Records of what parents were told</td>
<td>For as long as the record they relate to, as evidence that the information was given.</td>
</tr>
<tr class="even">
<td>Registration forms not accepted (a parent's form for a child, or a coach's request for staff access)</td>
<td>30 days from the decision, then deleted automatically. The activity log keeps a note of the rejection.</td>
</tr>
<tr class="odd">
<td>All data of an organisation that leaves the Service</td>
<td>60 days from termination, to allow export, then deleted from live systems; backup copies age out within 14 days on-server and 30 days off-site.</td>
</tr>
<tr class="even">
<td>Deletion log</td>
<td>Retained indefinitely as evidence that retention is applied. Contains record types, counts, and dates only &mdash; no personal data.</td>
</tr>
</tbody>
</table>
<p>Deletion is carried out automatically. Where an erasure request is made and granted, the roster entry, messages, photographs, requested changes, registration record, and any cross-organisation links are deleted immediately, and the requester is told which concussion records were retained under Article 9(2)(f), and the date on which each will be deleted.</p>
<h2>4. Review and availability</h2>
<p>This document is reviewed at least annually, and whenever the nature of the processing changes. It is retained until at least six months after the processing it describes has ceased, and will be provided to the Information Commissioner on request, without charge.</p>
<p>Sports Welfare Systems Ltd (trading as GRTP Tracker)</p>
<p>Email: ${PRIVACY_EMAIL} &middot; Web: grtp.co.uk</p>
<p><em>ICO registration number: ZC200130. Registered in England and Wales, Companies House No. 17341126. Registered office: 9 Whitfield Avenue, Paddington, Warrington WA1 3NF.</em></p>`,
};

function LegalPage({ page }) {
  const meta = LEGAL_CONTENT[page] || LEGAL_CONTENT.privacy;
  // Three of the four documents here are about personal data, so questions
  // on them are data-protection questions. The Terms are a commercial
  // contract and go to support.
  const questionsEmail = page === "terms" ? SUPPORT_EMAIL : PRIVACY_EMAIL;
  return (
    <div style={{ fontFamily: "'-apple-system','Segoe UI',Roboto,Helvetica,Arial,sans-serif", background: "#f9fafb", minHeight: "100vh", paddingBottom: 60 }}>
      <GlobalStyles />
      <div style={{ background: "#1a4a2e", padding: "0 20px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LogoMark size={24} />
            <a href="https://grtp.co.uk" style={{ color: "#fff", fontWeight: 800, fontSize: 16, textDecoration: "none" }}>GRTP Tracker</a>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            {[["Privacy Policy", "/privacy"], ["Terms", "/terms"], ["DPA", "/dpa"], ["Safeguarding Policy", "/appropriate-policy-document"]].map(([label, path]) => (
              <a key={path} href={path}
                style={{ color: page === path.slice(1) ? "#c9991a" : "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 80px" }}>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "32px 28px" }}>
          <h1 style={{ fontFamily: "Georgia,serif", color: "#1a4a2e", fontSize: 26, margin: "0 0 4px" }}>{meta.title}</h1>
          <p style={{ color: "#6b7280", fontSize: 13, fontStyle: "italic", margin: "0 0 8px" }}>Sports Welfare Systems Ltd &middot; {meta.version}</p>
          <div style={{ background: "#f3f7f4", borderLeft: "3px solid #1a4a2e", padding: "10px 14px", fontSize: 13, color: "#374151", margin: "16px 0 24px", borderRadius: "0 6px 6px 0" }}>
            This is the version currently in force. It is reviewed at least annually and whenever the processing changes.
            Questions: <a href={`mailto:${questionsEmail}`} style={{ color: "#1a4a2e", fontWeight: 600 }}>{questionsEmail}</a>
          </div>
          <div
            style={{ fontSize: 15, lineHeight: 1.7, color: "#374151" }}
            dangerouslySetInnerHTML={{ __html: LEGAL_HTML[page] }}
          />
        </div>
        <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 24, textAlign: "center" }}>
          &copy; Sports Welfare Systems Ltd, trading as GRTP Tracker. Registered in England and Wales. &nbsp;
          <a href="/privacy" style={{ color: "#9ca3af" }}>Privacy</a> &middot; <a href="/terms" style={{ color: "#9ca3af" }}>Terms</a> &middot; <a href="/dpa" style={{ color: "#9ca3af" }}>DPA</a> &middot; <a href="/appropriate-policy-document" style={{ color: "#9ca3af" }}>Safeguarding Policy</a>
        </p>
      </div>
    </div>
  );
}

// ── Parent Portal Page ────────────────────────────────────────────────
// Permanent parent-facing page — accessible via /parent/:token
// Shows cleared status or active protocol, plus ability to request changes.
// Where somebody a parent named as an additional contact says yes or
// no. Deliberately spare: it shows the child's name, who is asking, and
// two buttons. Nothing about a concussion appears here — a person who
// has not agreed to receive health information must not be shown any
// while deciding whether to receive it.
//
// The invitation's links carry ?confirm=1 or ?remove=1 so a single click
// from the email finishes the job; the buttons are for anyone who lands
// here without them.
// The page behind the link every injury recipient is sent.
//
// Public and token-keyed, like the contact invitation and the tracking
// link: the person holding it has no account here, and for a non-roster
// incident may be a stranger to the organisation entirely.
//
// It renders only what the server returns, which is decided by the view
// level recorded when the disclosure was made — a parent gets the injury
// and what was done, a confirmed contact gets classification and body
// part, and an address nobody verified is not told what the injury was
// at all. Doing the trimming server-side rather than here is deliberate:
// a field this page simply does not receive cannot be leaked by a
// rendering mistake.
// What somebody sees before we know who they are.
//
// Holding the link is not the same as being the person it was sent to.
// An email gets forwarded, a phone gets handed over, a reply chain keeps
// the link quoted at the bottom — so nothing that identifies a child
// appears until a code sent to the address on the record comes back.
//
// It is deliberately not a blank wall. The one fact that is safe to show
// — how many players this person is a contact for who are restricted —
// names nobody, and is exactly what a coach needs before picking a team.
// A blank page would lose that; a named page would lose the point.
function VerifyToView({ token, data, onVerified }) {
  const [stage, setStage] = useState("idle");
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Where the code went, mostly hidden ("j•••@o•••.com"), so somebody with
  // a work and a home address checks the right one; and a short wait
  // before another can be sent, since each new code cancels the last and
  // an email can take a minute to arrive.
  const [sentTo, setSentTo] = useState(null);
  const [sendCount, setSendCount] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const org = data.organisationName || "this organisation";
  const restricted = data.restrictedCount || 0;
  // The parent portal: the same code, its own routes, and words for a
  // parent rather than for "a contact".
  const portal = data.recordKind === "portal";

  async function sendCode() {
    setBusy(true); setError(null);
    try {
      const r = await api.requestPassCode(token, { portal });
      setSentTo((r && r.sentTo) || null);
      setSendCount(n => n + 1);
      setResendIn(30);
      setCode("");
      setStage("sent");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function submitCode(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.verifyPassCode(token, code.trim(), { portal });
      onVerified();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  const card = { background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 24, maxWidth: 460, margin: "0 auto" };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "'Inter', system-ui, sans-serif", padding: "40px 16px" }}>
      <GlobalStyles />
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <LogoMark size={28} />
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1a4a2e" }}>GRTP Tracker</div>
        </div>

        {/* Safe to show unverified: it names nobody, and a coach who
            reads it knows to check before selecting a team. */}
        {restricted > 0 && (
          <div style={{ background: "#fdf0f0", border: "1px solid #f0c9c9", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13.5, color: "#8a1a1a", lineHeight: 1.5 }}>
              {restricted} player{restricted === 1 ? "" : "s"} you are listed as a contact for at {org} {restricted === 1 ? "is" : "are"} currently restricted from contact.
            </div>
          </div>
        )}

        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 19, color: "#1a4a2e", margin: "0 0 8px" }}>
          {portal
            ? `Your parent portal at ${org}`
            : data.recordKind === "injury"
            ? `${org} has recorded a first aid incident`
            : `${org} has a concussion protocol in progress`}
        </h1>
        <p style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.65, margin: "0 0 18px" }}>
          {portal
            ? "Before we show your child's record, we need to check this is you: links get forwarded, and phones get handed around. We'll email a short code to the address the school or club holds for you."
            : "This concerns a player you are listed as a contact for. We do not show who, or any detail, until we know you are the person this was sent to — links get forwarded, and phones get handed around."}
        </p>

        {data.canVerify === false ? (
          <p style={{ fontSize: 13.5, color: "#92400e", background: "#fffbeb", border: "1px solid #f0d88a", borderRadius: 8, padding: "12px 14px", margin: 0, lineHeight: 1.6 }}>
            {data.unverifiableReason}
          </p>
        ) : stage === "idle" ? (
          <>
            <button onClick={sendCode} disabled={busy}
              style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 0", fontSize: 14.5, fontWeight: 700, cursor: busy ? "default" : "pointer", fontFamily: "inherit", opacity: busy ? 0.7 : 1 }}>
              {busy ? "Sending…" : "Email me a code"}
            </button>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "10px 0 0", lineHeight: 1.5 }}>
              The code goes to the address {org} holds for you — not to anyone who has been forwarded this link.
            </p>
          </>
        ) : (
          <form onSubmit={submitCode}>
            <p style={{ fontSize: 13.5, color: "#374151", margin: "0 0 6px", lineHeight: 1.6 }}>
              {sendCount > 1 ? "We've sent a new 6-digit code" : "We've emailed a 6-digit code"}
              {sentTo ? <> to <strong>{sentTo}</strong></> : ""}. It lasts 10 minutes.
              {sendCount > 1 ? " Use the newest one: earlier codes no longer work." : ""}
            </p>
            <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>
              Not there? It can take a minute. Check your junk or spam folder, and any other inbox you use.
            </p>
            <input
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" autoComplete="one-time-code" placeholder="000000" autoFocus
              style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", fontSize: 22, letterSpacing: 6, textAlign: "center", border: "1px solid #d1d5db", borderRadius: 8, fontFamily: "inherit", marginBottom: 10 }}
            />
            <button type="submit" disabled={busy || code.length < 6}
              style={{ width: "100%", background: code.length < 6 ? "#9ca3af" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 0", fontSize: 14.5, fontWeight: 700, cursor: busy || code.length < 6 ? "default" : "pointer", fontFamily: "inherit" }}>
              {busy ? "Checking…" : portal ? "Open my portal" : "Show me the record"}
            </button>
            <button type="button" onClick={sendCode} disabled={busy || resendIn > 0}
              style={{ width: "100%", background: "none", border: "none", color: resendIn > 0 ? "#9ca3af" : "#1a4a2e", fontSize: 12.5, fontWeight: 600, cursor: busy || resendIn > 0 ? "default" : "pointer", padding: "10px 0 0", fontFamily: "inherit" }}>
              {resendIn > 0 ? `Send another code (in ${resendIn}s)` : "Send another code"}
            </button>
          </form>
        )}

        {error && (
          <p style={{ fontSize: 13, color: "#c0272d", margin: "12px 0 0", lineHeight: 1.5 }}>{error}</p>
        )}

        <p style={{ fontSize: 11.5, color: "#9ca3af", margin: "18px 0 0", lineHeight: 1.5 }}>
          Once verified, this device stays verified — you will not be asked again for a while.
          If you were not expecting this, you can ignore it; without the code nothing is shown.
        </p>
      </div>
    </div>
  );
}

// Everything else the holder of this link may see at this organisation.
//
// Shared by the concussion tracker and the injury record page, because
// what it renders is a person's entitlements, not one record's
// neighbours — a coach who opens an injury email is the same coach with
// the same players, and should not get half the picture because of
// which message happened to arrive first.
//
// The record currently being read is filtered out: it is already on the
// screen above, and listing it again reads as a duplicate rather than
// as context.
function AlsoHeldByYou({ held, currentToken }) {
  if (!held) return null;

  const isCurrent = (row) => currentToken && (row.link || "").includes(currentToken);
  const players = (held.players || []).filter((p) => !isCurrent(p));
  const injuries = (held.injuries || []).filter((i) => !isCurrent(i));
  if (players.length === 0 && injuries.length === 0) return null;

  const restricted = players.filter((p) => !p.clearedToPlay).length;
  const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" };
  const rowStyle = (last) => ({
    display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
    borderBottom: last ? "none" : "1px solid #f3f4f6", textDecoration: "none", color: "inherit",
  });
  const heading = { fontWeight: 700, fontSize: 12, color: "#1a4a2e", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 };

  return (
    <div style={{ marginTop: 28, marginBottom: 24 }}>
      {/* Only the players whose record has this reader's email address on
          it. "Also at <club>" read as though it were everyone at the
          club, which a coach seeing their whole squad listed reasonably
          took for a leak. */}
      <div style={heading}>Other players you're named on at {held.organisationName || "this organisation"}</div>
      <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8, lineHeight: 1.5 }}>
        Only players whose record includes your email address, as a parent, coach or contact, or as the person who logged it.
      </div>

      {players.length > 0 && (
        <>
          <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>
            {restricted === 0
              ? "Nobody on a concussion protocol here is currently restricted."
              : <><strong>{restricted}</strong> {restricted === 1 ? "player is" : "players are"} still on a concussion protocol and must not return to contact until cleared.</>}
          </div>
          <div style={card}>
            {players.map((p, i) => (
              <a key={p.link || i} href={p.link} style={rowStyle(i === players.length - 1)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, color: "#111827" }}>
                    {p.identityWithheld ? <span style={{ color: "#6b7280", fontStyle: "italic" }}>Name withheld</span> : p.name}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.currentStage ? `${p.currentStage.label}: ${p.currentStage.name}` : "Protocol in progress"}
                    {p.paused ? " · resting after symptoms returned" : ""}
                  </div>
                </div>
                {/* Three states. A protocol stopped early is neither
                    running nor completed: it must not read as "Restricted",
                    which says the club is still tracking them, nor as
                    "Schedule complete", which says the 21 days were seen
                    through. Falls back to the old pair for a payload
                    served before protocolState existed. */}
                {(() => {
                  const state = p.protocolState || (p.clearedToPlay ? "cleared" : "active");
                  const look = state === "cleared"
                    ? { text: "Schedule complete", fg: "#1a4a2e", bg: "#f0f6f2", br: "#cfe2d6" }
                    : state === "removed_early"
                      ? { text: "Protocol stopped", fg: "#4b5563", bg: "#f3f4f6", br: "#d1d5db" }
                      : { text: "Restricted", fg: "#8a1a1a", bg: "#fdf0f0", br: "#f0c9c9" };
                  return (
                    <span style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, borderRadius: 4, padding: "3px 8px",
                      color: look.fg, background: look.bg, border: `1px solid ${look.br}` }}>
                      {look.text}
                    </span>
                  );
                })()}
              </a>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 8, lineHeight: 1.5 }}>
            "Schedule complete" means the 21-day programme has finished. "Protocol stopped" means it was ended early,
            usually because a clinician took over. Neither is a medical clearance — that decision
            stays with a qualified professional or {held.organisationName || "the organisation"}'s designated lead.
          </div>
        </>
      )}

      {injuries.length > 0 && (
        <div style={{ marginTop: players.length > 0 ? 20 : 0 }}>
          <div style={heading}>Injury and first aid records</div>
          {/* No fitness badge here, deliberately. An injury record says
              what happened and what was done; whether somebody may play
              is a decision a person makes, and this page has no business
              implying one either way. */}
          <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>
            A record of what happened and what was done. These carry no return-to-play status.
          </div>
          <div style={card}>
            {injuries.map((inj, i) => (
              <a key={inj.link || i} href={inj.link} style={rowStyle(i === injuries.length - 1)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, color: "#111827" }}>{inj.subjectName || <span style={{ color: "#6b7280", fontStyle: "italic" }}>Name withheld</span>}</div>
                  <div style={{ fontSize: 12.5, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[inj.bodyPart, inj.nature].filter(Boolean).join(" — ") || "Recorded"}
                    {inj.incidentDate ? ` · ${formatDate(inj.incidentDate)}` : ""}
                    {inj.returnedAt ? ` · returned ${formatDate(inj.returnedAt)}` : ""}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InjuryRecordPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [alsoHeld, setAlsoHeld] = useState(null);
  // Bumped after verifying, to refetch everything now that the server
  // knows who is asking.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.getInjuryRecord(token).then(setData).catch((e) => setError(e.message));
    // Same call the concussion tracker makes. An injury pass resolves
    // the person, so this returns their concussion protocols here too —
    // which is the question somebody is more likely to need answered.
    api.getPassPlayers(token).then(setAlsoHeld).catch(() => setAlsoHeld(null));
  }, [token, reloadKey]);

  const shell = (children) => (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "system-ui, sans-serif", padding: "40px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto", background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <LogoMark size={28} />
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1a4a2e" }}>GRTP Tracker</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) return shell(<p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: 0 }}>{error}</p>);
  if (!data) return shell(<p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>Loading…</p>);
  // An injury has no operational status worth showing on its own — strip
  // the name, date, nature and body part and nothing useful is left — so
  // the whole record waits until we know who is reading.
  if (data.verificationRequired) {
    return <VerifyToView token={token} data={data} onVerified={() => setReloadKey((k) => k + 1)} />;
  }

  const row = (label, value) => value ? (
    <div style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: 14 }}>
      <div style={{ width: 120, color: "#6b7280", flexShrink: 0 }}>{label}</div>
      <div style={{ color: "#111827" }}>{value}</div>
    </div>
  ) : null;

  const adviceLabel = {
    seek_medical: "Seek medical attention",
    rest: "Rest",
    none: "None needed",
  }[data.adviceGiven];

  return shell(
    <>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a4a2e", margin: "0 0 6px" }}>
        A first aid record from {data.organisationName}
      </h1>
      <p style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 18px" }}>
        This was recorded at the time by whoever was there.
      </p>

      {/* Only on the injured person's or their family's own link: the
          server sends canAcknowledge for a full view and nothing else. */}
      {data.canAcknowledge && (
        <FamilyConfirmCard
          acknowledgedAt={data.acknowledgedAt || null}
          onConfirm={async () => {
            try {
              return await api.acknowledgeInjuryRecord(token);
            } catch (e) {
              // The device stopped counting as verified since the page
              // loaded: reloading shows the code screen again.
              if (e.code === "VERIFICATION_REQUIRED") setReloadKey((k) => k + 1);
              throw e;
            }
          }}
          prompt={`Tap below to let ${data.organisationName} know you have read this record. The date and time are kept with it.`}
        />
      )}

      <div style={{ marginBottom: 18 }}>
        {row("Who", data.subjectName)}
        {row("When", [data.incidentDate, data.incidentTime].filter(Boolean).join(" · "))}
        {row("During", data.activity)}
        {row("Injury", [data.nature, data.bodyPart].filter(Boolean).join(" — "))}
        {row("First aid given", data.treatment)}
        {row("Advice given", adviceLabel)}
      </div>

      {/* Carried on every level, because the whole point is that this is
          a record and not a recommendation. The parent's version adds
          that the decision is theirs. */}
      <div style={{ background: "#eef6f1", border: "1px solid #bcd9c8", borderRadius: 8, padding: "14px 16px", fontSize: 13.5, color: "#1a4a2e", lineHeight: 1.65 }}>
        {data.guidance}
      </div>

      {data.registrationUrl && (
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #e2eaf2" }}>
          <p style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.65, margin: "0 0 12px" }}>
            {data.subjectName} is not registered with {data.organisationName}. If they play there, registering means
            you get their records directly in future rather than a one-off link like this one.
          </p>
          <a href={data.registrationUrl}
            style={{ display: "inline-block", background: "#1a4a2e", color: "#fff", borderRadius: 8, padding: "10px 16px", fontSize: 13.5, fontWeight: 700, textDecoration: "none" }}>
            Register with {data.organisationName}
          </a>
        </div>
      )}

      <AlsoHeldByYou held={alsoHeld} currentToken={token} />

      <p style={{ fontSize: 12.5, color: "#9ca3af", lineHeight: 1.6, margin: "18px 0 0" }}>
        If you were not expecting this, you can ignore it. {data.organisationName} holds this record — contact them
        directly with any questions about it.
      </p>
    </>
  );
}

function ContactInvitePage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const acted = useRef(false);

  const act = async (choice) => {
    setBusy(true);
    try {
      if (choice === "confirm") await api.confirmContactInvite(token);
      else await api.removeContactInvite(token);
      setOutcome(choice);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    api.getContactInvite(token)
      .then((d) => {
        setData(d);
        if (d.declined) { setOutcome("remove"); return; }
        const params = new URLSearchParams(window.location.search);
        // One click in the email is the whole point; landing here should
        // not require a second. Guarded so React's development double-
        // render cannot fire it twice.
        if (!acted.current && (params.get("confirm") || params.get("remove"))) {
          acted.current = true;
          act(params.get("remove") ? "remove" : "confirm");
        } else if (d.confirmed) {
          setOutcome("confirm");
        }
      })
      .catch((e) => setError(e.message));
  }, [token]);

  const shell = (children) => (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "system-ui, sans-serif", padding: "40px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto", background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <LogoMark size={28} />
          <div style={{ fontWeight: 800, fontSize: 15, color: "#1a4a2e" }}>GRTP Tracker</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (error) return shell(<p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: 0 }}>{error}</p>);
  if (!data) return shell(<p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>Loading…</p>);

  if (outcome === "confirm") {
    return shell(
      <>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a4a2e", margin: "0 0 10px" }}>Thank you — that's confirmed</h1>
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: "0 0 14px" }}>
          You'll be emailed if any player at {data.organisationName} who lists you is ever suspected of having a concussion, and at each stage of their recovery. That includes any added later, so you won't be asked again.
        </p>
        <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 18px" }}>
          Please keep what you receive to yourself and to anyone directly responsible for their care.
        </p>
        <button onClick={() => act("remove")} disabled={busy}
          style={{ background: "#fff", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          {busy ? "Working…" : "Actually, remove me"}
        </button>
      </>
    );
  }

  if (outcome === "remove") {
    return shell(
      <>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a4a2e", margin: "0 0 10px" }}>You've been removed</h1>
        <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: 0 }}>
          You won't receive anything further from {data.organisationName}, and your details have been taken off every player's record there.
          If this was a mistake, ask a parent or guardian to add you again.
        </p>
      </>
    );
  }

  return shell(
    <>
      <h1 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a4a2e", margin: "0 0 12px" }}>
        Can {data.organisationName} send you concussion updates?
      </h1>
      <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: "0 0 12px" }}>
        You've been listed as a contact for {(data.players || []).length > 1 ? "these players" : "this player"} at{" "}
        {data.organisationName}. If any of them is ever suspected of having a concussion, you'd be emailed when their
        recovery protocol starts and at each stage of it, over about 21 days.
      </p>
      {/* Shown because one answer covers all of them. Being asked to agree
          to "players who list you" without being told which ones is not a
          decision anybody can actually make. */}
      {(data.players || []).length > 0 && (
        <div style={{ background: "#f8fafc", border: "1px solid #e2eaf2", borderRadius: 8, padding: "11px 14px", margin: "0 0 14px", fontSize: 13.5, color: "#374151", lineHeight: 1.7 }}>
          {data.players.map((p, i) => (
            <div key={i}>
              {p.name}
              {p.ageGrade && <span style={{ color: "#6b7280" }}> · {p.ageGrade}</span>}
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 20px" }}>
        You would not be sent their symptoms or the notes staff wrote about the incident — those go only to each player's parent or guardian.
        This is information about a child's health: please keep it to yourself and don't forward it.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => act("confirm")} disabled={busy}
          style={{ flex: "1 1 160px", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 16px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          {busy ? "Working…" : "Yes, send me updates"}
        </button>
        <button onClick={() => act("remove")} disabled={busy}
          style={{ flex: "1 1 160px", background: "#fff", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          No, remove me
        </button>
      </div>
      <p style={{ fontSize: 12.5, color: "#9ca3af", lineHeight: 1.6, margin: "18px 0 0" }}>
        If you do nothing, nothing is sent to you.
      </p>
    </>
  );
}

// A message photo in the parent portal. The route needs the verified
// device header, which an <img src> can't send, so it is fetched and
// shown from a local object URL (released when the photo leaves the page).
function PortalPhoto({ token, attachmentId, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let made = null;
    let live = true;
    api.getParentPortalAttachment(token, attachmentId)
      .then((u) => { made = u; if (live) setUrl(u); else URL.revokeObjectURL(u); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [token, attachmentId]);
  const box = { width: 72, height: 72, borderRadius: 8, border: "1px solid #e2eaf2" };
  if (!url) {
    return <div style={{ ...box, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, color: "#9ca3af", textAlign: "center" }}>{failed ? "Photo unavailable" : "Loading…"}</div>;
  }
  return <img src={url} alt="Attachment" onClick={() => onOpen(url)} style={{ ...box, objectFit: "cover", cursor: "pointer" }} />;
}

function ParentPortalPage({ token }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [view, setView]         = useState("home"); // "home" | "edit_contacts" | "add_org"
  const [form, setForm]         = useState({});
  const [orgSearch, setOrgSearch] = useState("");
  const [orgResults, setOrgResults] = useState([]);
  const orgSearchRequestIdRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [notif, setNotif]       = useState(null);
  const [messageComposeText, setMessageComposeText] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState("");
  const [savingMessageEdit, setSavingMessageEdit] = useState(false);
  const [messageImages, setMessageImages] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Which organisation's record is on screen. Null means the one whose
  // link this is, which is what a parent with a single organisation
  // always sees.
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  // Bumped after the emailed code is entered, to load the record now the
  // server knows this device is the parent's.
  const [reloadKey, setReloadKey] = useState(0);

  const showNotif = (msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 4000);
  };
  // Any action refused because this device is no longer verified (it
  // lapsed mid-visit) goes back to the code screen instead of an error.
  const handleActionError = (e) => {
    if (e && e.code === "VERIFICATION_REQUIRED") { setReloadKey((k) => k + 1); return; }
    showNotif(e.message, "error");
  };

  useEffect(() => {
    // The contact form is filled in by the effect below, which follows
    // whichever organisation is selected rather than only the first.
    api.getParentPortal(token)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, reloadKey]);

  // Each organisation holds its own copy of the contacts, and a
  // correction is reviewed by the one it was sent to. So opening the
  // form has to load THAT organisation's values — otherwise a parent
  // switching to the club would be shown the school's details and
  // "correct" them into a change request the club never needed.
  useEffect(() => {
    if (!data) return;
    const list = (data.organisations && data.organisations.length) ? data.organisations : [data];
    const current = list.find(o => o.organisationId === selectedOrgId) || list[0];
    setForm({
      parentName: current.parentName || "",
      parentEmail: current.parentEmail || "",
      schoolCoachName: current.schoolCoachName || "",
      schoolCoachEmail: current.schoolCoachEmail || "",
      schoolCoach2Name: current.schoolCoach2Name || "",
      schoolCoach2Email: current.schoolCoach2Email || "",
      clubCoach2Name: current.clubCoach2Name || "",
      clubCoach2Email: current.clubCoach2Email || "",
      clubCoachName: current.clubCoachName || "",
      clubCoachEmail: current.clubCoachEmail || "",
      otherContact1Name: current.otherContact1Name || "",
      otherContact1Email: current.otherContact1Email || "",
      otherContact2Name: current.otherContact2Name || "",
      otherContact2Email: current.otherContact2Email || "",
    });
  }, [data, selectedOrgId]);

  const handleSubmitContactChange = async () => {
    setSubmitting(true);
    try {
      await api.submitParentChangeRequest(token, "contact_details", form, p.rosterPlayerId);
      showNotif("Your request has been submitted and is awaiting approval.");
      setView("home");
    } catch (e) {
      handleActionError(e);
    } finally { setSubmitting(false); }
  };

  const refreshMessages = async () => {
    try {
      const d = await api.getParentPortal(token);
      setData(d);
    } catch (_) { /* keep showing existing data rather than erroring the whole page */ }
  };

  const handleSendMessage = async () => {
    if (!messageComposeText.trim() && messageImages.length === 0) return;
    setMessageSending(true);
    try {
      await api.sendParentPortalMessage(token, messageComposeText.trim(), messageImages.map(i => i.dataUrl), p.rosterPlayerId);
      setMessageComposeText("");
      setMessageImages([]);
      await refreshMessages();
    } catch (e) {
      handleActionError(e);
    } finally {
      setMessageSending(false);
    }
  };

  const handleMessageImageSelect = async (e) => {
    const { images, error } = await readMessageImageFiles(e.target.files, messageImages.length);
    if (images.length) setMessageImages(prev => [...prev, ...images]);
    if (error) showNotif(error, "error");
    e.target.value = "";
  };

  const handleSaveEditedMessage = async (messageId) => {
    if (!editingMessageText.trim() || savingMessageEdit) return;
    setSavingMessageEdit(true);
    try {
      await api.editParentPortalMessage(token, messageId, editingMessageText.trim());
      setEditingMessageId(null);
      await refreshMessages();
    } catch (e) {
      handleActionError(e);
    } finally {
      setSavingMessageEdit(false);
    }
  };

  const handleOrgSearch = async (q) => {
    setOrgSearch(q);
    if (q.length < 2) { setOrgResults([]); return; }
    // Fast typing can fire several searches in a row with no guaranteed
    // response order — without this, an earlier query's results could
    // land after a later one's and silently overwrite the correct,
    // more-recent results with stale matches for what's no longer even
    // in the search box.
    const requestId = ++orgSearchRequestIdRef.current;
    try {
      const results = await api.searchOrganisations(q);
      if (requestId === orgSearchRequestIdRef.current) setOrgResults(results);
    } catch (_) {}
  };

  const handleSubmitOrgLink = async (org) => {
    setSubmitting(true);
    try {
      // Filed against the organisation on screen, not the one whose link
      // the parent happens to be holding. A nomination is made BY one
      // organisation's roster entry, so from the club's view it must be
      // the club asking — otherwise the school reviews a request it has
      // nothing to do with, and the resulting link joins the wrong pair.
      await api.submitParentChangeRequest(token, "org_link", { linkedOrgId: org.id, linkedOrgName: org.name }, p.rosterPlayerId);
      showNotif(`Link request to ${org.name} submitted — ${p.organisationName} will review it, then ${org.name} must confirm.`);
      setView("home");
    } catch (e) {
      handleActionError(e);
    } finally { setSubmitting(false); }
  };

  const headerStyle = {
    background: "#1a4a2e", padding: "16px 20px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  };
  const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, fontFamily: "inherit", marginBottom: 10 };
  const label = (t) => <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{t}</div>;

  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>Loading…</div>;
  if (error)   return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#c0272d", padding: 24 }}>{error}</div>;
  // Nothing about the child until the emailed code proves this is the
  // parent: the same check as every other record link.
  if (data.verificationRequired) {
    return <VerifyToView token={token} data={data} onVerified={() => setReloadKey((k) => k + 1)} />;
  }

  // A child at a school and a club has a record at each, and one link
  // now shows both. Everything below renders from `p` — one
  // organisation at a time — so selecting a different one re-renders the
  // whole page for that organisation rather than merging two records
  // into a picture that belongs to neither.
  //
  // The fallback to [data] is for a response from a server that predates
  // this; the page must not go blank mid-deploy.
  const orgs = (data.organisations && data.organisations.length) ? data.organisations : [data];
  const p = orgs.find(o => o.organisationId === selectedOrgId) || orgs[0];
  const proto = p.protocol;
  const stageColor = proto ? (proto.stage.color || "#1a4a2e") : "#1a4a2e";

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "system-ui, sans-serif" }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <LogoMark size={32} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>GRTP Tracker</div>
            <div style={{ color: "#a8d4b8", fontSize: 11 }}>{p.organisationName}</div>
          </div>
        </div>
        {view !== "home" && (
          <button onClick={() => setView("home")} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>← Back</button>
        )}
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: "20px 16px 40px" }}>

        {/* One child, one link, but a record at each organisation. They
            are shown one at a time rather than merged: the protocol,
            the contacts and the messages all belong to a particular
            organisation, and a combined view would belong to neither. */}
        {orgs.length > 1 && view === "home" && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>
              {p.playerName} is registered with {orgs.length} organisations
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {orgs.map((o) => {
                const active = o.organisationId === p.organisationId;
                const onProtocol = o.protocol && o.protocol.status !== "cleared";
                return (
                  <button
                    key={o.organisationId}
                    onClick={() => { setSelectedOrgId(o.organisationId); setMessageComposeText(""); setMessageImages([]); setEditingMessageId(null); }}
                    style={{
                      flex: "1 1 140px", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                      border: `2px solid ${active ? "#1a4a2e" : "#e2eaf2"}`,
                      background: active ? "#f0f7f3" : "#fff",
                      borderRadius: 8, padding: "8px 10px",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: active ? 800 : 600, color: active ? "#1a4a2e" : "#374151" }}>
                      {o.organisationName}
                    </div>
                    <div style={{ fontSize: 11.5, color: onProtocol ? "#c0272d" : "#6b7280", marginTop: 2 }}>
                      {onProtocol ? "On a protocol now" : "Cleared to play"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* The first email about a concussion asks the family to open it
            and confirm. Only for a protocol still running: there is nothing
            to ask about once the schedule has finished. Keyed on the
            record, so switching organisation can't carry one child's
            confirmed state onto another record. */}
        {view === "home" && proto && proto.id && proto.status !== "cleared" && (
          <FamilyConfirmCard
            key={`${p.organisationId}:${proto.id}`}
            acknowledgedAt={proto.parentAcknowledgedAt || null}
            onConfirm={async () => {
              try {
                const r = await api.acknowledgeParentPortal(token, proto.id, p.rosterPlayerId);
                refreshMessages();
                return r;
              } catch (e) {
                if (e.code === "VERIFICATION_REQUIRED") setReloadKey((k) => k + 1);
                throw e;
              }
            }}
            prompt={`Tap below to let ${p.organisationName} know you have read the concussion update about ${p.playerName}. The date and time are recorded on their concussion record.`}
          />
        )}

        {/* Player header */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", marginBottom: 16, overflow: "hidden" }}>
          <div style={{ background: stageColor, padding: "16px 20px" }}>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>{p.playerName}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 13 }}>{[p.ageGrade, p.position, p.organisationName].filter(Boolean).join(" · ")}</div>
          </div>

          {/* Status */}
          <div style={{ padding: "16px 20px" }}>
            {!proto || proto.status === "cleared" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 28 }}>✅</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e" }}>Cleared to play</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>No active concussion protocol. You'll be notified automatically if one is started.</div>
                </div>
              </div>
            ) : proto.paused ? (
              <div>
                <div style={{ background: "#fef9ec", border: "1px solid #c9991a", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}>⏸ 48-hour rest period in effect</div>
                  <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>Symptoms returned. Resumes {new Date(proto.pauseUntil).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}.</div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ background: stageColor + "15", border: `1px solid ${stageColor}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: stageColor, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{proto.stage.label} · Day {proto.day} of 21</div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>{proto.stage.name}</div>
                </div>
                {/* Activities */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Today's guidance</div>
                  {/* Every line, not the first few: the later ones are the
                      safety instructions (no contact, when to call NHS 111,
                      when to stop), and a parent must see all of them. */}
                  {proto.stage.activities.map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, color: "#374151", marginBottom: 4 }}>
                      <span style={{ color: "#1a4a2e", flexShrink: 0 }}>•</span><span>{a}</span>
                    </div>
                  ))}
                </div>
                {/* Next stage */}
                {proto.nextStage && (
                  <div style={{ background: "#f8fafb", border: "1px solid #e2eaf2", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Next: <strong>{proto.nextStage.label} — {proto.nextStage.name}</strong></div>
                  </div>
                )}
                {/* RTP date */}
                <div style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 2 }}>Earliest return to match play</div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: "#1a4a2e" }}>
                    {proto.stage5Earliest && new Date(proto.stage5Earliest) > new Date(proto.earliestRTP)
                      ? new Date(new Date(proto.stage5Earliest).getTime() + 7*86400000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                      : new Date(proto.earliestRTP).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                    }
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Subject to completing all stages symptom-free</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pending changes notice */}
        {p.pendingChanges?.length > 0 && (
          <div style={{ background: "#fff8e1", border: "1px solid #c9991a", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}>⏳ Pending requests</div>
            {p.pendingChanges.map((r, i) => (
              <div key={i} style={{ fontSize: 12.5, color: "#92400e", marginTop: 4 }}>
                {r.change_type === "contact_details" ? "Contact details update" : "Organisation link request"} — awaiting approval
              </div>
            ))}
          </div>
        )}

        {/* Concussion history */}
        {p.history?.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Concussion history</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: p.history.length > 1 ? "#c0272d" : "#6b7280" }}>
                {p.history.length} logged
              </div>
            </div>
            {p.history.length > 1 && (
              <div style={{ background: "#fdf2f2", border: "1px solid #f3c6c6", borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 12.5, color: "#7a1f24", lineHeight: 1.6 }}>
                {p.playerName} has {p.history.length} suspected concussions on record. Repeat concussions carry increased risk — please make sure this history has been shared with any treating clinician.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {p.history.map(h => (
                <div key={h.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: h.isCurrent ? "#f0f7f3" : "#f8fafb", border: "1px solid #e2eaf2", borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{formatDate(h.incidentDate)}</div>
                    <div style={{ fontSize: 11.5, color: "#6b7280" }}>
                      {h.isCurrent ? "Ongoing" : h.outcome === "cleared" ? "Cleared to play" : h.outcome === "removed_early" ? "Removed from protocol early" : "Ongoing"}
                    </div>
                  </div>
                  {h.isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: "#1a4a2e", background: "#d7ecdf", padding: "3px 8px", borderRadius: 99 }}>CURRENT</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 20, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827", marginBottom: 12 }}>Messages</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            {(!p.messages || p.messages.length === 0) && (
              <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>
                No messages yet. Send a message below if you have a question for {p.organisationName}.
              </div>
            )}
            {p.messages && p.messages.map(m => {
              const isMine = m.senderType === "parent";
              const isEditing = editingMessageId === m.id;
              return (
                <div key={m.id} style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                  <div style={{ background: isMine ? "#f0f7f3" : "#f8fafb", border: `1px solid ${isMine ? "#b6d8c5" : "#e2eaf2"}`, borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>
                      {isMine ? "You" : (m.senderName || p.organisationName)}
                    </div>
                    {isEditing ? (
                      <div>
                        <textarea value={editingMessageText} onChange={e => setEditingMessageText(e.target.value)} rows={2} style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: 8, fontSize: 13.5, fontFamily: "inherit", resize: "vertical" }} />
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                          <button onClick={() => handleSaveEditedMessage(m.id)} disabled={savingMessageEdit} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: savingMessageEdit ? "default" : "pointer", opacity: savingMessageEdit ? 0.7 : 1, fontFamily: "inherit" }}>{savingMessageEdit ? "Saving..." : "Save"}</button>
                          <button onClick={() => setEditingMessageId(null)} disabled={savingMessageEdit} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13.5, color: "#111827", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.body}</div>
                    )}
                    {m.attachments && m.attachments.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {m.attachments.map(att => (
                          <PortalPhoto key={att.id} token={token} attachmentId={att.id} onOpen={setLightboxUrl} />
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                      <span>{new Date(m.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{m.editedAt ? " (edited)" : ""}</span>
                      {isMine && !isEditing && (
                        <button onClick={() => { setEditingMessageId(m.id); setEditingMessageText(m.body); }} style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 10.5, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {messageImages.length > 0 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {messageImages.map((img, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img src={img.dataUrl} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid #e2eaf2" }} />
                  <button
                    type="button" onClick={() => setMessageImages(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#c0272d", color: "#fff", border: "2px solid #fff", fontSize: 11, lineHeight: "16px", cursor: "pointer", padding: 0 }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <textarea
            value={messageComposeText}
            onChange={e => setMessageComposeText(e.target.value)}
            placeholder={`Send a message to ${p.organisationName}…`}
            rows={3}
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: 10, fontSize: 14, fontFamily: "inherit", resize: "vertical" }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
            <label style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
              📎 Attach photo{messageImages.length > 0 ? ` (${messageImages.length}/${MAX_MESSAGE_IMAGES})` : ""}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleMessageImageSelect} style={{ display: "none" }} />
            </label>
            <button
              onClick={handleSendMessage}
              disabled={messageSending || (!messageComposeText.trim() && messageImages.length === 0)}
              style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: messageSending || (!messageComposeText.trim() && messageImages.length === 0) ? 0.6 : 1, fontFamily: "inherit" }}
            >
              {messageSending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
        {lightboxUrl && (
          <div onClick={() => setLightboxUrl(null)} style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, cursor: "zoom-out" }}>
            <img src={lightboxUrl} alt="" style={{ maxWidth: "92vw", maxHeight: "92vh", borderRadius: 8 }} />
          </div>
        )}

        {/* Edit contacts view */}
        {view === "edit_contacts" && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#111827", marginBottom: 4 }}>Update contact details</div>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.6 }}>Changes will be reviewed by {p.organisationName} before taking effect.</p>
            {label("Your name")}
            <input value={form.parentName} onChange={e => setForm(f => ({ ...f, parentName: e.target.value }))} style={inp} />
            {label("Your email")}
            <EmailInput value={form.parentEmail} onChange={v => setForm(f => ({ ...f, parentEmail: v }))} style={inp} />
            {label("School coach name")}
            <input value={form.schoolCoachName} onChange={e => setForm(f => ({ ...f, schoolCoachName: e.target.value }))} style={inp} />
            {label("School coach email")}
            <EmailInput value={form.schoolCoachEmail} onChange={v => setForm(f => ({ ...f, schoolCoachEmail: v }))} style={inp} />
            {label("Second school coach name")}
            <input value={form.schoolCoach2Name} onChange={e => setForm(f => ({ ...f, schoolCoach2Name: e.target.value }))} style={inp} />
            {label("Second school coach email")}
            <EmailInput value={form.schoolCoach2Email} onChange={v => setForm(f => ({ ...f, schoolCoach2Email: v }))} style={inp} />
            {label("Club coach name")}
            <input value={form.clubCoachName} onChange={e => setForm(f => ({ ...f, clubCoachName: e.target.value }))} style={inp} />
            {label("Club coach email")}
            <EmailInput value={form.clubCoachEmail} onChange={v => setForm(f => ({ ...f, clubCoachEmail: v }))} style={inp} />
            {label("Second club coach name")}
            <input value={form.clubCoach2Name} onChange={e => setForm(f => ({ ...f, clubCoach2Name: e.target.value }))} style={inp} />
            {label("Second club coach email")}
            <EmailInput value={form.clubCoach2Email} onChange={v => setForm(f => ({ ...f, clubCoach2Email: v }))} style={inp} />

            {/* The people the parent added at registration. Without these
                they could not change or remove someone who was receiving
                their child's health updates — they had to ask the club to
                run a roster-wide find and replace. */}
            <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.55, margin: "18px 0 10px", paddingTop: 14, borderTop: "1px solid #e2eaf2" }}>
              Anyone else who should receive {p.playerName}'s updates. Clear the email to stop sending to them.
            </div>
            {label("Additional contact 1 name")}
            <input value={form.otherContact1Name} onChange={e => setForm(f => ({ ...f, otherContact1Name: e.target.value }))} placeholder="e.g. Grandparent" style={inp} />
            {label("Additional contact 1 email")}
            <EmailInput value={form.otherContact1Email} onChange={v => setForm(f => ({ ...f, otherContact1Email: v }))} style={inp} />
            {label("Additional contact 2 name")}
            <input value={form.otherContact2Name} onChange={e => setForm(f => ({ ...f, otherContact2Name: e.target.value }))} placeholder="Optional" style={inp} />
            {label("Additional contact 2 email")}
            <EmailInput value={form.otherContact2Email} onChange={v => setForm(f => ({ ...f, otherContact2Email: v }))} style={inp} />

            <button onClick={handleSubmitContactChange} disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.7 : 1, fontFamily: "inherit" }}>
              {submitting ? "Submitting…" : "Submit for approval"}
            </button>
          </div>
        )}

        {/* Add org view */}
        {view === "add_org" && (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2eaf2", padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#111827", marginBottom: 4 }}>Link to another organisation</div>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.6 }}>If {p.playerName} also plays for another school or club, link them so both organisations can track concussion protocols. Both organisations must approve the link.</p>
            {label("Search for school or club")}
            <input value={orgSearch} onChange={e => handleOrgSearch(e.target.value)} placeholder="Type to search…" style={{ ...inp, marginBottom: 4 }} />
            {orgResults.length > 0 && (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                {orgResults.map(org => (
                  <button key={org.id} onClick={() => handleSubmitOrgLink(org)} disabled={submitting} style={{ width: "100%", display: "block", textAlign: "left", padding: "10px 14px", background: "#fff", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontFamily: "inherit" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{org.name}</div>
                    {org.org_type && <div style={{ fontSize: 12, color: "#6b7280", textTransform: "capitalize" }}>{org.org_type}</div>}
                  </button>
                ))}
              </div>
            )}
            {orgSearch.length >= 2 && orgResults.length === 0 && (
              <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 12 }}>No organisations found matching "{orgSearch}"</div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {view === "home" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => setView("edit_contacts")} style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 10, padding: "14px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              ✏️ Update contact details
              <div style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginTop: 2 }}>Change parent email, coach contacts etc. — requires approval</div>
            </button>
            <button onClick={() => setView("add_org")} style={{ background: "#fff", color: "#3d5166", border: "1px solid #e2eaf2", borderRadius: 10, padding: "14px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              🔗 Link to another school or club
              <div style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginTop: 2 }}>If {p.playerName} plays for multiple organisations — requires approval</div>
            </button>
          </div>
        )}

        {/* Bottom section — context-sensitive */}
        {proto && proto.status !== "cleared" ? (
          /* Active protocol — show disclaimer */
          <div style={{ marginTop: 24, padding: "12px 16px", background: "#fff", border: "1px solid #e2eaf2", borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.6 }}>
              This has been logged as a <strong>suspected concussion</strong>. Return to match play should not happen before Day 21. An earlier return is only possible where a doctor is overseeing the player's recovery, and {p.organisationName} will let you know if that happens. <strong>Do not assume a return to play until you are told.</strong><br /><br />
              <a href="https://www.englandrugby.com/run/player-welfare/headcase" style={{ color: "#1a4a2e" }} target="_blank" rel="noreferrer">Learn more about the RFU HEADCASE GRAS programme →</a>
            </div>
          </div>
        ) : (
          /* Cleared / no protocol — show GRAS stages explainer */
          <div style={{ marginTop: 24, background: "#fff", border: "1px solid #e2eaf2", borderRadius: 10, padding: "16px" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1a4a2e", marginBottom: 4 }}>About the GRAS programme</div>
            <p style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 14, lineHeight: 1.6 }}>
              If a concussion is ever logged, {p.playerName} will follow the RFU's 6-stage Graduated Return to Activity &amp; Sport programme — a minimum of 21 days before returning to match play.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { label: "Stage 1", name: "Initial Relative Rest", days: "Days 0–1", color: "#c0272d" },
                { label: "Stage 2/3", name: "Daily Activity & Exercise", days: "Days 2–7", color: "#c9991a" },
                { label: "Stage 4", name: "Non-Contact Training", days: "Day 8+", color: "#c9991a" },
                { label: "Stage 5", name: "Full Contact Practice", days: "Day 15+", color: "#2d7a3a" },
                { label: "Stage 6", name: "Return to Match Play", days: "Day 21+", color: "#1a4a2e" },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ background: s.color, borderRadius: 4, padding: "3px 8px", minWidth: 68, textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#fff", letterSpacing: "0.04em", textTransform: "uppercase" }}>{s.label}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#374151" }}>{s.name}</span>
                    <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>{s.days}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
              <a href="https://www.englandrugby.com/run/player-welfare/headcase" style={{ fontSize: 12, color: "#1a4a2e", fontWeight: 600 }} target="_blank" rel="noreferrer">
                Learn more about HEADCASE GRAS →
              </a>
            </div>
          </div>
        )}

        {/* Who has opened your child's records.

            The codes and expiries make a leaked link harder to use. This
            is the half that tells you whether one is being used, and it
            is the only part you can act on — an open by somebody you do
            not recognise is a reason to ask the club who they wrote to.

            Unverified opens are shown deliberately. They are the signal
            that a link has gone somewhere it should not; hiding them
            would leave only the reassuring half of the picture. */}
        {view === "home" && Array.isArray(data.accessLog) && data.accessLog.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 18, marginTop: 16, border: "1px solid #e2eaf2" }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "#1a4a2e", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6 }}>
              Who has opened these records
            </div>
            <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.55 }}>
              Everyone your club or school has shared a record with has to confirm their identity by email before
              anything is shown. If you do not recognise somebody here, ask them who they wrote to.
            </p>
            <div style={{ border: "1px solid #eef2f6", borderRadius: 8, overflow: "hidden" }}>
              {data.accessLog.slice(0, 12).map((a, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: i < Math.min(data.accessLog.length, 12) - 1 ? "1px solid #f3f4f6" : "none" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.openedBy || <span style={{ color: "#6b7280", fontStyle: "italic" }}>an older shared link</span>}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
                      {a.recordType === "injury" ? "First aid record" : "Concussion protocol"}
                      {" · "}
                      {new Date(a.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, borderRadius: 4, padding: "2px 7px",
                    color: a.identified ? "#1a4a2e" : "#92400e",
                    background: a.identified ? "#f0f6f2" : "#fffbeb",
                    border: `1px solid ${a.identified ? "#cfe2d6" : "#f0d88a"}` }}>
                    {a.identified ? "Confirmed" : "Not confirmed"}
                  </span>
                </div>
              ))}
            </div>
            {data.accessLog.length > 12 && (
              <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 8 }}>
                Showing the 12 most recent of {data.accessLog.length}.
              </div>
            )}
            <p style={{ fontSize: 11.5, color: "#9ca3af", margin: "10px 0 0", lineHeight: 1.5 }}>
              "Not confirmed" means somebody opened the link but did not complete the email check, so they were
              shown nothing that identifies your child.
            </p>
          </div>
        )}

        <div style={{ marginTop: 16, textAlign: "center", fontSize: 11, color: "#7a95ad" }}>
          GRTP Tracker · Sports Welfare Systems Ltd · grtp.co.uk
        </div>
      </div>

      {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
    </div>
  );
}

function ParentTrackingPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledgedAt, setAcknowledgedAt] = useState(null);

  // Everything else this same person holds here. Fetched alongside
  // rather than behind a separate link, so a coach who opens any one of
  // their emails lands on all of them — the inbox is not a filing
  // system and nobody searches it on a touchline.
  const [alsoHeld, setAlsoHeld] = useState(null);
  // Bumped after verifying, to refetch now the server knows who is asking.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.getPublicPlayerView(token)
      .then(d => {
        setData(d);
        if (d.parentAcknowledgedAt) setAcknowledgedAt(d.parentAcknowledgedAt);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // Older protocol-wide links cannot answer this and return null. The
    // page above must render regardless, so this never blocks it.
    api.getPassPlayers(token).then(setAlsoHeld).catch(() => setAlsoHeld(null));
  }, [token, reloadKey]);

  useEffect(() => {
    if (data?.organisationName) {
      document.title = `${data.organisationName} — GRTP Tracker`;
    }
  }, [data]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#9ca3af" }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 28, maxWidth: 380, textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><LogoMark size={40} /></div>
          <div style={{ fontWeight: 700, color: "#c0272d", marginBottom: 6 }}>Link Not Found</div>
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }

  // Nothing that identifies a child until the reader is known. The
  // status on its own was never a lighter version of the answer — "a
  // player is restricted" is not actionable when you cannot tell which
  // of your squad it is.
  if (data.verificationRequired) {
    return <VerifyToView token={token} data={data} onVerified={() => setReloadKey((k) => k + 1)} />;
  }

  const day = data.protocolDay;
  const stage = data.currentStage;
  const isCleared = data.status === "cleared";

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", paddingBottom: 60 }}>
      <GlobalStyles />
      <div style={{ background: "#1a4a2e", padding: "0 16px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", display: "flex", alignItems: "center", gap: 14, height: 58 }}>
          <LogoMark size={32} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{data.organisationName || "GRTP Tracker"}</div>
            <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>GRAS Progress Tracker</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 16px" }}>
        {/* A minimal pass carries no name: the address it was sent to was
            typed by one person and confirmed by nobody, so a transposed
            character would put a child's name in front of a stranger.
            The protocol is still shown, because that is the part that
            keeps a child off the pitch. */}
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 4px" }}>
          {data.identityWithheld ? "A player at this organisation" : data.name}
        </h2>
        {data.identityWithheld ? (
          <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 20px", lineHeight: 1.6 }}>
            We have not confirmed that this address reaches the person it was meant for, so the player is not named here.
            If you were expecting this, ask {data.organisationName || "the organisation"} to confirm your details.
          </p>
        ) : (
          <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 20px" }}>{data.ageGrade || ""}</p>
        )}

        {/* Only for the family. It writes parent_acknowledged_at, which is
            evidence that the PARENT saw the update; offering it to a coach
            would record a claim about somebody else. Older protocol-wide
            links report full as well, which is right: they only ever went
            to the family. First on the page, because confirming is what
            the email asked them to open it for. */}
        {data.status !== "archived" && data.viewLevel === "full" && (
          <FamilyConfirmCard
            acknowledgedAt={acknowledgedAt}
            onConfirm={async () => {
              try {
                const r = await api.acknowledgeGRTPUpdate(token);
                setAcknowledgedAt(r.acknowledgedAt);
                return r;
              } catch (e) {
                if (e.code === "VERIFICATION_REQUIRED") setReloadKey((k) => k + 1);
                throw e;
              }
            }}
            prompt={`Tap below to let ${data.organisationName || "the school or club"} know you have read this update about ${data.name || "this player"}. The date and time are recorded on their concussion record.`}
          />
        )}

        {data.paused ? (
          <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: "#92400e", fontSize: 15, marginBottom: 6 }}>⏸ 48-Hour Rest Period</div>
            <p style={{ fontSize: 14, color: "#78350f", margin: 0 }}>
              {data.name} is currently resting following a return of symptoms. They will resume the protocol automatically once the rest period ends.
            </p>
          </div>
        ) : isCleared ? (
          <div style={{ background: "#1a4a2e11", border: "2px solid #1a4a2e", borderRadius: 10, padding: 20, marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
            <div style={{ fontWeight: 700, color: "#1a4a2e", fontSize: 16, marginBottom: 10 }}>
              {data.name} has reached the end of the GRAS programme's recommended 21-day schedule.
            </div>
            <div style={{ fontSize: 13, color: "#374151", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, textAlign: "left" }}>
              <strong>This is not an automatic medical clearance.</strong> The decision to return to play must still be made by a qualified medical professional or {data.organisationName || "the school/club"}'s designated lead.
            </div>
          </div>
        ) : (
          <>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                <span>Day 0</span>
                <span style={{ fontWeight: 700, color: "#1a4a2e" }}>Day {Math.min(day, data.totalDays)} of {data.totalDays}</span>
                <span>Day {data.totalDays}+</span>
              </div>
              <div style={{ height: 10, background: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min((day / data.totalDays) * 100, 100)}%`, background: "linear-gradient(90deg, #c0272d, #c9991a, #1a4a2e)", borderRadius: 99 }} />
              </div>
            </div>

            <div style={{ background: "#fff", border: "2px solid #1a4a2e", borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#1a4a2e", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Current Stage</div>
              <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 12 }}>{stage.label}: {stage.name}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Guidance at this Stage</div>
              <ul style={{ margin: "0 0 10px 20px", padding: 0, fontSize: 14, color: "#374151", lineHeight: 1.8 }}>
                {stage.activities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
              <div style={{ background: "#f9fafb", borderLeft: "3px solid #1a4a2e", padding: "8px 12px", fontSize: 13, color: "#374151", borderRadius: "0 4px 4px 0" }}>
                {stage.notes}
              </div>
            </div>

            {data.nextStage && (
              <div style={{ fontSize: 13, color: "#6b7280", textAlign: "center", marginBottom: 16 }}>
                Expected to progress to <strong>{data.nextStage.label}: {data.nextStage.name}</strong> on Day {data.nextStage.dayStart}.
              </div>
            )}
          </>
        )}

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
            If {data.name} experiences any return of concussion symptoms, they must stop all activity immediately and inform a member of staff.
          </p>
        </div>

        <AlsoHeldByYou held={alsoHeld} currentToken={token} />

        <div style={{ textAlign: "center" }}>
          <a href="https://www.englandrugby.com/run/player-welfare/headcase" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#1a4a2e", textDecoration: "underline" }}>
            Learn more about the RFU's HEADCASE concussion guidance
          </a>
        </div>
      </div>
    </div>
  );
}


// ── Trial Expired Screen ──────────────────────────────────────────────
function TrialExpiredScreen({ orgName, onLogout, reason = "trial_expired" }) {
  const [lapse, setLapse] = useState(null); // { deletionDueAt, exportAvailable }
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tier, setTier] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [existingBilling, setExistingBilling] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [payingByCard, setPayingByCard] = useState(false);

  // When the data will be deleted, and whether this person may take it.
  useEffect(() => {
    api.getOrganisationStatus().then(setLapse).catch(() => setLapse({}));
  }, []);

  // The Terms promise 60 days after an account ends in which the customer
  // may export its data. This is where that happens: the lapsed screen is
  // the only one a lapsed organisation can reach.
  async function handleDownloadData() {
    setExporting(true);
    setExportError("");
    try {
      const { blob, filename } = await api.downloadLapsedExport();
      await nativeApp.saveFile(blob, filename);
    } catch (e) {
      setExportError(e.message || "The download failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    api.getTrialBillingDetails()
      .then(data => {
        setExistingBilling(data);
        if (data.billingAddress) setBillingAddress(data.billingAddress);
        if (data.billingEmail)   setBillingEmail(data.billingEmail);
        if (data.poNumber)       setPoNumber(data.poNumber);
        if (data.tier)           setTier(data.tier);
      })
      .catch(() => setExistingBilling({}))
      .finally(() => setLoadingBilling(false));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!billingAddress.trim() || !billingEmail.trim()) {
      setError("Please enter your billing address and finance email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.requestTrialInvoice({ tier, billingAddress, billingEmail, poNumber });
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  async function handlePayByCard() {
    if (!tier || payingByCard) return;
    setPayingByCard(true);
    setError("");
    try {
      const { url } = await api.createCheckoutSession(tier);
      window.location.href = url;
    } catch (e) {
      setError(e.message || "Could not start checkout. Please try again or contact support.");
      setPayingByCard(false);
    }
  }

  const containerStyle = { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: "#f0f4f8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "inherit", marginBottom: 4 };

  if (loadingBilling) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Loading...</div>
      </div>
    );
  }

  const hasBillingOnFile = existingBilling && existingBilling.billingAddress && existingBilling.billingEmail;

  if (submitted) {
    return (
      <div style={containerStyle}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, maxWidth: 480, width: "100%", textAlign: "center", border: "1px solid #e2eaf2" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#1a4a2e", marginBottom: 12 }}>Invoice request received</div>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, margin: "0 0 24px" }}>
            We'll send your invoice to <strong>{billingEmail}</strong> within 1 business day.
            As soon as payment clears, we'll activate your account and you'll be back in.
          </p>
          <p style={{ fontSize: 13, color: "#9ca3af" }}>Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#1a4a2e", fontWeight: 600 }}>{SUPPORT_EMAIL}</a></p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 36, maxWidth: 520, width: "100%", border: "1px solid #e2eaf2" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <LogoMark size={36} />
          <div style={{ fontWeight: 800, fontSize: 20, color: "#111827", marginTop: 16, marginBottom: 8 }}>
            {reason === "cancelled" ? "Your subscription has ended" : "Your free trial has ended"}
          </div>
          <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
            {reason === "cancelled"
              ? <>The subscription for <strong>{orgName}</strong> has ended.</>
              : <>Your 30-day free trial for <strong>{orgName}</strong> has ended.</>}
            {hasBillingOnFile ? " Your billing details are already on file — just click below to continue." : " Enter your billing details below to continue."}
          </p>
        </div>

        {/* Your data: when it goes, and how to take it with you. Shown
            whatever the organisation decides about paying. */}
        {lapse && (
          <div style={{ background: "#f8fafc", border: "1px solid #e2eaf2", borderRadius: 10, padding: 16, marginBottom: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 6 }}>Your data</div>
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6, margin: "0 0 12px" }}>
              {lapse.deletionDueAt
                ? <>Unless you continue, everything {orgName} holds in GRTP Tracker will be permanently deleted on <strong>{formatDate(lapse.deletionDueAt)}</strong>. You can download a complete copy before then.</>
                : <>Everything {orgName} holds in GRTP Tracker is kept safe while you decide, and you can download a complete copy at any time.</>}
            </p>
            {lapse.exportAvailable ? (
              <button type="button" onClick={handleDownloadData} disabled={exporting}
                style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: exporting ? "default" : "pointer", fontFamily: "inherit" }}>
                {exporting ? "Preparing download…" : "Download all your data"}
              </button>
            ) : (
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                Only an organisation admin with two-factor authentication can download it. If that isn't possible, email{" "}
                <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: "#1a4a2e", fontWeight: 600 }}>{PRIVACY_EMAIL}</a> and we will provide it.
              </p>
            )}
            {exportError && <div style={{ fontSize: 12, color: "#c0272d", marginTop: 8 }}>{exportError}</div>}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {hasBillingOnFile ? (
            /* Billing already on file — show summary, not the full form */
            <div style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1a4a2e", marginBottom: 8 }}>✓ Billing details on file</div>
              <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-line", marginBottom: 4 }}>{billingAddress}</div>
              <div style={{ fontSize: 13, color: "#374151" }}>{billingEmail}</div>
              {poNumber && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>PO: {poNumber}</div>}
              <button type="button" onClick={() => setExistingBilling({})}
                style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "6px 0 0", fontFamily: "inherit", textDecoration: "underline" }}>
                Update billing details
              </button>
            </div>
          ) : (
          <>
          {/* Tier selector */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 8 }}>Pricing plan</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[["grassroots","Grassroots","£6.99/mo","Up to 30 players · 5 staff"],["club_school","Club / School","£11.99/mo","Up to 300 players · 25 staff"],["large_club_school","Large Club / School","£14.99/mo","Up to 750 players · 50 staff"]].map(([val,label,price,desc]) => (
                <button key={val} type="button" onClick={() => setTier(val)}
                  style={{ border: `2px solid ${tier === val ? "#1a4a2e" : "#e5e7eb"}`, background: tier === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "10px 14px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13.5, fontWeight: tier === val ? 700 : 500, color: tier === val ? "#1a4a2e" : "#374151" }}>{label}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: tier === val ? "#1a4a2e" : "#374151" }}>{price}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 2 }}>{desc} · billed annually</div>
                </button>
              ))}
            </div>
          </div>

          {/* Billing details */}
          <div style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1a4a2e", marginBottom: 12 }}>Billing details</div>
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Billing address <span style={{ color: "#c0272d" }}>*</span></label>
            <textarea value={billingAddress} onChange={e => setBillingAddress(e.target.value)}
              placeholder={"Finance Department\nYour Organisation\n1 Example Street\nTown\nAB1 2CD"}
              rows={4} style={{ ...inp, resize: "vertical" }} />
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4, marginTop: 8 }}>Finance / billing email <span style={{ color: "#c0272d" }}>*</span></label>
            <input type="email" value={billingEmail} onChange={e => setBillingEmail(e.target.value)}
              placeholder="finance@yourorg.co.uk" style={inp} />
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4, marginTop: 8 }}>PO number <span style={{ fontSize: 11, fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
            <input value={poNumber} onChange={e => setPoNumber(e.target.value)}
              placeholder="PO-2026-1234" style={inp} />
          </div>
          </>
          )}

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>{error}</div>
          )}

          <button type="submit" disabled={submitting || !billingAddress.trim() || !billingEmail.trim()}
            style={{ width: "100%", background: (!billingAddress.trim() || !billingEmail.trim()) ? "#9ca3af" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: (submitting || !billingAddress.trim() || !billingEmail.trim()) ? "default" : "pointer", marginBottom: 12, fontFamily: "inherit" }}>
            {submitting ? "Submitting..." : "Request Invoice"}
          </button>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button type="button" onClick={handlePayByCard} disabled={!tier || payingByCard}
              title={!tier ? "Choose a plan above first" : ""}
              style={{ background: (!tier || payingByCard) ? "#f9fafb" : "#fff", border: `1px solid ${(!tier || payingByCard) ? "#e5e7eb" : "#1a4a2e"}`, borderRadius: 8, padding: "11px 0", fontSize: 13, fontWeight: 600, color: (!tier || payingByCard) ? "#9ca3af" : "#1a4a2e", cursor: (!tier || payingByCard) ? "default" : "pointer", fontFamily: "inherit" }}>
              {payingByCard ? "Redirecting..." : "💳 Pay by card"}
            </button>
            <button type="button" onClick={onLogout}
              style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "11px 0", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>
              Sign out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Shown when a card-at-signup org logs in but its Checkout session never
// completed (e.g. the initial checkout-session creation failed, or they
// closed the Stripe tab without paying). Unlike TrialExpiredScreen, the
// plan was already chosen at signup, so this is just a single retry
// button — no tier picker, no billing-address form.
// The phone app's stand-in for the trial-expired, account-ended and
// payment-pending screens. Those take payment and show prices, and an app
// in Apple's store may not offer any purchase outside Apple's own system
// or point people to one. Coaches are not the ones paying either: the
// person who can act is the organisation's administrator, on the web.
function AppAccountInactiveScreen({ orgName, onLogout }) {
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: "#f0f4f8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 420, width: "100%", border: "1px solid #e2eaf2", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><LogoMark size={44} /></div>
        <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 10 }}>This account isn't active</div>
        <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.6, margin: "0 0 22px" }}>
          {orgName ? <><strong>{orgName}</strong>'s</> : "Your organisation's"} GRTP Tracker account isn't active at the moment, so the app can't open its records.
          Please speak to your organisation's administrator.
        </p>
        <button type="button" onClick={onLogout}
          style={{ width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "11px 0", fontSize: 14, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function PaymentPendingScreen({ orgName, tier, onLogout }) {
  const [payingByCard, setPayingByCard] = useState(false);
  const [error, setError] = useState("");

  const tierLabels = { grassroots: "Grassroots — £6.99/mo", club_school: "Club / School — £11.99/mo", large_club_school: "Large Club / School — £14.99/mo" };

  async function handlePayByCard() {
    if (!tier || payingByCard) return;
    setPayingByCard(true);
    setError("");
    try {
      const { url } = await api.createCheckoutSession(tier);
      window.location.href = url;
    } catch (e) {
      setError(e.message || "Could not start checkout. Please try again or contact support.");
      setPayingByCard(false);
    }
  }

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: "#f0f4f8", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 36, maxWidth: 440, width: "100%", border: "1px solid #e2eaf2", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>💳</div>
        <div style={{ fontWeight: 800, fontSize: 19, color: "#111827", marginBottom: 8 }}>Complete your payment</div>
        <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 6px" }}>
          Your account for <strong>{orgName}</strong> is set up, but checkout didn't complete last time.
        </p>
        {tier && <p style={{ fontSize: 14, color: "#374151", fontWeight: 700, margin: "0 0 20px" }}>{tierLabels[tier] || tier}</p>}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 14, textAlign: "left" }}>{error}</div>
        )}
        <button type="button" onClick={handlePayByCard} disabled={!tier || payingByCard}
          style={{ width: "100%", background: (!tier || payingByCard) ? "#9ca3af" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: (!tier || payingByCard) ? "default" : "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          {payingByCard ? "Redirecting..." : "💳 Pay by card"}
        </button>
        <button type="button" onClick={onLogout}
          style={{ width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "11px 0", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>
          Sign out
        </button>
        <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 16 }}>
          Prefer to pay by invoice instead? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#1a4a2e", fontWeight: 600 }}>{SUPPORT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, onShowSignup, onShowForgotPassword }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingToken, setPendingToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  // The phone app only. biometric: what this phone calls it ("Face ID"),
  // or null. savedSignIn: a device token is stored and can be unlocked.
  // offer: a password sign-in just succeeded and the next step is asking
  // whether to stay signed in on this phone; holds what to pass to onLogin.
  const [biometric, setBiometric] = useState(null);
  const [savedSignIn, setSavedSignIn] = useState(false);
  const [offer, setOffer] = useState(null);
  const triedAutoUnlock = useRef(false);

  // An organisation that is not active, but whose people are still let in
  // to a restricted screen. Shared by the password step and the two-factor
  // step, which used to disagree: only the password step knew about it.
  function handleLapsedLogin(e) {
    if (e.message === "trial_expired") { onLogin({ trialExpired: true }); return true; }
    if (e.message === "cancelled") { onLogin({ accountEnded: true }); return true; }
    if (e.message === "pending_payment") { onLogin({ paymentPending: true, pendingTier: e.tier || null }); return true; }
    return false;
  }

  useEffect(() => {
    if (!nativeApp.isNativeApp()) return;
    (async () => {
      const name = await nativeApp.biometricName();
      setBiometric(name);
      const saved = name ? await nativeApp.hasSavedSignIn() : false;
      setSavedSignIn(saved);
      // Opening the app goes straight to Face ID, once per launch.
      // Cancelling it, or coming back here after a timeout, leaves the
      // button and the password form rather than prompting unasked.
      if (saved && !triedAutoUnlock.current && nativeApp.consumeColdStart()) {
        triedAutoUnlock.current = true;
        handleUnlock();
      }
    })();
  }, []);

  async function handleUnlock() {
    setSubmitting(true);
    setError("");
    let saved;
    try {
      saved = await nativeApp.readSignIn();
    } catch {
      setSubmitting(false); // cancelled, or the biometrics were refused
      return;
    }
    let data;
    try {
      data = await api.appSession(saved.deviceToken);
    } catch (e) {
      // A lapsed organisation is refused, but the token was still rotated.
      if (e.deviceToken) await nativeApp.saveSignIn(saved.deviceId, e.deviceToken).catch(() => {});
      setSubmitting(false);
      if (handleLapsedLogin(e)) return;
      if (e.status === 401) {
        // Revoked, expired or replaced: the saved sign-in is dead.
        await nativeApp.forgetSignIn();
        nativeApp.rememberDeviceId(null);
        setSavedSignIn(false);
        setError("Please sign in with your password.");
      } else if (e.status) {
        // The server answered and said why (an invoice still due, say).
        setError(e.message);
      } else {
        // No answer at all is not a refusal. Keep the saved sign-in.
        setError("Couldn't reach GRTP Tracker. Check your signal and try again.");
      }
      return;
    }
    // The server has already rotated the token. If the new one cannot be
    // saved (on Android, a second fingerprint prompt cancelled after a
    // slow answer), the phone would be left holding the old one, and its
    // next use would look like a stolen copy. Retire this phone cleanly
    // instead: the coach is signed in now and is offered Face ID again
    // next time they use their password.
    try {
      await nativeApp.saveSignIn(saved.deviceId, data.deviceToken);
      nativeApp.rememberDeviceId(saved.deviceId);
    } catch {
      await api.revokeOwnAppDevice(saved.deviceId).catch(() => {});
      await nativeApp.forgetSignIn();
      nativeApp.rememberDeviceId(null);
    }
    setSubmitting(false);
    onLogin({ daysLeftInTrial: data.daysLeftInTrial ?? null, trialExpired: false });
  }

  // After a full password (and two-factor) sign-in on the phone app,
  // offer to stay signed in, unless it is already set up.
  async function finishLogin(loginData) {
    if (nativeApp.isNativeApp() && biometric && !savedSignIn) {
      setOffer(loginData);
      return;
    }
    onLogin(loginData);
  }

  async function acceptOffer() {
    const loginData = offer;
    setSubmitting(true);
    setError("");
    try {
      const { deviceId, deviceToken } = await api.registerAppDevice(nativeApp.nativePlatform(), await nativeApp.deviceLabel());
      try {
        await nativeApp.saveSignIn(deviceId, deviceToken);
        nativeApp.rememberDeviceId(deviceId);
      } catch {
        // Biometric prompt cancelled: the token would be stranded, so
        // withdraw it rather than leave a live credential nobody holds.
        await api.revokeOwnAppDevice(deviceId).catch(() => {});
      }
    } catch {
      // Not being able to set it up must never stop someone getting in.
    }
    setSubmitting(false);
    onLogin(loginData);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await api.login(email, password);
      if (result.mfaRequired) {
        setPendingToken(result.pendingToken);
      } else {
        await finishLogin({ daysLeftInTrial: result.daysLeftInTrial ?? null, trialExpired: false });
      }
    } catch (e) {
      if (!handleLapsedLogin(e)) setError(e.message || "Incorrect email or password.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaSubmit(e) {
    e.preventDefault();
    if (!mfaCode.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await api.verifyMfa(pendingToken, mfaCode.trim());
      await finishLogin({ daysLeftInTrial: result?.daysLeftInTrial ?? null, trialExpired: false });
    } catch (e) {
      if (!handleLapsedLogin(e)) setError(e.message || "Incorrect verification code.");
    } finally {
      setSubmitting(false);
    }
  }

  if (offer) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><LogoMark size={40} /></div>
          <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>Use {biometric} next time?</div>
          <p style={{ color: "#4b5563", fontSize: 14, lineHeight: 1.55, margin: "0 0 20px" }}>
            Stay signed in on this phone and open GRTP Tracker with {biometric} instead of your password.
            Only the {biometric === "Face ID" || biometric === "face unlock" ? "faces" : "fingerprints"} set up on this phone right now can unlock it.
            An admin at your organisation can sign this phone out at any time.
          </p>
          <button type="button" onClick={acceptOffer} disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "11px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.7 : 1, marginBottom: 10 }}>
            {submitting ? "Setting up..." : `Use ${biometric}`}
          </button>
          <button type="button" onClick={() => { const d = offer; setOffer(null); onLogin(d); }} disabled={submitting} style={{ width: "100%", background: "none", border: "1px solid #d1d5db", color: "#374151", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  if (pendingToken) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <form onSubmit={handleMfaSubmit} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><LogoMark size={40} /></div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e" }}>Two-Factor Authentication</div>
            <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>Enter the 6-digit code from your authenticator app, or one of your backup codes.</div>
          </div>
          <input
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            autoFocus
            placeholder="123456"
            inputMode="numeric"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 18, textAlign: "center", letterSpacing: 4, marginBottom: 12, fontFamily: "inherit" }}
          />
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}
          <button type="submit" disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
            {submitting ? "Checking..." : "Verify"}
          </button>
          <button type="button" onClick={() => { setPendingToken(null); setMfaCode(""); setError(""); }} style={{ width: "100%", background: "none", border: "none", color: "#6b7280", fontSize: 13, marginTop: 14, cursor: "pointer" }}>
            ← Back to login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onSubmit={handleSubmit} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}><LogoMark size={48} /></div>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#1a4a2e", letterSpacing: "-0.02em" }}>GRTP Tracker</div>
          <div style={{ color: "#6b7280", fontSize: 12, fontWeight: 500, marginTop: 3 }}>Graduated Return to Activity & Sport</div>
        </div>
        {savedSignIn && biometric && (
          <>
            <button type="button" onClick={handleUnlock} disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: submitting ? 0.7 : 1, marginBottom: 16 }}>
              {submitting ? "Unlocking..." : `Unlock with ${biometric}`}
            </button>
            <div style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>or sign in with your password</div>
          </>
        )}
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          // Not in the phone app: there, focusing the box on arrival opens
          // the keyboard straight away, and on a standard-size iPhone it
          // covers the password box and "Log In" before anyone has typed.
          // On the website a ready cursor is still the quickest start.
          autoFocus={!savedSignIn && !nativeApp.isNativeApp()}
          placeholder="you@example.com"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Password</label>
          {onShowForgotPassword && (
            <button type="button" onClick={onShowForgotPassword} style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
              Forgot password?
            </button>
          )}
        </div>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Enter password"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
        />
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <button type="submit" disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
          {submitting ? "Checking..." : "Log In"}
        </button>
        {onShowSignup && (
          <button type="button" onClick={onShowSignup} style={{ width: "100%", background: "none", border: "none", color: "#1a4a2e", fontSize: 13, fontWeight: 600, marginTop: 14, cursor: "pointer", textDecoration: "underline" }}>
            Don't have an account? Sign up
          </button>
        )}
        <div style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 16, marginBottom: 14 }}>
          Contains medical information — staff access only.
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ background: "#1a4a2e", borderRadius: 8, padding: "2px" }}>
            <SupportButton pageContext="Login screen" />
          </div>
        </div>
      </form>
    </div>
  );
}

function SignupScreen({ onBackToLogin }) {
  const [organisationName, setOrganisationName] = useState("");
  // Not validated on the way in and not a gate: evidence for the human
  // who decides whether this is a real club before its registration
  // links go live. Treating it as proof would be worse than not asking.
  const [postcode, setPostcode] = useState("");
  const [orgReference, setOrgReference] = useState("");
  const [website, setWebsite] = useState("");
  const [signupRole, setSignupRole] = useState("");
  const [orgType, setOrgType] = useState("");
  const [tier, setTier] = useState("");
  const [paymentRoute, setPaymentRoute] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!organisationName || !orgType || !tier || !paymentRoute || !email || !password) return;
    if ((paymentRoute === "invoice" || paymentRoute === "trial") && (!billingAddress.trim() || !billingEmail.trim())) {
      setError("Please enter your billing address and finance email.");
      return;
    }
    if (!paymentRoute) { setError("Please select a payment option."); return; }
    if (!tosAccepted) {
      setError("Please confirm you accept the Terms of Service and Data Processing Agreement.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const result = await api.signup(organisationName, orgType, tier, paymentRoute, email, password, tosAccepted, { billingAddress, billingEmail, poNumber, marketingOptIn, postcode, orgReference, website, signupRole });
      if (paymentRoute === "card" && result && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Something went wrong creating your account.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📬</div>
          <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>
            {paymentRoute === "trial" ? "Trial started!" : paymentRoute === "invoice" ? "Request received" : paymentRoute === "card" ? "Account created" : "Check your email"}
          </div>
          <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>
            {paymentRoute === "trial"
              ? <>Welcome! Check your email at <strong>{email}</strong> to verify your address, then log in to start your 30-day free trial. No payment needed until you're ready.</>
              : paymentRoute === "invoice"
              ? <>We'll send an invoice to <strong>{email}</strong> within 1 business day. Once payment clears, you'll receive an activation email.</>
              : paymentRoute === "card"
              ? <>Your account has been created, but we couldn't start checkout just now. Please check your email at <strong>{email}</strong> to verify your address, then log in and pay from your dashboard.</>
              : <>We've sent a verification link to <strong>{email}</strong>. Click it to confirm your address, then come back here to log in.</>
            }
          </p>
          <button onClick={onBackToLogin} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onSubmit={handleSubmit} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><LogoMark size={40} /></div>
          <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e" }}>Create your account</div>
          <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>Set up GRTP tracking for your club or school</div>
        </div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Club / school name</label>
        <input
          value={organisationName}
          onChange={e => setOrganisationName(e.target.value)}
          autoFocus
          placeholder="e.g. Example RFC"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
        />
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Type of organisation</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[["school", "🏫", "School"], ["club", "🏉", "Club"], ["academy", "🌟", "Academy"]].map(([val, icon, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setOrgType(val)}
              style={{ border: `2px solid ${orgType === val ? "#1a4a2e" : "#e5e7eb"}`, background: orgType === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "10px 6px", fontSize: 13, fontWeight: orgType === val ? 700 : 500, color: orgType === val ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}
            >
              <div style={{ fontSize: 20, marginBottom: 2 }}>{icon}</div>
              {label}
            </button>
          ))}
        </div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Pricing plan</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {[["grassroots","Grassroots","£6.99/mo","Up to 30 players · 5 staff"],["club_school","Club / School","£11.99/mo","Up to 300 players · 25 staff"],["large_club_school","Large Club / School","£14.99/mo","Up to 750 players · 50 staff"]].map(([val,label,price,desc]) => (
            <button key={val} type="button" onClick={() => setTier(val)}
              style={{ border: `2px solid ${tier === val ? "#1a4a2e" : "#e5e7eb"}`, background: tier === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "10px 14px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13.5, fontWeight: tier === val ? 700 : 500, color: tier === val ? "#1a4a2e" : "#374151" }}>{label}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: tier === val ? "#1a4a2e" : "#374151" }}>{price}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 2 }}>{desc} · billed annually</div>
            </button>
          ))}
        </div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>How would you like to pay?</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          <button type="button" onClick={() => setPaymentRoute("trial")}
            style={{ border: `2px solid ${paymentRoute === "trial" ? "#1a4a2e" : "#e5e7eb"}`, background: paymentRoute === "trial" ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "12px 8px", cursor: "pointer", fontFamily: "inherit", textAlign: "center", gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>🎯</div>
            <div style={{ fontSize: 13, fontWeight: paymentRoute === "trial" ? 700 : 500, color: paymentRoute === "trial" ? "#1a4a2e" : "#374151" }}>Start free trial</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>30 days free · no card needed</div>
          </button>
          <button type="button" onClick={() => setPaymentRoute("invoice")}
            style={{ border: `2px solid ${paymentRoute === "invoice" ? "#1a4a2e" : "#e5e7eb"}`, background: paymentRoute === "invoice" ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "12px 8px", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>🧾</div>
            <div style={{ fontSize: 13, fontWeight: paymentRoute === "invoice" ? 700 : 500, color: paymentRoute === "invoice" ? "#1a4a2e" : "#374151" }}>Invoice / BACS</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>We will email you an invoice</div>
          </button>
          <button type="button" onClick={() => setPaymentRoute("card")}
            style={{ border: `2px solid ${paymentRoute === "card" ? "#1a4a2e" : "#e5e7eb"}`, background: paymentRoute === "card" ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "12px 8px", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>💳</div>
            <div style={{ fontSize: 13, fontWeight: paymentRoute === "card" ? 700 : 500, color: paymentRoute === "card" ? "#1a4a2e" : "#374151" }}>Card payment</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Pay now, activate instantly</div>
          </button>
        </div>
        {(paymentRoute === "invoice" || paymentRoute === "trial") && (
          <div style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1a4a2e", marginBottom: 4 }}>Billing details</div>
            {paymentRoute === "trial" && (
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>
                You won't be charged during your trial. We collect these now so converting at the end is instant — no form to fill in when the time comes.
              </p>
            )}
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Billing address <span style={{ color: "#c0272d" }}>*</span></label>
            <textarea
              value={billingAddress}
              onChange={e => setBillingAddress(e.target.value)}
              placeholder={"Finance Department\nYour Organisation\n1 Example Street\nTown\nAB1 2CD"}
              rows={4}
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, marginBottom: 10, fontFamily: "inherit", resize: "vertical" }}
            />
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Finance / billing email <span style={{ color: "#c0272d" }}>*</span></label>
            <input
              type="email"
              value={billingEmail}
              onChange={e => setBillingEmail(e.target.value)}
              placeholder="finance@school.sch.uk"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, marginBottom: 10, fontFamily: "inherit" }}
            />
            <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 4 }}>PO number <span style={{ fontSize: 11, fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
            <input
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              placeholder="PO-2026-1234"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, fontFamily: "inherit" }}
            />
          </div>
        )}
        {/* Asked so the organisation can be checked against an
            independent record before its registration links go live.
            Explained rather than just demanded, because a form that
            wants a club number without saying why reads as nosy. */}
        <div style={{ background: "#f0f6f2", border: "1px solid #cfe2d6", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, color: "#14331f", lineHeight: 1.6, marginBottom: 10 }}>
            We check every new organisation is a real club or school before its registration links
            go live. It takes us about a day, and it is what stops somebody setting up in your name.
          </div>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Postcode</label>
          <input value={postcode} onChange={e => setPostcode(e.target.value)}
            placeholder="CW11 1AA" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }} />
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
            {orgType === "school" ? "School URN" : "RFU club number"}{" "}
            <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional, but it speeds us up)</span>
          </label>
          <input value={orgReference} onChange={e => setOrgReference(e.target.value)}
            placeholder={orgType === "school" ? "e.g. 123456" : "e.g. 12345"} style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }} />
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Website <span style={{ fontWeight: 400, color: "#6b7280" }}>(optional)</span></label>
          <input value={website} onChange={e => setWebsite(e.target.value)}
            placeholder="yourclub.co.uk" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }} />
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Your role there</label>
          <input value={signupRole} onChange={e => setSignupRole(e.target.value)}
            placeholder="e.g. Welfare Officer, Club Secretary" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, fontFamily: "inherit" }} />
        </div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Your email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
        />
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Password</label>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
        />
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "0 0 16px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={tosAccepted}
            onChange={e => setTosAccepted(e.target.checked)}
            style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
          />
          <span style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
            I confirm I am authorised to act for this organisation and accept the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Terms of Service</a>,{" "}
            <a href="/dpa" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Data Processing Agreement</a>, and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Privacy Policy</a>.
          </span>
        </label>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "0 0 16px", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={e => setMarketingOptIn(e.target.checked)}
            style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
          />
          <span style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
            Yes — send me occasional tips for getting the most out of the GRAS protocol, and a heads-up when new features ship. You can unsubscribe any time.
          </span>
        </label>
        <button type="submit" disabled={submitting || !tosAccepted || !tier || !paymentRoute || ((paymentRoute === "invoice" || paymentRoute === "trial") && (!billingAddress.trim() || !billingEmail.trim()))} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: (submitting || !tosAccepted || !tier || !paymentRoute) ? "default" : "pointer", opacity: (submitting || !tosAccepted || !tier || !paymentRoute) ? 0.6 : 1 }}>
          {submitting ? (paymentRoute === "card" ? "Redirecting to payment..." : "Creating account...") : paymentRoute === "trial" ? "Start Free Trial" : paymentRoute === "invoice" ? "Request Account & Invoice" : paymentRoute === "card" ? "Continue to Payment" : "Sign Up"}
        </button>
        <button type="button" onClick={onBackToLogin} style={{ width: "100%", background: "none", border: "none", color: "#1a4a2e", fontSize: 13, fontWeight: 600, marginTop: 14, cursor: "pointer", textDecoration: "underline" }}>
          Already have an account? Log in
        </button>
      </form>
    </div>
  );
}


// ── Adult Self-Registration Page ─────────────────────────────────
// Adult players register themselves directly — no parent/guardian needed.
// Different wording, different fields (player email instead of parent email,
// no school coach), same pending queue with registration_type = "adult".
function AdultRegistrationPage({ token }) {
  const [orgName, setOrgName] = useState("");
  const [loadError, setLoadError] = useState("");
  const [screen, setScreen] = useState("loading"); // loading | form | success | error

  const [form, setForm] = useState({
    playerName: "", playerEmail: "", position: "",
    clubCoachName: "", clubCoachEmail: "", clubCoach2Name: "", clubCoach2Email: "", consentGiven: false,
  });
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [playerEmailConfirm, setPlayerEmailConfirm] = useState("");

  useEffect(() => {
    api.getAdultRegistration(token)
      .then(data => { setOrgName(data.organisationName); setScreen("form"); })
      .catch(e => { setLoadError(e.message); setScreen("error"); });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.playerName.trim()) return setSubmitError("Please enter your full name.");
    if (!form.playerEmail.trim()) return setSubmitError("Please enter your email address.");
    if (form.playerEmail.trim().toLowerCase() !== playerEmailConfirm.trim().toLowerCase()) {
      return setSubmitError("The two email addresses you typed don't match. Please check them both.");
    }
    if (!form.consentGiven) return setSubmitError("Please confirm your consent to continue.");
    setSubmitting(true); setSubmitError("");
    try {
      await api.submitAdultRegistration(token, {
        playerName: form.playerName.trim(),
        playerEmail: form.playerEmail.trim(),
        position: form.position.trim(),
        clubCoachName: form.clubCoachName.trim(),
        clubCoachEmail: form.clubCoachEmail.trim(),
        clubCoach2Name: form.clubCoach2Name.trim(),
        clubCoach2Email: form.clubCoach2Email.trim(),
        consentGiven: true,
      });
      setScreen("success");
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  }

  const containerStyle = { fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh" };
  const headerStyle = { background: "#1a4a2e", padding: "0 16px" };
  const headerInner = { maxWidth: 560, margin: "0 auto", display: "flex", alignItems: "center", gap: 14, height: 56 };
  const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 14, fontFamily: "inherit", marginBottom: 4 };

  if (screen === "loading") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={32} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>GRTP Tracker</div></div></div>
      <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Loading...</div>
    </div>
  );

  if (screen === "error") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={32} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>GRTP Tracker</div></div></div>
      <div style={{ maxWidth: 560, margin: "40px auto", padding: "0 16px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: "#111827", marginBottom: 8 }}>Registration link not found</div>
        <p style={{ color: "#6b7280", fontSize: 14 }}>{loadError || "This link may have expired or is incorrect. Please contact your club."}</p>
      </div>
    </div>
  );

  if (screen === "success") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={28} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName}</div></div></div>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "40px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", marginBottom: 10 }}>Registration received</h2>
        <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          Thanks {form.playerName}. Your registration has been submitted to {orgName}.<br />
          A member of staff will confirm your details and add you to the squad.
        </p>
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}>
        <div style={headerInner}>
          <LogoMark size={28} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName || "GRTP Tracker"}</div>
            <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Adult Player Registration</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "28px 16px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 6px" }}>
          {orgName ? `${orgName} — Player Registration` : "Player Registration"}
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 24px", lineHeight: 1.6 }}>
          Register yourself as a player with {orgName}. This allows staff to manage concussion protocols
          and keep you informed directly if you sustain a head injury during play.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Your details */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 14 }}>Your details</div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Full name <span style={{ color: "#c0272d" }}>*</span></label>
            <input value={form.playerName} onChange={e => setForm(f => ({ ...f, playerName: e.target.value }))} placeholder="e.g. Tom Davies" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Your email address <span style={{ color: "#c0272d" }}>*</span></label>
            <EmailInput value={form.playerEmail} onChange={v => setForm(f => ({ ...f, playerEmail: v }))} placeholder="tom@example.com" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Type your email address again <span style={{ color: "#c0272d" }}>*</span></label>
            <input type="email" value={playerEmailConfirm} onChange={e => setPlayerEmailConfirm(e.target.value)} autoComplete="off" style={inp} />
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>GRAS stage updates will be sent to this address, so please check it carefully.</p>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Position (optional)</label>
            <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="e.g. Prop" style={inp} />
          </div>

          {/* Club coach */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 6 }}>Club coach (optional)</div>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>If you'd like your coach to receive GRTP updates too, enter their details here.</p>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Coach name</label>
            <input value={form.clubCoachName} onChange={e => setForm(f => ({ ...f, clubCoachName: e.target.value }))} placeholder="e.g. Dave Wilson" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Coach email</label>
            <EmailInput value={form.clubCoachEmail} onChange={v => setForm(f => ({ ...f, clubCoachEmail: v }))} placeholder="coach@club.co.uk" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Second coach name (optional)</label>
            <input value={form.clubCoach2Name} onChange={e => setForm(f => ({ ...f, clubCoach2Name: e.target.value }))} placeholder="e.g. Team manager" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Second coach email</label>
            <EmailInput value={form.clubCoach2Email} onChange={v => setForm(f => ({ ...f, clubCoach2Email: v }))} placeholder="manager@club.co.uk" style={inp} />
          </div>

          {/* Consent */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 10 }}>How your information is used</div>
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, marginBottom: 14 }}>
              By registering, you agree that {orgName} may record details of any suspected concussion you sustain during rugby
              activities, use those details to manage a Graduated Return to Play protocol on your behalf, and contact you — and
              any coach you list above — at each stage of your recovery. Records are stored securely in the EU and kept for
              seven years from the date of an incident, then deleted automatically; {orgName} keeps them to meet its duty of
              care, so they are not deleted on request while that duty applies. You can ask for a copy, ask for corrections,
              object to how your information is used, or ask us to stop emailing a contact, at any time.
              Full detail is in GRTP Tracker's <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1a4a2e", fontWeight: 600 }}>Privacy Policy</a>.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.consentGiven}
                onChange={e => setForm(f => ({ ...f, consentGiven: e.target.checked }))}
                style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
              />
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.55 }}>
                I consent to {orgName} recording and managing my concussion protocol data as described above.
              </span>
            </label>
          </div>

          {submitError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#c0272d", fontSize: 13, marginBottom: 14 }}>
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !form.consentGiven}
            style={{ width: "100%", background: form.consentGiven ? "#1a4a2e" : "#9ca3af", color: "#fff", border: "none", borderRadius: 8, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: submitting || !form.consentGiven ? "default" : "pointer", fontFamily: "inherit" }}
          >
            {submitting ? "Submitting..." : "Submit Registration"}
          </button>
        </form>
      </div>
    </div>
  );
}

// A coach requesting staff access — publicly shareable link, but unlike
// the player registration pages above, submitting this form never
// creates a real account. It lands in a pending queue an admin reviews
// (and can adjust the requested teams/age grades for) before approving,
// since approval grants visibility into children's concussion records —
// a materially bigger thing to hand out than adding a name to a roster.
function StaffRegistrationPage({ token }) {
  const [orgName, setOrgName] = useState("");
  const [ageGradeOptions, setAgeGradeOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [screen, setScreen] = useState("loading"); // loading | form | success | error

  const [form, setForm] = useState({
    name: "", email: "", password: "", ageGrades: [], teams: [], note: "", newsOptIn: false,
  });
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getStaffRegistration(token)
      .then(data => {
        setOrgName(data.organisationName);
        setAgeGradeOptions(data.ageGrades || []);
        setTeamOptions(data.teams || []);
        setScreen("form");
      })
      .catch(e => { setLoadError(e.message); setScreen("error"); });
  }, [token]);

  function toggle(field, value) {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value) ? f[field].filter(v => v !== value) : [...f[field], value],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return setSubmitError("Please enter your full name.");
    if (!form.email.trim()) return setSubmitError("Please enter your email address.");
    if (form.password.length < 8) return setSubmitError("Password must be at least 8 characters.");
    setSubmitting(true); setSubmitError("");
    try {
      await api.submitStaffRegistration(token, {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        ageGrades: form.ageGrades,
        teams: form.teams,
        note: form.note.trim(),
        newsOptIn: form.newsOptIn,
      });
      setScreen("success");
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  }

  const containerStyle = { fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh" };
  const headerStyle = { background: "#1a4a2e", padding: "0 16px" };
  const headerInner = { maxWidth: 560, margin: "0 auto", display: "flex", alignItems: "center", gap: 14, height: 56 };
  const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 14, fontFamily: "inherit", marginBottom: 4 };
  const chip = (active) => ({ background: active ? "#1a4a2e" : "#fff", color: active ? "#fff" : "#6b7280", border: `1px solid ${active ? "#1a4a2e" : "#d1d5db"}`, borderRadius: 99, padding: "5px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" });

  if (screen === "loading") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={32} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>GRTP Tracker</div></div></div>
      <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Loading...</div>
    </div>
  );

  if (screen === "error") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={32} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15, letterSpacing: "-0.01em" }}>GRTP Tracker</div></div></div>
      <div style={{ maxWidth: 560, margin: "40px auto", padding: "0 16px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: "#111827", marginBottom: 8 }}>Registration link not found</div>
        <p style={{ color: "#6b7280", fontSize: 14 }}>{loadError || "This link may have expired or is incorrect. Please contact your club."}</p>
      </div>
    </div>
  );

  if (screen === "success") return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}><div style={headerInner}><LogoMark size={28} /><div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName}</div></div></div>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "40px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", marginBottom: 10 }}>Request submitted</h2>
        <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          Thanks {form.name}. Your request for staff access at {orgName} is awaiting review.<br />
          Once approved, you'll get an email to confirm your address before you can log in.
        </p>
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      <GlobalStyles />
      <div style={headerStyle}>
        <div style={headerInner}>
          <LogoMark size={28} />
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>{orgName || "GRTP Tracker"}</div>
            <div style={{ color: "#c9991a", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase" }}>Staff Access Request</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "28px 16px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 6px" }}>
          {orgName ? `Request staff access at ${orgName}` : "Request staff access"}
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 24px", lineHeight: 1.6 }}>
          This doesn't grant access immediately — a member of staff at {orgName} will review your request
          (and can adjust the teams/age grades below) before your account is activated.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 14 }}>Your details</div>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Full name <span style={{ color: "#c0272d" }}>*</span></label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Dave Wilson" style={inp} />
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Email address <span style={{ color: "#c0272d" }}>*</span></label>
            <EmailInput value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="dave@example.com" style={inp} />
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "4px 0 0" }}>This becomes your login once approved — you'll need to confirm it's really you first.</p>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4, marginTop: 8 }}>Password <span style={{ color: "#c0272d" }}>*</span></label>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="At least 8 characters" style={inp} />
          </div>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>Which age grades do you coach?</div>
            <p style={{ fontSize: 12.5, color: "#9ca3af", margin: "0 0 12px" }}>Optional — pick as many as apply. Staff can review and adjust this before approving.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {ageGradeOptions.map(g => (
                <button type="button" key={g} onClick={() => toggle("ageGrades", g)} style={chip(form.ageGrades.includes(g))}>{g}</button>
              ))}
            </div>
          </div>

          {teamOptions.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>Which teams are you attached to?</div>
              <p style={{ fontSize: 12.5, color: "#9ca3af", margin: "0 0 12px" }}>Optional — e.g. 1st XV, Women's.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {teamOptions.map(t => (
                  <button type="button" key={t.id} onClick={() => toggle("teams", t.name)} style={chip(form.teams.includes(t.name))}>{t.name}</button>
                ))}
              </div>
            </div>
          )}

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Anything else? (optional)</label>
            <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={3} placeholder="e.g. I'm the new U12s coach, starting this season" style={{ ...inp, resize: "vertical" }} />
          </div>

          <StaffNewsOptIn checked={form.newsOptIn} onChange={v => setForm(f => ({ ...f, newsOptIn: v }))} />

          {submitError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#c0272d", fontSize: 13, marginBottom: 14 }}>
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "13px 0", fontSize: 15, fontWeight: 700, cursor: submitting ? "default" : "pointer", fontFamily: "inherit", opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? "Submitting..." : "Request Access"}
          </button>
        </form>
      </div>
    </div>
  );
}

function VerifyEmailScreen({ token }) {
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    api.verifyEmail(token)
      .then((data) => {
        setStatus("success");
        setMessage(data.message || "Email verified. You can now log in.");
      })
      .catch((e) => {
        setStatus("error");
        setMessage(e.message || "This verification link is invalid or has already been used.");
      });
  }, [token]);

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", textAlign: "center" }}>
        {status === "verifying" && (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
            <div style={{ color: "#6b7280" }}>Verifying your email...</div>
          </>
        )}
        {status === "success" && (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>Email Verified</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>{message}</p>
            <a href="/" style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-block" }}>
              Go to Login
            </a>
          </>
        )}
        {status === "error" && (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 8 }}>Verification Failed</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

// The GRTP news tick-box for staff joining an organisation. Unticked by
// default and never required: news is marketing, so it goes only to
// people who ask for it (staff still get service updates either way).
function StaffNewsOptIn({ checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "0 0 14px", cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
      />
      <span style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}>
        Send me occasional GRTP Tracker news: new features and tips for using it. You can turn this off any time.
      </span>
    </label>
  );
}

function AcceptInvitePage({ token }) {
  const [status, setStatus] = useState("loading"); // loading | ready | success | error
  const [details, setDetails] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newsOptIn, setNewsOptIn] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getInviteDetails(token)
      .then((data) => {
        setDetails(data);
        setStatus("ready");
      })
      .catch((e) => {
        setError(e.message || "This invite link is invalid or has expired.");
        setStatus("error");
      });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.acceptInvite(token, password, newsOptIn);
      setStatus("success");
    } catch (e) {
      setError(e.message || "Something went wrong setting your password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        {status === "loading" && (
          <div style={{ textAlign: "center", color: "#6b7280" }}>Loading invite...</div>
        )}

        {status === "error" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 8 }}>Invite Not Found</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{error}</p>
          </div>
        )}

        {status === "success" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>You're All Set</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>Your password has been set. You can now log in.</p>
            <a href="/" style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-block" }}>
              Go to Login
            </a>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={handleSubmit}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><LogoMark size={40} /></div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e" }}>Join {details.organisationName}</div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>Set a password for {details.email}</div>
            </div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              placeholder="At least 8 characters"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
            />
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Confirm password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
            />
            <StaffNewsOptIn checked={newsOptIn} onChange={setNewsOptIn} />
            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Setting password..." : "Set Password & Continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ForgotPasswordScreen({ onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.forgotPassword(email.trim());
      setSubmitted(true);
    } catch (e) {
      // The backend always returns a generic success message regardless
      // of whether the email exists, specifically so this branch should
      // never fire for "email not found" — only for genuine failures
      // (network error, rate limited, etc).
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        {submitted ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📬</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>Check your email</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>
              If <strong>{email}</strong> has an account, we've sent a link to reset the password. It expires in 1 hour.
            </p>
            <button onClick={onBackToLogin} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><LogoMark size={40} /></div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e" }}>Reset your password</div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>Enter your email and we'll send you a reset link</div>
            </div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              placeholder="you@example.com"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
            />
            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting || !email.trim()} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: (submitting || !email.trim()) ? 0.7 : 1, marginBottom: 12 }}>
              {submitting ? "Sending..." : "Send Reset Link"}
            </button>
            <button type="button" onClick={onBackToLogin} style={{ width: "100%", background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer" }}>
              ← Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ResetPasswordScreen({ token }) {
  const [status, setStatus] = useState("loading"); // loading | ready | success | error
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getResetPasswordValidity(token)
      .then(() => setStatus("ready"))
      .catch((e) => {
        setError(e.message || "This reset link is invalid or has expired.");
        setStatus("error");
      });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.resetPassword(token, password);
      setStatus("success");
    } catch (e) {
      setError(e.message || "Something went wrong resetting your password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        {status === "loading" && (
          <div style={{ textAlign: "center", color: "#6b7280" }}>Checking your link...</div>
        )}

        {status === "error" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 8 }}>Link Not Valid</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>{error}</p>
            <a href="/" style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-block" }}>
              Back to Login
            </a>
          </div>
        )}

        {status === "success" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>Password Reset</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 16px" }}>Your password has been changed. You can now log in.</p>
            <a href="/" style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-block" }}>
              Go to Login
            </a>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={handleSubmit}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><LogoMark size={40} /></div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e" }}>Choose a new password</div>
            </div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>New password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              placeholder="At least 8 characters"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
            />
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
            />
            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.7 : 1 }}>
              {submitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function UnsubscribeScreen({ token }) {
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    api.unsubscribeMailingList(token)
      .then(() => setStatus("success"))
      .catch((e) => {
        setError(e.message || "This unsubscribe link is invalid.");
        setStatus("error");
      });
  }, [token]);

  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 380, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", textAlign: "center" }}>
        {status === "loading" && <div style={{ color: "#6b7280" }}>Unsubscribing...</div>}
        {status === "error" && (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 8 }}>Link Not Valid</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>{error}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 8 }}>Unsubscribed</div>
            <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>You won't receive any more GRTP Tracker product news emails.</p>
          </>
        )}
      </div>
    </div>
  );
}

function BillingSuccessScreen() {
  // Stripe can beat its own webhook back to us by a second or two, so
  // this polls rather than trusting the redirect alone — the webhook is
  // still the only thing that actually activates the org; this screen
  // just waits for that to have happened before declaring success.
  const [phase, setPhase] = useState(() => (api.isLoggedIn() ? "confirming" : "not_logged_in"));
  const [tier, setTier] = useState(null);

  useEffect(() => {
    if (phase !== "confirming") return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8; // ~16 seconds at 2s intervals — generous for a webhook that's normally near-instant

    async function poll() {
      attempts++;
      try {
        const result = await api.getOrganisationStatus();
        if (cancelled) return;
        if (result.status === "active") {
          setTier(result.tier);
          setPhase("active");
          return;
        }
      } catch (_) {
        // Most likely the session lapsed during checkout (req() clears
        // the token on a 401), in which case api.isLoggedIn() is now
        // false too — fall back to the "please log in" state rather
        // than looping on a request that will keep failing.
        if (!cancelled) setPhase("not_logged_in");
        return;
      }
      if (attempts >= maxAttempts) {
        if (!cancelled) setPhase("timeout");
        return;
      }
      setTimeout(poll, 2000);
    }
    poll();
    return () => { cancelled = true; };
  }, [phase]);

  const tierLabels = { grassroots: "Grassroots", club_school: "Club/School", large_club_school: "Large Club/School" };
  const boxStyle = { fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 36, maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" };
  const continueBtn = { background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "11px 22px", fontSize: 14, fontWeight: 700, textDecoration: "none", display: "inline-block" };

  return (
    <div style={boxStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}><LogoMark size={44} /></div>

        {phase === "not_logged_in" && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1a4a2e", marginBottom: 10 }}>Payment received</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>Your payment went through. Log in to continue to your dashboard.</p>
            <a href="/" style={continueBtn}>Go to Login</a>
          </>
        )}

        {phase === "confirming" && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1a4a2e", marginBottom: 10 }}>Confirming your payment...</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>This usually takes just a few seconds.</p>
          </>
        )}

        {phase === "active" && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1a4a2e", marginBottom: 10 }}>You're all set!</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
              Your payment was successful and your {tierLabels[tier] || "GRTP Tracker"} plan is now active.
            </p>
            <a href="/" style={continueBtn}>Continue to Dashboard</a>
          </>
        )}

        {phase === "timeout" && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#1a4a2e", marginBottom: 10 }}>Almost there</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
              Your payment was received — it's just taking a little longer than usual to confirm. Try continuing to your dashboard; if it still shows as unpaid, give it a minute and refresh.
            </p>
            <a href="/" style={continueBtn}>Continue to Dashboard</a>
          </>
        )}
      </div>
    </div>
  );
}

function SuperadminPage() {
  const [authed, setAuthed] = useState(() => api.isSuperadminLoggedIn());
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saTab, setSaTab] = useState("orgs"); // "orgs" | "analytics" | "activity" | "mailing"
  const [saMailingList, setSaMailingList] = useState(null);
  const [saMailingLoading, setSaMailingLoading] = useState(false);
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [mailSending, setMailSending] = useState(false);
  const [mailConfirming, setMailConfirming] = useState(false);
  const [svcSubject, setSvcSubject] = useState("");
  const [svcBody, setSvcBody] = useState("");
  const [svcSending, setSvcSending] = useState(false);
  const [svcConfirming, setSvcConfirming] = useState(false);
  const [saAuditLog, setSaAuditLog] = useState(null);
  const [saOrgActivity, setSaOrgActivity] = useState(null);
  const [saActivitySubTab, setSaActivitySubTab] = useState("audit"); // "audit" | "orgs"
  const [saActivityLoading, setSaActivityLoading] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgDetail, setOrgDetail] = useState(null);
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notif, setNotif] = useState(null);
  const [saRecoveryBusyId, setSaRecoveryBusyId] = useState(null);
  const [maxUsersInput, setMaxUsersInput] = useState("");
  const [maxPlayersInput, setMaxPlayersInput] = useState("");
  const [orgTypeInput, setOrgTypeInput] = useState("");
  const [saAnalytics, setSaAnalytics] = useState(null);
  const [saAnalyticsLoading, setSaAnalyticsLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [saEmailHealth, setSaEmailHealth] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);

  const showNotif = useCallback((msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 4000);
  }, []);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAllOrganisations();
      setOrgs(data);
    } catch (e) {
      if (e.isSuperadminAuthError) setAuthed(false);
      else showNotif(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [showNotif]);

  useEffect(() => {
    if (authed) loadOrgs();
  }, [authed, loadOrgs]);

  // Surfaced on every superadmin visit regardless of which tab is open —
  // a failed or stale backup was previously only ever a console.log line
  // nobody watches day-to-day.
  useEffect(() => {
    if (!authed) return;
    api.getBackupStatus().then(setBackupStatus).catch(() => {});
    api.getSuperadminEmailHealth().then(setSaEmailHealth).catch(() => {});
  }, [authed]);

  // Runs the whole nightly backup on demand and reports where the copy
  // went, so a change to backup storage can be proved the same day rather
  // than at 03:15 tomorrow.
  async function handleRunBackupNow() {
    setBackupRunning(true);
    try {
      const r = await api.runBackupNow();
      const where = r.storedIn === "b2" ? "uploaded to Backblaze B2" : r.storedIn === "email" ? "emailed" : "kept on the server only";
      showNotif(`Backup complete — ${where}${r.sizeBytes ? ` (${Math.round(r.sizeBytes / 1024)}KB)` : ""}.`);
    } catch (e) {
      if (e.isSuperadminAuthError) setAuthed(false);
      else showNotif(`Backup failed: ${e.message}`, "error");
    } finally {
      setBackupRunning(false);
      api.getBackupStatus().then(setBackupStatus).catch(() => {});
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (!password || mfaCode.length !== 6) return;
    setSubmitting(true);
    setLoginError("");
    try {
      await api.superadminLogin(password, mfaCode);
      setAuthed(true);
    } catch (e) {
      setLoginError(e.message || "Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleLogout() {
    api.superadminLogout();
    setAuthed(false);
    setOrgs([]);
    setSelectedOrgId(null);
    setOrgDetail(null);
  }

  async function openDetail(orgId) {
    setSelectedOrgId(orgId);
    setDetailLoading(true);
    try {
      const data = await api.getOrganisationDetail(orgId);
      setOrgDetail(data);
      setMaxUsersInput(String(data.maxUsers));
      setMaxPlayersInput(String(data.maxPlayers));
      setOrgTypeInput(data.org_type || "club");
    } catch (e) {
      showNotif(e.message, "error");
      setSelectedOrgId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleSetStatus(orgId, status) {
    try {
      const result = await api.setOrganisationStatus(orgId, status);
      showNotif(result.message);
      loadOrgs();
      if (selectedOrgId === orgId) openDetail(orgId);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  // Invoice-paid orgs have no Stripe subscription, so this is the only
  // thing that ever records how long a payment covers — prompts for a
  // date (defaulting to a year from today) rather than a fixed "renew
  // for 12 months" button, since invoice terms aren't always exactly a year.
  async function handleRecordInvoicePayment(org) {
    const defaultDate = new Date(Date.now() + 365 * 86400000).toISOString().split("T")[0];
    const input = window.prompt(`Record invoice payment for ${org.name} — paid until (YYYY-MM-DD):`, defaultDate);
    if (!input) return;
    // Strict YYYY-MM-DD only — a slash format like "02/09/2026" is
    // genuinely ambiguous (2 Sept to a UK reader, 9 Feb to JS's own
    // US-style Date parser) and would silently record the wrong date
    // rather than fail loudly, so it's rejected rather than "helpfully"
    // parsed.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input) || isNaN(new Date(input).getTime())) {
      return showNotif("Enter the date as YYYY-MM-DD (e.g. 2026-09-02) — other formats are ambiguous and won't be accepted.", "error");
    }
    try {
      const result = await api.recordInvoicePayment(org.id, input);
      showNotif(result.message);
      loadOrgs();
      if (selectedOrgId === org.id) openDetail(org.id);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function handleSendPasswordReset(staff) {
    setSaRecoveryBusyId(staff.id);
    try {
      const r = await api.sendUserPasswordReset(staff.id);
      setNotif({ msg: r.message, type: "success" });
    } catch (e) {
      setNotif({ msg: e.message, type: "error" });
    } finally {
      setSaRecoveryBusyId(null);
    }
  }

  async function handleResetUserMfa(staff) {
    // Confirmed rather than immediate: this removes a security control
    // from somebody else's account, and it is the one action here that
    // makes an account easier to reach rather than harder. The wording
    // says what it does and what it does not.
    const ok = window.confirm(
      `Clear two-factor authentication for ${staff.email}?\n\n` +
      `They'll be able to sign in with their password alone, and will be asked to set 2FA up again before using admin features.\n\n` +
      `Their password is NOT changed, any session they currently have is ended, and they will be emailed to say this happened.\n\n` +
      `Only do this if you're satisfied they are who they say they are.`
    );
    if (!ok) return;

    setSaRecoveryBusyId(staff.id);
    try {
      const r = await api.resetUserMfa(staff.id);
      setNotif({ msg: r.message, type: "success" });
      // The badge on this row is now wrong until the detail reloads.
      const refreshed = await api.getOrganisationDetail(selectedOrgId);
      setOrgDetail(refreshed);
    } catch (e) {
      setNotif({ msg: e.message, type: "error" });
    } finally {
      setSaRecoveryBusyId(null);
    }
  }

  async function handleSetMaxUsers(orgId) {
    const parsed = parseInt(maxUsersInput, 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
      return showNotif("Enter a positive whole number.", "error");
    }
    try {
      const result = await api.setOrganisationMaxUsers(orgId, parsed);
      showNotif(result.message);
      loadOrgs();
      openDetail(orgId);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function handleSetMaxPlayers(orgId) {
    const parsed = parseInt(maxPlayersInput, 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
      return showNotif("Enter a positive whole number.", "error");
    }
    try {
      const result = await api.setOrganisationMaxPlayers(orgId, parsed);
      showNotif(result.message);
      loadOrgs();
      openDetail(orgId);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  const pageWrap = { fontFamily: "'Inter', system-ui, sans-serif", background: "#f9fafb", minHeight: "100vh" };

  if (!authed) {
    return (
      <div style={{ ...pageWrap, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <form onSubmit={handleLogin} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 32, maxWidth: 360, width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#1a4a2e" }}>Superadmin</div>
            <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>GRTP Tracker — all organisations</div>
          </div>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
            placeholder="Superadmin password"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit" }}
          />
          <input
            type="text"
            inputMode="numeric"
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            placeholder="6-digit authenticator code"
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit", letterSpacing: "0.2em", textAlign: "center" }}
          />
          {loginError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
              {loginError}
            </div>
          )}
          <button type="submit" disabled={submitting || !password || mfaCode.length !== 6} style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: submitting ? "default" : "pointer", opacity: (submitting || !password || mfaCode.length !== 6) ? 0.6 : 1 }}>
            {submitting ? "Checking..." : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  const STATUS_COLORS = { active: "#1a4a2e", pending_payment: "#c9991a", pending_invoice: "#3b5bdb", suspended: "#c0272d", trial: "#0891b2", trial_expired: "#ea580c", cancelled: "#6b7280" };
  const TYPE_ICONS = { school: "🏫", club: "🏉", academy: "🌟" };

  return (
    <div style={pageWrap}>
      <div style={{ background: "#111827", padding: "0 16px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, height: 56 }}>
          <span style={{ fontSize: 18 }}>🔐</span>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 14 }}>Superadmin</div>
          <button onClick={handleLogout} style={{ marginLeft: "auto", background: "none", border: "1px solid #ffffff33", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 6 }}>
            Log Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#111827", marginBottom: 4 }}>Superadmin</h2>
        <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "2px solid #e5e7eb", paddingBottom: 0 }}>
          {[["orgs", "🏢 Organisations"], ["analytics", "📊 Platform Analytics"], ["activity", "📜 Activity"], ["mailing", "📧 Mailing List"]].map(([tab, label]) => (
            <button key={tab} type="button"
              onClick={() => {
                setSaTab(tab);
                // Always refetch rather than only-if-not-loaded — the whole
                // point of both tabs is to reflect the current state of the
                // org list, which changes constantly from this same screen
                // (status changes, marking orgs as test, deletions). Caching
                // the first load meant those changes never showed up here
                // without a full page reload.
                if (tab === "analytics") {
                  setSaAnalyticsLoading(true);
                  api.getSuperadminAnalytics()
                    .then(d => setSaAnalytics(d))
                    .catch(e => showNotif(e.message, "error"))
                    .finally(() => setSaAnalyticsLoading(false));
                }
                if (tab === "activity") {
                  setSaActivityLoading(true);
                  Promise.all([api.getSuperadminAuditLog(), api.getAllOrgsActivityLog()])
                    .then(([audit, orgActivity]) => { setSaAuditLog(audit); setSaOrgActivity(orgActivity); })
                    .catch(e => showNotif(e.message, "error"))
                    .finally(() => setSaActivityLoading(false));
                }
                if (tab === "mailing") {
                  setSaMailingLoading(true);
                  api.getSuperadminMailingList()
                    .then(d => setSaMailingList(d))
                    .catch(e => showNotif(e.message, "error"))
                    .finally(() => setSaMailingLoading(false));
                }
              }}
              style={{ background: "none", border: "none", borderBottom: `3px solid ${saTab === tab ? "#1a4a2e" : "transparent"}`, padding: "8px 16px", fontSize: 14, fontWeight: saTab === tab ? 700 : 500, color: saTab === tab ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", marginBottom: -2 }}>
              {label}
            </button>
          ))}
        </div>

        {/* Emails the service refused in the last 24 hours. Before this,
            a refusal reached only the server log, so a day of lost
            concussion updates went unnoticed. Shown only when there were
            some. */}
        {saEmailHealth && saEmailHealth.refused > 0 && (
          <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "#991b1b", lineHeight: 1.55 }}>
            <strong>⚠️ {saEmailHealth.refused} email{saEmailHealth.refused !== 1 ? "s" : ""} couldn't be sent in the last 24 hours</strong>
            {" "}(first at {new Date(saEmailHealth.firstAt).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}, last at {new Date(saEmailHealth.lastAt).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}).
            {saEmailHealth.dailyLimit && " Resend's daily limit was reached: it resets at midnight UTC. A paid plan has no daily limit."}
            {saEmailHealth.monthlyLimit && " Resend's monthly limit was reached."}
            {saEmailHealth.reasons && (
              <div style={{ marginTop: 6, fontSize: 12.5 }}>
                {Object.entries(saEmailHealth.reasons).map(([k, n]) => `${k}: ${n}`).join(" · ")}
                {saEmailHealth.lastMessage ? ` — latest: "${saEmailHealth.lastMessage}"` : ""}
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: 12.5 }}>Organisation admins are shown a notice asking them to contact families directly.</div>
          </div>
        )}

        {/* Always shown, not only when something is wrong: the healthy
            state is worth seeing too, and the button needs a home either
            way. */}
        {backupStatus && (
          <div style={{
            background: backupStatus.healthy ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${backupStatus.healthy ? "#86efac" : "#fca5a5"}`,
            borderRadius: 10, padding: "12px 16px", marginBottom: 20, fontSize: 13,
            color: backupStatus.healthy ? "#14532d" : "#991b1b",
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <div style={{ flex: "1 1 320px" }}>
              {backupStatus.healthy ? (
                <>
                  <strong>✓ Off-site backup healthy.</strong>{" "}
                  Last success: {new Date(backupStatus.lastSuccessAt).toLocaleString("en-GB")}.
                </>
              ) : (
                <>
                  <strong>⚠️ Backup problem —</strong>{" "}
                  {backupStatus.lastError
                    ? `last attempt failed: ${backupStatus.lastError}.`
                    : "no successful off-server backup in over 36 hours."}{" "}
                  {backupStatus.lastSuccessAt
                    ? `Last success: ${new Date(backupStatus.lastSuccessAt).toLocaleString("en-GB")}.`
                    : "No successful backup has ever been recorded."}
                </>
              )}
            </div>
            <button onClick={handleRunBackupNow} disabled={backupRunning}
              style={{ background: backupRunning ? "#9ca3af" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: backupRunning ? "default" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {backupRunning ? "Backing up…" : "Back up now"}
            </button>
          </div>
        )}

        {saTab === "analytics" && (
          <div>
            {saAnalyticsLoading ? (
              <div style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>
                <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.4 }}>📊</div>
                Loading platform analytics...
              </div>
            ) : !saAnalytics ? null : (() => {
              const CONTEXT_LABELS   = { match: "Match play", training_contact: "Contact training", training_non_contact: "Non-contact training", other: "Other / unknown" };
              const CONTEXT_COLORS   = { match: "#c0272d", training_contact: "#c9991a", training_non_contact: "#1a7a4a", other: "#6b7280" };
              const MECHANISM_LABELS = { tackle_bc: "Tackle (ball carrier)", tackle_tackler: "Tackle (tackler)", ruck_maul: "Ruck / maul", scrum: "Scrum", collision: "Player collision", fall: "Fall / ground contact", other: "Other" };
              const MECHANISM_COLORS = { tackle_bc: "#c0272d", tackle_tackler: "#c9421a", ruck_maul: "#c9991a", scrum: "#1a3a8a", collision: "#7a1a8a", fall: "#1a7a4a", other: "#6b7280" };
              const AGE_GRADE_ORDER  = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult","Unknown"];
              const ad = saAnalytics;

              const SaStatCard = ({ label, value, sub, accent = "#1a4a2e", icon }) => (
                <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#7a95ad", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                    {icon && <span style={{ fontSize: 16, opacity: 0.5 }}>{icon}</span>}
                  </div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: accent, lineHeight: 1.1, letterSpacing: "-0.02em" }}>{value ?? "—"}</div>
                  {sub && <div style={{ fontSize: 11, color: "#9ca3af" }}>{sub}</div>}
                </div>
              );

              const SaBar = ({ label, count, total, color = "#1a4a2e", max, sub }) => {
                const pct   = max > 0 ? (count / max) * 100 : 0;
                const share = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                      <div>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: "#374151" }}>{label}</span>
                        {sub && <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>{sub}</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{share}%</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color, minWidth: 24, textAlign: "right" }}>{count}</span>
                      </div>
                    </div>
                    <div style={{ background: "#f0f4f8", borderRadius: 99, height: 10, overflow: "hidden" }}>
                      <div style={{ background: color, width: `${pct}%`, height: "100%", borderRadius: 99, transition: "width 0.5s cubic-bezier(.4,0,.2,1)" }} />
                    </div>
                  </div>
                );
              };

              const SaChartCard = ({ title, subtitle, children }) => (
                <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "22px 24px", marginBottom: 16 }}>
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>{title}</div>
                    {subtitle && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{subtitle}</div>}
                  </div>
                  {children}
                </div>
              );

              const formatMonth = (ym) => { const [y,m] = ym.split("-"); return new Date(y, m-1).toLocaleDateString("en-GB", { month: "short", year: "numeric" }); };

              const TIER_LABELS = { grassroots: "Grassroots", club_school: "Club / School", club_school_founder: "Club / School (Founder)", large_club_school: "Large Club / School", academy: "Academy / Union", unknown: "No tier set" };
              const TIER_COLORS = { grassroots: "#1a7a4a", club_school: "#1a4a2e", club_school_founder: "#c9991a", large_club_school: "#1a3a8a", academy: "#7a1a8a", unknown: "#6b7280" };
              const STATUS_LABELS = { active: "Active", trial: "On trial", trial_expired: "Trial expired", pending_payment: "Awaiting card payment", pending_invoice: "Awaiting invoice", suspended: "Suspended", cancelled: "Cancelled" };
              const STATUS_COLORS_A = { active: "#1a4a2e", trial: "#0891b2", trial_expired: "#ea580c", pending_payment: "#c9991a", pending_invoice: "#3b5bdb", suspended: "#c0272d", cancelled: "#6b7280" };

              return (
                <div>
                  <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
                    Anonymised aggregate data across all active organisations on the platform. Season runs August–July.
                    {ad.testOrgCount > 0 && ` ${ad.testOrgCount} org${ad.testOrgCount !== 1 ? "s" : ""} marked as test ${ad.testOrgCount !== 1 ? "are" : "is"} excluded throughout.`}
                  </p>

                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Business</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                    <SaStatCard icon="💷" label="MRR" value={`£${ad.mrr.toFixed(2)}`} sub="estimated, active orgs" accent="#1a4a2e" />
                    <SaStatCard icon="🏫" label="Active organisations" value={ad.orgCount} />
                    <SaStatCard icon="📈" label="Trial → paid" value={ad.trialConversion.conversionRate !== null ? `${ad.trialConversion.conversionRate}%` : "—"} sub={`${ad.trialConversion.convertedToActive} of ${ad.trialConversion.startedTrial} trials`} accent="#c9991a" />
                    <SaStatCard icon="📉" label="Cancelled" value={ad.churnedCount} accent="#c0272d" />
                  </div>

                  <SaChartCard title="Signups by month" subtitle="New organisations created, excluding test orgs">
                    {(() => {
                      const entries = Object.entries(ad.signupsByMonth).sort();
                      if (!entries.length) return <div style={{ fontSize: 13, color: "#9ca3af" }}>No signups yet.</div>;
                      const max = Math.max(...entries.map(([,v]) => v));
                      const total = entries.reduce((s,[,v]) => s+v, 0);
                      return entries.map(([month, count]) => <SaBar key={month} label={formatMonth(month)} count={count} total={total} max={max} color="#1a4a2e" />);
                    })()}
                  </SaChartCard>

                  <SaChartCard title="Organisations by status" subtitle="Every org that's ever signed up, current status">
                    {(() => {
                      const entries = Object.entries(ad.statusBreakdown).sort((a,b) => b[1]-a[1]);
                      const max = Math.max(...entries.map(([,v]) => v));
                      const total = entries.reduce((s,[,v]) => s+v, 0);
                      return entries.map(([status, count]) => <SaBar key={status} label={STATUS_LABELS[status] || status} count={count} total={total} max={max} color={STATUS_COLORS_A[status] || "#6b7280"} />);
                    })()}
                  </SaChartCard>

                  <SaChartCard title="Active orgs by tier" subtitle="Founder-rate Club/School shown separately from standard">
                    {(() => {
                      const entries = Object.entries(ad.tierBreakdown).sort((a,b) => b[1]-a[1]);
                      if (!entries.length) return <div style={{ fontSize: 13, color: "#9ca3af" }}>No active organisations yet.</div>;
                      const max = Math.max(...entries.map(([,v]) => v));
                      const total = entries.reduce((s,[,v]) => s+v, 0);
                      return entries.map(([tier, count]) => <SaBar key={tier} label={TIER_LABELS[tier] || tier} count={count} total={total} max={max} color={TIER_COLORS[tier] || "#6b7280"} />);
                    })()}
                  </SaChartCard>

                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, marginTop: 24 }}>Product &amp; clinical</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 12 }}>
                    <SaStatCard icon="🏉" label="Total concussions" value={ad.total} sub="all time" />
                    <SaStatCard icon="⚡" label="Active protocols" value={ad.active} accent="#c9991a" />
                    <SaStatCard icon="↩️" label="Symptom returns" value={ad.symptomReturns} accent="#c0272d" />
                    <SaStatCard icon="⏱" label="Avg days to clear" value={ad.avgDaysToClear ?? "—"} accent="#1a3a8a" />
                    <SaStatCard icon="✅" label="Active in last 30d" value={ad.recentlyActiveOrgs} sub="of active orgs" accent="#1a7a4a" />
                  </div>

                  <SaChartCard title="By age group" subtitle="Which age groups are most affected across all organisations">
                    {(() => {
                      const sorted = AGE_GRADE_ORDER.filter(g => ad.byAgeGrade[g]).map(g => [g, ad.byAgeGrade[g]])
                        .concat(Object.entries(ad.byAgeGrade).filter(([g]) => !AGE_GRADE_ORDER.includes(g)));
                      const max = Math.max(...sorted.map(([,v]) => v));
                      const total = sorted.reduce((s,[,v]) => s+v, 0);
                      return sorted.map(([grade, count]) => <SaBar key={grade} label={grade} count={count} total={total} max={max} color="#1a4a2e" />);
                    })()}
                  </SaChartCard>

                  {ad.byMonth && Object.keys(ad.byMonth).length > 0 && (
                    <SaChartCard title="Monthly trend" subtitle="Platform-wide incidents per month">
                      {(() => {
                        const entries = Object.entries(ad.byMonth).sort();
                        const max = Math.max(...entries.map(([,v]) => v));
                        const total = entries.reduce((s,[,v]) => s+v, 0);
                        return entries.map(([month, count]) => <SaBar key={month} label={formatMonth(month)} count={count} total={total} max={max} color="#c9991a" />);
                      })()}
                    </SaChartCard>
                  )}

                  {ad.symptomReturnsByMonth && Object.keys(ad.symptomReturnsByMonth).length > 0 && (
                    <SaChartCard title="Symptom returns by month" subtitle="How often a player's symptoms came back mid-protocol, over time">
                      {(() => {
                        const entries = Object.entries(ad.symptomReturnsByMonth).sort();
                        const max = Math.max(...entries.map(([,v]) => v));
                        const total = entries.reduce((s,[,v]) => s+v, 0);
                        return entries.map(([month, count]) => <SaBar key={month} label={formatMonth(month)} count={count} total={total} max={max} color="#c0272d" />);
                      })()}
                    </SaChartCard>
                  )}

                  {ad.orgTypeBreakdown && Object.keys(ad.orgTypeBreakdown).length > 0 && (
                    <SaChartCard title="By organisation type" subtitle="Concussion records, schools vs clubs vs academies">
                      {(() => {
                        const ORG_TYPE_LABELS = { school: "Schools", club: "Clubs", academy: "Academies", Unknown: "Untyped" };
                        const entries = Object.entries(ad.orgTypeBreakdown).sort((a,b) => b[1]-a[1]);
                        const max = Math.max(...entries.map(([,v]) => v));
                        const total = entries.reduce((s,[,v]) => s+v, 0);
                        return entries.map(([type, count]) => <SaBar key={type} label={ORG_TYPE_LABELS[type] || type} count={count} total={total} max={max} color="#1a3a8a" />);
                      })()}
                    </SaChartCard>
                  )}

                  {ad.byContext && Object.keys(ad.byContext).length > 0 && (
                    <SaChartCard title="Where it happened" subtitle={`${Object.values(ad.byContext).reduce((a,b)=>a+b,0)} of ${ad.total} incidents with context recorded`}>
                      {Object.entries(ad.byContext).sort((a,b)=>b[1]-a[1]).map(([key,count]) => {
                        const max = Math.max(...Object.values(ad.byContext));
                        return <SaBar key={key} label={CONTEXT_LABELS[key]||key} count={count} total={ad.total} max={max} color={CONTEXT_COLORS[key]||"#6b7280"} />;
                      })}
                    </SaChartCard>
                  )}

                  {ad.byMechanism && Object.keys(ad.byMechanism).length > 0 && (
                    <SaChartCard title="How it happened" subtitle={`${Object.values(ad.byMechanism).reduce((a,b)=>a+b,0)} of ${ad.total} incidents with mechanism recorded`}>
                      {Object.entries(ad.byMechanism).sort((a,b)=>b[1]-a[1]).map(([key,count]) => {
                        const max = Math.max(...Object.values(ad.byMechanism));
                        return <SaBar key={key} label={MECHANISM_LABELS[key]||key} count={count} total={ad.total} max={max} color={MECHANISM_COLORS[key]||"#6b7280"} />;
                      })}
                    </SaChartCard>
                  )}

                  {ad.byOrg?.length > 0 && (
                    <SaChartCard title="By organisation" subtitle="Concussion records per active organisation">
                      {ad.byOrg.map((o, i) => (
                        <div key={o.orgId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < ad.byOrg.length-1 ? "1px solid #f3f4f6" : "none" }}>
                          <div>
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#374151" }}>{o.orgName}</span>
                            {o.orgType && <span style={{ fontSize: 11, color: "#9ca3af", background: "#f3f4f6", borderRadius: 99, padding: "1px 8px", marginLeft: 8, textTransform: "capitalize" }}>{o.orgType}</span>}
                          </div>
                          <span style={{ fontSize: 15, fontWeight: 800, color: "#1a4a2e" }}>{o.count}</span>
                        </div>
                      ))}
                    </SaChartCard>
                  )}

                  <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>
                    Excludes test entries. Context and mechanism data only includes incidents where those fields were recorded.
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {saTab === "activity" && (() => {
          const orgLookup = Object.fromEntries(orgs.map(o => [o.id, o.name]));
          const formatAction = (a) => a.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
          const formatDetails = (d) => {
            if (!d || typeof d !== "object") return null;
            return Object.entries(d).map(([k, v]) => `${k.replace(/_/g, " ")}: ${typeof v === "boolean" ? (v ? "yes" : "no") : v}`).join(" · ");
          };
          const formatWhen = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

          const LogRow = ({ time, primary, secondary, detail }) => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "#374151" }}>
                  {primary}{secondary && <span style={{ fontWeight: 500, color: "#9ca3af" }}> — {secondary}</span>}
                </div>
                {detail && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{detail}</div>}
              </div>
              <div style={{ fontSize: 11.5, color: "#9ca3af", whiteSpace: "nowrap", flexShrink: 0 }}>{formatWhen(time)}</div>
            </div>
          );

          return (
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[["audit", "Admin actions"], ["orgs", "Organisation activity"]].map(([sub, label]) => (
                  <button key={sub} type="button" onClick={() => setSaActivitySubTab(sub)}
                    style={{ background: saActivitySubTab === sub ? "#1a4a2e" : "#fff", color: saActivitySubTab === sub ? "#fff" : "#374151", border: "1px solid " + (saActivitySubTab === sub ? "#1a4a2e" : "#d1d5db"), borderRadius: 99, padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    {label}
                  </button>
                ))}
              </div>

              {saActivityLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>
              ) : saActivitySubTab === "audit" ? (
                <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "8px 20px" }}>
                  <p style={{ fontSize: 12.5, color: "#9ca3af", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                    Every status change, deletion, and org-level edit made from this superadmin panel. Most recent 200.
                  </p>
                  {!saAuditLog?.length ? (
                    <div style={{ textAlign: "center", padding: 30, color: "#9ca3af", fontSize: 13 }}>No admin actions recorded yet.</div>
                  ) : saAuditLog.map(e => (
                    <LogRow key={e.id} time={e.createdAt}
                      primary={formatAction(e.action)}
                      secondary={orgLookup[e.organisationId] || (e.organisationId ? "deleted org" : null)}
                      detail={formatDetails(e.details)} />
                  ))}
                </div>
              ) : (
                <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "8px 20px" }}>
                  <p style={{ fontSize: 12.5, color: "#9ca3af", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                    Everything happening inside every organisation on the platform — staff actions, Stripe events, roster changes. Most recent 200.
                  </p>
                  {!saOrgActivity?.length ? (
                    <div style={{ textAlign: "center", padding: 30, color: "#9ca3af", fontSize: 13 }}>No activity recorded yet.</div>
                  ) : saOrgActivity.map(e => (
                    <LogRow key={e.id} time={e.createdAt}
                      primary={formatAction(e.action)}
                      secondary={e.organisationName || "Unknown org"}
                      detail={[e.userEmail, formatDetails(e.details)].filter(Boolean).join(" · ") || null} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {saTab === "orgs" && (
        <div>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20 }}>Every organisation that has signed up, across the whole platform.</p>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>
        ) : (
          <div>
            {[["school","Schools"],["club","Clubs"],["academy","Academies"],[null,"Untyped"]].map(([type, label]) => {
              const group = orgs.filter(o => type === null ? !o.org_type : o.org_type === type);
              if (group.length === 0) return null;
              return (
                <div key={type || "untyped"} style={{ marginBottom: 28 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 16 }}>{TYPE_ICONS[type] || "❓"}</span>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#374151" }}>{label}</div>
                    <span style={{ fontSize: 12, color: "#9ca3af", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 10, padding: "1px 8px" }}>{group.length}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {group.map((org) => (
              <div key={org.id} style={{ background: org.isTest ? "#fffbeb" : "#fff", border: org.isTest ? "1px dashed #d1a03a" : "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{org.name}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: STATUS_COLORS[org.status] || "#6b7280", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        {org.status.replace(/_/g, " ")}
                      </span>
                      {/* Findable at a glance. The approval gate was
                          invisible from the list, so spotting which
                          organisations were waiting meant opening every
                          one of them in turn. */}
                      {!org.registrationApprovedAt && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#78350f", background: "#fef3c7", border: "1px solid #c9991a", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.3 }}>
                          needs checking
                        </span>
                      )}
                      {org.isTest && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#92600a", background: "#fef3c7", border: "1px solid #d1a03a", padding: "2px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: 0.3 }}>
                          Test
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                      {org.userCount} staff ({org.adminCount} admin{org.adminCount !== 1 ? "s" : ""}) · {org.rosterCount} on roster (max {org.maxPlayers}) · {org.playerCount} concussion record{org.playerCount !== 1 ? "s" : ""} · max {org.maxUsers} logins
                    </div>
                    {(org.status === "trial" || org.status === "trial_expired") && (() => {
                      const daysSinceSignup = Math.floor((Date.now() - new Date(org.createdAt).getTime()) / 86400000);
                      const daysUntilEnd = org.trialEndsAt ? Math.ceil((new Date(org.trialEndsAt).getTime() - Date.now()) / 86400000) : null;
                      return (
                        <div style={{ fontSize: 12, color: org.status === "trial_expired" ? "#c2410c" : "#0e7490", marginTop: 3, fontWeight: 600 }}>
                          {org.status === "trial_expired"
                            ? `Trial ended ${daysUntilEnd !== null ? Math.abs(daysUntilEnd) : "?"} day${Math.abs(daysUntilEnd) !== 1 ? "s" : ""} ago`
                            : `Started ${daysSinceSignup === 0 ? "today" : `${daysSinceSignup} day${daysSinceSignup !== 1 ? "s" : ""} ago`}${daysUntilEnd !== null ? ` · ends in ${daysUntilEnd} day${daysUntilEnd !== 1 ? "s" : ""}` : ""}`
                          }
                        </div>
                      );
                    })()}
                    {org.paidUntil && (() => {
                      const daysUntilRenewal = Math.ceil((new Date(org.paidUntil).getTime() - Date.now()) / 86400000);
                      const overdue = daysUntilRenewal < 0;
                      return (
                        <div style={{ fontSize: 12, color: overdue ? "#c0272d" : "#374151", marginTop: 3, fontWeight: 600 }}>
                          {overdue
                            ? `Invoice renewal overdue by ${Math.abs(daysUntilRenewal)} day${Math.abs(daysUntilRenewal) !== 1 ? "s" : ""}`
                            : `Paid until ${new Date(org.paidUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} (${daysUntilRenewal} day${daysUntilRenewal !== 1 ? "s" : ""})`
                          }
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {org.status !== "active" && (
                      <button onClick={() => handleSetStatus(org.id, "active")} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleRecordInvoicePayment(org)}
                      title="Record an invoice payment and set how long it covers — the only thing that tracks renewal for organisations with no Stripe subscription"
                      style={{ background: "#fff", color: "#3b5bdb", border: "1px solid #3b5bdb", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >
                      💷 Record Invoice Payment
                    </button>
                    {org.status === "active" && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Suspend ${org.name}? Every staff member there will immediately lose access.`)) {
                            handleSetStatus(org.id, "suspended");
                          }
                        }}
                        style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                      >
                        Suspend
                      </button>
                    )}
                    <select
                      value={org.status}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        if (newStatus === org.status) return;
                        if (window.confirm(`Set ${org.name}'s status to "${newStatus.replace(/_/g, " ")}"?`)) {
                          handleSetStatus(org.id, newStatus);
                        }
                      }}
                      title="Set any status directly — mainly useful for testing (e.g. simulating a trial expiry or a failed payment)"
                      style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      {["pending_payment", "pending_invoice", "trial", "trial_expired", "active", "suspended"].map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        api.setOrganisationTestFlag(org.id, !org.isTest)
                          .then(() => { showNotif(`${org.name} is ${org.isTest ? "no longer" : "now"} marked as a test org.`); loadOrgs(); })
                          .catch(e => showNotif(e.message, "error"));
                      }}
                      title="Test orgs are excluded from platform analytics but can still be managed normally"
                      style={{ background: org.isTest ? "#fef3c7" : "#fff", color: "#92600a", border: "1px solid #d1a03a", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >
                      {org.isTest ? "Unmark test" : "Mark as test"}
                    </button>
                    <button onClick={() => openDetail(org.id)} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      Details
                    </button>
                    <button
                      onClick={() => {
                        const typed = window.prompt(`Type the organisation name to confirm deletion:\n\n"${org.name}"\n\nThis cannot be undone.`);
                        if (typed === org.name) {
                          api.deleteOrganisation(org.id, org.name)
                            .then(() => { setNotif({ msg: `${org.name} deleted.`, type: "success" }); loadOrgs(); })
                            .catch(e => setNotif({ msg: e.message, type: "error" }));
                        } else if (typed !== null) {
                          setNotif({ msg: "Name did not match — deletion cancelled.", type: "error" });
                        }
                      }}
                      style={{ background: "#fff", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}</div></div>);})}</div>
        )}
        </div>
      )}

      {selectedOrgId && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(17,24,39,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 460, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
            {detailLoading || !orgDetail ? (
              <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>Loading...</div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, color: "#111827" }}>{orgDetail.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{orgDetail.playerCount} player record{orgDetail.playerCount !== 1 ? "s" : ""}</div>
                    {orgDetail.tier && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2, textTransform: "capitalize" }}>{orgDetail.tier.replace(/_/g, " ")}</div>}
                  </div>
                  <button onClick={() => { setSelectedOrgId(null); setOrgDetail(null); }} style={{ background: "none", border: "none", fontSize: 20, color: "#9ca3af", cursor: "pointer", padding: 4 }}>×</button>
                </div>

                <div style={{ marginBottom: 16 }}>
                  {/* The registration check. Placed first because it is the
                      only decision here that governs whether this
                      organisation can reach families at all, and because
                      an unapproved org is the one thing you open this
                      panel to deal with. */}
                  {(() => {
                    const approved = !!orgDetail.registrationApprovedAt;
                    const evidence = [
                      ["Postcode", orgDetail.postcode],
                      [orgDetail.org_type === "school" ? "School URN" : "RFU club number", orgDetail.orgReference],
                      ["Website", orgDetail.website],
                      ["Signed up by", orgDetail.signupRole],
                    ].filter(([, v]) => v);
                    // All three URLs checked against the live sites. The
                    // first attempt at both was wrong — the RFU one 404d
                    // and the DfE one silently dropped its parameters and
                    // landed on the search home — which is the same way
                    // the HEADCASE link had been dead for months.
                    //
                    // Given a URN, go straight to the establishment; the
                    // record carries the address, phone and whether the
                    // school is still open, which is the whole point.
                    const urn = String(orgDetail.orgReference || "").trim();
                    const lookup = orgDetail.org_type === "school"
                      ? (/^\d{5,7}$/.test(urn)
                          ? `https://get-information-schools.service.gov.uk/Establishments/Establishment/Details/${urn}`
                          : `https://get-information-schools.service.gov.uk/Search/Results?SearchType=Text&TextSearchModel.Text=${encodeURIComponent(orgDetail.name || "")}`)
                      : "https://www.englandrugby.com/play/find-rugby";
                    return (
                      <div style={{ marginBottom: 16, background: approved ? "#f0f7f3" : "#fffbeb",
                        border: `2px solid ${approved ? "#b6d8c5" : "#c9991a"}`, borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: approved ? "#1a4a2e" : "#92400e", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          {approved ? "Registration links live" : "Awaiting registration check"}
                        </div>
                        {!approved && (
                          <div style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, marginBottom: 10 }}>
                            This organisation cannot issue registration links to families yet. Check it against an
                            independent record before approving — not against what it typed below.
                          </div>
                        )}
                        {evidence.length > 0 ? (
                          <div style={{ marginBottom: 10 }}>
                            {evidence.map(([k, v]) => (
                              <div key={k} style={{ fontSize: 12, color: "#374151", marginBottom: 3 }}>
                                {k}: <strong>{v}</strong>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: "#92400e", fontStyle: "italic", marginBottom: 10 }}>
                            Nothing was captured at signup — this organisation registered before we started asking.
                          </div>
                        )}
                        <a href={lookup} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, fontWeight: 600, color: "#1a4a2e", display: "inline-block", marginBottom: 10 }}>
                          {orgDetail.org_type === "school"
                            ? (/^\d{5,7}$/.test(urn) ? "Open this URN on the DfE register" : "Search the DfE register for this name")
                            : "Find this club on the RFU directory"} →
                        </a>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            disabled={approvalSaving}
                            onClick={async () => {
                              const next = !approved;
                              if (!next && !window.confirm(`Close ${orgDetail.name}'s registration links? Any link already sent to parents stops working immediately.`)) return;
                              setApprovalSaving(true);
                              try {
                                await api.setRegistrationApproval(orgDetail.id, next);
                                setOrgDetail(await api.getOrganisationDetail(orgDetail.id));
                                loadOrgs();
                              } catch (e) { showNotif(e.message, "error"); }
                              finally { setApprovalSaving(false); }
                            }}
                            style={{ background: approved ? "#fff" : "#1a4a2e", color: approved ? "#c0272d" : "#fff",
                              border: approved ? "1px solid #c0272d" : "none", borderRadius: 6, padding: "8px 16px",
                              fontSize: 13, fontWeight: 700, cursor: approvalSaving ? "default" : "pointer", opacity: approvalSaving ? 0.6 : 1 }}>
                            {approvalSaving ? "Saving…" : approved ? "Close registration links" : "Approve — this is a real organisation"}
                          </button>
                        </div>
                        {approved && orgDetail.registrationApprovedBy && (
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
                            {orgDetail.registrationApprovedBy === "backfill"
                              ? "Approved automatically: this organisation pre-dates the check."
                              : `Approved by ${orgDetail.registrationApprovedBy}.`}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(orgDetail.billing_address || orgDetail.billing_email || orgDetail.po_number) && (
                    <div style={{ marginBottom: 16, background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1a4a2e", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>Billing Details</div>
                      {orgDetail.billing_address && <div style={{ fontSize: 12, color: "#374151", marginBottom: 4, whiteSpace: "pre-line" }}>{orgDetail.billing_address}</div>}
                      {orgDetail.billing_email && <div style={{ fontSize: 12, color: "#374151", marginBottom: 4 }}>Finance email: <strong>{orgDetail.billing_email}</strong></div>}
                      {orgDetail.po_number && <div style={{ fontSize: 12, color: "#374151" }}>PO number: <strong>{orgDetail.po_number}</strong></div>}
                    </div>
                  )}
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Staff Login Limit</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="number"
                      min="1"
                      value={maxUsersInput}
                      onChange={e => setMaxUsersInput(e.target.value)}
                      style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 14, fontFamily: "inherit" }}
                    />
                    <button onClick={() => handleSetMaxUsers(orgDetail.id)} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      Save
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Player Roster Limit</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="number"
                      min="1"
                      value={maxPlayersInput}
                      onChange={e => setMaxPlayersInput(e.target.value)}
                      style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 14, fontFamily: "inherit" }}
                    />
                    <button onClick={() => handleSetMaxPlayers(orgDetail.id)} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      Save
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                    Currently {orgDetail.rosterCount} player{orgDetail.rosterCount !== 1 ? "s" : ""} on the roster.
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>Organisation Type</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    {[["school", "🏫 School"], ["club", "🏉 Club"], ["academy", "🌟 Academy"]].map(([val, label]) => (
                      <button key={val} type="button"
                        onClick={() => setOrgTypeInput(val)}
                        style={{ flex: 1, border: `2px solid ${orgTypeInput === val ? "#1a4a2e" : "#e5e7eb"}`, background: orgTypeInput === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "8px 4px", fontSize: 12.5, fontWeight: orgTypeInput === val ? 700 : 500, color: orgTypeInput === val ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await api.setOrganisationOrgType(orgDetail.id, orgTypeInput);
                        setOrgDetail(d => ({ ...d, org_type: orgTypeInput }));
                        showNotif(`${orgDetail.name} type set to ${orgTypeInput}.`);
                      } catch (e) { showNotif(e.message, "error"); }
                    }}
                    style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    Save Type
                  </button>
                </div>

                <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Staff ({orgDetail.staff.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {orgDetail.staff.map((s) => (
                    <div key={s.id} style={{ padding: "8px 10px", background: "#f9fafb", borderRadius: 6, fontSize: 13 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                        <span>{s.email}</span>
                        <span style={{ display: "flex", gap: 6 }}>
                          {s.isOrgAdmin && <Badge color="#1a4a2e">Admin</Badge>}
                          {s.mfaEnabled && <Badge color="#6b7280">2FA on</Badge>}
                          {s.status === "invited" && <Badge color="#c9991a">Invited</Badge>}
                          {s.status === "pending_verification" && <Badge color="#c9991a">Unverified</Badge>}
                        </span>
                      </div>

                      {/* Recovery. Neither action sets or reveals a
                          password — that would be the ability to sign in
                          as this person and read every child's record
                          their organisation holds. */}
                      {s.status !== "invited" && (
                        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                          <button
                            onClick={() => handleSendPasswordReset(s)}
                            disabled={saRecoveryBusyId === s.id}
                            style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", opacity: saRecoveryBusyId === s.id ? 0.5 : 1 }}
                          >
                            Send password reset
                          </button>
                          {s.mfaEnabled && (
                            <button
                              onClick={() => handleResetUserMfa(s)}
                              disabled={saRecoveryBusyId === s.id}
                              style={{ background: "#fff", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 6, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", opacity: saRecoveryBusyId === s.id ? 0.5 : 1 }}
                            >
                              Reset 2FA
                            </button>
                          )}
                          {s.status === "pending_verification" && (
                            <span style={{ fontSize: 11.5, color: "#92400e", alignSelf: "center" }}>
                              Can't log in until they verify their email
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {saTab === "mailing" && (
          <div>
            {saMailingLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>
            ) : !saMailingList ? null : (
              <>
                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                  <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, flex: 1 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Active subscribers</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#1a4a2e" }}>{saMailingList.activeCount}</div>
                  </div>
                  <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, flex: 1 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Total ever subscribed</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#374151" }}>{saMailingList.totalCount}</div>
                  </div>
                </div>

                {/* Service updates go to every member of staff without
                    asking, which the law allows only because they are
                    not marketing. The rule is spelled out right here,
                    where the email is written. */}
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>Send a service update to all staff</div>
                  <p style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 8, lineHeight: 1.55 }}>
                    Goes to all {saMailingList.staffCount} staff member{saMailingList.staffCount !== 1 ? "s" : ""} with a finished account at active and trial clubs and schools. Test organisations and parents never get it.
                  </p>
                  <p style={{ fontSize: 12.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", marginBottom: 14, lineHeight: 1.55 }}>
                    Only for news about the service they already use: a new feature, the phone app, planned maintenance, a policy change. <strong>No prices, offers, upgrades or "tell another club".</strong> Anything like that goes in a news campaign below, which only reaches people who asked for it.
                  </p>
                  <input
                    value={svcSubject}
                    onChange={e => setSvcSubject(e.target.value)}
                    placeholder="Subject, e.g. The GRTP Tracker iPhone app is here"
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, marginBottom: 10, fontFamily: "inherit" }}
                  />
                  <textarea
                    value={svcBody}
                    onChange={e => setSvcBody(e.target.value)}
                    placeholder="Write the update here..."
                    rows={8}
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit", resize: "vertical" }}
                  />
                  {!svcConfirming ? (
                    <button
                      onClick={() => setSvcConfirming(true)}
                      disabled={!svcSubject.trim() || !svcBody.trim() || !saMailingList.staffCount}
                      style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (!svcSubject.trim() || !svcBody.trim() || !saMailingList.staffCount) ? 0.6 : 1, fontFamily: "inherit" }}
                    >
                      Send to {saMailingList.staffCount} staff
                    </button>
                  ) : (
                    <div style={{ background: "#fef9ec", border: "1px solid #c9991a", borderRadius: 8, padding: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 8 }}>
                        Send "{svcSubject}" to {saMailingList.staffCount} staff now? Check it has no prices or offers. This can't be undone.
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={async () => {
                            // Same synchronous double-click guard as the campaign send.
                            if (svcSending) return;
                            setSvcSending(true);
                            try {
                              const result = await api.sendServiceUpdate(svcSubject.trim(), svcBody.trim());
                              if (result.partialFailure) {
                                showNotif(`Sent to ${result.recipientCount} of ${result.totalRequested} staff — some may not have received it. Check the server logs.`, "error");
                              } else {
                                showNotif(`Sent to ${result.recipientCount} staff member${result.recipientCount !== 1 ? "s" : ""}.`);
                              }
                              setSvcSubject(""); setSvcBody(""); setSvcConfirming(false);
                              const refreshed = await api.getSuperadminMailingList();
                              setSaMailingList(refreshed);
                            } catch (e) {
                              showNotif(e.message, "error");
                            } finally {
                              setSvcSending(false);
                            }
                          }}
                          disabled={svcSending}
                          style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: svcSending ? 0.6 : 1, fontFamily: "inherit" }}
                        >
                          {svcSending ? "Sending..." : "Yes, send now"}
                        </button>
                        <button onClick={() => setSvcConfirming(false)} disabled={svcSending} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {saMailingList.serviceUpdates?.length > 0 && (
                    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, textTransform: "uppercase" }}>Past service updates</div>
                      {saMailingList.serviceUpdates.map(u => (
                        <div key={u.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f9fafb", borderRadius: 6, fontSize: 13 }}>
                          <span style={{ fontWeight: 600 }}>{u.subject}</span>
                          <span style={{ color: "#6b7280" }}>{new Date(u.sent_at).toLocaleDateString("en-GB")} · {u.recipient_count} sent</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 24 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>Send a news campaign</div>
                  <p style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 14 }}>
                    Goes out immediately to all {saMailingList.activeCount} active subscriber{saMailingList.activeCount !== 1 ? "s" : ""}. This can't be undone.
                  </p>
                  <input
                    value={mailSubject}
                    onChange={e => setMailSubject(e.target.value)}
                    placeholder="Subject"
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, marginBottom: 10, fontFamily: "inherit" }}
                  />
                  <textarea
                    value={mailBody}
                    onChange={e => setMailBody(e.target.value)}
                    placeholder="Write your update here..."
                    rows={8}
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, marginBottom: 12, fontFamily: "inherit", resize: "vertical" }}
                  />
                  {!mailConfirming ? (
                    <button
                      onClick={() => setMailConfirming(true)}
                      disabled={!mailSubject.trim() || !mailBody.trim() || saMailingList.activeCount === 0}
                      style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: (!mailSubject.trim() || !mailBody.trim() || saMailingList.activeCount === 0) ? 0.6 : 1, fontFamily: "inherit" }}
                    >
                      Send to {saMailingList.activeCount} subscriber{saMailingList.activeCount !== 1 ? "s" : ""}
                    </button>
                  ) : (
                    <div style={{ background: "#fef9ec", border: "1px solid #c9991a", borderRadius: 8, padding: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 8 }}>
                        Send "{mailSubject}" to {saMailingList.activeCount} subscriber{saMailingList.activeCount !== 1 ? "s" : ""} now? This can't be undone.
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={async () => {
                            // Guard synchronously, not just via the disabled
                            // attribute — React batches the setMailSending
                            // state update, so a second rapid click (e.g. an
                            // accidental double-click) could otherwise fire
                            // this handler again before the button visually
                            // disables, sending the campaign twice.
                            if (mailSending) return;
                            setMailSending(true);
                            try {
                              const result = await api.sendMailingListCampaign(mailSubject.trim(), mailBody.trim());
                              if (result.partialFailure) {
                                showNotif(`Sent to ${result.recipientCount} of ${result.totalRequested} subscribers — some recipients may not have received it. Check the server logs.`, "error");
                              } else {
                                showNotif(`Sent to ${result.recipientCount} subscriber${result.recipientCount !== 1 ? "s" : ""}.`);
                              }
                              setMailSubject(""); setMailBody(""); setMailConfirming(false);
                              const refreshed = await api.getSuperadminMailingList();
                              setSaMailingList(refreshed);
                            } catch (e) {
                              showNotif(e.message, "error");
                            } finally {
                              setMailSending(false);
                            }
                          }}
                          disabled={mailSending}
                          style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: mailSending ? 0.6 : 1, fontFamily: "inherit" }}
                        >
                          {mailSending ? "Sending..." : "Yes, send now"}
                        </button>
                        <button onClick={() => setMailConfirming(false)} disabled={mailSending} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {saMailingList.campaigns.length > 0 && (
                  <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 24 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 12 }}>Past campaigns</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {saMailingList.campaigns.map(c => (
                        <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f9fafb", borderRadius: 6, fontSize: 13 }}>
                          <span style={{ fontWeight: 600 }}>{c.subject}</span>
                          <span style={{ color: "#6b7280" }}>{new Date(c.sent_at).toLocaleDateString("en-GB")} · {c.recipient_count} sent</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 12 }}>Recent subscribers</div>
                  {saMailingList.recentSubscribers.length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: 13 }}>No subscribers yet.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {saMailingList.recentSubscribers.map((s, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "#f9fafb", borderRadius: 6, fontSize: 13 }}>
                          <span>{s.email}{s.unsubscribed_at ? <span style={{ color: "#c0272d", marginLeft: 8, fontSize: 11 }}>(unsubscribed)</span> : ""}</span>
                          <span style={{ color: "#9ca3af", fontSize: 11.5 }}>{({ homepage: "Homepage", app_signup: "Club/school signup", staff_signup: "Staff joining", account_settings: "Turned on in Security" })[s.source] || s.source} ·{new Date(s.subscribed_at).toLocaleDateString("en-GB")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, danger, onConfirm, onCancel }) {
  // Self-managed busy state so every caller of this shared modal gets
  // double-submit protection for free — previously the Confirm button
  // stayed clickable for the whole duration of onConfirm's async work
  // (often not fully done until the caller's own promise resolves and
  // closes the modal), so a second click before the response landed
  // could re-fire the same destructive/approval action.
  const [confirming, setConfirming] = useState(false);

  async function handleConfirmClick() {
    if (confirming) return;
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 400, width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,0.25)" }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: danger ? "#c0272d" : "#1a4a2e", marginBottom: 10 }}>
          {danger ? "⚠ " : ""}{title}
        </div>
        <p style={{ fontSize: 14, color: "#374151", margin: "0 0 20px", lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleConfirmClick}
            disabled={confirming}
            style={{ flex: 1, background: danger ? "#c0272d" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: confirming ? "default" : "pointer", opacity: confirming ? 0.7 : 1 }}
          >
            {confirming ? "Working..." : confirmLabel}
          </button>
          <button onClick={onCancel} disabled={confirming} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: confirming ? "default" : "pointer", opacity: confirming ? 0.7 : 1 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────────

// Outside the component so they're genuine constants, not recreated per render
const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const WARNING_MS =  2 * 60 * 1000; // warn 2 minutes before logout

function AppInner() {
  // Public parent-tracking route — checked first via state initializer
  // (safe to call before other hooks since it's itself a hook call), since
  // this page must be reachable without a staff password at all.
  const [publicToken] = useState(() => {
    const match = window.location.pathname.match(/^\/track\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Parent portal route — permanent link emailed on registration approval
  const [parentPortalToken] = useState(() => {
    const match = window.location.pathname.match(/^\/parent\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Somebody a parent named as an additional contact, deciding whether
  // to receive this child's concussion updates at all. They have no
  // account and have agreed to nothing, so the page is public and keyed
  // only on the token in their invitation.
  const [contactInviteToken] = useState(() => {
    const match = window.location.pathname.match(/^\/contact\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // The link every injury recipient is sent. Public and token-keyed for
  // the same reason as the invitation above: the reader has no account,
  // and for a non-roster incident may be a stranger to the club.
  const [injuryRecordToken] = useState(() => {
    const match = window.location.pathname.match(/^\/injury-record\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Parent registration form route — a school or club generates this link
  // and sends it to parents. Detected the same way as the tracking link.
  const [registrationToken] = useState(() => {
    const path = window.location.pathname;
    // Must NOT match /register/adult/... or /register/staff/... — those
    // are their own separate routes.
    if (path.startsWith("/register/adult/") || path.startsWith("/register/staff/")) return null;
    const match = path.match(/^\/register\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  const [adultRegistrationToken] = useState(() => {
    const match = window.location.pathname.match(/^\/register\/adult\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Staff self-registration route — a coach requesting access, same
  // public/no-session shape as the parent/adult registration routes above.
  const [staffRegistrationToken] = useState(() => {
    const match = window.location.pathname.match(/^\/register\/staff\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Email verification route — also fully public, checked the same way.
  const [verifyToken] = useState(() => {
    const match = window.location.pathname.match(/^\/verify-email\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Staff invite acceptance route — also fully public, same reasoning:
  // the invited person doesn't have a password to log in with yet.
  const [inviteToken] = useState(() => {
    const match = window.location.pathname.match(/^\/accept-invite\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Password reset route — also fully public. Unlike inviteToken above,
  // this person already has a password (they've just forgotten it), but
  // the reasoning for being unauthenticated is the same: they can't log
  // in to reach an authenticated route in the first place.
  const [resetPasswordToken] = useState(() => {
    const match = window.location.pathname.match(/^\/reset-password\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Mailing-list unsubscribe link — fully public, one click, no login
  // needed (this is what every unsubscribe link in every campaign email
  // points to).
  const [unsubscribeToken] = useState(() => {
    const match = window.location.pathname.match(/^\/unsubscribe\/([a-f0-9]+)$/i);
    return match ? match[1] : null;
  });

  // Stripe redirects here after a successful checkout. Not gated on
  // being logged in at the routing level — if the session lapsed during
  // checkout, the screen itself falls back to a simpler "you're paid up,
  // please log in" message rather than blocking the route entirely.
  const [isBillingSuccessRoute] = useState(() => window.location.pathname === "/billing/success");

  // Superadmin route — a completely separate area with its own password,
  // not tied to any organisation. Checked the same simple way as the
  // other path-based routes above.
  const [isSuperadminRoute] = useState(() => window.location.pathname === "/superadmin");

  // Static legal pages — served from the app so no separate homepage
  // deploy is needed. /privacy, /terms, /dpa resolve here.
  const [legalRoute] = useState(() => {
    const path = window.location.pathname;
    if (path === "/privacy" || path === "/privacy/") return "privacy";
    if (path === "/terms"   || path === "/terms/")   return "terms";
    if (path === "/dpa"     || path === "/dpa/")     return "dpa";
    // Published rather than kept internally: Schedule 1, Part 4 of the
    // Data Protection Act 2018 requires this document to exist and be
    // available to the Information Commissioner on request, and there is
    // nothing in it a parent or a school's DPO should not be able to read.
    if (path === "/appropriate-policy-document" || path === "/appropriate-policy-document/") return "apd";
    return null;
  });

  // Visiting /signup directly (e.g. from the homepage's Sign Up button)
  // should open the signup screen immediately, not silently fall back to
  // the login screen just because no in-app button was clicked yet.
  const [showSignup, setShowSignup] = useState(() => window.location.pathname === "/signup");
  // /forgot-password is where the "was this you?" email sends people, so it
  // opens the reset form straight away, signed in or not (see LOGIN GATE).
  const forgotPasswordRoute = window.location.pathname === "/forgot-password";
  const [showForgotPassword, setShowForgotPassword] = useState(() => forgotPasswordRoute);
  const [authed, setAuthed] = useState(() => api.isLoggedIn());
  const [isOrgAdmin, setIsOrgAdmin] = useState(() => api.isOrgAdmin());
  const [orgName, setOrgNameState] = useState(() => api.orgName());
  const [orgType, setOrgType] = useState(() => api.orgType());
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(null);
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  // Why the lapsed screen is showing: a trial that ended, or a
  // subscription that did. The screen reads differently for each.
  const [lapseReason, setLapseReason] = useState("trial_expired");
  const [showPaymentPending, setShowPaymentPending] = useState(false);
  const [pendingTier, setPendingTier] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("dashboard");
  const [selectedId, setSelectedId] = useState(null);
  const [notif, setNotif] = useState(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const inactivityTimer = useRef(null);
  const warningTimer = useRef(null);
  const [undoTarget, setUndoTarget] = useState(null); // { playerId, expiresAt }
  const [newlyAdvanced, setNewlyAdvanced] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // Dashboard filters. These narrow what is on screen AND what the export
  // buttons produce — see exportIsFiltered, where the two are tied together.
  const [filterTeam, setFilterTeam] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [crossOrgAlerts, setCrossOrgAlerts] = useState([]);
  const [emailHealth, setEmailHealth] = useState(null);
  const [resolvingBounce, setResolvingBounce] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, confirmLabel, onConfirm }
  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [teamsList, setTeamsList] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [addTeamSubmitting, setAddTeamSubmitting] = useState(false);
  const [staffRegLink, setStaffRegLink] = useState("");
  const [pendingStaffRegs, setPendingStaffRegs] = useState([]);
  const [staffRegsLoading, setStaffRegsLoading] = useState(false);
  const [staffRegSelections, setStaffRegSelections] = useState({}); // { [id]: { ageGrades: [], teams: [] } }
  const [staffRegActionId, setStaffRegActionId] = useState(null);
  const [rosterList, setRosterList] = useState([]);
  const [rosterSearch, setRosterSearch] = useState("");
  // All grades collapsed by default — user clicks to expand
  const ALL_ROSTER_GRADES = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult"];
  const [collapsedGrades, setCollapsedGrades] = useState(() => new Set([...ALL_ROSTER_GRADES, "__unknown__"]));
  function toggleGrade(grade) {
    setCollapsedGrades(prev => {
      const next = new Set(prev);
      if (next.has(grade)) next.delete(grade); else next.add(grade);
      return next;
    });
  }
  const [bulkDeleteAgeGrade, setBulkDeleteAgeGrade] = useState("");
  const [brandColorInput, setBrandColorInput] = useState(() => api.orgBrandColor() || "#1a4a2e");
  const [logoPreview, setLogoPreview] = useState(() => api.orgLogoDataUrl());
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingError, setBrandingError] = useState("");
  const [mfaStatus, setMfaStatus] = useState(null); // { enabled, required }
  const [mfaSetupData, setMfaSetupData] = useState(null); // { secret, qrDataUrl }
  const [mfaSetupCode, setMfaSetupCode] = useState("");
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null); // shown once, right after confirming
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState("");
  const [activityLog, setActivityLog] = useState(null);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAgeGradePrompt, setShowAgeGradePrompt] = useState(false);
  const [ageGradeDeciding, setAgeGradeDeciding] = useState(false);
  const [ageGradeResultSummary, setAgeGradeResultSummary] = useState(null);
  const [showArchivedRoster, setShowArchivedRoster] = useState(false);
  const [pendingRegCount, setPendingRegCount] = useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const unreadCountRequestIdRef = useRef(0);
  const [messageThreads, setMessageThreads] = useState([]);
  const [messageThreadsLoading, setMessageThreadsLoading] = useState(false);
  const [openThread, setOpenThread] = useState(null); // { threadId, playerName, playerId, messages }
  const [threadLoading, setThreadLoading] = useState(false);
  const [messageComposeText, setMessageComposeText] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState("");
  const [savingMessageEdit, setSavingMessageEdit] = useState(false);
  const [messageImages, setMessageImages] = useState([]);
  const [messageLightboxUrl, setMessageLightboxUrl] = useState(null);
  const [parentChangeRequests, setParentChangeRequests] = useState([]);
  const [parentChangeCount, setParentChangeCount] = useState(0);
  const [changeRejectTarget, setChangeRejectTarget] = useState(null); // { id, playerName, message, error }
  const [changeRejecting, setChangeRejecting] = useState(false);
  const [approvingChangeId, setApprovingChangeId] = useState(null);
  // The Registrations badge counts everything waiting for an admin on that
  // screen: registrations, link requests (in pendingRegCount) and parents'
  // change requests.
  const registrationsWaiting = pendingRegCount + parentChangeCount;
  const [pendingRegs, setPendingRegs] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [registrationLink, setRegistrationLink] = useState(null);
  const [adultRegistrationLink, setAdultRegistrationLink] = useState(null);
  const [regLinkLoading, setRegLinkLoading] = useState(false);
  // Withheld until the organisation has been confirmed as a real club or
  // school. Signing up proves only that somebody can click a link in
  // their own inbox, and these links are the one surface that reaches
  // families who are not the customer.
  const [regAwaitingApproval, setRegAwaitingApproval] = useState(false);
  // Duplicate approval warning state
  const [approveWarning, setApproveWarning] = useState(null); // { registrationId, playerName, message }
  const [rejectTarget, setRejectTarget] = useState(null); // { id, playerName, parentEmail, reason, notifyParent }
  const [rejecting, setRejecting] = useState(false);
  // Bulk registration approval — selectedRegIds is a Set of registration
  // IDs the staff member has ticked. bulkApproving tracks the in-flight
  // request; bulkApproveResult holds the last summary to show after it
  // completes (approved count, and anything skipped that still needs
  // individual attention).
  const [selectedRegIds, setSelectedRegIds] = useState(() => new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [approvingRegId, setApprovingRegId] = useState(null);
  const [bulkApproveResult, setBulkApproveResult] = useState(null);
  // Bulk contact replace state
  const [showBulkReplace, setShowBulkReplace] = useState(false);
  const [bulkField, setBulkField] = useState("club_coach_email");
  const [bulkOldValue, setBulkOldValue] = useState("");
  const [bulkNewValue, setBulkNewValue] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null); // { count, players }
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkDone, setBulkDone] = useState(null); // { updated, field, oldValue, newValue }
  const [archivedRoster, setArchivedRoster] = useState([]);
  const [archivedRosterLoading, setArchivedRosterLoading] = useState(false);
  const [removeEarlyTarget, setRemoveEarlyTarget] = useState(null); // the player object, or null if dialog closed
  const [removeEarlyReason, setRemoveEarlyReason] = useState("");
  const [removeEarlySubmitting, setRemoveEarlySubmitting] = useState(false);
  const [notifHistory, setNotifHistory] = useState({}); // keyed by player id
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterEditingId, setRosterEditingId] = useState(null); // null = not editing, "new" = adding, else an id
  const [rosterForm, setRosterForm] = useState(null);
  const [rosterSubmitting, setRosterSubmitting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvText, setImportCsvText] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [rosterSearchQuery, setRosterSearchQuery] = useState("");
  const [rosterSearchResults, setRosterSearchResults] = useState([]);
  const [showRosterDropdown, setShowRosterDropdown] = useState(false);
  // Which suggestion the keyboard is on. -1 means "none highlighted yet",
  // so the first Down arrow lands on the first result. Without this the
  // roster list was mouse-only: a keyboard user had to type the whole
  // name, and typing a name does not link the record to its roster entry.
  const [rosterActiveIndex, setRosterActiveIndex] = useState(-1);
  const blankLogConcussionForm = () => ({
    // Set only when the player was picked from the roster search, and
    // cleared the moment the name is typed over — it identifies WHICH
    // child this record is about, which a name alone cannot do once two
    // players share one. Blank for a manually-entered player who isn't
    // on the roster at all.
    rosterPlayerId: "",
    name: "", ageGrade: "", team: "", position: "",
    incidentDate: new Date().toISOString().split("T")[0],
    incidentTime: "",
    incidentDetails: "", incidentContext: "", incidentMechanism: "", symptoms: [], otherSymptom: "",
    parentName: "", parentEmail: "", staffEmail: "",
    schoolCoachName: "", schoolCoachEmail: "", schoolCoach2Name: "", schoolCoach2Email: "",
    clubCoachName: "", clubCoachEmail: "", clubCoach2Name: "", clubCoach2Email: "",
    otherContact1Name: "", otherContact1Email: "",
    otherContact2Name: "", otherContact2Email: "",
    consentGiven: false,
    isTest: false,
  });
  const [form, setForm] = useState(blankLogConcussionForm);

  // ── Injuries and first aid ─────────────────────────────────────────
  //
  // A register, not a status tracker. There is deliberately no stage and
  // no progression here: a concussion protocol can carry a schedule
  // because a national body wrote one, and nothing comparable exists for
  // a hamstring. Anything that moved an injury forward on its own would
  // be this app giving medical advice.
  //
  // rosterPlayerId blank means somebody who is not on the roster at all —
  // a visiting player, a spectator's child. They can still be recorded,
  // with contacts typed in or with none at all.
  const blankInjuryForm = () => ({
    rosterPlayerId: "",
    subjectName: "", ageGrade: "", team: "", subjectDob: "", subjectIsMinor: null,
    incidentDate: new Date().toISOString().split("T")[0],
    incidentTime: "", activity: "", bodyPart: "", nature: "", natureOther: "",
    contact: null, description: "", carriedOn: "",
    treatment: "", adviceGiven: "", adviceAccepted: null, referral: "",
    consentGiven: false, shareConsentGiven: false,
    // Typed in for somebody not on the roster. A roster player's contacts
    // come from their record and are ticked rather than typed.
    manualContacts: [{ name: "", email: "" }],
    selectedContacts: [],
    isTest: false,
  });
  const [injuryForm, setInjuryForm] = useState(blankInjuryForm);
  // Picking who was hurt used to be a <select> listing the whole roster.
  // A club with 300 players got 300 options in a native picker, which on a
  // phone is a scroll wheel you thumb through on a touchline in the rain.
  // These drive a type-to-search instead.
  const [injurySearch, setInjurySearch] = useState("");
  const [injuryActiveIndex, setInjuryActiveIndex] = useState(-1);
  const [injuries, setInjuries] = useState([]);
  // The injury record currently open. Injuries used to be write-only from
  // the UI: logged, then frozen, with no way to answer the hospital
  // question the app itself asks, record what it turned out to be, or say
  // the player came back.
  const [selectedInjuryId, setSelectedInjuryId] = useState(null);
  const [injuryEdit, setInjuryEdit] = useState({ confirmedDiagnosis: "", returnedAt: "", treatment: "" });
  const [injuryEditSaving, setInjuryEditSaving] = useState(false);
  const [injuryGuidance, setInjuryGuidance] = useState(null);
  // Who has already been told, fetched with the record itself. Shown
  // because "who was told about my child's injury?" is a question a
  // parent is entitled to ask and the club has to be able to answer.
  const [injuryRecipients, setInjuryRecipients] = useState([]);
  const [shareSelection, setShareSelection] = useState([]);
  const [shareManual, setShareManual] = useState({ name: "", email: "" });
  const [shareConsent, setShareConsent] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [crossOrgInjuries, setCrossOrgInjuries] = useState([]);
  const [injurySaving, setInjurySaving] = useState(false);
  const [injuryResult, setInjuryResult] = useState(null);
  // The first aid log's own filters, deliberately separate from the
  // dashboard's. Somebody narrowing the register down to one team for an
  // insurer should not find the dashboard narrowed when they go back to
  // it — and the reverse would be worse, an export quietly inheriting a
  // filter set on another screen.
  const [logFilters, setLogFilters] = useState({ from: "", to: "", team: "", query: "", type: "all", followup: false });
  const [logExporting, setLogExporting] = useState(false);
  // Tracked separately from the players' own `loading` flag because the
  // register is built from both, and either one arriving first would let
  // the first aid log say "nothing recorded yet" about an organisation
  // that has records. On a register an empty state is a claim, so the
  // three cases are kept apart: still loading, loaded, and could not
  // load — that last one must never be shown as "nothing to show".
  const [injuriesStatus, setInjuriesStatus] = useState("loading");

  function openInjury(id) {
    const inj = injuries.find(i => i.id === id);
    setSelectedInjuryId(id);
    setInjuryEdit({
      confirmedDiagnosis: (inj && inj.confirmedDiagnosis) || "",
      returnedAt: (inj && inj.returnedAt) || "",
      treatment: (inj && inj.treatment) || "",
    });
    setView("injury_detail");
    setInjuryRecipients([]);
    setShareSelection([]);
    setShareManual({ name: "", email: "" });
    setShareConsent(false);
    if (!rosterList.length) loadRoster();
    api.getInjury(id)
      .then(full => setInjuryRecipients(full.recipients || []))
      .catch(() => setInjuryRecipients([]));
  }

  // A disclosure taken today, not one inherited from the day the record
  // was written. The server refuses without its own consent; this asks
  // for it in the same breath as choosing who.
  async function shareInjuryNow(id) {
    const recipients = [...shareSelection.map(e => ({ email: e })),
      ...(shareManual.email.trim() ? [{ email: shareManual.email.trim(), name: shareManual.name.trim() || null }] : [])];
    if (!recipients.length) return showNotif("Choose at least one person to tell.", "error");
    if (!shareConsent) return showNotif("Consent to share must be confirmed before this injury can be sent to anyone.", "error");
    setSharing(true);
    try {
      const res = await api.shareInjury(id, { recipients, shareConsentGiven: true });
      const full = await api.getInjury(id);
      setInjuryRecipients(full.recipients || []);
      setShareSelection([]);
      setShareManual({ name: "", email: "" });
      setShareConsent(false);
      const sent = (res.sentTo || []).length;
      const held = (res.withheldFrom || []).length;
      const out = (res.optedOut || []).length;
      showNotif(
        [sent ? `Sent to ${sent}.` : null,
         held ? `${held} held back until they answer the wider question.` : null,
         out ? `${out} has asked not to be contacted.` : null].filter(Boolean).join(" ") || "Nothing was sent."
      );
    } catch (e) { showNotif(e.message, "error"); }
    finally { setSharing(false); }
  }

  // The question the app itself asks 48 hours later, and until now had
  // nowhere to answer. A yes is what the RFU's reporting duty turns on,
  // so the answer surfaces the guidance rather than filing it silently.
  async function answerInjuryFollowup(id, admitted) {
    try {
      const res = await api.answerInjuryFollowup(id, admitted);
      await loadInjuries();
      showNotif(admitted ? "Recorded as admitted to hospital." : "Recorded as not admitted.");
      if (res && res.guidance) setInjuryGuidance(res.guidance);
    } catch (e) { showNotif(e.message, "error"); }
  }

  async function saveInjuryEdit(id) {
    setInjuryEditSaving(true);
    try {
      await api.updateInjury(id, {
        confirmedDiagnosis: injuryEdit.confirmedDiagnosis.trim() || null,
        returnedAt: injuryEdit.returnedAt || null,
        treatment: injuryEdit.treatment.trim() || null,
      });
      await loadInjuries();
      showNotif("Record updated.");
    } catch (e) { showNotif(e.message, "error"); }
    finally { setInjuryEditSaving(false); }
  }

  async function loadInjuries() {
    try {
      const [own, elsewhere] = await Promise.all([
        api.getInjuries(),
        api.getCrossOrgInjuries().catch(() => []),
      ]);
      setInjuries(own || []);
      setCrossOrgInjuries(elsewhere || []);
      setInjuriesStatus("loaded");
    } catch (e) {
      // A failure here must not take the dashboard down with it: the
      // concussion protocols are the part somebody may be relying on
      // right now, and this section sits underneath them.
      console.error("Could not load injuries:", e.message);
      setInjuriesStatus("error");
    }
  }

  // Data protection page. eraseResult is kept after the erasure finishes
  // so the admin can read back what was deleted and what was retained —
  // that summary is what they will quote to the parent who asked.
  // Cross-organisation state. linkedStatus is fetched the moment a player
  // is chosen on the log form, so staff see that another organisation is
  // already managing a protocol before they fill anything in — not after
  // they hit submit.
  const [linkedStatus, setLinkedStatus] = useState(null);
  const [duplicateOverrideReason, setDuplicateOverrideReason] = useState("");
  const [crossOrgProtocols, setCrossOrgProtocols] = useState([]);
  const [linkRequests, setLinkRequests] = useState([]);
  const [outgoingLinkRequests, setOutgoingLinkRequests] = useState([]);
  const [resolvingLinkId, setResolvingLinkId] = useState(null);

  const [retentionReport, setRetentionReport] = useState(null);
  const [exportingOrg, setExportingOrg] = useState(false);
  const [dpSearch, setDpSearch] = useState("");
  const [eraseTarget, setEraseTarget] = useState(null);
  const [eraseConfirmName, setEraseConfirmName] = useState("");
  const [erasing, setErasing] = useState(false);
  const [eraseResult, setEraseResult] = useState(null);

  const showNotif = useCallback((msg, type = "success") => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 4000);
  }, []);

  // Shared by every admin-only action's catch block. If the backend
  // specifically says MFA needs setting up first, don't just show a
  // toast the person might miss or dismiss without acting on — send
  // them straight to the screen where they can actually fix it, with a
  // message that explains why they were just redirected.
  const handleAdminActionError = useCallback((e) => {
    if (e.code === "MFA_SETUP_REQUIRED") {
      setView("security");
      loadMfaStatus();
      showNotif("Set up two-factor authentication below to unlock admin features.", "error");
    } else {
      showNotif(e.message, "error");
    }
  }, [showNotif]);

  const loadPlayers = useCallback(async () => {
    try {
      // Always fetch the complete dataset (including archived) regardless of
      // which view is showing. Filtering for what's actually displayed
      // happens separately via filteredPlayers — this keeps stage-advance
      // detection comparing like-for-like data, and keeps counts (like the
      // archive badge) accurate no matter which view triggered the refresh.
      const data = await api.getPlayers(true);
      setPlayers(prev => {
        const advanced = [];

        data.forEach(p => {
          const old = prev.find(o => o.id === p.id);
          if (old && p.current_stage > old.current_stage && !p.paused) {
            advanced.push({ name: p.name, from: old.current_stage, to: p.current_stage });
          }
        });
        if (advanced.length) setNewlyAdvanced(a => [...a, ...advanced]);
        return data;
      });
      setError(null);
    } catch (e) {
      if (e.isAuthError) {
        setAuthed(false);
      } else {
        setError("Could not connect to server. Check the backend is running.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authed || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    loadPlayers();
    loadInjuries();
    const interval = setInterval(loadPlayers, 60000);
    return () => clearInterval(interval);
  }, [authed, showTrialExpired, showPaymentPending, publicToken, verifyToken, inviteToken, isSuperadminRoute, loadPlayers]);

  // Checks once per dashboard load whether the annual age-grade
  // advancement prompt should show. Admin-only, since only an admin can
  // actually act on it — a regular staff member seeing this prompt with
  // no way to do anything about it would just be confusing. The backend
  // is the actual source of truth for the September-onward, once-per-
  // organisation-per-year logic; this just asks it and reflects the answer.
  useEffect(() => {
    if (!authed || !isOrgAdmin || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    api.getAgeGradeAdvancementStatus()
      .then(status => { if (status.shouldPrompt) setShowAgeGradePrompt(true); })
      .catch(() => {});
  }, [authed, isOrgAdmin, showTrialExpired, showPaymentPending, publicToken, verifyToken, inviteToken, isSuperadminRoute]);

  useEffect(() => {
    if (!authed || !isOrgAdmin || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    // The badge counts everything on the Registrations page awaiting an
    // admin, which is now registrations and link requests both. A link
    // request left unanswered means another organisation is waiting and
    // nothing is being shared, so it needs to be as visible as a pending
    // registration rather than only discoverable by opening the page.
    Promise.all([
      api.getPendingRegistrationsCount().then(r => r.pending || 0).catch(() => 0),
      api.getPlayerLinkRequests().then(r => (r.requests || []).length).catch(() => 0),
    ])
      .then(([pending, linkCount]) => setPendingRegCount(pending + linkCount))
      .catch(() => {});
    api.getCrossOrgAlerts()
      .then(r => setCrossOrgAlerts(r.alerts || []))
      .catch(() => {});
    api.getEmailHealth().then(setEmailHealth).catch(() => {});
  }, [authed, isOrgAdmin, showTrialExpired, showPaymentPending, publicToken, parentPortalToken, registrationToken, verifyToken, inviteToken, isSuperadminRoute]);

  // Deliberately not admin-gated, unlike the alerts above. The person who
  // needs to know a player is mid-protocol elsewhere is the coach about
  // to put them on the pitch, not the administrator.
  useEffect(() => {
    if (!authed || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    loadCrossOrgProtocols();
  }, [authed, showTrialExpired, showPaymentPending, publicToken, parentPortalToken, registrationToken, verifyToken, inviteToken, isSuperadminRoute]);

  // Unread messages badge — every staff member gets this (not admin-only),
  // since who can see a given thread depends on team assignment/who logged
  // it, not on admin status.
  useEffect(() => {
    if (!authed || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    api.getMessagesUnreadCount()
      .then(r => setMessagesUnreadCount(r.unread || 0))
      .catch(() => {});
  }, [authed, showTrialExpired, showPaymentPending, publicToken, parentPortalToken, registrationToken, verifyToken, inviteToken, isSuperadminRoute]);

  // Load parent change requests — separate effect so it never fires on public routes
  useEffect(() => {
    if (!authed || !isOrgAdmin || showTrialExpired || showPaymentPending) return;
    api.getParentChangeRequests()
      .then(r => { setParentChangeRequests(r); setParentChangeCount(r.length); })
      .catch(() => {});
  }, [authed, isOrgAdmin, showTrialExpired, showPaymentPending]);

  // Load this org's teams once at startup — every staff member needs the
  // list for the Team dropdown on the roster/concussion forms, not just
  // admins (who additionally get to add/remove teams, on the Staff page).
  useEffect(() => {
    if (!authed || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;
    api.getTeams().then(setTeamsList).catch(() => {});
  }, [authed, showTrialExpired, showPaymentPending, publicToken, parentPortalToken, registrationToken, verifyToken, inviteToken, isSuperadminRoute]);

  // Load notification history whenever a player detail view is opened,
  // caching by player ID so navigating back and forth doesn't re-fetch
  // on every visit. Cache is intentionally not invalidated here — if a
  // webhook fires while the detail view is open, the refresh button on
  // the notification log handles that.
  useEffect(() => {
    if (!selectedId || view !== "detail") return;
    if (notifHistory[selectedId]) return; // already loaded
    api.getPlayerNotifications(selectedId)
      .then(events => setNotifHistory(prev => ({ ...prev, [selectedId]: events })))
      .catch(() => {}); // non-critical — detail view still works without it
  }, [selectedId, view]);

  // Keep the browser tab title in sync with whichever organisation is
  // actually logged in, rather than a name fixed at build time. Falls
  // back to the generic name before login, or if orgName is ever empty
  // for any reason.
  useEffect(() => {
    document.title = orgName ? `${orgName} — GRTP Tracker` : "GRTP Tracker";
  }, [orgName]);

  // Defensive second layer: a non-admin should never be able to land on
  // the staff management screen, even if they triggered it some other way
  // than clicking the (already admin-only) button. The backend enforces
  // the real rule regardless — this just avoids showing a broken/empty
  // admin screen first before the API call would fail anyway.
  useEffect(() => {
    if (view === "staff" && !isOrgAdmin) {
      setView("dashboard");
    }
    if (view === "branding" && !isOrgAdmin) {
      setView("dashboard");
    }
  }, [view, isOrgAdmin]);

  // Every screen opens at its top. Screens here are state on one page, so
  // without this a new screen kept the old one's scroll position: tap a
  // player low down the dashboard and their page opened halfway down, with
  // their name and buttons out of sight. Opening a message thread counts
  // as a new screen too.
  const openThreadId = openThread ? openThread.threadId : null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, openThreadId]);

  // Android's back button (the phone app only). Screens here are state,
  // not browser history, so left alone it would close the app from any
  // screen: a coach on a player's page presses back and loses their
  // place. It now does what the on-screen "← Back" links do, asks before
  // throwing away a half-filled form, and on the dashboard puts the app
  // in the background rather than closing it.
  useEffect(() => {
    nativeApp.onBackButton(() => {
      if (confirmAction) { setConfirmAction(null); return true; }
      if (rejectTarget) { setRejectTarget(null); return true; }
      if (changeRejectTarget) { if (!changeRejecting) setChangeRejectTarget(null); return true; }
      if (view === "new" || view === "new_injury") {
        const leaveTo = view === "new_injury" ? "first_aid_log" : "dashboard";
        setConfirmAction({
          title: "Leave without saving?",
          message: "What you've entered on this form will be lost.",
          confirmLabel: "Leave",
          danger: true,
          onConfirm: () => {
            setConfirmAction(null);
            if (view === "new") setForm(blankLogConcussionForm());
            else setInjurySearch("");
            setView(leaveTo);
          },
        });
        return true;
      }
      if (view === "messages" && openThread) { setOpenThread(null); return true; }
      if (view === "injury_detail") { setView("first_aid_log"); return true; }
      if (view !== "dashboard") { setView("dashboard"); return true; }
      return false;
    });
    return () => nativeApp.onBackButton(null);
  }, [view, confirmAction, rejectTarget, changeRejectTarget, changeRejecting, openThread]);

  // Keeping what's on screen up to date. Most screens loaded once, when
  // they were opened, so a phone put down at training and picked up an
  // hour later showed the hour-old picture. Now the screen reloads when
  // the app (or browser tab) comes back into view, and in the phone app
  // when you pull down from the top. A player's or an injury's own page is
  // worked out from the lists reloaded here, so it updates with them.
  //
  // Held in a ref so the listeners, attached once, always run against the
  // current screen rather than the one that was open when they were made.
  const refreshRef = useRef(null);
  const lastRefreshRef = useRef(0);
  refreshRef.current = async () => {
    lastRefreshRef.current = Date.now();
    const jobs = [
      loadPlayers(),
      loadInjuries(),
      api.getMessagesUnreadCount().then(r => setMessagesUnreadCount(r.unread || 0)).catch(() => {}),
    ];
    if (view === "messages") {
      // Reloads an open conversation quietly, rather than through the
      // list loader, which would close it.
      if (openThread) {
        jobs.push(api.getMessageThread(openThread.threadId)
          .then(d => setOpenThread(t => (t && d && t.threadId === d.threadId ? d : t)))
          .catch(() => {}));
      } else {
        jobs.push(loadMessageThreads());
      }
    }
    // Keeps the Registrations badge true on every screen, not just that one,
    // and the "emails couldn't be sent" notice current.
    if (isOrgAdmin) {
      jobs.push(loadParentChangeRequests());
      jobs.push(api.getEmailHealth().then(setEmailHealth).catch(() => {}));
    }
    if (view === "activity") jobs.push(loadActivityLog());
    if (view === "registrations") jobs.push(loadPendingRegistrations(), loadLinkRequests());
    if (view === "analytics") jobs.push(loadAnalytics());
    if (view === "security") jobs.push(loadMfaStatus());
    if (view === "roster") jobs.push(loadRoster());
    if (view === "staff") jobs.push(loadStaff(), loadPendingStaffRegistrations());
    await Promise.allSettled(jobs);
  };
  useEffect(() => {
    if (!authed || showTrialExpired || showPaymentPending || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return undefined;
    let comeBackTimer = null;
    const stopComeBack = nativeApp.onComeBack(() => {
      // Switching apps and straight back again needn't reload everything.
      if (Date.now() - lastRefreshRef.current < 10000) return;
      // A phone waking up often has no connection for the first second or
      // so; asking straight away would flash "Could not connect".
      clearTimeout(comeBackTimer);
      comeBackTimer = setTimeout(() => { if (refreshRef.current) refreshRef.current(); }, 1500);
    });
    const stopPull = nativeApp.enablePullToRefresh(() => (refreshRef.current ? refreshRef.current() : null));
    const stopSwipe = nativeApp.enableEdgeSwipeBack();
    return () => { clearTimeout(comeBackTimer); stopComeBack(); stopPull(); stopSwipe(); };
  }, [authed, showTrialExpired, showPaymentPending, legalRoute, publicToken, parentPortalToken, registrationToken, adultRegistrationToken, staffRegistrationToken, verifyToken, inviteToken, isSuperadminRoute]);

  // All hooks above this point are called unconditionally on every render,
  // satisfying React's rules of hooks — these early returns only skip the
  // rest of the component's logic and JSX, which is safe.
  if (legalRoute) {
    return <LegalPage page={legalRoute} />;
  }
  if (publicToken) {
    return <ParentTrackingPage token={publicToken} />;
  }
  if (parentPortalToken) {
    return <ParentPortalPage token={parentPortalToken} />;
  }
  if (contactInviteToken) {
    return <ContactInvitePage token={contactInviteToken} />;
  }
  if (injuryRecordToken) {
    return <InjuryRecordPage token={injuryRecordToken} />;
  }
  if (registrationToken) {
    return <ParentRegistrationPage token={registrationToken} />;
  }
  if (adultRegistrationToken) {
    return <AdultRegistrationPage token={adultRegistrationToken} />;
  }
  if (staffRegistrationToken) {
    return <StaffRegistrationPage token={staffRegistrationToken} />;
  }
  if (verifyToken) {
    return <VerifyEmailScreen token={verifyToken} />;
  }
  if (inviteToken) {
    return <AcceptInvitePage token={inviteToken} />;
  }
  if (resetPasswordToken) {
    return <ResetPasswordScreen token={resetPasswordToken} />;
  }
  if (unsubscribeToken) {
    return <UnsubscribeScreen token={unsubscribeToken} />;
  }
  if (isBillingSuccessRoute) {
    return <BillingSuccessScreen />;
  }
  if (isSuperadminRoute) {
    return <SuperadminPage />;
  }

  // Refs to stable functions so the modal buttons and setTimeout callbacks
  // always call the current versions without stale-closure problems.
  const handleLogoutRef = useRef(handleLogout);
  const resetRef = useRef(null);
  useEffect(() => { handleLogoutRef.current = handleLogout; });

  // Set up and tear down activity listeners while authenticated.
  useEffect(() => {
    if (!authed || legalRoute || publicToken || parentPortalToken || registrationToken || adultRegistrationToken || staffRegistrationToken || verifyToken || inviteToken || isSuperadminRoute) return;

    function reset() {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current)    clearTimeout(warningTimer.current);
      setShowTimeoutWarning(false);
      warningTimer.current = setTimeout(() => setShowTimeoutWarning(true), TIMEOUT_MS - WARNING_MS);
      inactivityTimer.current = setTimeout(() => handleLogoutRef.current({ keepDevice: true }), TIMEOUT_MS);
    }

    resetRef.current = reset; // expose reset for modal button
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset(); // start the clock on login

    return () => {
      events.forEach(ev => window.removeEventListener(ev, reset));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (warningTimer.current)    clearTimeout(warningTimer.current);
      resetRef.current = null;
    };
  }, [authed]);

  // In the phone app, signing out on purpose also forgets this phone, on
  // the server and in the keychain. Timing out for inactivity only ends
  // the session, so Face ID gets the coach straight back in. (Called from
  // onClick as well, so the argument may be a click event.)
  function handleLogout(opts) {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current)    clearTimeout(warningTimer.current);
    setShowTimeoutWarning(false);
    const keepDevice = opts && opts.keepDevice === true;
    if (nativeApp.isNativeApp() && !keepDevice) {
      const deviceId = nativeApp.savedDeviceId();
      nativeApp.forgetSignIn();
      nativeApp.rememberDeviceId(null);
      // The revoke needs the session, so the session ends only after it
      // has been answered. Sent together, the logout could arrive first.
      (deviceId ? api.revokeOwnAppDevice(deviceId).catch(() => {}) : Promise.resolve())
        .finally(() => api.logout());
    } else {
      api.logout();
    }
    setAuthed(false);
    setIsOrgAdmin(false);
    setOrgNameState("");
    setOrgType("");
    setPlayers([]);
    setView("dashboard");
    setShowTrialExpired(false);
    setShowPaymentPending(false);
    setPendingTier(null);
  }

  async function loadStaff() {
    setStaffLoading(true);
    try {
      const data = await api.getStaff();
      setStaffList(data);
    } catch (e) {
      handleAdminActionError(e);
    } finally {
      setStaffLoading(false);
    }
  }

  function handleOpenStaffManagement() {
    setView("staff");
    loadStaff();
    loadPendingStaffRegistrations();
    // This screen has its own copies of the registration link cards, and
    // they carry the same fault the Registrations screen did: each card
    // only renders once its link is loaded, and the only control that
    // loads it sits inside the card. Fixing one screen and leaving the
    // other is how a bug comes back.
    loadRegistrationLinks();
  }

  async function loadPendingStaffRegistrations() {
    setStaffRegsLoading(true);
    try {
      const data = await api.getPendingStaffRegistrations();
      setPendingStaffRegs(data);
      setStaffRegSelections(prev => {
        const next = { ...prev };
        data.forEach(r => {
          if (!next[r.id]) next[r.id] = { ageGrades: r.requestedAgeGrades || [], teams: r.requestedTeams || [] };
        });
        return next;
      });
    } catch (e) {
      handleAdminActionError(e);
    } finally {
      setStaffRegsLoading(false);
    }
  }

  function toggleStaffRegSelection(id, field, value) {
    setStaffRegSelections(prev => {
      const current = prev[id] || { ageGrades: [], teams: [] };
      const list = current[field];
      const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
      return { ...prev, [id]: { ...current, [field]: next } };
    });
  }

  async function handleApproveStaffReg(reg) {
    const sel = staffRegSelections[reg.id] || { ageGrades: [], teams: [] };
    setStaffRegActionId(reg.id);
    try {
      await api.approveStaffRegistration(reg.id, sel.ageGrades, sel.teams);
      showNotif(`${reg.name} approved — they'll get an email to verify their address.`);
      setPendingStaffRegs(prev => prev.filter(r => r.id !== reg.id));
      loadStaff();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setStaffRegActionId(null);
    }
  }

  function handleRejectStaffReg(reg) {
    setConfirmAction({
      title: "Reject Staff Request?",
      message: `Reject ${reg.name}'s (${reg.email}) request for staff access? They won't be notified, and can submit another request later if needed.`,
      confirmLabel: "Reject",
      danger: true,
      onConfirm: async () => {
        try {
          await api.rejectStaffRegistration(reg.id);
          showNotif(`${reg.name}'s request has been rejected.`);
          setPendingStaffRegs(prev => prev.filter(r => r.id !== reg.id));
        } catch (e) {
          showNotif(e.message, "error");
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleInviteStaff(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteSubmitting(true);
    try {
      await api.inviteStaff(inviteEmail.trim());
      showNotif(`Invite sent to ${inviteEmail.trim()}.`);
      setInviteEmail("");
      loadStaff();
    } catch (e) {
      handleAdminActionError(e);
    } finally {
      setInviteSubmitting(false);
    }
  }

  function handleRemoveStaff(staffMember) {
    setConfirmAction({
      title: "Remove Staff Member?",
      message: `Remove ${staffMember.email}'s access? They will no longer be able to log in. This can't be undone, though they could be re-invited later.`,
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await api.removeStaff(staffMember.id);
          showNotif(`${staffMember.email} has been removed.`);
          loadStaff();
        } catch (e) {
          handleAdminActionError(e);
        }
        setConfirmAction(null);
      },
    });
  }

  function handleToggleAdmin(staffMember) {
    const makingAdmin = !staffMember.isOrgAdmin;
    setConfirmAction({
      title: makingAdmin ? "Make Admin?" : "Remove Admin Access?",
      message: makingAdmin
        ? `Give ${staffMember.email} full admin access? They'll be able to invite and remove staff, and manage other admins too.`
        : `Remove ${staffMember.email}'s admin access? They'll keep their regular staff login, just without the ability to manage other staff.`,
      confirmLabel: makingAdmin ? "Make Admin" : "Remove Admin",
      danger: !makingAdmin,
      onConfirm: async () => {
        try {
          await api.toggleStaffAdmin(staffMember.id);
          showNotif(makingAdmin ? `${staffMember.email} is now an admin.` : `${staffMember.email}'s admin access has been removed.`);
          loadStaff();
        } catch (e) {
          handleAdminActionError(e);
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleToggleStaffAgeGrade(staffMember, grade) {
    const current = staffMember.assignedAgeGrades || [];
    const next = current.includes(grade) ? current.filter(g => g !== grade) : [...current, grade];
    try {
      await api.setStaffAgeGrades(staffMember.id, next);
      setStaffList(prev => prev.map(s => s.id === staffMember.id ? { ...s, assignedAgeGrades: next } : s));
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function handleToggleStaffTeam(staffMember, teamName) {
    const current = staffMember.assignedTeams || [];
    const next = current.includes(teamName) ? current.filter(t => t !== teamName) : [...current, teamName];
    try {
      await api.setStaffTeams(staffMember.id, next);
      setStaffList(prev => prev.map(s => s.id === staffMember.id ? { ...s, assignedTeams: next } : s));
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function handleAddTeam(e) {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setAddTeamSubmitting(true);
    try {
      const created = await api.addTeam(newTeamName.trim());
      setTeamsList(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTeamName("");
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setAddTeamSubmitting(false);
    }
  }

  async function handleDeleteTeam(team) {
    setConfirmAction({
      title: "Remove this team?",
      message: `"${team.name}" will no longer be selectable for players or staff. Anyone already assigned to it keeps that assignment, but it stops routing to them going forward — reassign anyone who still needs it first.`,
      confirmLabel: "Remove Team",
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteTeam(team.id);
          setTeamsList(prev => prev.filter(t => t.id !== team.id));
          setStaffList(prev => prev.map(s => ({ ...s, assignedTeams: (s.assignedTeams || []).filter(t => t !== team.name) })));
          showNotif(`"${team.name}" removed.`);
        } catch (e) {
          showNotif(e.message, "error");
        }
        setConfirmAction(null);
      },
    });
  }

  async function loadRoster() {
    setRosterLoading(true);
    try {
      const data = await api.getRoster();
      setRosterList(data);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setRosterLoading(false);
    }
  }

  async function loadArchivedRoster() {
    setArchivedRosterLoading(true);
    try {
      const data = await api.getArchivedRoster();
      setArchivedRoster(data);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setArchivedRosterLoading(false);
    }
  }

  function handleOpenDataProtection() {
    setView("dataprotection");
    // The page searches the roster to find a data subject, so it needs
    // the roster loaded — an admin may land here without having opened
    // the Roster page in this session.
    loadRoster();
    setDpSearch("");
    setEraseTarget(null);
    setEraseResult(null);
    setRetentionReport(null);
  }

  function handleOpenRoster() {
    setView("roster");
    loadRoster();
    setShowBulkReplace(false);
    setBulkPreview(null);
    setBulkDone(null);
    setRosterSearch("");
    setCollapsedGrades(new Set([...ALL_ROSTER_GRADES, "__unknown__"]));
  }

  const [rosterContactStatus, setRosterContactStatus] = useState([]);
  const emptyRosterForm = {
    name: "", ageGrade: "", team: "", position: "",
    parentName: "", parentEmail: "",
    schoolCoachName: "", schoolCoachEmail: "",
    schoolCoach2Name: "", schoolCoach2Email: "",
    clubCoachName: "", clubCoachEmail: "",
    clubCoach2Name: "", clubCoach2Email: "",
    otherContact1Name: "", otherContact1Email: "",
    otherContact2Name: "", otherContact2Email: "",
  };

  function handleStartAddRoster() {
    setRosterEditingId("new");
    setRosterForm({ ...emptyRosterForm });
  }

  function handleStartEditRoster(entry) {
    setRosterEditingId(entry.id);
    setRosterContactStatus(entry.additionalContacts || []);
    setRosterForm({
      name: entry.name || "",
      ageGrade: entry.age_grade || "",
      team: entry.team || "",
      position: entry.position || "",
      parentName: entry.parent_name || "",
      parentEmail: entry.parent_email || "",
      schoolCoachName: entry.school_coach_name || "",
      schoolCoachEmail: entry.school_coach_email || "",
      schoolCoach2Name: entry.school_coach_2_name || "",
      schoolCoach2Email: entry.school_coach_2_email || "",
      clubCoachName: entry.club_coach_name || "",
      clubCoachEmail: entry.club_coach_email || "",
      clubCoach2Name: entry.club_coach_2_name || "",
      clubCoach2Email: entry.club_coach_2_email || "",
      otherContact1Name: entry.other_contact_1_name || "",
      otherContact1Email: entry.other_contact_1_email || "",
      otherContact2Name: entry.other_contact_2_name || "",
      otherContact2Email: entry.other_contact_2_email || "",
    });
  }

  function handleCancelRosterEdit() {
    setRosterEditingId(null);
    setRosterForm(null);
  }

  async function handleSaveRoster() {
    if (!rosterForm.name.trim()) return showNotif("Player name is required.", "error");
    setRosterSubmitting(true);
    try {
      if (rosterEditingId === "new") {
        await api.addRosterPlayer(rosterForm);
        showNotif(`${rosterForm.name} added to the roster.`);
      } else {
        await api.updateRosterPlayer(rosterEditingId, rosterForm);
        showNotif(`${rosterForm.name} updated.`);
      }
      setRosterEditingId(null);
      setRosterForm(null);
      loadRoster();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setRosterSubmitting(false);
    }
  }

  function handleDeleteRoster(entry) {
    setConfirmAction({
      title: "Remove from Roster?",
      message: `Remove ${entry.name} from the roster? This only removes them from the search list — it does not affect any concussion already logged for them.`,
      confirmLabel: "Remove",
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteRosterPlayer(entry.id);
          showNotif(`${entry.name} removed from the roster.`);
          loadRoster();
        } catch (e) {
          showNotif(e.message, "error");
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleDeleteRosterByAgeGrade(ageGrade) {
    let freshRoster;
    try {
      freshRoster = await api.getRoster();
      setRosterList(freshRoster);
    } catch (e) {
      return showNotif(e.message, "error");
    }

    const count = freshRoster.filter(p => p.age_grade === ageGrade).length;
    if (count === 0) {
      return showNotif(`No players in ${ageGrade} to remove.`);
    }
    setConfirmAction({
      title: `Remove All ${ageGrade} Players?`,
      message: `This will remove all ${count} player${count !== 1 ? "s" : ""} in ${ageGrade} from the roster. This only removes them from the search list — it does not affect any concussion already logged for any of them. This can't be undone.`,
      confirmLabel: `Remove ${count} Player${count !== 1 ? "s" : ""}`,
      danger: true,
      onConfirm: async () => {
        try {
          const result = await api.deleteRosterByAgeGrade(ageGrade);
          showNotif(`${result.deletedCount} player${result.deletedCount !== 1 ? "s" : ""} removed from ${ageGrade}.`);
          setBulkDeleteAgeGrade("");
          loadRoster();
        } catch (e) {
          handleAdminActionError(e);
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleDeleteEntireRoster() {
    let freshRoster;
    try {
      freshRoster = await api.getRoster();
      setRosterList(freshRoster);
    } catch (e) {
      return showNotif(e.message, "error");
    }

    const count = freshRoster.length;
    if (count === 0) {
      return showNotif("The roster is already empty.");
    }
    setConfirmAction({
      title: "Delete the Entire Roster?",
      message: `This will remove all ${count} player${count !== 1 ? "s" : ""} from the roster completely. This only affects the roster — it does not touch any concussion already logged for anyone. This can't be undone.`,
      confirmLabel: `Delete All ${count} Player${count !== 1 ? "s" : ""}`,
      danger: true,
      onConfirm: async () => {
        try {
          const result = await api.deleteEntireRoster();
          showNotif(`${result.deletedCount} player${result.deletedCount !== 1 ? "s" : ""} removed. The roster is now empty.`);
          loadRoster();
        } catch (e) {
          handleAdminActionError(e);
        }
        setConfirmAction(null);
      },
    });
  }

  function handleOpenImportModal() {
    setShowImportModal(true);
    setImportCsvText("");
    setImportPreview(null);
    setImportFileName("");
  }

  function handleImportFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      setImportCsvText(text);
      try {
        const preview = await api.previewRosterImport(text);
        setImportPreview(preview);
      } catch (err) {
        showNotif(err.message, "error");
        setImportPreview(null);
      }
    };
    reader.readAsText(file);
  }

  async function handleConfirmImport() {
    setImportSubmitting(true);
    try {
      const result = await api.confirmRosterImport(importCsvText);
      // result.errors can include a "N players could not be imported —
      // plan limit reached" message even on an otherwise-successful
      // import — previously silently dropped, so an admin importing a
      // CSV near their player-plan cap would see "15 added" and have no
      // idea 5 rows were skipped until a parent later reported their
      // child wasn't in the system.
      showNotif(
        `${result.created} player${result.created !== 1 ? "s" : ""} added to the roster.` +
        (result.errors?.length ? ` ${result.errors.join(" ")}` : ""),
        result.errors?.length ? "warn" : "success"
      );
      setShowImportModal(false);
      loadRoster();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setImportSubmitting(false);
    }
  }

  async function loadMfaStatus() {
    try {
      const status = await api.getMfaStatus();
      setMfaStatus(status);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function loadActivityLog() {
    setActivityLogLoading(true);
    try {
      const log = await api.getActivityLog();
      setActivityLog(log);
    } catch (e) {
      handleAdminActionError(e);
    } finally {
      setActivityLogLoading(false);
    }
  }

  async function loadAnalytics() {
    setAnalyticsLoading(true);
    try {
      const data = await api.getAnalytics();
      setAnalyticsData(data);
    } catch (e) {
      handleAdminActionError(e);
    } finally {
      setAnalyticsLoading(false);
    }
  }

  async function handleStartMfaSetup() {
    setMfaSubmitting(true);
    try {
      const data = await api.startMfaSetup();
      setMfaSetupData(data);
      setMfaSetupCode("");
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setMfaSubmitting(false);
    }
  }

  async function handleConfirmMfaSetup() {
    if (!mfaSetupCode.trim()) return;
    setMfaSubmitting(true);
    try {
      const result = await api.confirmMfaSetup(mfaSetupCode.trim());
      setMfaBackupCodes(result.backupCodes);
      setMfaSetupData(null);
      loadMfaStatus();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setMfaSubmitting(false);
    }
  }

  async function handleDisableMfa() {
    if (!mfaDisablePassword.trim()) return;
    setMfaSubmitting(true);
    try {
      await api.disableMfa(mfaDisablePassword.trim());
      setMfaDisablePassword("");
      showNotif("Two-factor authentication has been turned off.");
      loadMfaStatus();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setMfaSubmitting(false);
    }
  }

  function handleRosterNameInput(value) {
    // Typing over the name breaks the link to whichever roster entry was
    // picked before. Keeping the old id would silently attach this record
    // to a different child than the one now named in the form — the exact
    // confusion this id exists to prevent — so it is cleared here and only
    // ever re-set by choosing from the dropdown again.
    setForm(f => ({ ...f, name: value, rosterPlayerId: "" }));
    // The linked-status answer belongs to the roster entry that was
    // chosen, so it goes when that link does.
    setLinkedStatus(null);
    setDuplicateOverrideReason("");
    setRosterSearchQuery(value);
    setShowRosterDropdown(true);
  }

  function handleSelectRosterPlayer(entry) {
    // The ENTIRE form is replaced, not just the contact fields — never
    // inherited from whichever player (or half-filled draft) was there
    // before. Previously this spread the existing form and only
    // overwrote name/age-grade/team/position/contact fields, leaving
    // consentGiven, symptoms, incidentDate and incidentDetails from
    // whatever was last typed — so backing out of one player's entry
    // and picking a different one via search kept an already-ticked
    // consent checkbox and stale symptoms/incident notes attached to
    // the new player, with nothing in the UI showing they weren't
    // actually confirmed for this specific child.
    // Ask straight away whether another organisation is already running a
    // protocol for this child. Cleared first so a previous player's
    // answer can never be shown against a new one.
    setLinkedStatus(null);
    setDuplicateOverrideReason("");
    api.getLinkedStatus(entry.id)
      .then((status) => setLinkedStatus({ ...status, rosterPlayerId: entry.id }))
      .catch(() => { /* non-fatal: the submit path checks again server-side */ });

    setForm({
      ...blankLogConcussionForm(),
      rosterPlayerId: entry.id,
      name: entry.name,
      ageGrade: entry.age_grade || "",
      team: entry.team || "",
      position: entry.position || "",
      parentName: entry.parent_name || "",
      parentEmail: entry.parent_email || "",
      schoolCoachName: entry.school_coach_name || "",
      schoolCoachEmail: entry.school_coach_email || "",
      schoolCoach2Name: entry.school_coach_2_name || "",
      schoolCoach2Email: entry.school_coach_2_email || "",
      clubCoachName: entry.club_coach_name || "",
      clubCoachEmail: entry.club_coach_email || "",
      clubCoach2Name: entry.club_coach_2_name || "",
      clubCoach2Email: entry.club_coach_2_email || "",
      // The registration form asks parents for additional contacts — a
      // second guardian, a grandparent who does the school run — and
      // stores them on the roster entry. Choosing that player here
      // copied everything except those two, so the people a parent
      // deliberately named were silently left off the notifications at
      // the one moment they were meant to receive them.
      otherContact1Name: entry.other_contact_1_name || "",
      otherContact1Email: entry.other_contact_1_email || "",
      otherContact2Name: entry.other_contact_2_name || "",
      otherContact2Email: entry.other_contact_2_email || "",
    });
    setShowRosterDropdown(false);
    setRosterSearchResults([]);
  }

  useEffect(() => {
    if (!rosterSearchQuery || rosterSearchQuery.trim().length < 2) {
      setRosterSearchResults([]);
      return;
    }
    const timeoutId = setTimeout(async () => {
      try {
        const results = await api.searchRoster(rosterSearchQuery);
        setRosterSearchResults(results);
      } catch {
        setRosterSearchResults([]);
      }
    }, 250); // small debounce so we're not searching on every single keystroke
    return () => clearTimeout(timeoutId);
  }, [rosterSearchQuery]);

  const selectedPlayer = players.find(p => p.id === selectedId);

  async function handleNewPlayer() {
    const allSymptoms = [...form.symptoms, ...(form.otherSymptom.trim() ? [form.otherSymptom.trim()] : [])];
    if (!form.name.trim()) return showNotif("Player name is required.", "error");
    if (!allSymptoms.length) return showNotif("Please record at least one symptom.", "error");

    const incidentDateObj = new Date(form.incidentDate);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    if (incidentDateObj > todayEnd) {
      return showNotif("Incident date cannot be in the future. Please check the date entered.", "error");
    }

    if (!form.isTest && !form.consentGiven) {
      return showNotif("Confirm the player's parent or guardian has been informed before logging a concussion for a real player.", "error");
    }

    const emailChecks = [
      ["Parent email", form.parentEmail],
      ["Staff email", form.staffEmail],
      ["School coach email", form.schoolCoachEmail],
      ["Club coach email", form.clubCoachEmail],
      ["Additional contact 1 email", form.otherContact1Email],
      ["Additional contact 2 email", form.otherContact2Email],
    ];
    for (const [label, value] of emailChecks) {
      if (value && !EMAIL_PATTERN.test(value)) {
        return showNotif(`${label} doesn't look like a valid email address.`, "error");
      }
    }

    try {
      const player = await api.createPlayer({
        rosterPlayerId: form.rosterPlayerId || null,
        duplicateOverrideReason: duplicateOverrideReason.trim() || undefined,
        name: form.name.trim(),
        ageGrade: form.ageGrade,
        team: form.team,
        position: form.position,
        incidentDate: form.incidentDate,
        incidentTime: form.incidentTime || null,
        incidentDetails: form.incidentDetails,
        incidentContext: form.incidentContext || null,
        incidentMechanism: form.incidentMechanism || null,
        symptoms: allSymptoms,
        parentName: form.parentName,
        parentEmail: form.parentEmail,
        staffEmail: form.staffEmail,
        schoolCoachName: form.schoolCoachName,
        schoolCoachEmail: form.schoolCoachEmail,
        schoolCoach2Name: form.schoolCoach2Name,
        schoolCoach2Email: form.schoolCoach2Email,
        clubCoach2Name: form.clubCoach2Name,
        clubCoach2Email: form.clubCoach2Email,
        clubCoachName: form.clubCoachName,
        clubCoachEmail: form.clubCoachEmail,
        otherContact1Name: form.otherContact1Name,
        otherContact1Email: form.otherContact1Email,
        otherContact2Name: form.otherContact2Name,
        otherContact2Email: form.otherContact2Email,
        consentGiven: form.consentGiven,
        isTest: form.isTest,
      });
      setPlayers(prev => [player, ...prev]);
      showNotif(form.isTest ? `${player.name} added as a test entry.` : `${player.name} added. Notifications sent.`);
      setView("dashboard");
      setForm(blankLogConcussionForm());
      setLinkedStatus(null);
      setDuplicateOverrideReason("");
      loadCrossOrgProtocols();
    } catch (e) {
      // The server checks for a protocol elsewhere again at submission —
      // the pre-flight check on player selection can be stale by the time
      // the form is filled in, and it is the server's answer that counts.
      if (e.code === "ACTIVE_PROTOCOL_ELSEWHERE") {
        // Merged rather than replaced: rebuilding the object from the
        // error alone would drop the cumulative history fetched when the
        // player was chosen, so the concussion-count line would vanish at
        // the exact moment somebody is deciding whether to override.
        setLinkedStatus(prev => ({
          ...(prev && prev.rosterPlayerId === form.rosterPlayerId ? prev : {}),
          activeProtocolsElsewhere: e.activeProtocols || [],
          rosterPlayerId: form.rosterPlayerId,
        }));
        showNotif("This player is already on a protocol at another organisation — see the warning above.", "warn");
        return;
      }
      showNotif(e.message, "error");
    }
  }

  async function handleSymptomReturn(player) {
    try {
      const updated = await api.symptomReturn(player.id);
      setPlayers(prev => prev.map(p => p.id === updated.id ? updated : p));
      setUndoTarget({ playerId: player.id, expiresAt: Date.now() + 5 * 60 * 1000 });
      showNotif(`${player.name} placed in 48hr rest. Stage 5 delayed 14 days. Tap to undo (5 min).`, "warn");
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  async function handleRemoveEarly() {
    if (!removeEarlyTarget || !removeEarlyReason.trim()) return;
    setRemoveEarlySubmitting(true);
    try {
      const updated = await api.removeEarly(removeEarlyTarget.id, removeEarlyReason.trim());
      setPlayers(prev => prev.map(p => p.id === updated.id ? updated : p));
      showNotif(`${removeEarlyTarget.name} removed from the protocol. Parents and staff notified.`, "warn");
      setRemoveEarlyTarget(null);
      setRemoveEarlyReason("");
      setView("dashboard");
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setRemoveEarlySubmitting(false);
    }
  }

  async function loadParentChangeRequests() {
    try {
      const data = await api.getParentChangeRequests();
      setParentChangeRequests(data);
      setParentChangeCount(data.length);
    } catch (_) {}
  }

  const MAX_LOGO_BYTES = 100 * 1024;

  function handleLogoFileChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setBrandingError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setBrandingError(`That image is ${Math.round(file.size / 1024)}KB — please use one under ${MAX_LOGO_BYTES / 1024}KB.`);
      return;
    }
    setBrandingError("");
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  }

  async function handleSaveBranding() {
    setBrandingSaving(true);
    setBrandingError("");
    try {
      await api.updateOrganisationBranding(brandColorInput, logoPreview || null);
      // Reloads so the new colour/logo apply everywhere at once (nav,
      // sidebar, badges) rather than threading fresh state through every
      // screen that reads them — branding changes rarely enough that this
      // is a fine trade for the simplicity.
      window.location.reload();
    } catch (e) {
      setBrandingError(e.message || "Something went wrong saving your branding.");
      setBrandingSaving(false);
    }
  }

  function handleResetBrandColor() {
    setBrandColorInput("#1a4a2e");
  }

  function handleRemoveLogo() {
    setLogoPreview(null);
  }

  async function loadMessageThreads() {
    setMessageThreadsLoading(true);
    setOpenThread(null);
    try {
      const threads = await api.getMessageThreads();
      setMessageThreads(threads);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setMessageThreadsLoading(false);
    }
  }

  async function handleOpenMessageThread(threadId) {
    setThreadLoading(true);
    try {
      const detail = await api.getMessageThread(threadId);
      setOpenThread(detail);
      // Opening a thread marks it read server-side — reflect that locally
      // and refresh the nav badge rather than waiting for the next poll.
      setMessageThreads(prev => prev.map(t => t.threadId === threadId ? { ...t, unread: false } : t));
      // Opening two threads in quick succession fires this twice with no
      // guaranteed response order — without the sequence guard, an
      // earlier thread's (now-stale, higher) count could land after a
      // later one's and overwrite the correct badge count.
      const requestId = ++unreadCountRequestIdRef.current;
      api.getMessagesUnreadCount()
        .then(r => { if (requestId === unreadCountRequestIdRef.current) setMessagesUnreadCount(r.unread || 0); })
        .catch(() => {});
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setThreadLoading(false);
    }
  }

  async function handleSendPlayerMessage() {
    if (!openThread?.threadId || (!messageComposeText.trim() && messageImages.length === 0)) return;
    setMessageSending(true);
    try {
      await api.sendThreadMessage(openThread.threadId, messageComposeText.trim(), messageImages.map(i => i.dataUrl));
      setMessageComposeText("");
      setMessageImages([]);
      await handleOpenMessageThread(openThread.threadId);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setMessageSending(false);
    }
  }

  async function handleStaffMessageImageSelect(e) {
    const { images, error } = await readMessageImageFiles(e.target.files, messageImages.length);
    if (images.length) setMessageImages(prev => [...prev, ...images]);
    if (error) showNotif(error, "error");
    e.target.value = "";
  }

  async function handleSaveEditedStaffMessage(messageId) {
    if (!editingMessageText.trim() || !openThread || savingMessageEdit) return;
    setSavingMessageEdit(true);
    try {
      await api.editStaffMessage(openThread.threadId, messageId, editingMessageText.trim());
      setEditingMessageId(null);
      await handleOpenMessageThread(openThread.threadId);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setSavingMessageEdit(false);
    }
  }

  async function loadPendingRegistrations() {
    setRegsLoading(true);
    try {
      const data = await api.getPendingRegistrations();
      setPendingRegs(data);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setRegsLoading(false);
    }
    loadRegistrationLinks();
    // Parents' change requests are reviewed on this screen too. They used
    // to load only at sign-in, so in the phone app, which stays signed in
    // for days, a new one never appeared.
    loadParentChangeRequests();
  }

  // Fetched whenever the Registrations screen opens. It used to wait for
  // a "Show Link" button, and because the adult and staff cards only
  // rendered once their link was loaded, that button was the only way to
  // make them appear — on a different screen. Nobody found them.
  //
  // Failure is quiet on purpose: the pending-registrations queue below is
  // the reason somebody opened this screen, and a toast about links they
  // may not want would be noise in front of the work.
  async function loadRegistrationLinks() {
    setRegLinkLoading(true);
    try {
      const r = await api.getRegistrationLink();
      setRegAwaitingApproval(Boolean(r.awaitingApproval));
      setRegistrationLink(r.link || null);
      setAdultRegistrationLink(r.adultLink || null);
      setStaffRegLink(r.staffLink || "");
    } catch (e) {
      console.error("Could not load registration links:", e.message);
    } finally {
      setRegLinkLoading(false);
    }
  }

  async function handleApproveRegistration(id, force = false) {
    if (approvingRegId === id) return; // already in flight — a rapid double-click shouldn't fire twice
    setApprovingRegId(id);
    try {
      const result = await api.approveRegistration(id, force);
      if (result.warning) {
        // Backend found a name match on the roster — show a confirmation
        // dialog rather than proceeding automatically. The staff member
        // can choose to approve anyway (force=true) or reject instead.
        setApproveWarning({ registrationId: id, playerName: result.playerName || id, message: result.message });
        return;
      }
      setPendingRegs(prev => prev.filter(r => r.id !== id));
      setPendingRegCount(prev => Math.max(0, prev - 1));
      showNotif(result.playerName + " approved and added to the roster.");
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setApprovingRegId(null);
    }
  }

  // A registration "has a warning" if it would already trigger the same
  // confirmation dialog handleApproveRegistration shows for a single
  // approve — a name match on the current roster, or no consent on
  // record. "Select all" only pre-ticks registrations without either,
  // so bulk-approving never silently sweeps past something that's
  // supposed to get a human look.
  function regHasWarning(reg) {
    const alreadyOnRoster = rosterList.some(p => p.name.trim().toLowerCase() === reg.player_name.trim().toLowerCase());
    return alreadyOnRoster || !reg.consent_given;
  }

  function toggleRegSelected(id) {
    setSelectedRegIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllSafeRegistrations() {
    const safeIds = pendingRegs.filter(r => !regHasWarning(r)).map(r => r.id);
    setSelectedRegIds(new Set(safeIds));
  }

  async function handleBulkApproveRegistrations() {
    if (selectedRegIds.size === 0) return;
    setBulkApproving(true);
    setBulkApproveResult(null);
    try {
      const result = await api.bulkApproveRegistrations(Array.from(selectedRegIds));
      const approvedIds = new Set(result.approved.map(r => r.registrationId));

      // Only the ones actually approved leave the queue — anything
      // skipped (duplicate/capacity/not-found) stays visible so it can
      // still be reviewed and actioned individually.
      setPendingRegs(prev => prev.filter(r => !approvedIds.has(r.id)));
      setPendingRegCount(prev => Math.max(0, prev - result.approved.length));
      setSelectedRegIds(new Set());
      setBulkApproveResult(result);

      const skippedTotal = result.skippedDuplicates.length + result.skippedCapacity.length;
      showNotif(`${result.approved.length} approved.` + (skippedTotal > 0 ? ` ${skippedTotal} skipped — see below.` : ""));
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setBulkApproving(false);
    }
  }

  async function handleBulkReplacePreview() {
    if (!bulkOldValue.trim()) return showNotif("Enter the current value to search for.", "error");
    setBulkLoading(true);
    setBulkPreview(null);
    try {
      const result = await api.rosterBulkReplacePreview(bulkField, bulkOldValue.trim());
      setBulkPreview(result);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleBulkReplaceConfirm() {
    setBulkLoading(true);
    try {
      const result = await api.rosterBulkReplaceConfirm(bulkField, bulkOldValue.trim(), bulkNewValue.trim());
      setBulkDone(result);
      setBulkPreview(null);
      loadRoster(); // refresh the roster list to reflect the changes
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setBulkLoading(false);
    }
  }

  // Opens the reject window. The parent is emailed by default; a duplicate
  // of a child already on the roster starts unticked, because "we couldn't
  // accept your registration" would alarm a family whose child is in fact
  // registered.
  function handleRejectRegistration(id, playerName, { duplicate = false } = {}) {
    const reg = pendingRegs.find(r => r.id === id);
    // A likely duplicate starts unticked; if the admin does tell the parent,
    // "already registered" is the likely reason.
    setRejectTarget({ id, playerName, parentEmail: reg ? reg.parent_email : null, reason: "", notifyParent: !duplicate, parentReason: duplicate ? "duplicate" : "none" });
  }

  async function confirmRejectRegistration() {
    if (!rejectTarget || rejecting) return;
    const { id, playerName, reason, notifyParent, parentEmail, parentReason } = rejectTarget;
    setRejecting(true);
    setRejectTarget(t => (t ? { ...t, error: null } : t));
    try {
      const notify = notifyParent && !!parentEmail;
      const r = await api.rejectRegistration(id, reason.trim() || null, notify, notify ? parentReason : "none");
      setPendingRegs(prev => prev.filter(x => x.id !== id));
      setPendingRegCount(prev => Math.max(0, prev - 1));
      setRejectTarget(null);
      if (r && r.parentNotifyFailed) {
        showNotif(playerName + " registration rejected, but the email to the parent didn't send. Please let them know yourself.", "error");
      } else {
        showNotif(playerName + " registration rejected." + (r && r.parentNotified ? " The parent has been emailed." : ""));
      }
    } catch (e) {
      // Shown inside the window: a toast would sit behind it.
      setRejectTarget(t => (t ? { ...t, error: e.message } : t));
    } finally {
      setRejecting(false);
    }
  }

    async function handleAgeGradeDecision(action) {
    setAgeGradeDeciding(true);
    try {
      const result = await api.decideAgeGradeAdvancement(action);
      if (action === "advance") {
        setAgeGradeResultSummary(result.summary);
        const msgParts = [`Season rollover complete — ${result.summary?.advanced ?? 0} players advanced`];
        if ((result.summary?.archived ?? 0) > 0) msgParts.push(`${result.summary.archived} aged out`);
        if ((result.protocolsArchived ?? 0) > 0) msgParts.push(`${result.protocolsArchived} old protocol${result.protocolsArchived !== 1 ? "s" : ""} archived`);
        showNotif(msgParts.join(", ") + ".");
        loadRoster();
        loadPlayers();
      } else {
        showNotif("Age grades kept the same for the new season.");
      }
      setConfirmAction(null);
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setAgeGradeDeciding(false);
    }
  }

  async function handleUndo() {
    if (!undoTarget) return;
    if (Date.now() > undoTarget.expiresAt) {
      showNotif("Undo window has expired.", "error");
      setUndoTarget(null);
      return;
    }
    try {
      const updated = await api.undoSymptomReturn(undoTarget.playerId);
      setPlayers(prev => prev.map(p => p.id === updated.id ? updated : p));
      setUndoTarget(null);
      showNotif("Symptom return undone — player restored to previous state.");
    } catch (e) {
      showNotif(e.message, "error");
      setUndoTarget(null);
    }
  }

  function handleDeletePlayer(player) {
    const isTestEntry = player.is_test;
    setConfirmAction({
      title: "Archive Record?",
      message: isTestEntry
        ? `Archive the test entry for ${player.name}? It will be removed from the active dashboard. To permanently delete test entries, use the Admin area in the Archive view.`
        : `Archive the record for ${player.name}? This removes it from the active dashboard but keeps it on file for safeguarding records. It is not permanently deleted.`,
      confirmLabel: "Archive",
      danger: false,
      onConfirm: async () => {
        try {
          await api.deletePlayer(player.id);
          setPlayers(prev => prev.filter(p => p.id !== player.id));
          showNotif(`${player.name}'s record has been archived.`);
          setView("dashboard");
        } catch (e) {
          showNotif(e.message, "error");
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleUnarchive(player) {
    try {
      const updated = await api.unarchivePlayer(player.id);
      setPlayers(prev => prev.map(p => p.id === updated.id ? updated : p));
      showNotif(`${player.name}'s record has been restored to the active dashboard.`);
    } catch (e) {
      showNotif(e.message, "error");
    }
  }

  function handlePermanentDelete(player) {
    setConfirmAction({
      title: "Permanently Delete Record?",
      message: `Permanently delete ${player.name}'s record? This cannot be undone — it will not go to archive, it will be gone completely.`,
      confirmLabel: "Delete Permanently",
      danger: true,
      onConfirm: async () => {
        try {
          await api.permanentlyDeletePlayer(player.id);
          setPlayers(prev => prev.filter(p => p.id !== player.id));
          showNotif(`${player.name}'s record permanently deleted.`);
        } catch (e) {
          handleAdminActionError(e);
        }
        setConfirmAction(null);
      },
    });
  }

  async function handleClearAllData() {
    try {
      const result = await api.clearAllData();
      setPlayers([]);
      setShowClearAllConfirm(false);
      showNotif(`All data cleared. ${result.deletedCount} record(s) permanently deleted.`, "warn");
    } catch (e) {
      handleAdminActionError(e);
    }
  }

  async function handleExport() {
    try {
      const blob = await api.exportData();
      notifySaved(await saveBlob(blob, `grtp-backup-${new Date().toISOString().split("T")[0]}.json`), "Backup downloaded.");
    } catch (e) {
      showNotif("Export failed: " + e.message, "error");
    }
  }

  // Every download goes through here. In a browser it downloads; in the
  // phone app, where a download link does nothing, it opens the share
  // sheet (nativeApp.saveFile).
  function saveBlob(blob, filename) {
    return nativeApp.saveFile(blob, filename);
  }
  // The "downloaded" message is only true in a browser. On a phone the
  // share sheet is its own confirmation, and closing it is a choice.
  function notifySaved(result, webMessage) {
    if (result === "downloaded") showNotif(webMessage);
  }

  // Both exports send the ids on screen whenever a filter is applied, so
  // the file can never contain more records than the person exporting it
  // could see. Unfiltered, they send nothing and the server exports
  // everything, exactly as these buttons always have.
  function currentExportSelection() {
    return exportIsFiltered ? exportablePlayers.map(p => p.id) : undefined;
  }
  function exportFilenameSuffix() {
    return exportIsFiltered ? "-filtered" : "";
  }

  async function handleExportCSV() {
    try {
      const blob = await api.exportCSV(currentExportSelection());
      notifySaved(await saveBlob(blob, `grtp-concussion-records${exportFilenameSuffix()}-${new Date().toISOString().split("T")[0]}.csv`), exportIsFiltered
        ? `${exportablePlayers.length} record${exportablePlayers.length === 1 ? "" : "s"} downloaded as CSV.`
        : "All records downloaded as CSV.");
    } catch (e) {
      showNotif("Export failed: " + e.message, "error");
    }
  }

  async function handleExportAllPDF() {
    try {
      const blob = await api.exportAllPDF(currentExportSelection(), exportDescription);
      notifySaved(await saveBlob(blob, `grtp-concussion-records${exportFilenameSuffix()}-${new Date().toISOString().split("T")[0]}.pdf`), exportIsFiltered
        ? `${exportablePlayers.length} record${exportablePlayers.length === 1 ? "" : "s"} downloaded as PDF.`
        : "All records downloaded as PDF.");
    } catch (e) {
      showNotif("Export failed: " + e.message, "error");
    }
  }

  async function handleDownloadPDF(player) {
    try {
      showNotif("Generating PDF...");
      const blob = await api.downloadPDF(player.id);
      const safeName = player.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      notifySaved(await saveBlob(blob, `grtp-${safeName}-${new Date().toISOString().split("T")[0]}.pdf`), "PDF downloaded.");
    } catch (e) {
      showNotif("PDF generation failed: " + e.message, "error");
    }
  }

  async function loadLinkRequests() {
    try {
      const data = await api.getPlayerLinkRequests();
      const requests = data.requests || [];
      setLinkRequests(requests);
      setOutgoingLinkRequests(data.outgoing || []);
      // The nav badge counts registrations and link requests together, so
      // it has to be recomputed here too — otherwise answering a request
      // clears it from the page while the sidebar still claims it is
      // waiting, and an admin keeps coming back to an empty queue.
      api.getPendingRegistrationsCount()
        .then(r => setPendingRegCount((r.pending || 0) + requests.length))
        .catch(() => {});
    } catch (_) { /* non-admins have no queue; the rest of the page is fine */ }
  }

  async function handleResolveLinkRequest(requestId, rosterPlayerId, playerName) {
    setResolvingLinkId(requestId);
    try {
      await api.resolvePlayerLinkRequest(requestId, rosterPlayerId);
      showNotif(`${playerName} linked. Both organisations will now see if the other records a concussion.`);
      loadLinkRequests();
      loadCrossOrgProtocols();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setResolvingLinkId(null);
    }
  }

  // The child isn't on this roster at all, so there is nothing to match
  // them to. Creates the entry from what the parent already gave the
  // other organisation, then links in the same step.
  async function handleAddFromNomination(requestId, playerName, force = false) {
    setResolvingLinkId(requestId);
    try {
      const result = await api.addPlayerFromNomination(requestId, force);
      // A same-named player already on the roster is a warning, not an
      // error: two children can share a name. The server returns it as a
      // 200 so the payload survives, and the decision comes back here.
      if (result && result.warning) {
        setResolvingLinkId(null);
        setConfirmAction({
          title: "Already on your roster",
          message: result.message,
          confirmLabel: "Add anyway",
          onConfirm: () => handleAddFromNomination(requestId, playerName, true),
        });
        return;
      }
      showNotif(`${playerName} added to your roster and linked. Their parent has been told.`);
      loadLinkRequests();
      loadRoster();
      loadCrossOrgProtocols();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setResolvingLinkId(null);
    }
  }

  async function handleRejectLinkRequest(requestId, playerName) {
    setResolvingLinkId(requestId);
    try {
      await api.rejectPlayerLinkRequest(requestId);
      showNotif(`Marked as not your player. ${playerName} was not linked.`, "warn");
      loadLinkRequests();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setResolvingLinkId(null);
    }
  }

  async function loadCrossOrgProtocols() {
    try {
      const data = await api.getCrossOrgProtocols();
      setCrossOrgProtocols(data.protocols || []);
    } catch (_) { /* the dashboard still works without this section */ }
  }

  // ── Data protection ──
  async function loadRetentionReport() {
    setRetentionReport(null);
    try {
      setRetentionReport(await api.getRetentionReport());
    } catch (e) {
      showNotif("Couldn't load the retention report: " + e.message, "error");
    }
  }

  async function handleExportOrganisation() {
    setExportingOrg(true);
    try {
      const { blob, filename } = await api.exportOrganisationData();
      notifySaved(await saveBlob(blob, filename), "Full organisation export downloaded.");
    } catch (e) {
      showNotif("Export failed: " + e.message, "error");
    } finally {
      setExportingOrg(false);
    }
  }

  async function handleSubjectAccessExport(rosterPlayer) {
    try {
      showNotif(`Gathering everything held about ${rosterPlayer.name}...`);
      const { blob, filename } = await api.exportSubjectAccess(rosterPlayer.id);
      notifySaved(await saveBlob(blob, filename), "Subject access export downloaded.");
    } catch (e) {
      showNotif("Export failed: " + e.message, "error");
    }
  }

  async function handleEraseDataSubject() {
    if (!eraseTarget) return;
    setErasing(true);
    try {
      const result = await api.eraseDataSubject(eraseTarget.id, eraseConfirmName.trim());
      setEraseResult(result);
      setEraseTarget(null);
      setEraseConfirmName("");
      // The roster and any retention figures on screen are both stale now.
      loadRoster();
      if (retentionReport) loadRetentionReport();
    } catch (e) {
      showNotif(e.message, "error");
    } finally {
      setErasing(false);
    }
  }

  const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, color: "#111827", background: "#fff", outline: "none", fontFamily: "inherit" };
  const btnP = { background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  const btnS = { background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" };

  // Somebody following "Reset my password" from a "was this you?" email
  // may be signed in on this browser already; they still need the form.
  if (authed && forgotPasswordRoute) {
    return (
      <>
        <GlobalStyles />
        <ForgotPasswordScreen onBackToLogin={() => window.location.assign("/")} />
      </>
    );
  }

  // ── LOGIN GATE ───────────────────────────────────────────────────
  if (!authed) {
    return (
      <>
        <GlobalStyles />
        {showSignup ? (
          <SignupScreen onBackToLogin={() => setShowSignup(false)} />
        ) : showForgotPassword ? (
          <ForgotPasswordScreen onBackToLogin={() => setShowForgotPassword(false)} />
        ) : (
          <LoginScreen onLogin={(data = {}) => {
            if (data.trialExpired || data.accountEnded) {
              setAuthed(true);
              setOrgNameState(api.orgName());
              setLapseReason(data.accountEnded ? "cancelled" : "trial_expired");
              setShowTrialExpired(true);
            } else if (data.paymentPending) {
              setAuthed(true);
              setOrgNameState(api.orgName());
              setPendingTier(data.pendingTier || null);
              setShowPaymentPending(true);
            } else {
              setAuthed(true);
              setIsOrgAdmin(api.isOrgAdmin());
              setOrgNameState(api.orgName());
              if (data.daysLeftInTrial !== null && data.daysLeftInTrial !== undefined) {
                setDaysLeftInTrial(data.daysLeftInTrial);
              }
            }
          }} onShowSignup={nativeApp.isNativeApp() ? undefined : () => setShowSignup(true)} onShowForgotPassword={() => setShowForgotPassword(true)} />
        )}
      </>
    );
  }

  // ── TRIAL EXPIRED ────────────────────────────────────────────────
  if (showTrialExpired) {
    if (nativeApp.isNativeApp()) return <AppAccountInactiveScreen orgName={orgName} onLogout={handleLogout} />;
    return <TrialExpiredScreen orgName={orgName} reason={lapseReason} onLogout={handleLogout} />;
  }

  // ── PAYMENT PENDING (card signup, checkout never completed) ───────
  if (showPaymentPending) {
    if (nativeApp.isNativeApp()) return <AppAccountInactiveScreen orgName={orgName} onLogout={handleLogout} />;
    return <PaymentPendingScreen orgName={orgName} tier={pendingTier} onLogout={handleLogout} />;
  }

  // ── STAFF MANAGEMENT ─────────────────────────────────────────────
  if (view === "registrations") {
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Registrations">
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
            <div>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a4a2e", margin: "0 0 4px" }}>Pending Registrations</h2>
              <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>Parent-submitted registration forms awaiting review</p>
            </div>
          </div>

          {/* Parents asking to change their child's contacts. These lived
              on the Staff page, where nobody looked for them. Each change
              is shown old -> new, and a removal says so: listing only the
              filled-in values hid a contact being taken off entirely. */}
          {parentChangeRequests.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #b6d8c5", borderLeft: "4px solid #1a4a2e", borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>
                Changes requested by parents ({parentChangeRequests.length})
              </div>
              <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.55 }}>
                Approving makes the change straight away (new contacts reach any concussion record that's running too) and emails the parent to say so.
              </p>
              {parentChangeRequests.map(pcr => {
                const payload = pcr.payload || {};
                const submitted = new Date(pcr.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                return (
                  <div key={pcr.id} style={{ border: "1px solid #e2eaf2", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
                      {pcr.player_name} <span style={{ color: "#9ca3af", fontWeight: 400 }}>{pcr.age_grade}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", margin: "2px 0 8px" }}>
                      {pcr.change_type === "contact_details"
                        ? `Contact details · sent ${submitted}`
                        : `Link to ${payload.linkedOrgName || "another organisation"} · sent ${submitted}`}
                    </div>
                    {pcr.change_type === "contact_details" && Array.isArray(pcr.changes) && (
                      pcr.changes.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>Nothing would change: the roster already matches.</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                          {pcr.changes.map(c => (
                            <div key={c.field} style={{ fontSize: 13, color: "#374151", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                              <span style={{ fontWeight: 600 }}>{CONTACT_FIELD_LABELS[c.field] || c.field}: </span>
                              <span style={{ color: "#6b7280", textDecoration: c.from ? "line-through" : "none" }}>{c.from || "none"}</span>
                              {" → "}
                              {c.to
                                ? <span style={{ color: "#1a4a2e", fontWeight: 600 }}>{c.to}</span>
                                : <span style={{ color: "#c0272d", fontWeight: 700 }}>removed</span>}
                            </div>
                          ))}
                        </div>
                      )
                    )}
                    {/* A server from before the comparison existed sends no
                        "changes": show the request as it came. */}
                    {pcr.change_type === "contact_details" && !Array.isArray(pcr.changes) && (
                      <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 10, overflowWrap: "anywhere" }}>
                        {Object.entries(payload).map(([k, v]) => `${k}: ${v || "removed"}`).join(" · ")}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        disabled={approvingChangeId === pcr.id}
                        onClick={async () => {
                          if (approvingChangeId) return;
                          setApprovingChangeId(pcr.id);
                          try {
                            const r = await api.approveParentChange(pcr.id);
                            setParentChangeRequests(prev => prev.filter(x => x.id !== pcr.id));
                            setParentChangeCount(prev => Math.max(0, prev - 1));
                            showNotif(`${pcr.player_name}'s ${pcr.change_type === "contact_details" ? "contacts updated" : "change approved"}.` + (r && r.parentNotified ? " The parent has been emailed." : ""));
                            if (rosterList.length) loadRoster();
                          } catch (e) {
                            showNotif(e.message, "error");
                            loadParentChangeRequests();
                          } finally {
                            setApprovingChangeId(null);
                          }
                        }}
                        style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: approvingChangeId === pcr.id ? "default" : "pointer", opacity: approvingChangeId === pcr.id ? 0.7 : 1 }}>
                        {approvingChangeId === pcr.id ? "Approving..." : "Approve"}
                      </button>
                      <button
                        onClick={() => setChangeRejectTarget({ id: pcr.id, playerName: pcr.player_name, message: "", error: null })}
                        style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {changeRejectTarget && (
            <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3500 }}>
              <div role="dialog" aria-modal="true" aria-labelledby="reject-change-title" style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "'Inter', system-ui, sans-serif" }}>
                <div id="reject-change-title" style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 10 }}>Reject the change for {changeRejectTarget.playerName}?</div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: "0 0 16px" }}>
                  Their contacts stay as they are, and the parent is emailed to say the change wasn't made.
                </p>
                <label htmlFor="reject-change-message" style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Message to the parent (optional)</label>
                <textarea
                  id="reject-change-message"
                  value={changeRejectTarget.message}
                  onChange={e => setChangeRejectTarget(t => ({ ...t, message: e.target.value.slice(0, 500) }))}
                  rows={3}
                  placeholder="e.g. Please call us so we can check the new details"
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: 8, fontSize: 14, fontFamily: "inherit", resize: "vertical", marginBottom: 4 }}
                />
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 18 }}>The parent will read this exactly as you write it.</div>
                {changeRejectTarget.error && (
                  <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: "#c0272d" }}>
                    {changeRejectTarget.error}
                  </div>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    disabled={changeRejecting}
                    onClick={async () => {
                      const { id, message } = changeRejectTarget;
                      setChangeRejecting(true);
                      try {
                        const r = await api.rejectParentChange(id, message.trim());
                        setParentChangeRequests(prev => prev.filter(x => x.id !== id));
                        setParentChangeCount(prev => Math.max(0, prev - 1));
                        setChangeRejectTarget(null);
                        showNotif("Change rejected." + (r && r.parentNotified ? " The parent has been emailed." : ""));
                      } catch (e) {
                        // Shown inside the window: a toast would sit behind it.
                        setChangeRejectTarget(t => (t ? { ...t, error: e.message } : t));
                      } finally {
                        setChangeRejecting(false);
                      }
                    }}
                    style={{ flex: 1, background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: changeRejecting ? "default" : "pointer", opacity: changeRejecting ? 0.7 : 1 }}
                  >
                    {changeRejecting ? "Rejecting..." : "Reject"}
                  </button>
                  <button
                    disabled={changeRejecting}
                    onClick={() => setChangeRejectTarget(null)}
                    style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: changeRejecting ? "default" : "pointer" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Link requests sit above the registration link because they
              are the only thing on this page that another organisation is
              waiting on. Nothing shares until one of these is answered. */}
          {linkRequests.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #c9991a", borderLeft: "4px solid #c9991a", borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#92400e", marginBottom: 4 }}>
                Linked player requests ({linkRequests.length})
              </div>
              <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.55 }}>
                A parent has told another organisation their child is also registered with you. Confirm which of your
                players it is, and a concussion recorded by either of you will alert the other. Nothing is shared until
                you confirm.
              </p>

              {linkRequests.map((r) => {
                const busy = resolvingLinkId === r.requestId;
                return (
                  <div key={r.requestId} style={{ borderTop: "1px solid #f3f4f6", paddingTop: 12, marginTop: 12 }}>
                    <div style={{ fontSize: 13.5, color: "#111827", lineHeight: 1.6, marginBottom: 10 }}>
                      <strong>{r.requestingOrgName}</strong> says <strong>{r.playerName}</strong>
                      {r.ageGrade ? ` (${r.ageGrade})` : ""} is also one of your players
                      {r.dateOfBirth && <> — born {new Date(r.dateOfBirth).toLocaleDateString("en-GB")}</>}.
                    </div>

                    {r.suggestedRosterPlayers.length === 0 ? (
                      <>
                      <div style={{ fontSize: 12.5, color: "#374151", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "9px 11px", marginBottom: 10, lineHeight: 1.55 }}>
                        <strong>This player isn't on your roster yet.</strong> That is normal if they have only just
                        joined, or registered with {r.requestingOrgName} first. Their parent gave these details when
                        they registered, so you can add them here rather than typing it all again &mdash; or, if they
                        aren't one of yours, mark it as not your player.
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #e5e7eb", color: "#6b7280" }}>
                          Adds <strong>{r.playerName}</strong>{r.ageGrade ? ` (${r.ageGrade})` : ""}
                          {r.dateOfBirth && <>, born {new Date(r.dateOfBirth).toLocaleDateString("en-GB")}</>} and their
                          parent's contact details. Nothing else comes across from {r.requestingOrgName}.
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddFromNomination(r.requestId, r.playerName)}
                        disabled={busy}
                        style={{ ...btnP, padding: "8px 16px", fontSize: 13, marginBottom: 10, opacity: busy ? 0.5 : 1 }}
                      >
                        {busy ? "Adding..." : "Add to our roster and link"}
                      </button>
                      </>
                    ) : (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                          Which of your players is this?
                        </div>
                        {r.suggestedRosterPlayers.map((s) => (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 11px", marginBottom: 6 }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                                {s.name}
                                {(s.ageGrade || s.team) && (
                                  <span style={{ fontWeight: 500, color: "#6b7280" }}>
                                    {" · "}{[s.ageGrade, s.team].filter(Boolean).join(" · ")}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11.5, lineHeight: 1.5, color: s.dateOfBirthMatches === true ? "#166534" : s.dateOfBirthMatches === false ? "#c0272d" : "#6b7280" }}>
                                {s.dateOfBirthMatches === true && <>✓ Date of birth matches ({new Date(s.dateOfBirth).toLocaleDateString("en-GB")})</>}
                                {s.dateOfBirthMatches === false && <>✗ Different date of birth ({s.dateOfBirth ? new Date(s.dateOfBirth).toLocaleDateString("en-GB") : "not recorded"}) — probably not the same child</>}
                                {s.dateOfBirthMatches === null && <>No date of birth to compare — check your own records before confirming</>}
                              </div>
                              {/* Offered because the date of birth matched, not the
                                  name. Families use the full name on one form and the
                                  everyday one on another, but the difference has to be
                                  shown rather than glossed over. */}
                              {s.nameMatches === false && (
                                <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "#92400e", marginTop: 2 }}>
                                  Suggested on date of birth — your roster has this player as “{s.name}”, {r.requestingOrgName} has “{r.playerName}”
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleResolveLinkRequest(r.requestId, s.id, s.name)}
                              disabled={busy}
                              style={{ ...btnP, padding: "7px 14px", fontSize: 13, opacity: busy ? 0.5 : 1 }}
                            >
                              {busy ? "Linking..." : "This one"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => handleRejectLinkRequest(r.requestId, r.playerName)}
                      disabled={busy}
                      style={{ ...btnS, padding: "7px 14px", fontSize: 12.5, opacity: busy ? 0.5 : 1 }}
                    >
                      Not our player
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Nominations this organisation has raised. Without this the
              screen showed nothing at all after approving a registration
              that named another club: the request existed, the other
              organisation had it, and the side that asked had no way of
              knowing either. Read-only — the decision is not ours to
              make, and we are not told whether the child is on their
              roster, because that is their data and not ours to see. */}
          {outgoingLinkRequests.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>
                Waiting on another organisation ({outgoingLinkRequests.length})
              </div>
              <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.55 }}>
                A parent told us these players are also registered elsewhere, and we have asked that organisation to
                confirm. Nothing is shared either way until they do. If it stays here, contact them directly &mdash;
                they may not have added the player to their own roster yet.
              </p>
              {outgoingLinkRequests.map((o) => (
                <div key={o.requestId} style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, marginTop: 10, fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                  <strong>{o.playerName}</strong>{o.ageGrade ? ` (${o.ageGrade})` : ""} &rarr; <strong>{o.targetOrgName}</strong>
                  <div style={{ fontSize: 11.5, color: "#6b7280" }}>
                    Asked {new Date(o.requestedAt).toLocaleDateString("en-GB")} · awaiting their confirmation
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Every registration link this organisation has, on the screen
              called Registrations.

              The adult and staff links used to live only on the Staff
              page, beside "invite colleagues" — which is not where
              anybody looks for a way to register a player, and which is
              blocked for an admin who has not set up 2FA. An adult
              player link that cannot be found is the same as one that
              does not exist.

              Loaded when the screen opens rather than behind a "Show
              Link" button: hiding an invitation link protects nothing
              on a page only staff can open, and the button was the
              reason two of the three were invisible. */}
          {regAwaitingApproval && (
            <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 18, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: "#92400e", fontSize: 15, marginBottom: 6 }}>
                Registration links are on their way
              </div>
              <p style={{ margin: "0 0 8px", fontSize: 13.5, color: "#78350f", lineHeight: 1.65 }}>
                We confirm every new organisation is a real club or school before its registration
                links go live. It is the one thing here that reaches families directly, and it is what
                stops somebody setting up in your name and collecting your members' details.
              </p>
              <p style={{ margin: 0, fontSize: 13.5, color: "#78350f", lineHeight: 1.65 }}>
                Everything else works now: add your roster, invite staff, and log incidents. We usually
                confirm within a working day. Anything urgent, email{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#92400e" }}>{SUPPORT_EMAIL}</a>.
              </p>
            </div>
          )}
          {(() => {
            const cards = regAwaitingApproval ? [] : [
              registrationLink && {
                key: "parent",
                title: "Age grade players (U7–U19)",
                blurb: "Share with parents. Their child appears in the queue below for you to review and approve onto the roster.",
                link: registrationLink,
                colour: "#1a4a2e",
              },
              adultRegistrationLink && {
                key: "adult",
                title: "🏉 Adult players",
                blurb: "Adult players register themselves — no parent involved. Adult age grade only.",
                link: adultRegistrationLink,
                colour: "#3d5166",
              },
              staffRegLink && {
                key: "staff",
                title: "Coaches and staff",
                blurb: "A coach requests access and you approve it before their account goes live.",
                link: staffRegLink,
                colour: "#c9991a",
              },
            ].filter(Boolean);

            if (regAwaitingApproval) return null;
            if (cards.length === 0) {
              return (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 20, color: "#6b7280", fontSize: 13.5 }}>
                  {regLinkLoading ? "Loading your registration links…" : "Registration links could not be loaded. Reload the page to try again."}
                </div>
              );
            }

            return (
              <div style={{ marginBottom: 20 }}>
                {cards.map((c) => (
                  <div key={c.key} style={{ background: "#fff", border: "1px solid #e5e7eb", borderLeft: `3px solid ${c.colour}`, borderRadius: 10, padding: 16, marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 6 }}>{c.title}</div>
                    <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>{c.blurb}</p>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "9px 12px", fontSize: 13, fontFamily: "monospace", color: "#374151", wordBreak: "break-all", lineHeight: 1.6, minWidth: 0 }}>
                        {c.link}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(c.link)
                          .then(() => showNotif("Link copied to clipboard."))
                          .catch(() => showNotif("Couldn't auto-copy — please select and copy the link above.", "error"))}
                        style={{ background: c.colour, color: readableOn(c.colour), border: "none", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
                {/* Said plainly rather than left as a gap. A club owner
                    who cannot see an adult link should know it is a
                    decision about schools, not a fault. */}
                {!adultRegistrationLink && (
                  <p style={{ fontSize: 12.5, color: "#9ca3af", margin: "2px 4px 0", lineHeight: 1.5 }}>
                    No adult self-registration link: it is issued to clubs and academies only, because schools have a
                    defined U7–U19 pathway. Change your organisation type if adults play for you.
                  </p>
                )}
              </div>
            );
          })()}

          {regsLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>
          ) : pendingRegs.length === 0 ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
              <div style={{ fontWeight: 700, color: "#111827", fontSize: 15, marginBottom: 6 }}>No pending registrations</div>
              <p style={{ color: "#9ca3af", fontSize: 14, margin: 0 }}>When parents complete the registration form, their submissions will appear here for review.</p>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={selectAllSafeRegistrations}
                  style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}
                >
                  Select all without warnings
                </button>
                {selectedRegIds.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedRegIds(new Set())}
                    style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer", padding: 0 }}
                  >
                    Clear selection
                  </button>
                )}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{selectedRegIds.size} selected</span>
                  <button
                    type="button"
                    disabled={selectedRegIds.size === 0 || bulkApproving}
                    onClick={handleBulkApproveRegistrations}
                    style={{ background: selectedRegIds.size === 0 ? "#e5e7eb" : "#1a4a2e", color: selectedRegIds.size === 0 ? "#9ca3af" : "#fff", border: "none", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: (selectedRegIds.size === 0 || bulkApproving) ? "default" : "pointer" }}
                  >
                    {bulkApproving ? "Approving..." : `✓ Approve ${selectedRegIds.size || ""} Selected`}
                  </button>
                </div>
              </div>

              {bulkApproveResult && (bulkApproveResult.skippedDuplicates.length > 0 || bulkApproveResult.skippedCapacity.length > 0) && (
                <div style={{ background: "#fffbeb", border: "1px solid #c9991a", borderRadius: 10, padding: 16, marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#92400e", marginBottom: 6 }}>
                    {bulkApproveResult.skippedDuplicates.length + bulkApproveResult.skippedCapacity.length} skipped — needs individual review
                  </div>
                  {bulkApproveResult.skippedDuplicates.length > 0 && (
                    <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 4 }}>
                      Possible duplicates: {bulkApproveResult.skippedDuplicates.map(s => s.playerName).join(", ")}
                    </div>
                  )}
                  {bulkApproveResult.skippedCapacity.length > 0 && (
                    <div style={{ fontSize: 12.5, color: "#374151" }}>
                      Roster limit reached before reaching: {bulkApproveResult.skippedCapacity.map(s => s.playerName).join(", ")}
                    </div>
                  )}
                  <button type="button" onClick={() => setBulkApproveResult(null)} style={{ background: "none", border: "none", color: "#92400e", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, marginTop: 8, textDecoration: "underline" }}>
                    Dismiss
                  </button>
                </div>
              )}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {pendingRegs.map(reg => {
                const alreadyOnRoster = rosterList.some(
                  p => p.name.trim().toLowerCase() === reg.player_name.trim().toLowerCase()
                );
                const hasWarning = alreadyOnRoster || !reg.consent_given;
                const isSelected = selectedRegIds.has(reg.id);
                return (
                <div key={reg.id} style={{ background: "#fff", border: "1px solid " + (isSelected ? "#1a4a2e" : alreadyOnRoster ? "#c9991a" : "#e5e7eb"), borderRadius: 10, padding: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRegSelected(reg.id)}
                        title={hasWarning ? "This registration has a warning — review it before approving." : "Select for bulk approval"}
                        style={{ width: 17, height: 17, marginTop: 3, flexShrink: 0, accentColor: "#1a4a2e", cursor: "pointer" }}
                      />
                      <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>{reg.player_name}</div>
                        {alreadyOnRoster && (
                          <span style={{ background: "#fffbeb", border: "1px solid #c9991a", color: "#92400e", borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 700 }}>
                            ⚠️ Already on roster
                          </span>
                        )}
                        {reg.registration_type === "adult" && (
                          <span style={{ background: "#e8f0f7", border: "1px solid #b8cfe0", color: "#3d5166", borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 700 }}>
                            🏉 Adult self-reg
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>{reg.age_grade || "No age grade"}{reg.position ? " · " + reg.position : ""}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                      Submitted {new Date(reg.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 13, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div>Parent: <strong>{reg.parent_name}</strong> — {reg.parent_email}</div>
                    {reg.school_coach_email && <div>School coach: {reg.school_coach_name || "—"} — {reg.school_coach_email}</div>}
                    {reg.school_coach_2_email && <div>Second school coach: {reg.school_coach_2_name || "—"} — {reg.school_coach_2_email}</div>}
                    {reg.club_coach_email && <div>Club coach: {reg.club_coach_name || "—"} — {reg.club_coach_email}</div>}
                    {reg.club_coach_2_email && <div>Second club coach: {reg.club_coach_2_name || "—"} — {reg.club_coach_2_email}</div>}
                    {reg.other_contact_1_email && <div>Additional: {reg.other_contact_1_name || "—"} — {reg.other_contact_1_email}</div>}
                    {reg.other_contact_2_email && <div>Additional: {reg.other_contact_2_name || "—"} — {reg.other_contact_2_email}</div>}
                  </div>
                  {/* The "registration received" email to this address
                      bounced: the address is wrong, and every concussion
                      update would bounce too. Caught before approval. */}
                  {reg.email_problem_at && (
                    <div role="alert" style={{ marginTop: 12, fontSize: 13, color: "#991b1b", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 12px", lineHeight: 1.5, overflowWrap: "anywhere" }}>
                      <strong>⚠ The email to {reg.parent_email} didn't arrive</strong> ({new Date(reg.email_problem_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}). The address is probably mistyped, so the family won't get any concussion updates at it. Please get the right address from them, then approve and correct it on the Roster (or reject with "Details incorrect" if they'd rather fill the form in again).
                      {reg.email_problem && <span style={{ display: "block", fontSize: 12, marginTop: 2 }}>Reason given: {reg.email_problem}</span>}
                    </div>
                  )}
                  {reg.consent_given ? (
                    <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 10px" }}>
                      <span>✅</span>
                      <span>Consent given on {new Date(reg.consent_given_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} ({reg.consent_version})</span>
                    </div>
                  ) : (
                    <div style={{ marginTop: 12, fontSize: 12, color: "#c0272d", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 10px" }}>
                      ⚠️ No consent recorded — do not approve without confirming directly with the parent.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button
                      onClick={() => handleApproveRegistration(reg.id)}
                      disabled={approvingRegId === reg.id}
                      style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: approvingRegId === reg.id ? "default" : "pointer", opacity: approvingRegId === reg.id ? 0.7 : 1 }}
                    >
                      {approvingRegId === reg.id ? "Approving..." : "✓ Approve & Add to Roster"}
                    </button>
                    <button
                      onClick={() => handleRejectRegistration(reg.id, reg.player_name)}
                      style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
            </>
          )}

          {confirmAction && (
            <ConfirmModal
              title={confirmAction.title}
              message={confirmAction.message}
              confirmLabel={confirmAction.confirmLabel}
              danger={confirmAction.danger}
              onConfirm={confirmAction.onConfirm}
              onCancel={() => setConfirmAction(null)}
            />
          )}
          {rejectTarget && (
            <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3500 }}>
              <div role="dialog" aria-modal="true" aria-labelledby="reject-reg-title" style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "'Inter', system-ui, sans-serif" }}>
                <div id="reject-reg-title" style={{ fontWeight: 800, fontSize: 17, color: "#c0272d", marginBottom: 10 }}>⚠ Reject {rejectTarget.playerName}'s registration?</div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.6, margin: "0 0 16px" }}>
                  They won't be added to your roster, and nobody the parent listed will be contacted. The form is deleted after 30 days.
                </p>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Note for your staff (optional, never sent to the parent)</label>
                <textarea
                  value={rejectTarget.reason}
                  onChange={e => setRejectTarget(t => ({ ...t, reason: e.target.value.slice(0, 500) }))}
                  rows={2}
                  placeholder="e.g. Already on our roster, or not one of our players"
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: 8, fontSize: 14, fontFamily: "inherit", resize: "vertical", marginBottom: 14 }}
                />
                {rejectTarget.parentEmail ? (
                  <>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: rejectTarget.notifyParent ? 12 : 20 }}>
                    <input
                      type="checkbox"
                      checked={rejectTarget.notifyParent}
                      onChange={e => setRejectTarget(t => ({ ...t, notifyParent: e.target.checked }))}
                      style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
                    />
                    <span style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.5 }}>
                      Email the parent ({rejectTarget.parentEmail}) to say it wasn't accepted.
                      <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginTop: 2 }}>Untick for spam or test entries.</span>
                    </span>
                  </label>
                  {rejectTarget.notifyParent && (
                    <div style={{ marginBottom: 20 }}>
                      <label htmlFor="reject-parent-reason" style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>What the parent is told</label>
                      <select
                        id="reject-parent-reason"
                        value={rejectTarget.parentReason}
                        onChange={e => setRejectTarget(t => ({ ...t, parentReason: e.target.value }))}
                        style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 14, fontFamily: "inherit", background: "#fff", color: "#111827" }}
                      >
                        {PARENT_REJECTION_REASONS.map(o => <option key={o.value} value={o.value}>{o.short}</option>)}
                      </select>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.5 }}>
                        {(PARENT_REJECTION_REASONS.find(o => o.value === rejectTarget.parentReason) || PARENT_REJECTION_REASONS[0]).hint} The email puts this in our own words.
                      </div>
                    </div>
                  )}
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px" }}>No parent email was given, so nobody will be told.</p>
                )}
                {rejectTarget.error && (
                  <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: "#c0272d" }}>
                    {rejectTarget.error}
                  </div>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={confirmRejectRegistration}
                    disabled={rejecting}
                    style={{ flex: 1, background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: rejecting ? "default" : "pointer", opacity: rejecting ? 0.7 : 1 }}
                  >
                    {rejecting ? "Rejecting..." : "Reject"}
                  </button>
                  <button
                    onClick={() => setRejectTarget(null)}
                    disabled={rejecting}
                    style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: rejecting ? "default" : "pointer" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {approveWarning && (
            <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3500 }}>
              <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "'Inter', system-ui, sans-serif" }}>
                <div style={{ fontWeight: 800, fontSize: 17, color: "#92400e", marginBottom: 10 }}>⚠️ Possible Duplicate</div>
                <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.7, marginBottom: 16 }}>{approveWarning.message}</p>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.6 }}>If this is a different player who happens to share the same name, click <strong>Approve Anyway</strong>. If it is a duplicate, click <strong>Reject Instead</strong>.</p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => { const id = approveWarning.registrationId; setApproveWarning(null); handleApproveRegistration(id, true); }}
                    style={{ flex: 1, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                  >
                    Approve Anyway
                  </button>
                  <button
                    onClick={() => { const id = approveWarning.registrationId; const name = approveWarning.playerName; setApproveWarning(null); handleRejectRegistration(id, name, { duplicate: true }); }}
                    style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "10px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                  >
                    Reject Instead
                  </button>
                  <button
                    onClick={() => setApproveWarning(null)}
                    style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
        </div>
      </AppShell>
    );
  }

    if (view === "staff") {
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Staff">
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 24 }}>Invite colleagues to access this dashboard, or remove someone who's left.</p>

          {/* Registration links — two separate links, clearly labelled */}
          {/* The same gate as the Registrations page. Both screens show
              these links, so both have to respect it — leaving one open
              would defeat the whole thing. */}
          {regAwaitingApproval && (
            <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 18, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, color: "#92400e", fontSize: 15, marginBottom: 6 }}>
                Registration links are on their way
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: "#78350f", lineHeight: 1.65 }}>
                We confirm every new organisation is a real club or school before its registration links
                go live — it is what stops somebody setting up in your name and collecting your members'
                details. Everything else works now, and we usually confirm within a working day.
              </p>
            </div>
          )}
          <div style={{ display: regAwaitingApproval ? "none" : "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>

            {/* Parent registration */}
            <div style={{ background: "#fff", border: "2px solid #1a4a2e", borderRadius: 10, padding: 18 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#1a4a2e", marginBottom: 4 }}>👨‍👩‍👦 Age Grade Player Registration (U7–U19)</div>
              <p style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px" }}>
                Share with parents/guardians to register their child and give parental consent. For all age grade players (U7–U19).
              </p>
              {registrationLink ? (
                <div>
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 11.5, fontFamily: "monospace", color: "#374151", wordBreak: "break-all", marginBottom: 8 }}>
                    {registrationLink}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(registrationLink).then(() => showNotif("Parent link copied.")).catch(() => showNotif("Copy failed — select the link manually.", "error"))}
                    style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit" }}
                  >Copy Age Grade Link</button>
                </div>
              ) : (
                <button
                  onClick={async () => { setRegLinkLoading(true); try { const r = await api.getRegistrationLink(); setRegistrationLink(r.link); if (r.adultLink) setAdultRegistrationLink(r.adultLink); if (r.staffLink) setStaffRegLink(r.staffLink); } catch (e) { showNotif(e.message, "error"); } finally { setRegLinkLoading(false); } }}
                  disabled={regLinkLoading}
                  style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit", opacity: regLinkLoading ? 0.7 : 1 }}
                >{regLinkLoading ? "Loading..." : "Show Link"}</button>
              )}
            </div>

            {/* Adult self-registration — clubs and academies only (adultLink null for schools) */}
            {adultRegistrationLink && (
            <div style={{ background: "#fff", border: "2px solid #3d5166", borderRadius: 10, padding: 18 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#3d5166", marginBottom: 4 }}>🏉 Adult Player Self-Registration</div>
              <p style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px" }}>
                Share with adult players to register themselves directly. No parent needed. For Adult age grade only.
              </p>
              {adultRegistrationLink ? (
                <div>
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 11.5, fontFamily: "monospace", color: "#374151", wordBreak: "break-all", marginBottom: 8 }}>
                    {adultRegistrationLink}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(adultRegistrationLink).then(() => showNotif("Adult link copied.")).catch(() => showNotif("Copy failed — select the link manually.", "error"))}
                    style={{ background: "#3d5166", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit" }}
                  >Copy Adult Link</button>
                </div>
              ) : (
                <button
                  onClick={async () => { setRegLinkLoading(true); try { const r = await api.getRegistrationLink(); setRegistrationLink(r.link); if (r.adultLink) setAdultRegistrationLink(r.adultLink); if (r.staffLink) setStaffRegLink(r.staffLink); } catch (e) { showNotif(e.message, "error"); } finally { setRegLinkLoading(false); } }}
                  disabled={regLinkLoading}
                  style={{ background: "#3d5166", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit", opacity: regLinkLoading ? 0.7 : 1 }}
                >{regLinkLoading ? "Loading..." : "Show Link"}</button>
              )}
            </div>
            )}

            {/* Staff self-registration — coach requests access, admin reviews before it's live */}
            <div style={{ background: "#fff", border: "2px solid #c9991a", borderRadius: 10, padding: 18 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#92400e", marginBottom: 4 }}>🧑‍🏫 Staff Registration Link</div>
              <p style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 12px" }}>
                Share with coaches to request staff access. Requests land below for you to review and approve — nothing is granted automatically.
              </p>
              {staffRegLink ? (
                <div>
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 11.5, fontFamily: "monospace", color: "#374151", wordBreak: "break-all", marginBottom: 8 }}>
                    {staffRegLink}
                  </div>
                  <button
                    onClick={() => navigator.clipboard.writeText(staffRegLink).then(() => showNotif("Staff link copied.")).catch(() => showNotif("Copy failed — select the link manually.", "error"))}
                    style={{ background: "#c9991a", color: "#2b2000", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit" }}
                  >Copy Staff Link</button>
                </div>
              ) : (
                <button
                  onClick={async () => { setRegLinkLoading(true); try { const r = await api.getRegistrationLink(); setRegistrationLink(r.link); if (r.adultLink) setAdultRegistrationLink(r.adultLink); if (r.staffLink) setStaffRegLink(r.staffLink); } catch (e) { showNotif(e.message, "error"); } finally { setRegLinkLoading(false); } }}
                  disabled={regLinkLoading}
                  style={{ background: "#c9991a", color: "#2b2000", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", width: "100%", fontFamily: "inherit", opacity: regLinkLoading ? 0.7 : 1 }}
                >{regLinkLoading ? "Loading..." : "Show Link"}</button>
              )}
            </div>
          </div>

          {pendingStaffRegs.length > 0 && (
            <div style={{ marginBottom: 24, background: "#fffbeb", border: "1px solid #c9991a", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#92400e", marginBottom: 12 }}>
                🧑‍🏫 {pendingStaffRegs.length} staff request{pendingStaffRegs.length !== 1 ? "s" : ""} awaiting review
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {pendingStaffRegs.map(reg => {
                  const sel = staffRegSelections[reg.id] || { ageGrades: [], teams: [] };
                  return (
                    <div key={reg.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{reg.name}</div>
                          <div style={{ fontSize: 12.5, color: "#6b7280" }}>{reg.email}</div>
                        </div>
                        <div style={{ fontSize: 11.5, color: "#9ca3af" }}>
                          Submitted {new Date(reg.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </div>
                      </div>
                      {reg.note && (
                        <div style={{ marginTop: 8, fontSize: 12.5, color: "#374151", background: "#f9fafb", border: "1px solid #f3f4f6", borderRadius: 6, padding: "8px 10px" }}>
                          {reg.note}
                        </div>
                      )}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                          Age grades to assign
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult"].map(grade => {
                            const active = sel.ageGrades.includes(grade);
                            return (
                              <button
                                key={grade}
                                type="button"
                                onClick={() => toggleStaffRegSelection(reg.id, "ageGrades", grade)}
                                style={{ background: active ? "#1a4a2e" : "#fff", color: active ? "#fff" : "#6b7280", border: `1px solid ${active ? "#1a4a2e" : "#d1d5db"}`, borderRadius: 99, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                              >
                                {grade}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {teamsList.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                            Teams to assign
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {teamsList.map(t => {
                              const active = sel.teams.includes(t.name);
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => toggleStaffRegSelection(reg.id, "teams", t.name)}
                                  style={{ background: active ? "#3b5bdb" : "#fff", color: active ? "#fff" : "#6b7280", border: `1px solid ${active ? "#3b5bdb" : "#d1d5db"}`, borderRadius: 99, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                                >
                                  {t.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                        <button
                          onClick={() => handleApproveStaffReg(reg)}
                          disabled={staffRegActionId === reg.id}
                          style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: staffRegActionId === reg.id ? "default" : "pointer", opacity: staffRegActionId === reg.id ? 0.7 : 1 }}
                        >
                          {staffRegActionId === reg.id ? "Approving..." : "✓ Approve"}
                        </button>
                        <button
                          onClick={() => handleRejectStaffReg(reg)}
                          disabled={staffRegActionId === reg.id}
                          style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pendingRegCount > 0 && (
            <div
              onClick={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }}
              style={{ marginBottom: 20, background: "#fffbeb", border: "1px solid #c9991a", borderRadius: 6, padding: "8px 12px", fontSize: 13, color: "#92400e", fontWeight: 600, cursor: "pointer" }}
            >
              ⏳ {pendingRegCount} pending registration{pendingRegCount !== 1 ? "s" : ""} awaiting review — click to review
            </div>
          )}

          <Sect title="Invite a Staff Member">
            <form onSubmit={handleInviteStaff} style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <Field label="Email address">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="colleague@yourclub.co.uk"
                    style={inp}
                  />
                </Field>
              </div>
              <button type="submit" disabled={inviteSubmitting} style={{ ...btnP, marginBottom: 12, opacity: inviteSubmitting ? 0.7 : 1 }}>
                {inviteSubmitting ? "Sending..." : "Send Invite"}
              </button>
            </form>
          </Sect>

          <Sect title="Teams" subtitle="Independent of age grade — e.g. 1st XV, 2nd XV, Women's, or a girls' team at a youth age grade.">
            <form onSubmit={handleAddTeam} style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: teamsList.length ? 14 : 0 }}>
              <div style={{ flex: 1 }}>
                <Field label="Team name">
                  <input
                    value={newTeamName}
                    onChange={e => setNewTeamName(e.target.value)}
                    placeholder="e.g. 1st XV"
                    style={inp}
                  />
                </Field>
              </div>
              <button type="submit" disabled={addTeamSubmitting} style={{ ...btnP, marginBottom: 12, opacity: addTeamSubmitting ? 0.7 : 1 }}>
                {addTeamSubmitting ? "Adding..." : "Add Team"}
              </button>
            </form>
            {teamsList.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {teamsList.map(t => (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 99, padding: "4px 6px 4px 12px" }}>
                    <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{t.name}</span>
                    <button
                      onClick={() => handleDeleteTeam(t)}
                      title={`Remove "${t.name}"`}
                      style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14, width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </Sect>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "#1a4a2e", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 14 }}>
              Current Staff{staffList.length ? ` (${staffList.length})` : ""}
            </div>
            {staffLoading ? (
              <div style={{ textAlign: "center", padding: 20, color: "#9ca3af" }}>Loading...</div>
            ) : staffList.length === 0 ? (
              <div style={{ textAlign: "center", padding: 20, color: "#9ca3af", fontSize: 14 }}>No staff yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {staffList.map((s) => (
                  <div key={s.id} style={{ padding: "10px 12px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{s.email}</span>
                          {s.isOrgAdmin && <Badge color="#1a4a2e">Admin</Badge>}
                          {s.status === "invited" && <Badge color="#c9991a">Invite Pending</Badge>}
                          {s.status === "pending_verification" && <Badge color="#c9991a">Unverified</Badge>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                        {/* The server refuses to demote the last admin —
                            an organisation with nobody able to administer
                            it cannot be recovered from inside the app. So
                            say that instead of offering a button whose
                            only possible outcome is an error. */}
                        {s.isOrgAdmin && staffList.filter(x => x.isOrgAdmin).length === 1 ? (
                          <span style={{ fontSize: 11.5, color: "#6b7280", fontStyle: "italic", maxWidth: 190, textAlign: "right", lineHeight: 1.4 }}>
                            Your only admin — promote someone else before removing this
                          </span>
                        ) : (
                          <button onClick={() => handleToggleAdmin(s)} style={{ background: "#fff", color: s.isOrgAdmin ? "#c9991a" : "#1a4a2e", border: `1px solid ${s.isOrgAdmin ? "#c9991a" : "#1a4a2e"}`, borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            {s.isOrgAdmin ? "Remove Admin" : "Make Admin"}
                          </button>
                        )}
                        {!s.isOrgAdmin && (
                          <button onClick={() => handleRemoveStaff(s)} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e5e7eb" }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                        Assigned age grades — messages route here
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult"].map(grade => {
                          const active = (s.assignedAgeGrades || []).includes(grade);
                          return (
                            <button
                              key={grade}
                              onClick={() => handleToggleStaffAgeGrade(s, grade)}
                              style={{ background: active ? "#1a4a2e" : "#fff", color: active ? "#fff" : "#6b7280", border: `1px solid ${active ? "#1a4a2e" : "#d1d5db"}`, borderRadius: 99, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                            >
                              {grade}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {teamsList.length > 0 && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e5e7eb" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                          Assigned teams — messages route here too
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {teamsList.map(t => {
                            const active = (s.assignedTeams || []).includes(t.name);
                            return (
                              <button
                                key={t.id}
                                onClick={() => handleToggleStaffTeam(s, t.name)}
                                style={{ background: active ? "#3b5bdb" : "#fff", color: active ? "#fff" : "#6b7280", border: `1px solid ${active ? "#3b5bdb" : "#d1d5db"}`, borderRadius: 99, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                              >
                                {t.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            danger={confirmAction.danger}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </AppShell>
    );
  }

  // ── LOG AN INJURY ─────────────────────────────────────────────────
  //
  // Deliberately shorter than the concussion form. It is filled in on a
  // wet touchline, and a form that takes five minutes gets completed
  // from memory on Tuesday, if at all — at which point the record is
  // worth very little. Everything not needed to answer "what happened
  // and what did we do" sits behind "add more detail".
  if (view === "new_injury") {
    const chosen = rosterList.find(r => r.id === injuryForm.rosterPlayerId) || null;
    const offRoster = injuryForm.rosterPlayerId === "__none__";

    // Who this child's record already knows about. Ticked rather than
    // typed, because these addresses came from the family and have been
    // confirmed by the people they belong to.
    const knownContacts = chosen ? [
      { email: chosen.parent_email, name: chosen.parent_name, role: "parent" },
      { email: chosen.school_coach_email, name: chosen.school_coach_name, role: "school coach" },
      { email: chosen.school_coach_2_email, name: chosen.school_coach_2_name, role: "school coach" },
      { email: chosen.club_coach_email, name: chosen.club_coach_name, role: "club coach" },
      { email: chosen.club_coach_2_email, name: chosen.club_coach_2_name, role: "club coach" },
      { email: chosen.other_contact_1_email, name: chosen.other_contact_1_name, role: "additional contact" },
      { email: chosen.other_contact_2_email, name: chosen.other_contact_2_name, role: "additional contact" },
    ].filter(c => c.email) : [];

    const toggleContact = (email) => setInjuryForm(f => ({
      ...f,
      selectedContacts: f.selectedContacts.includes(email)
        ? f.selectedContacts.filter(e => e !== email)
        : [...f.selectedContacts, email],
    }));

    async function submitInjury(e) {
      e.preventDefault();
      if (!offRoster && !injuryForm.rosterPlayerId) return showNotif("Choose a player, or record someone not on the roster.", "error");
      if (offRoster && !injuryForm.subjectName.trim()) return showNotif("Enter a name for the person involved.", "error");
      if (!injuryForm.isTest && !injuryForm.consentGiven) return showNotif("Consent must be confirmed before recording an injury.", "error");

      const recipients = offRoster
        ? injuryForm.manualContacts.filter(c => c.email.trim()).map(c => ({ email: c.email.trim(), name: c.name.trim() || null }))
        : knownContacts.filter(c => injuryForm.selectedContacts.includes(c.email)).map(c => ({ email: c.email, name: c.name, role: c.role }));

      // The server refuses this too; catching it here means the form says
      // which tick is missing instead of returning a bare error.
      if (!injuryForm.isTest && recipients.length > 0 && !injuryForm.shareConsentGiven) {
        return showNotif("Consent to share must be confirmed before this injury can be sent to anyone.", "error");
      }

      setInjurySaving(true);
      try {
        const result = await api.createInjury({
          rosterPlayerId: offRoster ? null : injuryForm.rosterPlayerId,
          subjectName: offRoster ? injuryForm.subjectName.trim() : (chosen ? chosen.name : ""),
          subjectIsMinor: offRoster ? injuryForm.subjectIsMinor : null,
          subjectDob: offRoster ? (injuryForm.subjectDob || null) : null,
          ageGrade: offRoster ? (injuryForm.ageGrade || null) : null,
          incidentDate: injuryForm.incidentDate,
          incidentTime: injuryForm.incidentTime || null,
          activity: injuryForm.activity || null,
          bodyPart: injuryForm.bodyPart || null,
          nature: injuryForm.nature || null,
          natureOther: injuryForm.natureOther || null,
          contact: injuryForm.contact,
          description: injuryForm.description || null,
          carriedOn: injuryForm.carriedOn || null,
          treatment: injuryForm.treatment || null,
          adviceGiven: injuryForm.adviceGiven || null,
          adviceAccepted: injuryForm.adviceAccepted,
          referral: injuryForm.referral || null,
          isTest: injuryForm.isTest,
          consentGiven: injuryForm.consentGiven,
          shareConsentGiven: injuryForm.shareConsentGiven,
          consentGivenBy: chosen ? chosen.parent_name : null,
          recipients,
        });
        setInjuryResult(result);
        await loadInjuries();
        showNotif("Injury recorded.");
      } catch (err) {
        showNotif(err.message || "Could not record the injury.", "error");
      } finally {
        setInjurySaving(false);
      }
    }

    // Shown after saving: who was written to, who was held back, and
    // whether this looks like it belongs in the concussion protocol
    // instead. A create that quietly did less than was asked is the
    // defect this screen exists to prevent.
    if (injuryResult) {
      return (
        <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout}
          onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
          pageTitle="Injury recorded">
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            <Sect title="Recorded" subtitle="This is a record of what happened. It is not a protocol and nothing will advance on its own.">
              {injuryResult.offerConcussionProtocol && (
                <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#c0272d", marginBottom: 4 }}>This was a head injury</div>
                  <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 10 }}>
                    A suspected concussion belongs on the GRTP protocol, not here. Starting one needs parental consent confirmed again, so it is not done automatically.
                  </div>
                  <button onClick={() => {
                    setForm({
                      ...blankLogConcussionForm(),
                      rosterPlayerId: injuryResult.injury.rosterPlayerId || "",
                      name: injuryResult.injury.subjectName,
                      ageGrade: injuryResult.injury.ageGrade || "",
                      incidentDate: injuryResult.injury.incidentDate,
                      incidentDetails: injuryResult.injury.description || "",
                    });
                    setInjuryResult(null);
                    setView("new");
                  }} style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    Start a concussion protocol
                  </button>
                </div>
              )}

              {injuryResult.sentTo.length > 0 && (
                <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
                  <strong>Sent to:</strong> {injuryResult.sentTo.join(", ")}
                </div>
              )}
              {injuryResult.withheldFrom.length > 0 && (
                <div style={{ background: "#fef9ec", border: "1px solid #f0d88a", borderRadius: 8, padding: "10px 12px", marginBottom: 8, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
                  <strong>Held back:</strong> {injuryResult.withheldFrom.join(", ")} — they agreed to concussion updates but have not yet been asked about injuries. We have asked them. Tell the parent they were not written to.
                </div>
              )}
              {injuryResult.optedOut.length > 0 && (
                <div style={{ background: "#fbe9e9", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 12px", marginBottom: 8, fontSize: 13, color: "#a81f25", lineHeight: 1.6 }}>
                  <strong>Not contacted:</strong> {injuryResult.optedOut.join(", ")} — this person has asked not to receive updates. Nothing was sent and they will not be asked again.
                </div>
              )}
              {injuryResult.sentTo.length === 0 && injuryResult.withheldFrom.length === 0 && injuryResult.optedOut.length === 0 && (
                <div style={{ fontSize: 13, color: "#6b7280" }}>Nobody was written to. The incident is on the record.</div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={() => { setInjuryResult(null); setInjuryForm(blankInjuryForm()); setView("dashboard"); }}
                  style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Back to dashboard
                </button>
                <button onClick={() => { setInjuryResult(null); setInjuryForm(blankInjuryForm()); }}
                  style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  Record another
                </button>
              </div>
            </Sect>
          </div>
        </AppShell>
      );
    }

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout}
        onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
        pageTitle="Log an injury">
        <form onSubmit={submitInjury} style={{ maxWidth: 680, margin: "0 auto" }}>

          <div style={{ background: "#eef6f1", border: "1px solid #bcd9c8", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#1a4a2e", lineHeight: 1.6 }}>
            This is a first aid record, not a concussion protocol. Nothing here advances on its own, and nothing is reported to the RFU or the HSE on your behalf.
          </div>

          <Sect title="Who">
            <Field label="Player">
              {(() => {
                const active = rosterList.filter(r => !r.archived);
                const chosen = injuryForm.rosterPlayerId && !offRoster
                  ? active.find(r => r.id === injuryForm.rosterPlayerId)
                  : null;
                const q = injurySearch.trim().toLowerCase();
                // Only search once there is something to go on, and cap the
                // list — a touchline decision needs the right five names,
                // not all three hundred.
                const matches = q.length < 1 ? [] : active.filter(r =>
                  `${r.name} ${r.age_grade || ""} ${r.team || ""}`.toLowerCase().includes(q)
                ).slice(0, 8);

                const pick = (r) => {
                  setInjuryForm(f => ({ ...f, rosterPlayerId: r.id, selectedContacts: [] }));
                  setInjurySearch("");
                  setInjuryActiveIndex(-1);
                };
                const clear = () => {
                  setInjuryForm(f => ({ ...f, rosterPlayerId: "", selectedContacts: [] }));
                  setInjurySearch("");
                  setInjuryActiveIndex(-1);
                };

                if (chosen) return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", border: "1px solid #bcd9c8", background: "#f2f8f4", borderRadius: 6, padding: "9px 12px" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#14331f" }}>
                      {chosen.name}{chosen.age_grade ? ` (${chosen.age_grade})` : ""}
                    </span>
                    <button type="button" onClick={clear}
                      style={{ marginLeft: "auto", background: "none", border: "none", color: "#1a4a2e", fontSize: 13, fontWeight: 600, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
                      Change
                    </button>
                  </div>
                );

                if (offRoster) return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", border: "1px solid #f0d88a", background: "#fffbeb", borderRadius: 6, padding: "9px 12px" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#78350f" }}>Someone not on our roster</span>
                    <button type="button" onClick={clear}
                      style={{ marginLeft: "auto", background: "none", border: "none", color: "#92400e", fontSize: 13, fontWeight: 600, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
                      Change
                    </button>
                  </div>
                );

                return (
                  <div style={{ position: "relative" }}>
                    <input
                      value={injurySearch}
                      onChange={e => { setInjurySearch(e.target.value); setInjuryActiveIndex(-1); }}
                      onKeyDown={e => {
                        if (!matches.length) return;
                        if (e.key === "ArrowDown") { e.preventDefault(); setInjuryActiveIndex(i => (i + 1) % matches.length); }
                        else if (e.key === "ArrowUp") { e.preventDefault(); setInjuryActiveIndex(i => (i <= 0 ? matches.length : i) - 1); }
                        else if (e.key === "Enter" && injuryActiveIndex >= 0) { e.preventDefault(); pick(matches[injuryActiveIndex]); }
                        else if (e.key === "Escape") { setInjurySearch(""); setInjuryActiveIndex(-1); }
                      }}
                      placeholder="Start typing a name…"
                      style={inp}
                      autoComplete="off"
                      role="combobox"
                      aria-expanded={matches.length > 0}
                      aria-controls="injury-player-suggestions"
                      aria-autocomplete="list"
                      aria-activedescendant={injuryActiveIndex >= 0 && matches[injuryActiveIndex]
                        ? `injury-player-${matches[injuryActiveIndex].id}` : undefined}
                    />
                    {matches.length > 0 && (
                      <div id="injury-player-suggestions" role="listbox" aria-label="Matching players"
                        style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, marginTop: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10, maxHeight: 240, overflowY: "auto" }}>
                        {matches.map((r, i) => (
                          <div key={r.id} id={`injury-player-${r.id}`} role="option" aria-selected={i === injuryActiveIndex}
                            onMouseDown={() => pick(r)}
                            onMouseEnter={() => setInjuryActiveIndex(i)}
                            style={{ padding: "10px 12px", cursor: "pointer", fontSize: 14, borderBottom: "1px solid #f3f4f6", background: i === injuryActiveIndex ? "#f9fafb" : "#fff" }}>
                            <div style={{ fontWeight: 600, color: "#111827" }}>{r.name}</div>
                            {(r.age_grade || r.team) && (
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{[r.age_grade, r.team].filter(Boolean).join(" · ")}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {q.length >= 1 && matches.length === 0 && (
                      <div style={{ fontSize: 12.5, color: "#92400e", marginTop: 6 }}>
                        Nobody on the roster matches “{injurySearch.trim()}”.
                      </div>
                    )}
                    {/* Always reachable, and never buried at the bottom of
                        three hundred options the way it was in the select. */}
                    <button type="button"
                      onClick={() => { setInjuryForm(f => ({ ...f, rosterPlayerId: "__none__", selectedContacts: [] })); setInjurySearch(""); setInjuryActiveIndex(-1); }}
                      style={{ marginTop: 8, background: "none", border: "none", padding: 0, color: "#1a4a2e", fontSize: 13, fontWeight: 600, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}>
                      Someone not on our roster
                    </button>
                  </div>
                );
              })()}
            </Field>

            {offRoster && (
              <>
                <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.55, margin: "0 0 10px" }}>
                  A visiting player, a spectator, anyone at the ground who needed first aid. You can record what happened without holding any contact details at all.
                </div>
                <TwoCol>
                  <Field label="Name">
                    <input value={injuryForm.subjectName} onChange={e => setInjuryForm(f => ({ ...f, subjectName: e.target.value }))} placeholder="e.g. Visiting player" style={inp} />
                  </Field>
                  <Field label="Date of birth (if known)">
                    <input type="date" value={injuryForm.subjectDob} onChange={e => setInjuryForm(f => ({ ...f, subjectDob: e.target.value }))} style={inp} />
                  </Field>
                </TwoCol>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", marginBottom: 4 }}>
                  <input type="checkbox" checked={injuryForm.subjectIsMinor === true}
                    onChange={e => setInjuryForm(f => ({ ...f, subjectIsMinor: e.target.checked ? true : null }))} />
                  Under 18
                </label>
                <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                  Needed to work out how long this record must be kept. A child's record is kept far longer than an adult's.
                </div>
              </>
            )}
          </Sect>

          <Sect title="What happened">
            <TwoCol>
              <Field label="Date"><input type="date" value={injuryForm.incidentDate} onChange={e => setInjuryForm(f => ({ ...f, incidentDate: e.target.value }))} style={inp} /></Field>
              <Field label="Time"><input type="time" value={injuryForm.incidentTime} onChange={e => setInjuryForm(f => ({ ...f, incidentTime: e.target.value }))} style={inp} /></Field>
            </TwoCol>
            <TwoCol>
              <Field label="Activity">
                <select value={injuryForm.activity} onChange={e => setInjuryForm(f => ({ ...f, activity: e.target.value }))} style={inp}>
                  <option value="">—</option><option value="match">Match</option><option value="training">Training</option><option value="other">Other</option>
                </select>
              </Field>
              <Field label="Body part">
                <input value={injuryForm.bodyPart} onChange={e => setInjuryForm(f => ({ ...f, bodyPart: e.target.value }))} placeholder="e.g. Left ankle" style={inp} />
              </Field>
            </TwoCol>
            <Field label="What it looks like">
              <select value={injuryForm.nature} onChange={e => setInjuryForm(f => ({ ...f, nature: e.target.value }))} style={inp}>
                <option value="">—</option>
                <optgroup label="Injury">
                  {["sprain", "strain", "knock or bruise", "dislocation", "suspected fracture", "cut or graze", "nosebleed", "dental", "eye injury", "head injury"].map(n => <option key={n} value={n}>{n}</option>)}
                </optgroup>
                <optgroup label="Illness or environmental">
                  {["dehydration", "heat illness or sunstroke", "hypothermia", "breathing difficulty or asthma", "allergic reaction", "fainting or collapse", "other"].map(n => <option key={n} value={n}>{n}</option>)}
                </optgroup>
              </select>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>What you saw, not a diagnosis. Record what it turned out to be later if you find out.</div>
              {/* "Other" on its own records that something happened and
                  not what, which is no use on the register a club hands
                  an inspector. */}
              {injuryForm.nature === "other" && (
                <div style={{ marginTop: 8 }}>
                  <input
                    value={injuryForm.natureOther}
                    onChange={e => setInjuryForm(f => ({ ...f, natureOther: e.target.value }))}
                    placeholder="Describe what you saw — e.g. insect sting, crush injury"
                    maxLength={200}
                    style={inp}
                    aria-label="Describe the injury"
                  />
                </div>
              )}
            </Field>
            <TwoCol>
              <Field label="Did they carry on?">
                <select value={injuryForm.carriedOn} onChange={e => setInjuryForm(f => ({ ...f, carriedOn: e.target.value }))} style={inp}>
                  <option value="">—</option><option value="yes">Yes</option><option value="no">No</option><option value="came_off_later">Played on, came off later</option>
                </select>
              </Field>
              <Field label="Contact or non-contact">
                <select value={injuryForm.contact === null ? "" : injuryForm.contact ? "1" : "0"}
                  onChange={e => setInjuryForm(f => ({ ...f, contact: e.target.value === "" ? null : e.target.value === "1" }))} style={inp}>
                  <option value="">—</option><option value="1">Contact</option><option value="0">Non-contact</option>
                </select>
              </Field>
            </TwoCol>
            <Field label="What happened">
              <textarea rows={2} value={injuryForm.description} onChange={e => setInjuryForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Went into a tackle, rolled the ankle, could not weight-bear" style={{ ...inp, resize: "vertical" }} />
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Facts only. Do not name another player or say whose fault it was — this record may be read by an insurer or a solicitor.</div>
            </Field>
          </Sect>

          <Sect title="What was done">
            <Field label="First aid given">
              <input value={injuryForm.treatment} onChange={e => setInjuryForm(f => ({ ...f, treatment: e.target.value }))} placeholder="e.g. Ice and elevation" style={inp} />
            </Field>
            <TwoCol>
              <Field label="Advice given">
                <select value={injuryForm.adviceGiven} onChange={e => setInjuryForm(f => ({ ...f, adviceGiven: e.target.value }))} style={inp}>
                  <option value="">—</option><option value="seek_medical">Seek medical attention</option><option value="rest">Rest</option><option value="none">None needed</option>
                </select>
              </Field>
              <Field label="Advice accepted">
                <select value={injuryForm.adviceAccepted === null ? "" : injuryForm.adviceAccepted ? "1" : "0"}
                  onChange={e => setInjuryForm(f => ({ ...f, adviceAccepted: e.target.value === "" ? null : e.target.value === "1" }))} style={inp}>
                  <option value="">—</option><option value="1">Yes</option><option value="0">No</option>
                </select>
              </Field>
            </TwoCol>
            <Field label="Referred on">
              <select value={injuryForm.referral} onChange={e => setInjuryForm(f => ({ ...f, referral: e.target.value }))} style={inp}>
                <option value="">—</option>
                <option value="none">Not referred</option>
                <option value="parent">Handed over to parent or guardian</option>
                <option value="gp">GP</option>
                <option value="ae_self">A&amp;E, made their own way</option>
                <option value="ambulance">Ambulance called</option>
                <option value="admitted">Admitted to hospital</option>
              </select>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                If an ambulance was called or you advised medical attention, we will ask you in 48 hours whether they were admitted — that is the question the RFU's reporting duty turns on, and it cannot be answered today.
              </div>
            </Field>
          </Sect>

          <Sect title="Who should be told" subtitle="Nobody is told unless you tick them. Each person ticked is a decision recorded against your name.">
            {!injuryForm.rosterPlayerId && <div style={{ fontSize: 13, color: "#9ca3af" }}>Choose a player first.</div>}

            {chosen && knownContacts.length === 0 && (
              <div style={{ fontSize: 13, color: "#9ca3af" }}>This player has no contacts on their record.</div>
            )}
            {chosen && knownContacts.map(c => (
              <label key={c.email} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", fontSize: 14, borderBottom: "1px solid #f3f4f6" }}>
                <input type="checkbox" checked={injuryForm.selectedContacts.includes(c.email)} onChange={() => toggleContact(c.email)} />
                <span style={{ flex: 1 }}>{c.name || c.email} <span style={{ color: "#9ca3af", fontSize: 12 }}>· {c.role}</span></span>
              </label>
            ))}

            {offRoster && (
              <>
                <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.55, marginBottom: 10 }}>
                  Leave blank to record the incident without telling anybody. Anyone you add here receives a link and nothing else — no name and no injury in the email, because nobody has checked this address.
                </div>
                {injuryForm.manualContacts.map((c, i) => (
                  <TwoCol key={i}>
                    <Field label={`Contact ${i + 1} name`}>
                      <input value={c.name} onChange={e => setInjuryForm(f => ({ ...f, manualContacts: f.manualContacts.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} style={inp} />
                    </Field>
                    <Field label="Email">
                      <input type="email" value={c.email} onChange={e => setInjuryForm(f => ({ ...f, manualContacts: f.manualContacts.map((x, j) => j === i ? { ...x, email: e.target.value } : x) }))} style={inp} />
                    </Field>
                  </TwoCol>
                ))}
                <button type="button" onClick={() => setInjuryForm(f => ({ ...f, manualContacts: [...f.manualContacts, { name: "", email: "" }] }))}
                  style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #bcd9c8", borderRadius: 6, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  + Another contact
                </button>
              </>
            )}
          </Sect>

          <Sect title="Consent">
            {/* The wording used to be fixed: "A parent or guardian has
                agreed to this injury being recorded and shared with the
                people ticked above." Two things were wrong with that.

                It named a parent even for an adult — an injury to a
                28-year-old prop, or to a spectator, is that person's own
                to consent to, and asking staff to attest that somebody's
                parent agreed is asking them to tick something untrue.

                And it claimed a sharing decision that may not have been
                made. With nobody ticked, the sentence attested to a
                disclosure that was not happening. Now it says only what
                is actually being done. */}
            {(() => {
              const shareCount = offRoster
                ? injuryForm.manualContacts.filter(c => c.email.trim()).length
                : injuryForm.selectedContacts.length;
              const chosenPlayer = !offRoster && injuryForm.rosterPlayerId
                ? rosterList.find(r => r.id === injuryForm.rosterPlayerId) : null;
              // A minor unless we positively know otherwise.
              const isAdult = offRoster
                ? injuryForm.subjectIsMinor === false
                : (chosenPlayer ? chosenPlayer.age_grade === "Adult" : false);
              const who = isAdult ? "The player" : "A parent or guardian";
              return (
                <>
                  {/* Recording. Always asked, because writing a real
                      person's injury down is the thing being agreed to. */}
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
                    <input type="checkbox" style={{ marginTop: 3 }} checked={injuryForm.consentGiven}
                      onChange={e => setInjuryForm(f => ({ ...f, consentGiven: e.target.checked }))} />
                    <span>{who} has agreed to this injury being <strong>recorded</strong>.</span>
                  </label>

                  {/* Disclosure. A separate decision, asked only when one
                      is actually being made, and asked again on every
                      later share — the consent given today covers the
                      people named today, not somebody added next month. */}
                  {shareCount > 0 && (
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#374151", lineHeight: 1.6, marginTop: 12, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                      <input type="checkbox" style={{ marginTop: 3 }} checked={injuryForm.shareConsentGiven}
                        onChange={e => setInjuryForm(f => ({ ...f, shareConsentGiven: e.target.checked }))} />
                      <span>
                        {who} has also agreed to it being <strong>sent to the {shareCount}{" "}
                        {shareCount === 1 ? "person" : "people"} ticked above</strong>.
                      </span>
                    </label>
                  )}
                </>
              );
            })()}
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#6b7280", marginTop: 10 }}>
              <input type="checkbox" checked={injuryForm.isTest} onChange={e => setInjuryForm(f => ({ ...f, isTest: e.target.checked }))} />
              This is a test entry, not a real person
            </label>
          </Sect>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 32 }}>
            <button type="button" onClick={() => { setInjuryForm(blankInjuryForm()); setInjurySearch(""); setView("dashboard"); }}
              style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              Cancel
            </button>
            <button type="submit" disabled={injurySaving}
              style={{ background: injurySaving ? "#9ca3af" : "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: injurySaving ? "default" : "pointer", fontFamily: "inherit" }}>
              {injurySaving ? "Saving…" : "Save injury"}
            </button>
          </div>
        </form>
        {/* "Leave without saving?", asked by back and swipe back, as on the
            concussion form. */}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            danger={confirmAction.danger}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </AppShell>
    );
  }

  // ── FIRST AID LOG ─────────────────────────────────────────────────
  // The accident book. One register covering both kinds of incident,
  // because "your first aid records for last season" is a single
  // question and answering it with two files is answering it badly.
  //
  // A concussion appears here as a line and nothing more: that a
  // protocol was started and how it ended. Its symptoms and staff notes
  // stay in the record itself. The server enforces that — this screen
  // never receives them — but the shape is the same on both sides so
  // that what is on screen is what comes out of the button.
  // ── INJURY RECORD ─────────────────────────────────────────────
  // Everything the server already allowed and nothing reached: the
  // hospital question the app asks and then gave you nowhere to answer,
  // what it turned out to be, and when they came back. Without this the
  // "follow-up due" badge pointed at a dead end and days lost could
  // never be filled in, because it derives from a return date only the
  // API could set.
  if (view === "injury_detail") {
    const inj = injuries.find(i => i.id === selectedInjuryId);
    if (!inj) {
      return (
        <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout}
          onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
          pageTitle="Injury record"
          pageActions={<button onClick={() => setView("first_aid_log")} style={{ fontSize: 13, fontWeight: 600, color: "#7a95ad", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← First Aid Log</button>}>
          <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, color: "#6b7280" }}>
            That record is no longer in the list. It may have been erased or archived.
          </div>
        </AppShell>
      );
    }

    const followupOutstanding = inj.followupDueAt && !inj.followupAnsweredAt;
    const row = (k, v) => v ? (
      <div key={k} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13.5 }}>
        <div style={{ width: 150, flexShrink: 0, color: "#6b7280" }}>{k}</div>
        <div style={{ color: "#111827" }}>{v}</div>
      </div>
    ) : null;

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout}
        onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
        pageTitle="Injury record"
        pageActions={<button onClick={() => setView("first_aid_log")} style={{ fontSize: 13, fontWeight: 600, color: "#7a95ad", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← First Aid Log</button>}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
            <h2 style={{ margin: 0, fontSize: 22, color: "#14331f" }}>{inj.subjectName}</h2>
            {inj.onRoster === false && <Badge color="#92400e">not on roster</Badge>}
            {inj.isTest && <Badge color="#6b21a8">test entry</Badge>}
          </div>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 18 }}>
            {[formatDate(inj.incidentDate), inj.incidentTime, inj.team || inj.ageGrade].filter(Boolean).join(" · ")}
          </div>

          {/* The one question the app chases. Answerable here, which is
              the whole reason this page exists. */}
          {followupOutstanding && (
            <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: "#92400e", fontSize: 15, marginBottom: 6 }}>Follow-up due</div>
              <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "#78350f", lineHeight: 1.6 }}>
                Was {inj.subjectName} admitted to hospital? Admission is the RFU's reporting trigger and
                cannot be known on the day, which is why we ask now rather than at the time.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => answerInjuryFollowup(inj.id, true)}
                  style={{ background: "#c9991a", color: readableOn("#c9991a"), border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Yes — admitted
                </button>
                <button onClick={() => answerInjuryFollowup(inj.id, false)}
                  style={{ background: "#fff", color: "#92400e", border: "1px solid #c9991a", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  No
                </button>
              </div>
            </div>
          )}

          {injuryGuidance && (
            <div style={{ background: "#fdf0f0", border: "2px solid #c0272d", borderRadius: 10, padding: 18, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: "#8a1a1a", fontSize: 15, marginBottom: 6 }}>Reportable Injury Event</div>
              <p style={{ margin: "0 0 10px", fontSize: 13.5, color: "#7a1f1f", lineHeight: 1.65 }}>{injuryGuidance}</p>
              <button onClick={() => setInjuryGuidance(null)}
                style={{ background: "none", border: "none", color: "#8a1a1a", fontSize: 13, fontWeight: 600, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                Dismiss
              </button>
            </div>
          )}

          <Sect title="What happened">
            {row("Activity", label(INJURY_ACTIVITY, inj.activity))}
            {row("Body part", inj.bodyPart)}
            {row("What it looked like", inj.nature === "other" && inj.natureOther ? `Other — ${inj.natureOther}` : inj.nature)}
            {row("Contact", inj.contact === null ? null : (inj.contact ? "Contact" : "Non-contact"))}
            {row("Carried on", label(INJURY_CARRIED_ON, inj.carriedOn))}
            {row("Description", inj.description)}
          </Sect>

          <Sect title="What was done">
            {row("First aid given", inj.treatment)}
            {row("Advice given", label(INJURY_ADVICE, inj.adviceGiven))}
            {row("Advice accepted", inj.adviceAccepted === null ? null : (inj.adviceAccepted ? "Yes" : "No"))}
            {row("Referred on", label(INJURY_REFERRAL, inj.referral))}
            {row("Admitted to hospital", inj.hospitalAdmitted === null ? null : (inj.hospitalAdmitted ? "Yes" : "No"))}
          </Sect>

          {/* Everything that can only be known later. */}
          <Sect title="Follow-up">
            <Field label="What it turned out to be">
              <input value={injuryEdit.confirmedDiagnosis}
                onChange={e => setInjuryEdit(f => ({ ...f, confirmedDiagnosis: e.target.value }))}
                placeholder="e.g. Grade 2 ankle sprain, confirmed by GP" style={inp} />
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                The original entry records what was seen on the day. This is the diagnosis, if one comes back.
              </div>
            </Field>
            <Field label="First aid given (update)">
              <input value={injuryEdit.treatment}
                onChange={e => setInjuryEdit(f => ({ ...f, treatment: e.target.value }))}
                placeholder="e.g. Ice and elevation" style={inp} />
            </Field>
            <Field label="Date they returned to play">
              <input type="date" value={injuryEdit.returnedAt}
                onChange={e => setInjuryEdit(f => ({ ...f, returnedAt: e.target.value }))} style={inp} />
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
                {inj.daysLost !== null && inj.daysLost !== undefined
                  ? `${inj.daysLost} day${inj.daysLost === 1 ? "" : "s"} lost so far.`
                  : "Days lost is worked out from this, and stays blank until it is filled in."}
              </div>
            </Field>
            <button onClick={() => saveInjuryEdit(inj.id)} disabled={injuryEditSaving}
              style={{ background: "var(--org-accent, #1a4a2e)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: injuryEditSaving ? "default" : "pointer", opacity: injuryEditSaving ? 0.6 : 1, fontFamily: "inherit" }}>
              {injuryEditSaving ? "Saving…" : "Save follow-up"}
            </button>
          </Sect>

          {/* What makes holding this lawful. Recorded since the module
              shipped, but never shown anywhere — so the one question
              anybody asks of a record like this ("who agreed to it?")
              could only be answered from the database. */}
          <Sect title="Consent and retention">
            {row("Consent given by", inj.consentGivenBy)}
            {row("Consent given", inj.consentGivenAt ? formatDate(inj.consentGivenAt) : null)}
            {row("Wording version", inj.consentVersion)}
            {row("Kept until", inj.retentionUntil ? formatDate(inj.retentionUntil) : null)}
            {row("Why that date", label(RETENTION_BASIS, inj.retentionBasis))}
            {!inj.consentGivenBy && !inj.consentGivenAt && (
              <div style={{ fontSize: 13.5, color: "#6b7280" }}>
                {inj.isTest
                  ? "A test entry. Test entries do not concern a real person and are not consented."
                  : "No consent is recorded against this entry."}
              </div>
            )}
          </Sect>

          {/* Who was told, and telling somebody else. Both endpoints
              existed on the server and neither was reachable, so an
              injury could be logged and never shared, and nobody could
              see who had been. */}
          <Sect title="Who was told">
            {injuryRecipients.length === 0 ? (
              <div style={{ fontSize: 13.5, color: "#6b7280" }}>
                Nobody has been told about this record.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {injuryRecipients.map((r, i) => (
                  <div key={`${r.contact_email || r.organisation_id}-${i}`}
                    style={{ padding: "7px 0", borderBottom: "1px solid #f3f4f6" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13.5 }}>
                      <span style={{ color: "#111827" }}>{r.contact_name || r.contact_email || r.organisation_id}</span>
                      {r.role && <span style={{ fontSize: 12, color: "#6b7280" }}>· {r.role}</span>}
                      <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
                        {r.withheld
                          ? "held back"
                          : r.shared_at ? formatDate(r.shared_at) : ""}
                      </span>
                    </div>
                    {/* Each disclosure carries its own consent. Showing it
                        per person is the point: one agreement covers the
                        people named at the time, not whoever is added later. */}
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                      {r.share_consent_at
                        ? `Consent to share recorded ${formatDate(r.share_consent_at)}`
                        : "No consent to share recorded against this disclosure"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Sect>

          <Sect title="Tell somebody else">
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
              Nobody is told unless you choose them here. Each person is a separate disclosure,
              recorded against your name with its own consent — the agreement given when this was
              logged covered the people named then.
            </p>
            {(() => {
              const rp = inj.rosterPlayerId ? rosterList.find(r => r.id === inj.rosterPlayerId) : null;
              const told = new Set(injuryRecipients.map(r => String(r.contact_email || "").toLowerCase()));
              const contacts = (rp ? [
                { email: rp.parent_email, name: rp.parent_name, role: "parent" },
                { email: rp.school_coach_email, name: rp.school_coach_name, role: "school coach" },
                { email: rp.school_coach_2_email, name: rp.school_coach_2_name, role: "school coach" },
                { email: rp.club_coach_email, name: rp.club_coach_name, role: "club coach" },
                { email: rp.club_coach_2_email, name: rp.club_coach_2_name, role: "club coach" },
                { email: rp.other_contact_1_email, name: rp.other_contact_1_name, role: "additional contact" },
                { email: rp.other_contact_2_email, name: rp.other_contact_2_name, role: "additional contact" },
              ] : []).filter(c => c.email && !told.has(String(c.email).toLowerCase()));

              const chosenCount = shareSelection.length + (shareManual.email.trim() ? 1 : 0);
              return (
                <>
                  {contacts.length > 0 && contacts.map(c => (
                    <label key={c.email} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: "#374151", padding: "6px 0", cursor: "pointer" }}>
                      <input type="checkbox" checked={shareSelection.includes(c.email)}
                        onChange={() => setShareSelection(sel =>
                          sel.includes(c.email) ? sel.filter(x => x !== c.email) : [...sel, c.email])} />
                      <span>{c.name || c.email} <span style={{ color: "#6b7280" }}>· {c.role}</span></span>
                    </label>
                  ))}
                  {rp && contacts.length === 0 && (
                    <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                      Everybody on this player's record has already been told.
                    </div>
                  )}
                  <div style={{ marginTop: 10, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                    <TwoCol>
                      <Field label="Somebody else — name">
                        <input value={shareManual.name} onChange={e => setShareManual(m => ({ ...m, name: e.target.value }))}
                          placeholder="e.g. Club physiotherapist" style={inp} />
                      </Field>
                      <Field label="Their email">
                        <input type="email" value={shareManual.email} onChange={e => setShareManual(m => ({ ...m, email: e.target.value }))}
                          placeholder="name@example.com" style={inp} />
                      </Field>
                    </TwoCol>
                  </div>
                  {chosenCount > 0 && (
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "#374151", lineHeight: 1.6, marginTop: 6, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                      <input type="checkbox" style={{ marginTop: 3 }} checked={shareConsent}
                        onChange={e => setShareConsent(e.target.checked)} />
                      <span>
                        {inj.ageGrade === "Adult" ? "The player" : "A parent or guardian"} has agreed to this injury
                        being <strong>sent to the {chosenCount} {chosenCount === 1 ? "person" : "people"} chosen above</strong>.
                      </span>
                    </label>
                  )}
                  <button onClick={() => shareInjuryNow(inj.id)} disabled={sharing || chosenCount === 0}
                    style={{ marginTop: 14, background: "var(--org-accent, #1a4a2e)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: (sharing || chosenCount === 0) ? "default" : "pointer", opacity: (sharing || chosenCount === 0) ? 0.5 : 1, fontFamily: "inherit" }}>
                    {sharing ? "Sending…" : "Share this record"}
                  </button>
                </>
              );
            })()}
          </Sect>

          <div style={{ fontSize: 12, color: "#9ca3af", margin: "18px 0 32px", lineHeight: 1.6 }}>
            Recorded {formatDate(inj.createdAt)}.
            {inj.retentionUntil ? ` Kept until ${formatDate(inj.retentionUntil)}.` : ""}
          </div>
        </div>
      </AppShell>
    );
  }

  if (view === "first_aid_log") {
    const CONCUSSION_OUTCOME = {
      cleared: "Completed the 21-day schedule",
      removed_early: "Removed early on medical advice",
    };

    // Test entries are never part of an accident book, and the server
    // drops them from the export regardless. Dropping them here too is
    // what keeps the count on the button equal to the rows in the file.
    // Archived records stay: filing a finished protocol away does not
    // remove it from the register it belongs in.
    const logRows = [
      ...injuries.filter(i => !i.isTest).map(i => ({
        kind: "injury",
        id: i.id,
        date: i.incidentDate || "",
        time: i.incidentTime || "",
        name: i.subjectName || "",
        team: i.team || "",
        ageGrade: i.ageGrade || "",
        onRoster: i.onRoster,
        what: [i.bodyPart, i.nature].filter(Boolean).join(" — ") || "Not recorded",
        // Only the returned case. daysLost is derived from returned_at
        // and is null without it, so a "days lost so far" branch here
        // could never run — and would be a lie if it did: nothing counts
        // days for somebody who has not come back yet.
        outcome: i.returnedAt
          ? `Returned ${formatDate(i.returnedAt)}${i.daysLost !== null && i.daysLost !== undefined ? ` — ${i.daysLost} day${i.daysLost === 1 ? "" : "s"} lost` : ""}`
          : "",
        followupDue: Boolean(i.followupDueAt && !i.followupAnsweredAt),
        // Surfaced on the row rather than left to the export. The page
        // calls itself the register you would hand an insurer, an
        // inspector or a governing body, and the first thing all three
        // ask is what happened next — which until now meant opening every
        // record or downloading the CSV to find out.
        activity: label(INJURY_ACTIVITY, i.activity) || "",
        // "Not referred" on every row is noise; the exceptions are the point.
        referral: i.referral && i.referral !== "none" ? label(INJURY_REFERRAL, i.referral) : "",
        admitted: i.hospitalAdmitted === true,
        // Null unless the record was shared with the family.
        familyAck: i.familyAcknowledgement || null,
        ackLive: !i.archived,
      })),
      ...players.filter(p => !p.is_test).map(p => ({
        kind: "concussion",
        id: p.id,
        date: p.incident_date || "",
        time: p.incident_time || "",
        name: p.name || "",
        team: p.team || "",
        ageGrade: p.age_grade || "",
        // Never badged either way — see the note in firstAidLogRows.
        // A missing roster link on a concussion means the record was
        // never tied to a registration row, not that the child is a
        // visitor, and the two read identically on a printed register.
        onRoster: null,
        what: "Suspected concussion",
        outcome: CONCUSSION_OUTCOME[p.status] || "In progress",
        followupDue: false,
        // A concussion has none of these: no referral was recorded, and
        // its context is captured on a different scale. Blank rather than
        // borrowed, so the column never asserts something that was not asked.
        activity: "",
        referral: "",
        admitted: false,
        familyAck: p.parent_email ? { sharedAt: p.created_at, acknowledgedAt: p.parent_acknowledged_at || null } : null,
        ackLive: p.status === "active" && !p.archived,
        ackWho: p.age_grade === "Adult" ? "Player" : "Parent",
      })),
    ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

    const logTeams = Array.from(new Set(logRows.map(r => r.team).filter(Boolean))).sort();
    const logQuery = logFilters.query.trim().toLowerCase();
    const shownRows = logRows.filter(r => {
      if (logFilters.type !== "all" && r.kind !== logFilters.type) return false;
      if (logFilters.team && r.team !== logFilters.team) return false;
      // Dates are stored and compared as YYYY-MM-DD strings, matching the
      // date inputs, so no Date parsing and no timezone comes into it.
      if (logFilters.from && r.date < logFilters.from) return false;
      if (logFilters.to && r.date > logFilters.to) return false;
      // The one filter that finds work rather than history.
      if (logFilters.followup && !r.followupDue) return false;
      if (!logQuery) return true;
      return r.name.toLowerCase().includes(logQuery)
        || r.team.toLowerCase().includes(logQuery)
        || r.ageGrade.toLowerCase().includes(logQuery)
        || r.what.toLowerCase().includes(logQuery);
    });

    // The register is two halves. Until both are in, it cannot describe
    // itself honestly — not the count on the buttons, and least of all
    // the empty state. Exporting is blocked outright while a half is
    // missing: a file that silently omits every injury is worse than no
    // file, because it looks complete to whoever receives it.
    const logStillLoading = loading || injuriesStatus === "loading";
    const logLoadFailed = injuriesStatus === "error";
    const logExportBlocked = logStillLoading || logLoadFailed;

    const logFiltered = Boolean(logQuery || logFilters.team || logFilters.from || logFilters.to || logFilters.type !== "all" || logFilters.followup);
    const logDescription = [
      logFilters.type === "injury" ? "injuries and first aid only" : logFilters.type === "concussion" ? "concussion protocols only" : null,
      logFilters.team && `team ${logFilters.team}`,
      logFilters.from && `from ${logFilters.from}`,
      logFilters.to && `to ${logFilters.to}`,
      logQuery && `matching "${logFilters.query.trim()}"`,
      logFilters.followup && "awaiting a follow-up answer",
    ].filter(Boolean).join(", ");

    async function downloadLog(format) {
      setLogExporting(true);
      try {
        const blob = await api.exportFirstAidLog({
          format,
          // Both lists go every time, filtered or not. Sending nothing
          // would mean "everything" to the server, so an unfiltered
          // screen and a filtered one would take different code paths —
          // and only one of them would be the one anybody tested.
          injuryIds: shownRows.filter(r => r.kind === "injury").map(r => r.id),
          concussionIds: shownRows.filter(r => r.kind === "concussion").map(r => r.id),
          filterDescription: logDescription || null,
        });
        notifySaved(await saveBlob(blob, `first-aid-log${logFiltered ? "-filtered" : ""}-${new Date().toISOString().split("T")[0]}.${format === "pdf" ? "pdf" : "csv"}`),
          `${shownRows.length} entr${shownRows.length === 1 ? "y" : "ies"} downloaded as ${format === "pdf" ? "PDF" : "CSV"}.`);
      } catch (e) {
        showNotif("Export failed: " + e.message, "error");
      } finally {
        setLogExporting(false);
      }
    }

    const fieldStyle = { padding: "8px 10px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit", background: "#fff", color: "#111827" };

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="First Aid Log"
        pageActions={(
          <button
            onClick={() => { setInjuryForm(blankInjuryForm()); setInjuryResult(null); if (!rosterList.length) loadRoster(); setView("new_injury"); }}
            style={ACTION_BTN_SECONDARY}
          >
            + Log an Injury
          </button>
        )}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
            Every incident recorded here, injuries and concussion protocols together — the register you would hand
            an insurer, an inspector or a governing body. Concussions appear as a line saying a protocol was started
            and how it ended; the symptoms and notes stay in the record itself and are never part of this log.
          </p>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 180px", minWidth: 150 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#7a95ad", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Search</label>
                <input value={logFilters.query} onChange={e => setLogFilters(f => ({ ...f, query: e.target.value }))}
                  placeholder="Name, team, injury…" style={{ ...fieldStyle, width: "100%", boxSizing: "border-box" }} />
              </div>
              {/* A pair with their own width, as on the dashboard. Each in a
                  bare wrapper, an empty iPhone date box (width 100% of
                  nothing) shrank to a sliver on a phone. */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flex: "1 1 240px", maxWidth: 360 }}>
                {[["From", "from"], ["To", "to"]].map(([label, key]) => (
                  <div key={key} style={{ minWidth: 0 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#7a95ad", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</label>
                    <input type="date" value={logFilters[key]} onChange={e => setLogFilters(f => ({ ...f, [key]: e.target.value }))}
                      style={{ ...fieldStyle, minHeight: 36, color: logFilters[key] ? "#111827" : "#6b7280" }} />
                  </div>
                ))}
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#7a95ad", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Team</label>
                <select value={logFilters.team} onChange={e => setLogFilters(f => ({ ...f, team: e.target.value }))} style={fieldStyle}>
                  <option value="">All teams</option>
                  {logTeams.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#7a95ad", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Type</label>
                <select value={logFilters.type} onChange={e => setLogFilters(f => ({ ...f, type: e.target.value }))} style={fieldStyle}>
                  <option value="all">Everything</option>
                  <option value="injury">Injuries and first aid</option>
                  <option value="concussion">Concussion protocols</option>
                </select>
              </div>
              {/* The only filter that finds work rather than history: the
                  records where the app asked whether somebody was admitted
                  to hospital and is still waiting for the answer. That is
                  what you want before an inspection, not after it. */}
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: "#374151", cursor: "pointer", paddingBottom: 8, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={logFilters.followup}
                    onChange={e => setLogFilters(f => ({ ...f, followup: e.target.checked }))} />
                  Needs a follow-up
                </label>
              </div>
              {logFiltered && (
                <button onClick={() => setLogFilters({ from: "", to: "", team: "", query: "", type: "all", followup: false })}
                  style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: "9px 4px" }}>
                  Clear filters
                </button>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
              <div style={{ fontSize: 12.5, color: "#6b7280", flex: 1, minWidth: 200 }}>
                {/* The buttons name the number they will produce, and that
                    number is the length of the list above them. Anything
                    vaguer invites somebody to send an insurer a file they
                    never actually looked at. Which also means saying
                    nothing at all until the list is really the list. */}
                {logStillLoading ? "Loading…"
                  : logLoadFailed ? "Export unavailable until the full register loads."
                  : logFiltered
                    ? <>Showing <strong>{shownRows.length}</strong> of {logRows.length} entries ({logDescription}).</>
                    : <>Showing all <strong>{logRows.length}</strong> entries.</>}
              </div>
              {["csv", "pdf"].map(fmt => {
                const disabled = logExporting || logExportBlocked || shownRows.length === 0;
                return (
                  <button key={fmt} onClick={() => downloadLog(fmt)} disabled={disabled}
                    title={logExportBlocked ? "The register is not fully loaded yet." : undefined}
                    style={{ background: "#fff", color: disabled ? "#9ca3af" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                    {fmt === "csv" ? "📊 CSV" : "📄 PDF"}
                    {logExportBlocked ? "" : ` — ${shownRows.length} ${shownRows.length === 1 ? "entry" : "entries"}`}
                  </button>
                );
              })}
            </div>
          </div>

          {logLoadFailed && (
            <div style={{ background: "#fffbeb", border: "1px solid #f0d88a", borderRadius: 10, padding: "14px 16px", color: "#92400e", fontSize: 13.5, lineHeight: 1.6, marginBottom: 14 }}>
              <strong>This register is incomplete.</strong> The injury records could not be loaded, so the list
              below is the concussion protocols only. Reload the page before exporting — do not hand this on
              as a full record.
            </div>
          )}

          {/* "Nothing recorded yet" is a statement about the club, and on
              a register that is not a throwaway line — somebody checking
              whether an incident was ever written up would take it as the
              answer. So it is only said once both halves are actually in,
              and never when one of them failed to arrive. */}
          {logStillLoading ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
              Loading the register…
            </div>
          ) : shownRows.length === 0 ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
              {logRows.length === 0
                ? "Nothing recorded yet. Injuries and concussion protocols will both appear here."
                : "No entries match these filters."}
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
              {shownRows.map(r => (
                <div key={`${r.kind}-${r.id}`}
                  onClick={() => { if (r.kind === "concussion") { setSelectedId(r.id); setView("detail"); } else { openInjury(r.id); } }}
                  style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}>
                  <div style={{ width: 88, flexShrink: 0, fontSize: 12.5, color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                    {r.date ? formatDate(r.date) : "No date"}
                    {r.time && <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{r.time}</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, color: "#111827" }}>
                      {r.name}
                      {r.onRoster === false && <span style={{ fontSize: 11, color: "#92400e", background: "#fef9ec", border: "1px solid #f0d88a", borderRadius: 4, padding: "1px 6px", marginLeft: 8, whiteSpace: "nowrap", display: "inline-block" }}>not on roster</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[r.what, r.team || r.ageGrade, r.activity, r.referral, r.outcome].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {/* Stacked on a phone (grtp-log-tags), so two or three
                      tags don't squeeze the name into a column one word wide. */}
                  <div className="grtp-log-tags" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {r.admitted && (
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: "#8a1a1a", background: "#fdf0f0", border: "1px solid #f0c9c9", borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
                      admitted
                    </span>
                  )}
                  {r.followupDue && (
                    <span style={{ fontSize: 11.5, color: "#92400e", background: "#fef9ec", border: "1px solid #f0d88a", borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>
                      follow-up due
                    </span>
                  )}
                  <span style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, borderRadius: 4, padding: "3px 8px",
                    color: r.kind === "concussion" ? "#8a1a1a" : "#1a4a2e",
                    background: r.kind === "concussion" ? "#fdf0f0" : "#f0f6f2",
                    border: `1px solid ${r.kind === "concussion" ? "#f0c9c9" : "#cfe2d6"}` }}>
                    {r.kind === "concussion" ? "Concussion" : "First aid"}
                  </span>
                  </div>
                  {/* The family's read confirmation, on its own line under
                      the name (lined up past the date column). Beside the
                      tags it took the width the name needed: on a phone
                      the name wrapped and the description was cut short. */}
                  {familyAckState(r.familyAck, { live: r.ackLive }) && (
                    <div style={{ flexBasis: "100%", paddingLeft: 100, marginTop: -6 }}>
                      <AckBadge ack={r.familyAck} live={r.ackLive} who={r.ackWho || "Family"} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── ACTIVITY LOG ──────────────────────────────────────────────────
  if (view === "activity") {
    const ACTION_LABELS = {
      concussion_logged: (d) => `Logged a concussion for ${d?.playerName || "a player"}`,
      symptom_return: (d) => `Recorded symptom return for ${d?.playerName || "a player"}`,
      player_cleared: (d) => `${d?.playerName || "A player"} was cleared (Day 21)${d?.automatic ? " — automatic" : ""}`,
      removed_early: (d) => `${d?.playerName || "A player"} was removed from the protocol early — ${d?.reason || "no reason recorded"}`,
      age_grades_advanced: (d) => `Advanced age grades for the new season — ${d?.advanced ?? 0} player${d?.advanced !== 1 ? "s" : ""} moved up, ${d?.archived ?? 0} archived (aged out)`,
      age_grades_kept: () => "Kept age grades the same for the new season",
      staff_invited: (d) => `Invited ${d?.invitedEmail || "a new staff member"}`,
      staff_removed: (d) => `Removed staff member ${d?.removedEmail || ""}`,
      staff_promoted: (d) => `Made ${d?.staffEmail || "a staff member"} an admin`,
      staff_demoted: (d) => `Removed ${d?.staffEmail || "a staff member"}'s admin access`,
      player_permanently_deleted: (d) => `Permanently deleted the record for ${d?.playerName || "a player"}`,
      all_data_cleared: (d) => `Cleared all data (${d?.deletedCount ?? "?"} record${d?.deletedCount !== 1 ? "s" : ""})`,
      roster_cleared: (d) => `Cleared the entire roster (${d?.deletedCount ?? "?"} player${d?.deletedCount !== 1 ? "s" : ""})`,
      roster_age_grade_cleared: (d) => `Removed all ${d?.ageGrade || ""} players from the roster (${d?.deletedCount ?? "?"})`,
      // Injuries and first aid. injury_shared is the one that matters
      // most here: it is the answer to "who was told about what happened
      // to my child?", and an admin should not have to read
      // "injury_shared" and guess. Unlabelled actions fall back to their
      // raw name, which is no answer at all.
      injury_logged: (d) =>
        `Recorded an injury for ${d?.subjectName || "somebody"}${d?.nature ? ` (${d.nature})` : ""}` +
        (d?.onRoster === false ? " — not on the roster" : ""),
      injury_shared: (d) =>
        `Shared ${d?.subjectName ? `${d.subjectName}'s` : "an"} injury record with ${d?.with || "nobody on file"}`,
      first_aid_log_exported: (d) =>
        `Exported the first aid log — ${d?.rowCount ?? "?"} entr${d?.rowCount === 1 ? "y" : "ies"}` +
        (d?.filter ? ` (${d.filter})` : ", the whole register"),
      // Data protection actions. These are the entries a DPO or an
      // inspector is most likely to ask about, so they say plainly who
      // took a copy of what, and what an erasure actually removed.
      organisation_export: () => "Exported all of this organisation's data",
      subject_access_export: (d) => `Exported all data held about ${d?.playerName || "a player"} (subject access request)`,
      sar_export: (d) => `Exported the full record for ${d?.playerName || "a player"} (subject access request)`,
      data_subject_erasure: (d) =>
        `Erased all data for ${d?.playerName || "a player"} on request` +
        (d?.concussionRecordsRetained
          ? ` — ${d.concussionRecordsRetained} concussion record${d.concussionRecordsRetained !== 1 ? "s" : ""} retained under safeguarding rules`
          : ""),

      // Registrations and contacts. These were the largest group writing
      // raw slugs into the log: approving a registration is one of the
      // commonest admin actions there is, and it read "registration_approved".
      registration_approved: (d) => `Approved ${d?.playerName || "a player"}'s registration onto the roster`,
      registrations_bulk_approved: (d) =>
        `Approved ${d?.count ?? "?"} registration${d?.count !== 1 ? "s" : ""} at once` +
        (d?.playerNames?.length ? ` — ${d.playerNames.join(", ")}` : ""),
      registration_rejected: (d) =>
        `Rejected ${d?.playerName || "a player"}'s registration` + (d?.notes ? ` — ${d.notes}` : "") +
        // Older entries carry no parentNotified at all; say nothing for them.
        (d?.parentNotified === true
          ? ". The parent was emailed" + (() => {
              const r = PARENT_REJECTION_REASONS.find(o => o.value === d.parentReason);
              return r && r.value !== "none" ? ` (reason: ${r.short.toLowerCase()}).` : ".";
            })()
          : d?.parentNotified === false ? ". The parent was not emailed." : ""),
      staff_registration_approved: (d) => `Approved staff access for ${d?.email || "a coach"}`,
      staff_registration_rejected: (d) => `Rejected the staff access request from ${d?.email || "a coach"}`,
      email_bounce_resolved: (d) => `Recorded contacting ${d?.role || "someone"} directly about ${d?.playerName || "a player"}, after an email bounced`,
      nominated_contact_confirmed: (d) =>
        `${d?.contactEmail || "A nominated contact"} confirmed they are happy to be contacted` +
        (d?.playerName ? ` about ${d.playerName}` : ""),
      parent_acknowledged: (d) =>
        `The family confirmed reading the ${d?.kind === "injury" ? "injury record" : "concussion update"}` +
        (d?.playerName ? ` for ${d.playerName}` : "") +
        ({ parent_portal: " (in the parent portal)", tracking_link: " (on their tracking link)", injury_link: " (on their record link)" }[d?.via] || ""),
      nominated_contact_removed: (d) =>
        `${d?.contactEmail || "A nominated contact"} asked to be removed` +
        (d?.playersCleared
          ? ` — taken off ${d.playersCleared} player${d.playersCleared !== 1 ? "s" : ""}`
          : ""),
      player_link_approved: (d) => `Linked ${d?.playerName || "a player"} to a parent's portal account`,
      player_link_rejected: (d) => `Rejected a request to link ${d?.playerName || "a player"} to a parent account`,
      player_added_from_nomination: (d) => `Added ${d?.playerName || "a player"} to the roster from a contact's nomination`,
      parent_change_approved: (d) =>
        `Approved a parent's requested change to ${d?.playerName || "a player"}'s details` +
        (d?.changeType ? ` (${d.changeType})` : ""),
      parent_change_rejected: (d) =>
        `Rejected a parent's requested change to ${d?.playerName || "a player"}'s details` +
        (d?.changeType ? ` (${d.changeType})` : ""),
      bulk_contact_replace: (d) =>
        `Replaced ${d?.field ? `the ${d.field.replace(/_/g, " ")}` : "a contact detail"} across the roster` +
        (d?.count ? ` — ${d.count} player${d.count !== 1 ? "s" : ""} updated` : ""),
      duplicate_protocol_override: (d) =>
        `Logged a second concussion for ${d?.playerName || "a player"} while one was already running` +
        (d?.reason ? ` — ${d.reason}` : ""),
      branding_updated: () => "Updated the organisation's colour and logo",
      linked_org_started_parallel_protocol: (d) =>
        `${d?.byOrganisation || "Another organisation"} started a concussion protocol for ${d?.playerName || "a shared player"} while yours was already running` +
        (d?.reason ? ` — ${d.reason}` : ""),

      // Billing. Written by the Stripe webhook rather than by a person,
      // so these carry no user email — the log shows them against the
      // organisation itself.
      stripe_subscription_activated: (d) =>
        `Subscription activated${d?.planKey ? ` on the ${d.planKey} plan` : ""}`,
      stripe_price_changed: (d) =>
        `Plan changed${d?.planKey ? ` to ${d.planKey}` : ""}`,
      stripe_subscription_cancelled: () => "Subscription cancelled",

      // Getting in, and anything that changes how getting in works. These
      // are the entries a DPO asks for first, and the log held none of
      // them until now — an account could be entered, or have its second
      // factor switched off, leaving no trace anywhere in the product.
      login_succeeded: (d) =>
        d?.method === "app_device"
          ? `Opened the phone app (${d?.platform === "ios" ? "iPhone" : d?.platform === "android" ? "Android" : "phone"}), signed in on that device`
          : `Signed in${d?.mfa ? " with two-factor" : ""}` +
            (d?.usedBackupCode ? " using a backup code, not the authenticator app" : ""),
      login_failed: (d) =>
        `Failed sign-in attempt — ${d?.reason === "mfa_code" ? "wrong two-factor code" : "wrong password"}`,
      sign_in_new_device: (d) =>
        `Signed in on ${d?.device || "a new device"} for the first time` +
        (d?.emailed === false ? " (the check-it-was-you email didn't send)" : ", and was emailed to check it was them"),
      mfa_enabled: () => "Turned on two-factor authentication",
      mfa_disabled: () => "Turned OFF two-factor authentication",
      mfa_reset_by_support: () => "Two-factor authentication was reset by GRTP support",
      password_reset_completed: () => "Completed a password reset — all other sessions were signed out",
      app_device_registered: (d) => `Set up the phone app to stay signed in on ${d?.platform === "ios" ? "an iPhone" : d?.platform === "android" ? "an Android phone" : "a phone"}`,
      app_device_revoked: (d) => d?.by === "admin" ? "An admin signed a phone out of the app remotely" : "Signed a phone out of the app",
      app_device_token_reused: () => "A phone's stay-signed-in key was used twice, so that phone was signed out as a precaution",
    };

    // A label for every action the backend writes. If one is ever missed,
    // show something a person can read rather than the database's own
    // snake_case: "Registration approved" beats "registration_approved".
    function describeEntry(entry) {
      const fn = ACTION_LABELS[entry.action];
      if (fn) return fn(entry.details);
      const words = String(entry.action || "").replace(/_/g, " ").trim();
      return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Unrecorded action";
    }

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Activity Log">
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 20 }}>
            A record of who's done what — the most recent 200 actions, newest first.
          </p>

          {activityLogLoading ? (
            <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>Loading...</div>
          ) : !activityLog || activityLog.length === 0 ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
              No activity recorded yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {activityLog.map((entry) => (
                <div key={entry.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 14, color: "#111827" }}>{describeEntry(entry)}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
                      {entry.userEmail || "System (automatic)"}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>
                    {new Date(entry.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── ANALYTICS ────────────────────────────────────────────────────
  if (view === "analytics") {
    const CONTEXT_LABELS  = { match: "Match play", training_contact: "Contact training", training_non_contact: "Non-contact training", other: "Other / unknown" };
    const CONTEXT_COLORS  = { match: "#c0272d", training_contact: "#c9991a", training_non_contact: "#1a7a4a", other: "#6b7280" };
    const MECHANISM_LABELS = { tackle_bc: "Tackle (ball carrier)", tackle_tackler: "Tackle (tackler)", ruck_maul: "Ruck / maul", scrum: "Scrum", collision: "Player collision", fall: "Fall / ground contact", other: "Other" };
    const MECHANISM_COLORS = { tackle_bc: "#c0272d", tackle_tackler: "#c9421a", ruck_maul: "#c9991a", scrum: "#1a3a8a", collision: "#7a1a8a", fall: "#1a7a4a", other: "#6b7280" };
    const AGE_GRADE_ORDER  = ["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult","Unknown"];
    const ad = analyticsData;

    const StatCard = ({ label, value, sub, accent = "#1a4a2e", icon }) => (
      <div className="grtp-stat" style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "20px 22px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#7a95ad", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
          {icon && <span style={{ fontSize: 18, opacity: 0.6 }}>{icon}</span>}
        </div>
        <div style={{ fontSize: 34, fontWeight: 800, color: accent, lineHeight: 1.1, letterSpacing: "-0.02em" }}>{value ?? "—"}</div>
        {sub && <div style={{ fontSize: 11.5, color: "#9ca3af" }}>{sub}</div>}
      </div>
    );

    const HorizBar = ({ label, count, total, color = "#1a4a2e", max }) => {
      const pct = max > 0 ? (count / max) * 100 : 0;
      const share = total > 0 ? Math.round((count / total) * 100) : 0;
      return (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#374151" }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{share}%</span>
              <span style={{ fontSize: 14, fontWeight: 800, color, minWidth: 24, textAlign: "right" }}>{count}</span>
            </div>
          </div>
          <div style={{ background: "#f0f4f8", borderRadius: 99, height: 10, overflow: "hidden" }}>
            <div style={{ background: color, width: `${pct}%`, height: "100%", borderRadius: 99, transition: "width 0.5s cubic-bezier(.4,0,.2,1)" }} />
          </div>
        </div>
      );
    };

    const ChartCard = ({ title, subtitle, children }) => (
      <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: "22px 24px", marginBottom: 16 }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{subtitle}</div>}
        </div>
        {children}
      </div>
    );

    const EmptyState = ({ msg }) => (
      <div style={{ textAlign: "center", padding: "28px 0", color: "#9ca3af", fontSize: 13 }}>
        <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📊</div>
        {msg}
      </div>
    );

    const formatMonth = (ym) => {
      const [y, m] = ym.split("-");
      return new Date(y, m - 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    };

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Analytics">
        {analyticsLoading ? (
          <div style={{ textAlign: "center", padding: 64, color: "#9ca3af" }}>
            <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.4 }}>📊</div>
            Loading analytics...
          </div>
        ) : !ad ? (
          <EmptyState msg="No concussion records yet. Data will appear here once incidents are logged." />
        ) : (
          <div style={{ maxWidth: 900 }}>
            <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
              Concussion data for {orgName}. Season runs August–July. Context and mechanism are optional fields captured at the time of logging.
            </p>

            {/* ── KPI row ── */}
            <div className="grtp-kpi" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
              <StatCard icon="🏉" label="Total concussions" value={ad.total} sub="all time" />
              <StatCard icon="📅" label="This season" value={ad.thisSeason} sub="Aug – present" accent="#1a4a2e" />
              <StatCard icon="⚡" label="Active protocols" value={ad.active} accent="#c9991a" />
              <StatCard icon="⏱" label="Avg days to clear" value={ad.avgDaysToClear ?? "—"} sub={ad.avgDaysToClear ? "completed protocols" : "none followed through to completion yet"} accent="#1a3a8a" />
            </div>
            <div className="grtp-kpi grtp-kpi-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
              <StatCard icon="↩️" label="Symptom returns" value={ad.symptomReturns} sub="protocols paused mid-course" accent="#c0272d" />
              {/* Context and mechanism are optional at logging time, so
                  these two have to separate "nobody has recorded one yet"
                  from "several are recorded and none of them was a match".
                  A dash means there is nothing to measure against; a zero
                  is a real count. Previously one tile said "—" for an
                  absent key while the other said "0" for an empty set, so
                  the same state read two different ways side by side. */}
              <StatCard icon="🏆" label="Match play"
                value={ad.byContext && Object.keys(ad.byContext).length ? (ad.byContext.match || 0) : "—"}
                sub={ad.byContext && Object.keys(ad.byContext).length ? "of logged contexts" : "no contexts recorded yet"} />
              <StatCard icon="💥" label="Tackle-related"
                value={ad.byMechanism && Object.keys(ad.byMechanism).length
                  ? (ad.byMechanism.tackle_bc || 0) + (ad.byMechanism.tackle_tackler || 0)
                  : "—"}
                sub={ad.byMechanism && Object.keys(ad.byMechanism).length ? "of logged mechanisms" : "no mechanisms recorded yet"} />
            </div>

            {/* ── Age grade ── */}
            <ChartCard title="By age group" subtitle="Which squads are most affected">
              {!ad.byAgeGrade || !Object.keys(ad.byAgeGrade).length ? (
                <EmptyState msg="No age grade data yet." />
              ) : (() => {
                const sorted = AGE_GRADE_ORDER
                  .filter(g => ad.byAgeGrade[g])
                  .map(g => [g, ad.byAgeGrade[g]])
                  .concat(Object.entries(ad.byAgeGrade).filter(([g]) => !AGE_GRADE_ORDER.includes(g)));
                const max = Math.max(...sorted.map(([,v]) => v));
                const total = sorted.reduce((s, [,v]) => s + v, 0);
                return sorted.map(([grade, count]) => (
                  <HorizBar key={grade} label={grade} count={count} total={total} max={max} color="#1a4a2e" />
                ));
              })()}
            </ChartCard>

            {/* ── Monthly trend ── */}
            {ad.byMonth && Object.keys(ad.byMonth).length > 0 && (
              <ChartCard title="Monthly trend" subtitle="Incidents per month across all time">
                {(() => {
                  const entries = Object.entries(ad.byMonth).sort();
                  const max = Math.max(...entries.map(([,v]) => v));
                  const total = entries.reduce((s, [,v]) => s + v, 0);
                  return entries.map(([month, count]) => (
                    <HorizBar key={month} label={formatMonth(month)} count={count} total={total} max={max} color="#c9991a" />
                  ));
                })()}
              </ChartCard>
            )}

            {/* ── Context ── */}
            <ChartCard
              title="Where it happened"
              subtitle={ad.byContext && Object.keys(ad.byContext).length ? `${Object.values(ad.byContext).reduce((a,b)=>a+b,0)} of ${ad.total} incidents have context recorded` : "No context data yet — select context when logging incidents to see this breakdown"}
            >
              {!ad.byContext || !Object.keys(ad.byContext).length ? (
                <EmptyState msg="No context recorded yet. Select 'Match play', 'Contact training' etc. when logging an incident." />
              ) : (() => {
                const entries = Object.entries(ad.byContext).sort((a,b) => b[1]-a[1]);
                const max = Math.max(...entries.map(([,v]) => v));
                const total = entries.reduce((s,[,v]) => s+v, 0);
                return entries.map(([key, count]) => (
                  <HorizBar key={key} label={CONTEXT_LABELS[key] || key} count={count} total={total} max={max} color={CONTEXT_COLORS[key] || "#6b7280"} />
                ));
              })()}
            </ChartCard>

            {/* ── Mechanism ── */}
            <ChartCard
              title="How it happened"
              subtitle={ad.byMechanism && Object.keys(ad.byMechanism).length ? `${Object.values(ad.byMechanism).reduce((a,b)=>a+b,0)} of ${ad.total} incidents have mechanism recorded` : "No mechanism data yet"}
            >
              {!ad.byMechanism || !Object.keys(ad.byMechanism).length ? (
                <EmptyState msg="No mechanism recorded yet. Select 'Tackle', 'Ruck/maul' etc. when logging an incident." />
              ) : (() => {
                const entries = Object.entries(ad.byMechanism).sort((a,b) => b[1]-a[1]);
                const max = Math.max(...entries.map(([,v]) => v));
                const total = entries.reduce((s,[,v]) => s+v, 0);
                return entries.map(([key, count]) => (
                  <HorizBar key={key} label={MECHANISM_LABELS[key] || key} count={count} total={total} max={max} color={MECHANISM_COLORS[key] || "#6b7280"} />
                ));
              })()}
            </ChartCard>

            <p style={{ fontSize: 12, color: "#7a95ad", marginTop: 8 }}>
              Data excludes test entries. Season defined as 1 August – 31 July. Analytics update in real time as incidents are logged.
            </p>
          </div>
        )}
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── MESSAGES ──────────────────────────────────────────────────────
  if (view === "messages") {
    const formatMsgTime = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    const myUserId = api.userId();

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Messages">
        {openThread ? (
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <button onClick={() => setOpenThread(null)} style={{ background: "none", border: "none", color: "#1a4a2e", fontWeight: 600, fontSize: 13, cursor: "pointer", marginBottom: 14, padding: 0, fontFamily: "inherit" }}>
              ← Back to all messages
            </button>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", marginBottom: 16 }}>{openThread.playerName}</div>

            {threadLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {openThread.messages.length === 0 && (
                  <div style={{ textAlign: "center", padding: 24, color: "#9ca3af", fontSize: 13 }}>No messages yet.</div>
                )}
                {openThread.messages.map(m => {
                  const isMine = m.senderType === "staff" && m.senderUserId === myUserId;
                  const isEditing = editingMessageId === m.id;
                  return (
                    <div key={m.id} style={{ alignSelf: m.senderType === "parent" ? "flex-start" : "flex-end", maxWidth: "80%" }}>
                      <div style={{ background: m.senderType === "parent" ? "#f8fafb" : "#f0f7f3", border: `1px solid ${m.senderType === "parent" ? "#e2eaf2" : "#b6d8c5"}`, borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>
                          {m.senderType === "parent" ? (m.senderName || "Parent/Guardian") : (isMine ? "You" : (m.senderName || "Staff"))}
                        </div>
                        {isEditing ? (
                          <div>
                            <textarea value={editingMessageText} onChange={e => setEditingMessageText(e.target.value)} rows={2} style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: 8, fontSize: 13.5, fontFamily: "inherit", resize: "vertical" }} />
                            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                              <button onClick={() => handleSaveEditedStaffMessage(m.id)} disabled={savingMessageEdit} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: savingMessageEdit ? "default" : "pointer", opacity: savingMessageEdit ? 0.7 : 1, fontFamily: "inherit" }}>{savingMessageEdit ? "Saving..." : "Save"}</button>
                              <button onClick={() => setEditingMessageId(null)} disabled={savingMessageEdit} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13.5, color: "#111827", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.body}</div>
                        )}
                        {m.attachments && m.attachments.length > 0 && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                            {m.attachments.map(att => (
                              <MessageAttachmentThumb key={att.id} attachmentId={att.id} onOpen={setMessageLightboxUrl} />
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                          <span>{formatMsgTime(m.createdAt)}{m.editedAt ? " (edited)" : ""}</span>
                          {isMine && !isEditing && (
                            <button onClick={() => { setEditingMessageId(m.id); setEditingMessageText(m.body); }} style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 10.5, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 10, padding: 14 }}>
              {messageImages.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {messageImages.map((img, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img src={img.dataUrl} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid #e2eaf2" }} />
                      <button
                        type="button" onClick={() => setMessageImages(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#c0272d", color: "#fff", border: "2px solid #fff", fontSize: 11, lineHeight: "16px", cursor: "pointer", padding: 0 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                value={messageComposeText}
                onChange={e => setMessageComposeText(e.target.value)}
                placeholder="Write a message to the parent/guardian…"
                rows={3}
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: 10, fontSize: 14, fontFamily: "inherit", resize: "vertical" }}
              />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                <label style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                  📎 Attach photo{messageImages.length > 0 ? ` (${messageImages.length}/${MAX_MESSAGE_IMAGES})` : ""}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleStaffMessageImageSelect} style={{ display: "none" }} />
                </label>
                <button
                  onClick={handleSendPlayerMessage}
                  disabled={messageSending || (!messageComposeText.trim() && messageImages.length === 0)}
                  style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: messageSending || (!messageComposeText.trim() && messageImages.length === 0) ? 0.6 : 1, fontFamily: "inherit" }}
                >
                  {messageSending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
            {messageLightboxUrl && (
              <div onClick={() => setMessageLightboxUrl(null)} style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, cursor: "zoom-out" }}>
                <img src={messageLightboxUrl} alt="" style={{ maxWidth: "92vw", maxHeight: "92vh", borderRadius: 8 }} />
              </div>
            )}
          </div>
        ) : (
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            {messageThreadsLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading…</div>
            ) : messageThreads.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>💬</div>
                No messages yet. Messages from parents and guardians will appear here.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {messageThreads.map(t => (
                  <button
                    key={t.threadId}
                    onClick={() => handleOpenMessageThread(t.threadId)}
                    style={{ textAlign: "left", background: t.unread ? "#f0f7f3" : "#fff", border: `1px solid ${t.unread ? "#1a4a2e" : "#e2eaf2"}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
                        {t.playerName}
                        {t.unread && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#c0272d", display: "inline-block", flexShrink: 0 }} />}
                      </div>
                      {t.latestMessage && <div style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{formatMsgTime(t.latestMessage.createdAt)}</div>}
                    </div>
                    {t.latestMessage && (
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.latestMessage.senderType !== "parent" && "You: "}{t.latestMessage.body}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── SECURITY (MFA) ───────────────────────────────────────────────
  if (view === "dataprotection") {
    const shellProps = {
      view, setView, orgName, isOrgAdmin, pendingRegCount: registrationsWaiting, onLogout: handleLogout,
      onOpenRoster: handleOpenRoster, onOpenStaff: handleOpenStaffManagement,
      onOpenActivity: () => { setView("activity"); loadActivityLog(); },
      onOpenSecurity: () => { setView("security"); loadMfaStatus(); },
      onOpenRegistrations: () => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); },
      onOpenAnalytics: () => { setView("analytics"); loadAnalytics(); },
      onOpenMessages: () => { setView("messages"); loadMessageThreads(); },
      onOpenDataProtection: handleOpenDataProtection,
      messagesUnreadCount, pageTitle: "Data Protection",
    };

    const query = dpSearch.trim().toLowerCase();
    const matches = query.length < 2
      ? []
      : rosterList.filter(p => p.name.toLowerCase().includes(query)).slice(0, 8);

    const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 18 };
    const cardTitle = { fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 6 };
    const cardBody = { fontSize: 13, color: "#4b5563", lineHeight: 1.6, marginBottom: 14 };

    return (
      <AppShell {...shellProps}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 20 }}>
            Tools for answering data protection requests and showing what happens to the records you hold.
            Every action here is written to your Activity log.
          </p>

          <div style={card}>
            <div style={cardTitle}>Answer a subject access or erasure request</div>
            <div style={cardBody}>
              Find the player, then either download everything held about them, or erase it.
              Search by name — at least two letters.
            </div>
            <input
              value={dpSearch}
              onChange={e => setDpSearch(e.target.value)}
              placeholder="Search the roster by name..."
              style={inp}
              autoComplete="off"
            />
            {query.length >= 2 && matches.length === 0 && (
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 12 }}>No players on the roster match that name.</div>
            )}
            {matches.map(player => (
              <div key={player.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderTop: "1px solid #f3f4f6", padding: "12px 0" }}>
                <div>
                  <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>{player.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{[player.age_grade, player.team].filter(Boolean).join(" · ") || "No age grade recorded"}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => handleSubjectAccessExport(player)} style={btnS}>Export their data</button>
                  <button
                    onClick={() => { setEraseTarget(player); setEraseConfirmName(""); setEraseResult(null); }}
                    style={{ ...btnS, color: "#c0272d", borderColor: "#fca5a5" }}
                  >
                    Erase
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={card}>
            <div style={cardTitle}>Export everything this organisation holds</div>
            <div style={cardBody}>
              A complete copy of your roster, concussion records, consent records, messages, and staff
              accounts, as a single file. Useful for your own records, for a data protection audit, or if
              you ever stop using GRTP Tracker. Access tokens are deliberately excluded.
            </div>
            <button onClick={handleExportOrganisation} disabled={exportingOrg} style={{ ...btnP, opacity: exportingOrg ? 0.6 : 1 }}>
              {exportingOrg ? "Preparing export..." : "Download full export"}
            </button>
          </div>

          <div style={card}>
            <div style={cardTitle}>How long records are kept</div>
            <div style={cardBody}>
              Concussion records are kept until the player's 25th birthday, or seven years from the
              incident — whichever is longer — then deleted automatically. That is seven years past
              the age of 18, the period a health record about a child is ordinarily kept for. The
              longer period exists because a claim relating to a childhood injury can be brought long
              after the injury itself, and the record is the club's evidence as much as the family's.
            </div>
            {!retentionReport ? (
              <button onClick={loadRetentionReport} style={btnS}>Show retention status</button>
            ) : (
              <div>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
                  {[
                    ["Concussion records held", retentionReport.concussion_records_held],
                    ["Roster entries held", retentionReport.roster_entries_held],
                    ["Due for deletion within 12 months", retentionReport.due_for_deletion_within_12_months.length],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#1a4a2e" }}>{value}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
                    </div>
                  ))}
                </div>
                {retentionReport.due_for_deletion_within_12_months.length > 0 && (
                  <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: "#92400e", marginBottom: 6 }}>Falling due within 12 months</div>
                    {retentionReport.due_for_deletion_within_12_months.slice(0, 10).map((r, i) => (
                      <div key={i} style={{ fontSize: 12.5, color: "#78350f" }}>
                        {r.name} — incident {new Date(r.incident_date).toLocaleDateString("en-GB")}, deleted {new Date(r.retention_until).toLocaleDateString("en-GB")}
                      </div>
                    ))}
                  </div>
                )}
                {retentionReport.records_without_a_retention_date > 0 && (
                  <div style={{ fontSize: 12.5, color: "#92400e", marginBottom: 10 }}>
                    {retentionReport.records_without_a_retention_date} record(s) have no retention date and will be kept until one is set.
                  </div>
                )}
                <div style={{ fontSize: 12.5, color: "#6b7280" }}>
                  {retentionReport.deletions_already_carried_out.length === 0
                    ? "No records have been deleted yet."
                    : `${retentionReport.deletions_already_carried_out.length} deletion event(s) recorded. The most recent was ${new Date(retentionReport.deletions_already_carried_out[0].deleted_at).toLocaleDateString("en-GB")}.`}
                </div>
              </div>
            )}
          </div>

          {eraseResult && (
            <div style={{ ...card, border: "2px solid #1a4a2e" }}>
              <div style={cardTitle}>Erasure complete — {eraseResult.data_subject}</div>
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
                <div>Roster entry, messages, photos, change requests, registration record and any
                  cross-organisation links have been permanently deleted.</div>
                {eraseResult.concussion_records_retained.length > 0 ? (
                  <div style={{ marginTop: 12, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: "#92400e", marginBottom: 6 }}>
                      {eraseResult.concussion_records_retained.length} concussion record(s) were kept
                    </div>
                    {eraseResult.concussion_records_retained.map((r, i) => (
                      <div key={i} style={{ fontSize: 12.5, color: "#78350f" }}>
                        Incident {new Date(r.incident_date).toLocaleDateString("en-GB")} — deleted automatically on {new Date(r.retention_until).toLocaleDateString("en-GB")}
                      </div>
                    ))}
                    <div style={{ fontSize: 12, color: "#78350f", marginTop: 8, lineHeight: 1.6 }}>
                      {eraseResult.retention_explanation}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: "#6b7280" }}>{eraseResult.retention_explanation}</div>
                )}
              </div>
              <button onClick={() => setEraseResult(null)} style={{ ...btnS, marginTop: 14 }}>Done</button>
            </div>
          )}

          {eraseTarget && (
            <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 100 }}>
              <div style={{ background: "#fff", borderRadius: 12, padding: 24, maxWidth: 460, width: "100%" }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#c0272d", marginBottom: 10 }}>
                  Erase all data for {eraseTarget.name}?
                </div>
                <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, marginBottom: 14 }}>
                  This permanently deletes their roster entry, messages and photos, change requests, and
                  registration record. It cannot be undone.
                  <br /><br />
                  Concussion records still within their safeguarding retention period are <strong>kept</strong>,
                  as the law allows for defending legal claims. You'll be told exactly what was kept.
                </div>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  Type <strong>{eraseTarget.name}</strong> to confirm
                </label>
                <input
                  value={eraseConfirmName}
                  onChange={e => setEraseConfirmName(e.target.value)}
                  style={{ ...inp, marginBottom: 16 }}
                  autoComplete="off"
                />
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={handleEraseDataSubject}
                    disabled={erasing || eraseConfirmName.trim().toLowerCase() !== eraseTarget.name.trim().toLowerCase()}
                    style={{
                      ...btnP, flex: 1, background: "#c0272d",
                      opacity: erasing || eraseConfirmName.trim().toLowerCase() !== eraseTarget.name.trim().toLowerCase() ? 0.45 : 1,
                    }}
                  >
                    {erasing ? "Erasing..." : "Erase permanently"}
                  </button>
                  <button onClick={() => { setEraseTarget(null); setEraseConfirmName(""); }} style={btnS}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AppShell>
    );
  }

  if (view === "security") {
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Security">
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 20 }}>
            Adds a second step to logging in, using a code from an app on your phone.
          </p>

          {!mfaStatus ? (
            <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>Loading...</div>
          ) : mfaBackupCodes ? (
            <div style={{ background: "#fff", border: "2px solid #1a4a2e", borderRadius: 10, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#1a4a2e", marginBottom: 8 }}>✅ Two-factor authentication is now on</div>
              <p style={{ fontSize: 13, color: "#374151", marginBottom: 14 }}>
                Save these 10 backup codes somewhere safe — each works once, and lets you log in if you ever lose access to your authenticator app. <strong>This is the only time they'll be shown.</strong>
              </p>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: 14, fontFamily: "monospace", fontSize: 14, lineHeight: 1.8, columns: 2 }}>
                {mfaBackupCodes.map((code, i) => <div key={i}>{code}</div>)}
              </div>
              <button onClick={() => setMfaBackupCodes(null)} style={{ marginTop: 16, width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                I've saved these codes
              </button>
            </div>
          ) : mfaSetupData ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e", marginBottom: 12 }}>Scan this with your authenticator app</div>
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <img src={mfaSetupData.qrDataUrl} alt="QR code for two-factor setup" style={{ width: 200, height: 200, border: "1px solid #e5e7eb", borderRadius: 8 }} />
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Can't scan it? Enter this code manually instead:</p>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px", fontFamily: "monospace", fontSize: 13, marginBottom: 16, wordBreak: "break-all" }}>
                {mfaSetupData.secret}
              </div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Enter the 6-digit code it shows</label>
              <input
                value={mfaSetupCode}
                onChange={e => setMfaSetupCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 18, textAlign: "center", letterSpacing: 4, marginBottom: 12, fontFamily: "inherit" }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleConfirmMfaSetup} disabled={mfaSubmitting} style={{ flex: 1, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: mfaSubmitting ? "default" : "pointer", opacity: mfaSubmitting ? 0.7 : 1 }}>
                  {mfaSubmitting ? "Checking..." : "Confirm & Turn On"}
                </button>
                <button onClick={() => setMfaSetupData(null)} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : mfaStatus.enabled ? (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ background: "#eafaf0", color: "#15803d", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 99 }}>● ON</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Two-factor authentication is active</span>
              </div>
              {mfaStatus.required && (
                <p style={{ fontSize: 13, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", marginTop: 12 }}>
                  This is required for your account since you're an organisation admin, and can't be turned off while you hold admin access.
                </p>
              )}
              {!mfaStatus.required && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Enter your password to turn this off</label>
                  <input
                    type="password"
                    value={mfaDisablePassword}
                    onChange={e => setMfaDisablePassword(e.target.value)}
                    placeholder="Your password"
                    style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, marginBottom: 10, fontFamily: "inherit" }}
                  />
                  <button onClick={handleDisableMfa} disabled={mfaSubmitting} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: mfaSubmitting ? "default" : "pointer" }}>
                    Turn Off Two-Factor Authentication
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ background: "#f3f4f6", color: "#6b7280", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 99 }}>○ OFF</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>Two-factor authentication is not set up</span>
              </div>
              {mfaStatus.required && (
                <p style={{ fontSize: 13, color: "#7f1d1d", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 12px", marginBottom: 14 }}>
                  This is required for your account as an organisation admin — admin actions (inviting staff, deleting records) are blocked until it's set up.
                </p>
              )}
              <button onClick={handleStartMfaSetup} disabled={mfaSubmitting} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: mfaSubmitting ? "default" : "pointer", opacity: mfaSubmitting ? 0.7 : 1 }}>
                {mfaSubmitting ? "Starting..." : "Set Up Two-Factor Authentication"}
              </button>
            </div>
          )}
          <AppDevicesPanel isOrgAdmin={isOrgAdmin} showNotif={showNotif} />
          <NewsPreferencePanel showNotif={showNotif} />
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  if (view === "branding") {
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Branding">
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 20 }}>
            Optional — personalise your dashboard with your club or school's own colour and crest. Leave either blank to keep GRTP's defaults.
          </p>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>Accent colour</div>
            <p style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 12 }}>Used for your nav bar and a few highlights around the dashboard.</p>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="color"
                value={brandColorInput}
                onChange={e => setBrandColorInput(e.target.value)}
                style={{ width: 48, height: 40, border: "1px solid #d1d5db", borderRadius: 6, padding: 2, cursor: "pointer", background: "none" }}
              />
              <input
                value={brandColorInput}
                onChange={e => setBrandColorInput(e.target.value)}
                placeholder="#1a4a2e"
                style={{ width: 110, boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 14, fontFamily: "monospace" }}
              />
              <button type="button" onClick={handleResetBrandColor} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 12.5, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                Reset to GRTP default
              </button>
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>Logo</div>
            <p style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 12 }}>Shown next to your organisation name in the sidebar. PNG, JPEG, GIF, WebP, or SVG, under 100KB.</p>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 56, height: 56, borderRadius: 8, border: "1px solid #e2eaf2", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>No logo</span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-block", width: "fit-content" }}>
                  Choose image
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" onChange={handleLogoFileChange} style={{ display: "none" }} />
                </label>
                {logoPreview && (
                  <button type="button" onClick={handleRemoveLogo} style={{ background: "none", border: "none", color: "#c0272d", fontSize: 12.5, cursor: "pointer", textDecoration: "underline", padding: 0, textAlign: "left" }}>
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          {brandingError && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", color: "#c0272d", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
              {brandingError}
            </div>
          )}

          <button onClick={handleSaveBranding} disabled={brandingSaving} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: brandingSaving ? "default" : "pointer", opacity: brandingSaving ? 0.7 : 1 }}>
            {brandingSaving ? "Saving..." : "Save Branding"}
          </button>
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── HELP ──────────────────────────────────────────────────────────
  if (view === "help") {
    const jumpTo = (id) => {
      document.getElementById("guide-" + id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const renderBlock = (block, i) => {
      if (block.type === "heading3") {
        return <div key={i} style={{ fontWeight: 800, fontSize: 15, color: "#111827", marginTop: 18, marginBottom: 8 }}>{block.text}</div>;
      }
      if (block.type === "para") {
        return <p key={i} style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, margin: "0 0 12px" }}>{block.text}</p>;
      }
      if (block.type === "list") {
        return (
          <ul key={i} style={{ margin: "0 0 12px", paddingLeft: 20 }}>
            {block.items.map((item, li) => (
              <li key={li} style={{ fontSize: 14, color: "#374151", lineHeight: 1.65, marginBottom: 6 }}>{item}</li>
            ))}
          </ul>
        );
      }
      if (block.type === "callout") {
        const palette = {
          info: { bg: "#f0f7f3", border: "#b6d8c5", color: "#1a4a2e" },
          warning: { bg: "#fef9ec", border: "#c9991a", color: "#92400e" },
          tip: { bg: "#f8fafb", border: "#e2eaf2", color: "#1a4a2e" },
        }[block.tone] || { bg: "#f8fafb", border: "#e2eaf2", color: "#1a4a2e" };
        return (
          <div key={i} style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 13.5, color: palette.color, lineHeight: 1.6 }}>
            {block.text}
          </div>
        );
      }
      if (block.type === "image") {
        return (
          <div key={i} style={{ margin: "10px 0 18px" }}>
            <img src={`/guide-images/${block.src}.png`} alt={block.alt || ""} style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #e2eaf2" }} />
          </div>
        );
      }
      return null;
    };
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Help">
        <div className="grtp-guide-layout" style={{ display: "flex", gap: 28, maxWidth: 1040, margin: "0 auto", alignItems: "flex-start" }}>
          <div className="grtp-guide-toc" style={{ width: 220, flexShrink: 0, position: "sticky", top: 0, background: "#fff", border: "1px solid #e2eaf2", borderRadius: 10, padding: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Contents</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 14 }}>
              {GUIDE_SECTIONS.map(s => (
                <button key={s.id} onClick={() => jumpTo(s.id)} style={{ textAlign: "left", background: "none", border: "none", color: "#1a4a2e", fontSize: 13, padding: "5px 6px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>
                  {s.title}
                </button>
              ))}
            </div>
            {/* Through saveFile, not a download link: in the phone app a
                download link does nothing, and this opens the share sheet. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await fetch("/GRTP-Tracker-User-Guide.pdf");
                  if (!res.ok) throw new Error("not found");
                  await nativeApp.saveFile(await res.blob(), "GRTP-Tracker-User-Guide.pdf");
                } catch {
                  showNotif("Couldn't download the guide. Please try again.", "error");
                }
              }}
              style={{ display: "block", width: "100%", textAlign: "center", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            >
              ⬇ Download as PDF
            </button>
          </div>
          <div className="grtp-guide-content" style={{ flex: 1, minWidth: 0, background: "#fff", border: "1px solid #e2eaf2", borderRadius: 10, padding: "24px 28px" }}>
            {GUIDE_SECTIONS.map(section => (
              <div key={section.id} id={`guide-${section.id}`} style={{ marginBottom: 34, scrollMarginTop: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 19, color: "#111827", marginBottom: 12, borderBottom: "2px solid #f0f7f3", paddingBottom: 8 }}>{section.title}</div>
                {section.blocks.map(renderBlock)}
              </div>
            ))}
          </div>
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      </AppShell>
    );
  }

  // ── ROSTER ────────────────────────────────────────────────────────
  if (view === "roster") {
    const rosterFieldStyle = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", marginBottom: 8 };
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Player Roster">
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
            <div>
              <p style={{ color: "#7a95ad", fontSize: 13, margin: 0 }}>Pre-load players so logging a concussion is a quick search, not a re-type.</p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => { const next = !showArchivedRoster; setShowArchivedRoster(next); if (next) loadArchivedRoster(); }}
                style={{ background: showArchivedRoster ? "#1a4a2e" : "#fff", color: showArchivedRoster ? "#fff" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                {showArchivedRoster ? "← Back to Roster" : "📁 Archived (Aged Out)"}
              </button>
              {isOrgAdmin && (
                <button
                  onClick={() => { setShowBulkReplace(v => !v); setBulkPreview(null); setBulkDone(null); }}
                  style={{ background: showBulkReplace ? "#1a4a2e" : "#fff", color: showBulkReplace ? "#fff" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  {showBulkReplace ? "← Back to Roster" : "✏️ Bulk Edit Contacts"}
                </button>
              )}
              <button onClick={handleOpenImportModal} style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Import CSV
              </button>
              {rosterEditingId === null && (
                <button onClick={handleStartAddRoster} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  + Add Player
                </button>
              )}
            </div>
          </div>

          {showBulkReplace ? (
            <div>
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 6 }}>Bulk Replace Contact Details</div>
                <p style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6, margin: "0 0 18px" }}>
                  Find all players where a contact field matches a value, and replace it across all of them at once. Useful when a coach leaves and 50 players all have their old details.
                </p>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Field to update</label>
                    <select
                      value={bulkField}
                      onChange={e => { setBulkField(e.target.value); setBulkPreview(null); setBulkDone(null); }}
                      style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, fontFamily: "inherit" }}
                    >
                      <option value="club_coach_email">Club coach — email</option>
                      <option value="club_coach_name">Club coach — name</option>
                      <option value="school_coach_email">School coach — email</option>
                      <option value="school_coach_name">School coach — name</option>
                      <option value="other_contact_1_email">Additional contact 1 — email</option>
                      <option value="other_contact_1_name">Additional contact 1 — name</option>
                      <option value="other_contact_2_email">Additional contact 2 — email</option>
                      <option value="other_contact_2_name">Additional contact 2 — name</option>
                    </select>
                  </div>
                  <div />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Current value (to find)</label>
                    <input
                      value={bulkOldValue}
                      onChange={e => { setBulkOldValue(e.target.value); setBulkPreview(null); setBulkDone(null); }}
                      placeholder="e.g. dave@sandbachRFC.co.uk"
                      style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, fontFamily: "inherit" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>New value (to replace with)</label>
                    <input
                      value={bulkNewValue}
                      onChange={e => { setBulkNewValue(e.target.value); setBulkDone(null); }}
                      placeholder="e.g. newcoach@sandbachRFC.co.uk (leave blank to clear)"
                      style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 10px", fontSize: 13, fontFamily: "inherit" }}
                    />
                  </div>
                </div>

                <button
                  onClick={handleBulkReplacePreview}
                  disabled={bulkLoading || !bulkOldValue.trim()}
                  style={{ background: "#374151", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: bulkLoading || !bulkOldValue.trim() ? "default" : "pointer", opacity: bulkLoading || !bulkOldValue.trim() ? 0.5 : 1 }}
                >
                  {bulkLoading && !bulkPreview ? "Searching..." : "Preview Changes"}
                </button>
              </div>

              {bulkDone && (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, color: "#15803d", fontSize: 14, marginBottom: 4 }}>✅ Done — {bulkDone.updated} player{bulkDone.updated !== 1 ? "s" : ""} updated</div>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    {bulkDone.field.replace(/_/g, " ")} changed from "{bulkDone.oldValue || "(blank)"}" to "{bulkDone.newValue || "(cleared)"}"
                  </div>
                </div>
              )}

              {bulkPreview && !bulkDone && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20 }}>
                  {bulkPreview.count === 0 ? (
                    <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 14, padding: "20px 0" }}>
                      No players found with that value in the selected field.
                    </div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>
                        {bulkPreview.count} player{bulkPreview.count !== 1 ? "s" : ""} will be updated
                      </div>
                      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.5 }}>
                        {bulkNewValue.trim() ? `"${bulkOldValue}" will be replaced with "${bulkNewValue}" for all players listed below.` : `"${bulkOldValue}" will be cleared for all players listed below.`}
                      </p>

                      {/* Redirecting a live protocol's emails is a bigger
                          deal than editing a roster row, so it is called
                          out rather than folded into the count. */}
                      {bulkPreview.activeProtocolCount > 0 && (
                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                          <div style={{ fontWeight: 700, fontSize: 12.5, color: "#92400e", marginBottom: 4 }}>
                            {bulkPreview.activeProtocolCount} player{bulkPreview.activeProtocolCount !== 1 ? "s are" : " is"} currently on a protocol
                          </div>
                          <div style={{ fontSize: 12.5, color: "#78350f", lineHeight: 1.55 }}>
                            Their remaining stage emails will go to the new address instead of the old one:{" "}
                            {bulkPreview.activeProtocolPlayers.map(p => p.name).join(", ")}.
                            {" "}Records of who was emailed before now are unchanged.
                          </div>
                        </div>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 }}>
                        {bulkPreview.players.map(p => (
                          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 10px", background: "#f9fafb", borderRadius: 6 }}>
                            <span style={{ fontWeight: 600 }}>{p.name}</span>
                            <span style={{ color: "#9ca3af" }}>{p.current}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          onClick={handleBulkReplaceConfirm}
                          disabled={bulkLoading}
                          style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: bulkLoading ? "default" : "pointer", opacity: bulkLoading ? 0.6 : 1 }}
                        >
                          {bulkLoading ? "Updating..." : "Confirm — Update " + bulkPreview.count + " Player" + (bulkPreview.count !== 1 ? "s" : "")}
                        </button>
                        <button
                          onClick={() => setBulkPreview(null)}
                          style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : showArchivedRoster ? (
            <div>
              <p style={{ color: "#6b7280", fontSize: 13.5, margin: "0 0 16px" }}>
                Players who reached U19 during age-grade advancement and were archived. Restore isn't currently supported — if someone ended up here by mistake, re-add them with "+ Add Player" instead.
              </p>
              {archivedRosterLoading ? (
                <div style={{ textAlign: "center", padding: 30, color: "#9ca3af" }}>Loading...</div>
              ) : archivedRoster.length === 0 ? (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
                  No archived roster entries.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {archivedRoster.map(p => (
                    <div key={p.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>{p.age_grade || "No age grade"}{p.position ? ` · ${p.position}` : ""}</div>
                      </div>
                      <button
                        onClick={() => {
                          setConfirmAction({
                            title: "Remove from Roster?",
                            message: `Permanently remove ${p.name} from the roster? This cannot be undone.`,
                            confirmLabel: "Remove",
                            danger: true,
                            onConfirm: async () => {
                              try {
                                await api.deleteRosterPlayer(p.id);
                                setArchivedRoster(prev => prev.filter(x => x.id !== p.id));
                                showNotif(`${p.name} removed from the roster.`);
                              } catch (e) {
                                showNotif(e.message, "error");
                              }
                              setConfirmAction(null);
                            },
                          });
                        }}
                        style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                      >
                        🗑 Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <>

          {isOrgAdmin && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "#7f1d1d", fontWeight: 600 }}>🔓 Admin tools:</span>
              {/* Adult deliberately excluded — the backend rejects it too, since
                  wiping an entire adult section in one click is a much bigger
                  blast radius than clearing one youth age group. */}
              <select
                value={bulkDeleteAgeGrade}
                onChange={e => setBulkDeleteAgeGrade(e.target.value)}
                style={{ border: "1px solid #fca5a5", borderRadius: 6, padding: "7px 10px", fontSize: 13, fontFamily: "inherit", background: "#fff" }}
              >
                <option value="">Select age grade...</option>
                {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19"].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <button
                onClick={() => bulkDeleteAgeGrade && handleDeleteRosterByAgeGrade(bulkDeleteAgeGrade)}
                disabled={!bulkDeleteAgeGrade}
                style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: bulkDeleteAgeGrade ? "pointer" : "default", opacity: bulkDeleteAgeGrade ? 1 : 0.5 }}
              >
                Remove All in This Age Grade
              </button>
              <button onClick={handleDeleteEntireRoster} style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
                Delete Entire Roster
              </button>
            </div>
          )}

          {rosterEditingId !== null && (
            <div style={{ background: "#fff", border: "2px solid #1a4a2e", borderRadius: 10, padding: 18, marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1a4a2e", marginBottom: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {rosterEditingId === "new" ? "Add Player to Roster" : "Edit Player"}
              </div>
              <TwoCol>
                <Field label="Full name *">
                  <input style={rosterFieldStyle} value={rosterForm.name} onChange={e => setRosterForm({ ...rosterForm, name: e.target.value })} placeholder="e.g. Alex Taylor" />
                </Field>
                <Field label="Age Grade">
                  <select value={rosterForm.ageGrade} onChange={e => setRosterForm({ ...rosterForm, ageGrade: e.target.value })} style={rosterFieldStyle}>
                    <option value="">Select</option>
                    {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult"].map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Team (optional)">
                  <select value={rosterForm.team} onChange={e => setRosterForm({ ...rosterForm, team: e.target.value })} style={rosterFieldStyle}>
                    <option value="">None</option>
                    {teamsList.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    {/* A team can be removed by an admin after players were already
                        assigned to it (existing assignments are deliberately left
                        alone — see the DELETE /api/teams/:id comment). Without this,
                        the dropdown would silently show "None" while the form still
                        held the old team name, and saving any unrelated field on
                        this player would then fail with a confusing "not a team on
                        this organisation" error. */}
                    {rosterForm.team && !teamsList.some(t => t.name === rosterForm.team) && (
                      <option value={rosterForm.team}>{rosterForm.team} (removed — clear or re-add it)</option>
                    )}
                  </select>
                </Field>
                <Field label="Position">
                  <input style={rosterFieldStyle} value={rosterForm.position} onChange={e => setRosterForm({ ...rosterForm, position: e.target.value })} placeholder="e.g. Flanker" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Parent / guardian name">
                  <input style={rosterFieldStyle} value={rosterForm.parentName} onChange={e => setRosterForm({ ...rosterForm, parentName: e.target.value })} placeholder="e.g. Sarah Taylor" />
                </Field>
                <Field label="Parent / guardian email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.parentEmail} onChange={v => setRosterForm(r => ({ ...r, parentEmail: v }))} placeholder="parent@example.com" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="School coach name">
                  <input style={rosterFieldStyle} value={rosterForm.schoolCoachName} onChange={e => setRosterForm({ ...rosterForm, schoolCoachName: e.target.value })} placeholder="e.g. J. Smith" />
                </Field>
                <Field label="School coach email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.schoolCoachEmail} onChange={v => setRosterForm(r => ({ ...r, schoolCoachEmail: v }))} placeholder="j.smith@yourclub.co.uk" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Second school coach name">
                  <input style={rosterFieldStyle} value={rosterForm.schoolCoach2Name} onChange={e => setRosterForm({ ...rosterForm, schoolCoach2Name: e.target.value })} placeholder="e.g. A. Jones" />
                </Field>
                <Field label="Second school coach email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.schoolCoach2Email} onChange={v => setRosterForm(r => ({ ...r, schoolCoach2Email: v }))} placeholder="a.jones@yourclub.co.uk" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Club coach name">
                  <input style={rosterFieldStyle} value={rosterForm.clubCoachName} onChange={e => setRosterForm({ ...rosterForm, clubCoachName: e.target.value })} placeholder="e.g. Dave Wilson" />
                </Field>
                <Field label="Club coach email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.clubCoachEmail} onChange={v => setRosterForm(r => ({ ...r, clubCoachEmail: v }))} placeholder="coach@localclub.co.uk" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Second club coach name">
                  <input style={rosterFieldStyle} value={rosterForm.clubCoach2Name} onChange={e => setRosterForm({ ...rosterForm, clubCoach2Name: e.target.value })} placeholder="e.g. Team manager" />
                </Field>
                <Field label="Second club coach email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.clubCoach2Email} onChange={v => setRosterForm(r => ({ ...r, clubCoach2Email: v }))} placeholder="manager@localclub.co.uk" />
                </Field>
              </TwoCol>
              {/* Set by parents at registration and, until now, changeable
                  only through the roster-wide find and replace. Clearing a
                  field here is how one person is taken off one child's
                  notifications.

                  The status list matters as much as the fields: an
                  address sitting here does NOT mean that person receives
                  anything. They are asked first, and until they answer
                  nothing is sent — so a club that could not see this
                  would believe a grandparent was being kept informed
                  when nobody had ever heard back from them.

                  It also covers the coach at the OTHER kind of
                  organisation, who is asked on the same terms, which is
                  why each line names the role. */}
              {rosterContactStatus.length > 0 && (
                <div style={{ background: "#f8fafc", border: "1px solid #e2eaf2", borderRadius: 6, padding: "9px 11px", marginBottom: 10, fontSize: 12.5, lineHeight: 1.6 }}>
                  {rosterContactStatus.map((c) => (
                    <div key={c.slot} style={{ color: "#374151" }}>
                      <strong>{c.name || c.email}</strong>
                      {c.role && c.role !== "additional contact" && (
                        <span style={{ color: "#6b7280" }}> ({c.role})</span>
                      )}
                      {" — "}
                      <span style={{ color: c.status === "confirmed" ? "#166534" : c.status === "declined" ? "#c0272d" : "#92400e" }}>
                        {c.status === "confirmed" ? "✓ has agreed to receive updates"
                          : c.status === "declined" ? "✗ has asked not to be contacted — they will not receive anything"
                          : c.status === "awaiting" ? "waiting for them to confirm — nothing is sent until they do"
                          : "will be asked to confirm before anything is sent"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <TwoCol>
                <Field label="Additional contact 1 name">
                  <input style={rosterFieldStyle} value={rosterForm.otherContact1Name} onChange={e => setRosterForm({ ...rosterForm, otherContact1Name: e.target.value })} placeholder="e.g. Grandparent, second guardian" />
                </Field>
                <Field label="Additional contact 1 email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.otherContact1Email} onChange={v => setRosterForm(r => ({ ...r, otherContact1Email: v }))} placeholder="Leave blank to stop their updates" />
                </Field>
              </TwoCol>
              <TwoCol>
                <Field label="Additional contact 2 name">
                  <input style={rosterFieldStyle} value={rosterForm.otherContact2Name} onChange={e => setRosterForm({ ...rosterForm, otherContact2Name: e.target.value })} placeholder="Optional" />
                </Field>
                <Field label="Additional contact 2 email">
                  <EmailInput style={rosterFieldStyle} value={rosterForm.otherContact2Email} onChange={v => setRosterForm(r => ({ ...r, otherContact2Email: v }))} placeholder="Leave blank to stop their updates" />
                </Field>
              </TwoCol>
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button onClick={handleSaveRoster} disabled={rosterSubmitting} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: rosterSubmitting ? "default" : "pointer", opacity: rosterSubmitting ? 0.7 : 1 }}>
                  {rosterSubmitting ? "Saving..." : "Save"}
                </button>
                <button onClick={handleCancelRosterEdit} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            {/* Search bar */}
            <div style={{ position: "relative", marginBottom: 12 }}>
              <input
                value={rosterSearch}
                onChange={e => setRosterSearch(e.target.value)}
                placeholder="Search players by name..."
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 14px 10px 36px", fontSize: 14, fontFamily: "inherit", background: "#fff" }}
              />
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 15, pointerEvents: "none" }}>🔍</span>
              {rosterSearch && (
                <button onClick={() => setRosterSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>

            {rosterLoading ? (
              <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>
            ) : (() => {
              const searchLower = rosterSearch.trim().toLowerCase();
              const isSearching = searchLower.length > 0;

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {ALL_ROSTER_GRADES.map(grade => {
                    const gradePlayers = rosterList.filter(e => (e.age_grade || "Unknown") === grade);
                    const displayed = isSearching
                      ? gradePlayers.filter(e => e.name.toLowerCase().includes(searchLower))
                      : gradePlayers;
                    const isCollapsed = !isSearching && collapsedGrades.has(grade);
                    const hasResults = displayed.length > 0;

                    // Hide grade entirely when searching and no matches
                    if (isSearching && !hasResults) return null;

                    return (
                      <div key={grade} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                        {/* Grade header */}
                        <button
                          onClick={() => !isSearching && toggleGrade(grade)}
                          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "none", border: "none", cursor: isSearching ? "default" : "pointer", fontFamily: "inherit", textAlign: "left" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: "#1a4a2e" }}>{grade}</span>
                            <span style={{ fontSize: 12, fontWeight: 500, color: gradePlayers.length > 0 ? "#6b7280" : "#9ca3af", background: gradePlayers.length > 0 ? "#f3f4f6" : "#fafafa", border: "1px solid #e5e7eb", borderRadius: 10, padding: "2px 8px" }}>
                              {gradePlayers.length} player{gradePlayers.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          {!isSearching && (
                            <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>
                              {isCollapsed ? "▶" : "▼"}
                            </span>
                          )}
                        </button>

                        {/* Players list */}
                        {!isCollapsed && (
                          <div style={{ borderTop: "1px solid #f3f4f6", padding: "8px 12px 12px" }}>
                            {displayed.length === 0 ? (
                              <div style={{ padding: "12px 4px", color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>
                                No players in {grade} yet
                              </div>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                                {displayed.map(entry => (
                                  <div key={entry.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6", flexWrap: "wrap", gap: 8 }}>
                                    <div>
                                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{entry.name}</div>
                                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                                        {[entry.age_grade, entry.position].filter(Boolean).join(" · ") || "No details added"}
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                      {entry.parent_token && (
                                        <button
                                          onClick={() => {
                                            // The portal emails the parent a code before it opens, so
                                            // without their address the link would open nothing.
                                            if (!(entry.parent_email || "").trim()) { showNotif("Add the parent's email address first: the portal emails them a code before it opens.", "error"); return; }
                                            const url = `${window.location.origin}/parent/${entry.parent_token}`;
                                            navigator.clipboard.writeText(url)
                                              .then(() => showNotif("Parent portal link copied."))
                                              .catch(() => showNotif("Copy failed — select manually.", "error"));
                                          }}
                                          title="Copy parent portal link"
                                          style={{ background: "#f0f7f3", color: "#1a4a2e", border: "1px solid #b6d8c5", borderRadius: 6, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                          🔗 Parent link
                                        </button>
                                      )}
                                      <button onClick={() => handleStartEditRoster(entry)} style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                                      <button onClick={() => handleDeleteRoster(entry)} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Remove</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Players with no/unknown age grade */}
                  {(() => {
                    const unknown = rosterList.filter(e => !e.age_grade || !ALL_ROSTER_GRADES.includes(e.age_grade));
                    const displayed = isSearching ? unknown.filter(e => e.name.toLowerCase().includes(searchLower)) : unknown;
                    if (isSearching && displayed.length === 0) return null;
                    const isCollapsed = !isSearching && collapsedGrades.has("__unknown__");
                    return (
                      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                        <button
                          onClick={() => !isSearching && toggleGrade("__unknown__")}
                          style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "none", border: "none", cursor: isSearching ? "default" : "pointer", fontFamily: "inherit", textAlign: "left" }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: "#6b7280" }}>No age grade</span>
                            <span style={{ fontSize: 12, fontWeight: 500, color: unknown.length > 0 ? "#6b7280" : "#9ca3af", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 10, padding: "2px 8px" }}>
                              {unknown.length} player{unknown.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          {!isSearching && <span style={{ color: "#9ca3af", fontSize: 13 }}>{isCollapsed ? "▶" : "▼"}</span>}
                        </button>
                        {!isCollapsed && displayed.length > 0 && (
                          <div style={{ borderTop: "1px solid #f3f4f6", padding: "8px 12px 12px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                              {displayed.map(entry => (
                                <div key={entry.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6", flexWrap: "wrap", gap: 8 }}>
                                  <div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{entry.name}</div>
                                    <div style={{ fontSize: 12, color: "#6b7280" }}>{entry.position || "No details added"}</div>
                                  </div>
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {entry.parent_token && (
                                      <button
                                        onClick={() => {
                                          // The portal emails the parent a code before it opens, so
                                            // without their address the link would open nothing.
                                            if (!(entry.parent_email || "").trim()) { showNotif("Add the parent's email address first: the portal emails them a code before it opens.", "error"); return; }
                                            const url = `${window.location.origin}/parent/${entry.parent_token}`;
                                          navigator.clipboard.writeText(url)
                                            .then(() => showNotif("Parent portal link copied."))
                                            .catch(() => showNotif("Copy failed — select manually.", "error"));
                                        }}
                                        title="Copy parent portal link"
                                        style={{ background: "#f0f7f3", color: "#1a4a2e", border: "1px solid #b6d8c5", borderRadius: 6, padding: "6px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                        🔗 Parent link
                                      </button>
                                    )}
                                    <button onClick={() => handleStartEditRoster(entry)} style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Edit</button>
                                    <button onClick={() => handleDeleteRoster(entry)} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Remove</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
        </>
        )}
        </div>

        {showImportModal && (
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 2000 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 540, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#1a4a2e", marginBottom: 10 }}>Import Roster from CSV</div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 16px", lineHeight: 1.6 }}>
                Upload a spreadsheet with a header row. Recognised columns: Name (required), Age Grade, Position, Parent Name, Parent Email, School Coach Name, School Coach Email, Club Coach Name, Club Coach Email. Any other columns are ignored.
              </p>

              <input type="file" accept=".csv,text/csv" onChange={handleImportFileSelected} style={{ marginBottom: 16 }} />

              {importPreview && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                    Found {importPreview.totalRows} player{importPreview.totalRows !== 1 ? "s" : ""} in {importFileName}
                  </div>
                  {importPreview.preview.length > 0 && (
                    <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12 }}>
                      <div style={{ fontWeight: 700, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }}>Preview (first {importPreview.preview.length})</div>
                      {importPreview.preview.map((p, i) => (
                        <div key={i} style={{ padding: "4px 0", borderTop: i > 0 ? "1px solid #f3f4f6" : "none" }}>
                          <strong>{p.name}</strong>{p.ageGrade ? ` — ${p.ageGrade}` : ""}{p.parentEmail ? ` · ${p.parentEmail}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                  {importPreview.errors.length > 0 && (
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: 10, fontSize: 12, color: "#92400e" }}>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>{importPreview.errors.length} row{importPreview.errors.length !== 1 ? "s" : ""} need attention:</div>
                      {importPreview.errors.map((err, i) => <div key={i} style={{ padding: "2px 0" }}>{err}</div>)}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                {importPreview && importPreview.totalRows > 0 && (
                  <button onClick={handleConfirmImport} disabled={importSubmitting} style={{ flex: 1, background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: importSubmitting ? "default" : "pointer", opacity: importSubmitting ? 0.7 : 1 }}>
                    {importSubmitting ? "Importing..." : `Import ${importPreview.totalRows} Player${importPreview.totalRows !== 1 ? "s" : ""}`}
                  </button>
                )}
                <button onClick={() => setShowImportModal(false)} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Season Rollover ──────────────────────────────────────────── */}
        {isOrgAdmin && (
          <div style={{ marginTop: 40, paddingTop: 28, borderTop: "2px dashed #e5e7eb" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#374151", marginBottom: 6 }}>🏉 Season Rollover</div>
            <p style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6, margin: "0 0 16px" }}>
              At the start of each new season, advance every player up one age grade.
              U19s move to Adult. Adult players stay on the roster permanently — they're senior players, not aged-out ones.
              Run this once before the season starts.
            </p>
            {ageGradeResultSummary && (
              <div style={{ background: "#f0f7f3", border: "1px solid #b6d8c5", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#1a4a2e", fontWeight: 500 }}>
                ✓ Last rollover: {ageGradeResultSummary.advanced} player{ageGradeResultSummary.advanced !== 1 ? "s" : ""} advanced,{" "}
                {ageGradeResultSummary.archived} archived.
              </div>
            )}
            <button
              onClick={() => setConfirmAction({
                title: "Start Season Rollover?",
                message: `This will advance every player up one age grade:\n\n• U12s → U13s, U13s → U14s ... U18s → U19s → Adult\n• Players already at Adult stay exactly where they are — Adult is a permanent grade\n\nThis cannot be undone. Review your roster before proceeding.`,
                confirmLabel: "Roll Over Season",
                danger: true,
                onConfirm: () => handleAgeGradeDecision("advance"),
              })}
              style={{ background: "#7a3e00", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            >
              🔄 Roll Over to New Season
            </button>
          </div>
        )}

        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            danger={confirmAction.danger}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </AppShell>
    );
  }
  if (view === "new") {
    const previewStage = getCorrectStage(form.incidentDate);
    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout} onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount} pageTitle="Log New Concussion"
        pageActions={<button onClick={() => { setForm(blankLogConcussionForm()); setView("dashboard"); }} style={{ fontSize: 13, fontWeight: 600, color: "#7a95ad", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← Back</button>}
      >
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <p style={{ color: "#7a95ad", fontSize: 13, marginBottom: 16 }}>Stage is set automatically from the incident date. Notifications are sent on submission.</p>

          <label style={{
            display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
            background: form.isTest ? "#fffbeb" : "#fff",
            border: `1px solid ${form.isTest ? "#c9991a" : "#e5e7eb"}`,
            borderRadius: 8, padding: "12px 16px", marginBottom: 20,
          }}>
            <input type="checkbox" checked={form.isTest}
              onChange={e => setForm(f => ({ ...f, isTest: e.target.checked }))}
              style={{ accentColor: "#c9991a", width: 16, height: 16 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: form.isTest ? "#92400e" : "#374151" }}>This is a test entry</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>No real email notifications will be sent, and this entry can be permanently deleted later (real entries are archived instead).</div>
            </div>
          </label>

          <Sect title="Player Details">
            <Field label="Full name *">
              <div style={{ position: "relative" }}>
                {/* A combobox, not a decorated text box: the suggestions
                    are real options with roles, and the arrow keys reach
                    them. Picking from the list is what links the record to
                    its roster entry, which in turn decides whether a
                    dual-registered player's other club gets alerted — so a
                    list only a mouse can reach is a safety difference, not
                    just an inconvenience. */}
                <input
                  value={form.name}
                  onChange={e => { handleRosterNameInput(e.target.value); setRosterActiveIndex(-1); }}
                  onFocus={() => form.name && setShowRosterDropdown(true)}
                  onBlur={() => setTimeout(() => { setShowRosterDropdown(false); setRosterActiveIndex(-1); }, 150)}
                  onKeyDown={(e) => {
                    const open = showRosterDropdown && rosterSearchResults.length > 0;
                    if (!open) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setRosterActiveIndex(i => (i + 1) % rosterSearchResults.length);
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setRosterActiveIndex(i => (i <= 0 ? rosterSearchResults.length : i) - 1);
                    } else if (e.key === "Enter" && rosterActiveIndex >= 0) {
                      // Only swallow Enter when a suggestion is actually
                      // highlighted, so it still submits the form otherwise.
                      e.preventDefault();
                      handleSelectRosterPlayer(rosterSearchResults[rosterActiveIndex]);
                      setRosterActiveIndex(-1);
                    } else if (e.key === "Escape") {
                      setShowRosterDropdown(false);
                      setRosterActiveIndex(-1);
                    }
                  }}
                  placeholder="e.g. Alex Taylor — start typing to search the roster"
                  style={inp}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={showRosterDropdown && rosterSearchResults.length > 0}
                  aria-controls="roster-suggestions"
                  aria-autocomplete="list"
                  aria-activedescendant={rosterActiveIndex >= 0 && rosterSearchResults[rosterActiveIndex]
                    ? `roster-option-${rosterSearchResults[rosterActiveIndex].id}` : undefined}
                />
                {showRosterDropdown && rosterSearchResults.length > 0 && (
                  <div
                    id="roster-suggestions"
                    role="listbox"
                    aria-label="Matching roster players"
                    style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, marginTop: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10, maxHeight: 200, overflowY: "auto" }}
                  >
                    {rosterSearchResults.map((entry, i) => (
                      <div
                        key={entry.id}
                        id={`roster-option-${entry.id}`}
                        role="option"
                        aria-selected={i === rosterActiveIndex}
                        onMouseDown={() => handleSelectRosterPlayer(entry)}
                        onMouseEnter={() => setRosterActiveIndex(i)}
                        style={{ padding: "9px 12px", cursor: "pointer", fontSize: 14, borderBottom: "1px solid #f3f4f6", background: i === rosterActiveIndex ? "#f9fafb" : "#fff" }}
                      >
                        <div style={{ fontWeight: 600, color: "#111827" }}>{entry.name}</div>
                        {(entry.age_grade || entry.position) && (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>{[entry.age_grade, entry.position].filter(Boolean).join(" · ")}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Whether this record is tied to a specific roster entry
                  decides whether a dual-registered player's other club is
                  alerted. Typing a name no longer implies that link, so
                  say plainly which state the form is in rather than
                  letting staff assume an alert went out when it didn't.
                  Suppressed for test entries, which never notify anyone. */}
              {/* Hidden only while the suggestion list is actually on
                  screen (it would sit underneath it). showRosterDropdown
                  alone is not enough — it stays true after typing a name
                  that matches nothing, which is precisely the case that
                  most needs the warning. */}
              {!form.isTest && form.name.trim() && !(showRosterDropdown && rosterSearchResults.length > 0) && (
                form.rosterPlayerId ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 7, fontSize: 12.5, color: "#166534", fontWeight: 600 }}>
                    <span aria-hidden="true">✓</span>
                    <span>Linked to this player's roster entry</span>
                  </div>
                ) : (
                  <div style={{ marginTop: 8, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, color: "#92400e", marginBottom: 3 }}>
                      Not linked to a roster entry
                    </div>
                    <div style={{ fontSize: 12.5, color: "#78350f", lineHeight: 1.55 }}>
                      This name was typed in rather than chosen from the roster. The record still saves and
                      notifies parents and contacts as normal — but if this player is also registered with
                      another school or club, that organisation <strong>won't</strong> be alerted.
                      To link them, search above and pick them from the list.
                    </div>
                  </div>
                )
              )}

              {/* Another organisation is already running a protocol for
                  this child. The single most important thing to say on
                  this form, so it sits directly under the name and
                  before anything else is filled in. */}
              {!form.isTest && linkedStatus && linkedStatus.rosterPlayerId === form.rosterPlayerId
                && linkedStatus.activeProtocolsElsewhere?.length > 0 && (
                <div style={{ marginTop: 10, background: "#fef2f2", border: "2px solid #c0272d", borderRadius: 8, padding: "12px 14px" }}>
                  <div style={{ fontWeight: 800, fontSize: 13.5, color: "#c0272d", marginBottom: 6 }}>
                    ⚠️ Already on a protocol at another organisation
                  </div>
                  {linkedStatus.activeProtocolsElsewhere.map((p, i) => (
                    <div key={i} style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.6, marginBottom: 6 }}>
                      <strong>{p.organisationName}</strong> has been managing a protocol since{" "}
                      {new Date(p.incidentDate).toLocaleDateString("en-GB")} — currently{" "}
                      <strong>{STAGES[p.currentStage]?.label || `Stage ${p.currentStage + 1}`}</strong>
                      {p.paused && p.pauseUntil ? `, paused until ${new Date(p.pauseUntil).toLocaleDateString("en-GB")}` : ""}.
                    </div>
                  ))}
                  <div style={{ fontSize: 12.5, color: "#7f1d1d", lineHeight: 1.6, marginBottom: 10 }}>
                    A player should be on one protocol at a time. Speak to them before logging a second one —
                    two schedules for one injury means two different clearance dates.
                    If this really is a separate incident, say so below and it will be recorded for both organisations.
                  </div>
                  <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#7f1d1d", marginBottom: 4 }}>
                    Reason for logging anyway
                  </label>
                  <input
                    value={duplicateOverrideReason}
                    onChange={e => setDuplicateOverrideReason(e.target.value)}
                    placeholder="e.g. Separate incident at Sunday training"
                    style={{ ...inp, borderColor: "#fca5a5" }}
                  />
                </div>
              )}

              {/* Repeat concussions across organisations are invisible to
                  any single one of them, and are exactly what should
                  inform a return-to-play decision. */}
              {!form.isTest && linkedStatus && linkedStatus.rosterPlayerId === form.rosterPlayerId
                && linkedStatus.cumulativeHistory?.atLinkedOrganisations > 0 && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 12px", lineHeight: 1.6 }}>
                  <strong>Concussion history:</strong> {linkedStatus.cumulativeHistory.totalConcussions} recorded in total
                  {" "}({linkedStatus.cumulativeHistory.atThisOrganisation} here,{" "}
                  {linkedStatus.cumulativeHistory.atLinkedOrganisations} at a linked organisation).
                  {linkedStatus.cumulativeHistory.mostRecentIncidentDate && (
                    <> Most recent: {new Date(linkedStatus.cumulativeHistory.mostRecentIncidentDate).toLocaleDateString("en-GB")}.</>
                  )}
                </div>
              )}
            </Field>
            <TwoCol>
              <Field label="Age Grade">
                <select value={form.ageGrade} onChange={e => setForm(f => ({ ...f, ageGrade: e.target.value }))} style={inp}>
                  <option value="">Select</option>
                  {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","U19","Adult"].map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Team (optional)">
                <select value={form.team} onChange={e => setForm(f => ({ ...f, team: e.target.value }))} style={inp}>
                  <option value="">None</option>
                  {teamsList.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  {/* Selecting an existing roster player whose team has since been
                      removed would otherwise show "None" while still silently
                      carrying the old team name, causing submission to fail. */}
                  {form.team && !teamsList.some(t => t.name === form.team) && (
                    <option value={form.team}>{form.team} (removed — clear or re-add it)</option>
                  )}
                </select>
              </Field>
            </TwoCol>
            <Field label="Position">
              <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="e.g. Flanker" style={inp} />
            </Field>
          </Sect>

          <Sect title="Incident">
            {/* Injuries have recorded a time since they were built. A
                concussion only ever captured a date, so the register
                answered "when" for half its rows and shrugged at the
                other half. Optional, because it is often not known. */}
            <TwoCol>
              <Field label="Date of suspected concussion *">
                <input type="date" value={form.incidentDate} max={new Date().toISOString().split("T")[0]} onChange={e => setForm(f => ({ ...f, incidentDate: e.target.value }))} style={inp} />
              </Field>
              <Field label="Time (if known)">
                <input type="time" value={form.incidentTime} onChange={e => setForm(f => ({ ...f, incidentTime: e.target.value }))} style={inp} />
              </Field>
            </TwoCol>
            <div style={{ background: "#1a4a2e11", border: "1px solid #1a4a2e33", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#1a4a2e", marginBottom: 12 }}>
              Day <strong>{getDaysSinceIncident(form.incidentDate)}</strong> of protocol — starting on <strong>{STAGES[previewStage].label}: {STAGES[previewStage].name}</strong>
            </div>
            <Field label="Incident details">
              <textarea value={form.incidentDetails} onChange={e => setForm(f => ({ ...f, incidentDetails: e.target.value }))}
                placeholder="Brief description of how the injury occurred..." rows={3} style={{ ...inp, resize: "vertical" }} />
            </Field>
            <Field label="Context (optional)">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[["match", "🏆 Match play"], ["training_contact", "⚡ Contact training"], ["training_non_contact", "🏃 Non-contact training"], ["other", "❓ Other / unknown"]].map(([val, label]) => (
                  <button key={val} type="button"
                    onClick={() => setForm(f => ({ ...f, incidentContext: f.incidentContext === val ? "" : val }))}
                    style={{ border: `2px solid ${form.incidentContext === val ? "#1a4a2e" : "#e5e7eb"}`, background: form.incidentContext === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 12.5, fontWeight: form.incidentContext === val ? 700 : 500, color: form.incidentContext === val ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Mechanism (optional)">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[["tackle_bc", "🏉 Tackle (ball carrier)"], ["tackle_tackler", "🏉 Tackle (tackler)"], ["ruck_maul", "💥 Ruck / maul"], ["scrum", "🔴 Scrum"], ["collision", "⚡ Player collision"], ["fall", "🌍 Fall / ground"], ["other", "❓ Other"]].map(([val, label]) => (
                  <button key={val} type="button"
                    onClick={() => setForm(f => ({ ...f, incidentMechanism: f.incidentMechanism === val ? "" : val }))}
                    style={{ border: `2px solid ${form.incidentMechanism === val ? "#1a4a2e" : "#e5e7eb"}`, background: form.incidentMechanism === val ? "#f0f7f3" : "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 12.5, fontWeight: form.incidentMechanism === val ? 700 : 500, color: form.incidentMechanism === val ? "#1a4a2e" : "#6b7280", cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    {label}
                  </button>
                ))}
              </div>
            </Field>
          </Sect>

          <Sect title="Symptoms Observed *">
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>Tick all that apply at time of assessment.</p>
            <div className="grtp-symptom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", marginBottom: 12 }}>
              {SYMPTOMS.map(s => (
                <label key={s} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#374151" }}>
                  <input type="checkbox" checked={form.symptoms.includes(s)}
                    onChange={e => setForm(f => ({ ...f, symptoms: e.target.checked ? [...f.symptoms, s] : f.symptoms.filter(x => x !== s) }))}
                    style={{ accentColor: "#c0272d", width: 15, height: 15 }} />
                  {s}
                </label>
              ))}
            </div>
            <Field label="Other symptom">
              <input value={form.otherSymptom} onChange={e => setForm(f => ({ ...f, otherSymptom: e.target.value }))} placeholder="Describe any other symptom..." style={inp} />
            </Field>
          </Sect>

          <Sect title="Parent / Guardian">
            <Field label="Parent / Guardian name">
              <input value={form.parentName} onChange={e => setForm(f => ({ ...f, parentName: e.target.value }))} placeholder="e.g. Sarah Taylor" style={inp} />
            </Field>
            <Field label="Parent email">
              <EmailInput value={form.parentEmail} onChange={v => setForm(f => ({ ...f, parentEmail: v }))} placeholder="parent@example.com" style={inp} />
            </Field>
          </Sect>

          <Sect title="Coaching Contacts (optional)">
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>Anyone added here will also receive GRTP updates. Leave blank if not applicable.</p>
            <TwoCol>
              <Field label="School coach name">
                <input value={form.schoolCoachName} onChange={e => setForm(f => ({ ...f, schoolCoachName: e.target.value }))} placeholder="e.g. J. Smith" style={inp} />
              </Field>
              <Field label="School coach email">
                <EmailInput value={form.schoolCoachEmail} onChange={v => setForm(f => ({ ...f, schoolCoachEmail: v }))} placeholder="j.smith@yourclub.co.uk" style={inp} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Second school coach name">
                <input value={form.schoolCoach2Name} onChange={e => setForm(f => ({ ...f, schoolCoach2Name: e.target.value }))} placeholder="e.g. A. Jones" style={inp} />
              </Field>
              <Field label="Second school coach email">
                <EmailInput value={form.schoolCoach2Email} onChange={v => setForm(f => ({ ...f, schoolCoach2Email: v }))} placeholder="a.jones@yourclub.co.uk" style={inp} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Club coach name">
                <input value={form.clubCoachName} onChange={e => setForm(f => ({ ...f, clubCoachName: e.target.value }))} placeholder="e.g. Dave Wilson" style={inp} />
              </Field>
              <Field label="Club coach email">
                <EmailInput value={form.clubCoachEmail} onChange={v => setForm(f => ({ ...f, clubCoachEmail: v }))} placeholder="coach@localclub.co.uk" style={inp} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Second club coach name">
                <input value={form.clubCoach2Name} onChange={e => setForm(f => ({ ...f, clubCoach2Name: e.target.value }))} placeholder="e.g. Team manager" style={inp} />
              </Field>
              <Field label="Second club coach email">
                <EmailInput value={form.clubCoach2Email} onChange={v => setForm(f => ({ ...f, clubCoach2Email: v }))} placeholder="manager@localclub.co.uk" style={inp} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Additional contact 1 name">
                <input value={form.otherContact1Name} onChange={e => setForm(f => ({ ...f, otherContact1Name: e.target.value }))} placeholder="e.g. Club welfare officer" style={inp} />
              </Field>
              <Field label="Additional contact 1 email">
                <EmailInput value={form.otherContact1Email} onChange={v => setForm(f => ({ ...f, otherContact1Email: v }))} placeholder="welfare@club.co.uk" style={inp} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Additional contact 2 name">
                <input value={form.otherContact2Name} onChange={e => setForm(f => ({ ...f, otherContact2Name: e.target.value }))} placeholder="e.g. Second parent" style={inp} />
              </Field>
              <Field label="Additional contact 2 email">
                <EmailInput value={form.otherContact2Email} onChange={v => setForm(f => ({ ...f, otherContact2Email: v }))} placeholder="secondparent@email.com" style={inp} />
              </Field>
            </TwoCol>
            <Field label="Responsible staff email (internal record, optional)">
              <EmailInput value={form.staffEmail} onChange={v => setForm(f => ({ ...f, staffEmail: v }))} placeholder="j.smith@yourclub.co.uk" style={inp} />
            </Field>
          </Sect>

          {/* Parental consent — UK GDPR Article 9 requires explicit, informed,
              affirmative consent before processing special category (health)
              data about a child. This section must not be pre-checked and
              must appear before submission. Test entries are exempt. */}
          {!form.isTest && (
            <div style={{ background: form.consentGiven ? "#f0fdf4" : "#fffbeb", border: `2px solid ${form.consentGiven ? "#86efac" : "#c9991a"}`, borderRadius: 10, padding: 20, marginBottom: 20 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827", marginBottom: 12 }}>
                ⚖️ Parent or guardian informed — Required
              </div>
              <p style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.7, margin: "0 0 16px" }}>
                Before logging this concussion, confirm the player's parent or guardian has been told:
              </p>
              <ul style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.8, margin: "0 0 16px 20px", padding: 0 }}>
                <li><strong>What is recorded</strong> — their child's name, age grade, symptoms, and recovery progress, held in GRTP Tracker, operated by Sports Welfare Systems Ltd and stored securely in the EU</li>
                <li><strong>Who sees it</strong> — the coaching, PE, and welfare staff responsible for this player, and anyone listed as a contact on this form</li>
                <li><strong>What is sent</strong> — automated email updates to those contacts at each stage of the Graduated Return to Play protocol</li>
                <li><strong>How long it is kept</strong> — until the player's 25th birthday, or seven years from the incident, whichever is later, then deleted automatically</li>
              </ul>
              <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, margin: "0 0 16px", fontStyle: "italic" }}>
                This record is kept to meet your organisation's safeguarding duty of care, so it does not depend on consent
                and is not deleted if a parent later objects. Parents keep every other right — a copy, corrections, or an
                objection to how it is used. If you have added contacts who are neither the parent nor your own staff, the
                parent should have agreed to those people receiving updates.
              </p>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.consentGiven}
                  onChange={e => setForm(f => ({ ...f, consentGiven: e.target.checked }))}
                  style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, accentColor: "#1a4a2e" }}
                />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.6 }}>
                  I confirm the player's parent or guardian has been informed as set out above.
                </span>
              </label>
            </div>
          )}

          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={handleNewPlayer} style={{ ...btnP, flex: 1, opacity: (!form.isTest && !form.consentGiven) ? 0.5 : 1 }}>Start GRAS Programme</button>
            <button onClick={() => { setForm(blankLogConcussionForm()); setView("dashboard"); }} style={btnS}>Cancel</button>
          </div>
        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
        {/* "Leave without saving?", asked by Android's back button and the
            iPhone's swipe back. This screen never showed it, so back on a
            half-filled concussion form appeared to do nothing at all. */}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            danger={confirmAction.danger}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </AppShell>
    );
  }

  // ── DETAIL ────────────────────────────────────────────────────
  if (view === "detail" && selectedPlayer) {
    const p = selectedPlayer;
    const stage = STAGES[p.current_stage];
    const day = protocolDay(p);
    const ended = protocolHasEnded(p);
    const daysUntilNext = p.current_stage < 4 ? Math.max(0, STAGES[p.current_stage + 1].dayStart - day) : null;
    const canUndo = undoTarget?.playerId === p.id && Date.now() < undoTarget.expiresAt;
    const resumeStage = p.pause_from_stage != null ? STAGES[p.pause_from_stage] : null;
    const pauseUntilDate = p.pause_until ? new Date(p.pause_until) : null;
    const hoursRemaining = pauseUntilDate ? Math.max(0, Math.ceil((pauseUntilDate - Date.now()) / 3600000)) : 0;

    return (
      <AppShell view={view} setView={setView} orgName={orgName} isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting} onLogout={handleLogout}
        onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
        pageTitle={p.name}
        pageActions={<button onClick={() => setView("dashboard")} style={{ fontSize: 13, fontWeight: 600, color: "#7a95ad", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>← Dashboard</button>}
      >
        <div style={{ maxWidth: 820, margin: "0 auto" }}>

          {/* Concussion emails that bounced: these people weren't told.
              First on the page, with what to do and a way to record it. */}
          {Array.isArray(p.emailBounces) && p.emailBounces.length > 0 && (
            <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderLeft: "4px solid #c0272d", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#991b1b", marginBottom: 6 }}>
                ✉ {p.emailBounces.length === 1 ? "An email about this player bounced" : `${p.emailBounces.length} emails about this player bounced`}
              </div>
              {p.emailBounces.map(b => (
                <div key={b.recipientEmail} style={{ borderTop: "1px solid #fecaca", paddingTop: 10, marginTop: 10 }}>
                  <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.55, overflowWrap: "anywhere" }}>
                    The email to <strong>{b.role}</strong> at <strong>{b.recipientEmail}</strong> on {new Date(b.bouncedAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} didn't arrive, so they may not know about this player's concussion or current stage.
                    {b.reason && <span style={{ display: "block", fontSize: 12, color: "#991b1b", marginTop: 2 }}>Reason given: {b.reason}</span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: "#7f1d1d", margin: "6px 0 8px", lineHeight: 1.5 }}>
                    Please contact them another way, and correct the address on the Roster if it's wrong.
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={handleOpenRoster}
                      style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                      Open the Roster
                    </button>
                    <button
                      disabled={resolvingBounce === b.recipientEmail}
                      onClick={async () => {
                        setResolvingBounce(b.recipientEmail);
                        try {
                          const updated = await api.resolveEmailBounce(p.id, b.recipientEmail);
                          setPlayers(prev => prev.map(x => x.id === updated.id ? updated : x));
                          showNotif("Recorded that they've been contacted.");
                        } catch (e) {
                          // Often someone else got there first, or the
                          // address was corrected: show the current picture.
                          showNotif(e.message, "error");
                          loadPlayers();
                        } finally {
                          setResolvingBounce(null);
                        }
                      }}
                      style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12.5, fontWeight: 700, cursor: resolvingBounce === b.recipientEmail ? "default" : "pointer", opacity: resolvingBounce === b.recipientEmail ? 0.7 : 1 }}>
                      {resolvingBounce === b.recipientEmail ? "Saving..." : "I've contacted them"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
            <div>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: 24, color: "#1a4a2e", margin: "0 0 6px" }}>{p.name}</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {p.age_grade && <Badge color="#6b7280">{p.age_grade}</Badge>}
                {p.position && <Badge color="#6b7280">{p.position}</Badge>}
                {p.is_test && <Badge color="#9333ea">Test Entry</Badge>}
                {p.archived && <Badge color="#6b7280">Archived</Badge>}
                {p.paused
                  ? <Badge color="#c9991a">48hr Rest</Badge>
                  : p.status === "removed_early"
                  ? <Badge color="#6b7280">Removed Early</Badge>
                  : <Badge color={p.status === "cleared" ? "#1a4a2e" : "#c0272d"}>{p.status === "cleared" ? "Protocol Complete" : "GRTP Active"}</Badge>
                }
                <button onClick={() => handleDownloadPDF(p)} style={{ background: "#fff", color: "#1a4a2e", border: "1px solid #1a4a2e", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginLeft: 4 }}>
                  📄 Incident Report (PDF)
                </button>
                {isOrgAdmin && (
                  <button
                    onClick={async () => {
                      try { await saveBlob(await api.downloadSarExport(p.id), `sar-${(p.name || "player").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${new Date().toISOString().split("T")[0]}.json`); }
                      catch (e) { showNotif(e.message, "error"); }
                    }}
                    title="Download everything held about this player, for answering a Subject Access Request"
                    style={{ background: "#fff", color: "#6b7280", border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  >
                    ⬇ Data Export (SAR)
                  </button>
                )}
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 13, color: "#6b7280", lineHeight: 1.8 }}>
              <div>Incident: <strong>{formatDate(p.incident_date)}</strong>{p.incident_time ? <span style={{ color: "#6b7280" }}> · {p.incident_time}</span> : null}</div>
              <div>
                {ended ? "Protocol ended:" : "Protocol day:"}{" "}
                <strong style={{ color: ended ? "#6b7280" : "#1a4a2e", fontSize: 15 }}>Day {day}</strong>
              </div>
            </div>
          </div>

          {/* Day progress bar */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
              <span>Day 0</span>
              {/* A finished protocol says where it stopped, not where it
                  would be. Highlighting a "current" stage on a record that
                  ended reads as though the programme is still running. */}
              <span style={{ fontWeight: 700, color: ended ? "#6b7280" : "#1a4a2e" }}>
                {ended
                  ? `${p.status === "cleared" ? "Completed" : "Stopped"} on Day ${Math.min(day, 21)}`
                  : `Day ${Math.min(day, 21)} of 21`}
              </span>
              <span>Day 21+</span>
            </div>
            <div style={{ height: 10, background: "#f3f4f6", borderRadius: 99, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.min((day / 21) * 100, 100)}%`, background: ended ? "#9ca3af" : "linear-gradient(90deg, #c0272d, #c9991a, #1a4a2e)", borderRadius: 99, transition: "width 0.4s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              {STAGES.map(s => (
                <div key={s.id} style={{ fontSize: 10, color: !ended && s.id === p.current_stage ? s.color : "#9ca3af", fontWeight: !ended && s.id === p.current_stage ? 700 : 400, textAlign: "center" }}>
                  S{stageNumber(s)}<br />{s.dayStart}{s.dayEnd ? `–${s.dayEnd}` : "+"}
                </div>
              ))}
            </div>
          </div>

          {/* Paused banner */}
          {p.paused && resumeStage && (
            <div style={{ background: "#fffbeb", border: "2px solid #c9991a", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: "#92400e", fontSize: 15, marginBottom: 4 }}>⏸ 48-Hour Symptom Rest</div>
              <p style={{ margin: "0 0 8px", fontSize: 14, color: "#78350f" }}>
                {p.name} is in a 48-hour rest period following symptom return. They will automatically resume at <strong>{resumeStage.label}: {resumeStage.name}</strong> in approximately <strong>{hoursRemaining} hours</strong>. Stage 5 (Full Contact) has been delayed by 14 days.
              </p>
              <div style={{ fontSize: 13, color: "#92400e" }}>Resumes: {formatDate(p.pause_until)}</div>
              {canUndo && (
                <button onClick={handleUndo} style={{ marginTop: 12, background: "#c9991a", color: "#2b2000", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  ↩ Undo — This Was a Mistake
                </button>
              )}
            </div>
          )}

          {/* Current stage */}
          {!p.paused && p.status !== "removed_early" && (
            <div style={{ background: "#fff", border: `2px solid ${stage.color}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: stage.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                  {stageNumber(stage)}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: stage.color, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Current Stage</div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>{stage.label}: {stage.name}</div>
                </div>
              </div>
              <ul style={{ margin: "0 0 10px 20px", padding: 0, fontSize: 14, color: "#374151", lineHeight: 1.9 }}>
                {stage.activities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
              <div style={{ background: stage.color + "11", borderLeft: `3px solid ${stage.color}`, padding: "8px 12px", fontSize: 13, color: "#374151", borderRadius: "0 4px 4px 0" }}>
                {stage.notes}
              </div>
              {daysUntilNext !== null && (
                <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280", textAlign: "center" }}>
                  {daysUntilNext === 0
                    ? <span style={{ color: "#1a4a2e", fontWeight: 700 }}>Ready to advance — will update at next check-in.</span>
                    : <span><strong style={{ color: "#1a4a2e" }}>{daysUntilNext} day{daysUntilNext !== 1 ? "s" : ""}</strong> until auto-advance to {STAGES[p.current_stage + 1]?.label}</span>
                  }
                </div>
              )}
            </div>
          )}

          {/* Protocol schedule complete - NOT a medical clearance */}
          {p.status === "cleared" && !p.paused && (
            <div style={{ background: "#1a4a2e11", border: "2px solid #1a4a2e", borderRadius: 10, padding: 16, marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>✅</div>
              <div style={{ fontWeight: 700, color: "#1a4a2e", fontSize: 16, marginBottom: 8 }}>{p.name} has reached the end of the GRAS programme's recommended 21-day schedule.</div>
              <div style={{ fontSize: 12.5, color: "#374151", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, textAlign: "left" }}>
                <strong>This is not an automatic medical clearance.</strong> Return to play must be confirmed by a qualified medical professional or your school/club's designated lead.
              </div>
            </div>
          )}

          {p.status === "removed_early" && (
            <div style={{ background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 10, padding: 16, marginBottom: 16, textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>⤴</div>
              <div style={{ fontWeight: 700, color: "#374151", fontSize: 16, marginBottom: 8 }}>{p.name} was removed from the GRAS programme early, before reaching Day 21.</div>
              <div style={{ fontSize: 12.5, color: "#374151", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, textAlign: "left" }}>
                <strong>Reason given:</strong> {p.removal_reason || "No reason recorded."}
                {p.removed_at && <div style={{ marginTop: 4, color: "#6b7280" }}>Removed on {formatDate(p.removed_at)}.</div>}
              </div>
            </div>
          )}

          {/* Symptom return */}
          {p.status !== "cleared" && p.status !== "removed_early" && !p.paused && (
            <div style={{ background: "#fff9f9", border: "1px solid #fca5a5", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#c0272d", marginBottom: 6 }}>Symptom Return</div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px" }}>
                If {p.name} reports any symptoms returning, use this button. Two things happen:
                <br/><strong>1.</strong> A 48-hour rest period starts — they automatically resume at <strong>{stage.label}</strong> afterwards.
                <br/><strong>2.</strong> Stage 5 (Full Contact) is pushed back 14 days from today, as required by the HEADCASE GRAS guidelines.
                <br/>Parents and staff will be notified of both.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button onClick={() => handleSymptomReturn(p)} style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  ⚠ Symptoms Returned — Start 48hr Rest
                </button>
                {canUndo && (
                  <button onClick={handleUndo} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    ↩ Undo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Taken off the protocol early: usually because a doctor has
              taken over the player's recovery. The guidance allows a return
              before Day 21 only with a doctor overseeing it, so this panel
              no longer offers a club official's clearance as a reason. */}
          {p.status === "active" && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#374151", marginBottom: 6 }}>Removed from Protocol</div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px" }}>
                If a doctor is now overseeing {p.name}'s recovery, or the protocol needs to stop for another reason, record that here. A return to play before Day 21 is only possible with a doctor overseeing it. A reason is required — this is logged on {p.name}'s record and sent to parents and staff, who are told it is not by itself a clearance to play.
              </p>
              <button onClick={() => { setRemoveEarlyTarget(p); setRemoveEarlyReason(""); }} style={{ background: "#fff", color: "#374151", border: "1px solid #9ca3af", borderRadius: 6, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                ⤴ Removed from Protocol
              </button>
            </div>
          )}

          {/* Email draft — manual copy stopgap. Hidden for removed_early players
              since a dedicated removal email was already sent automatically,
              and showing the stage-guidance draft here would be misleading. */}
          {p.status !== "removed_early" && (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Parent / Staff Notification Draft</div>
              <button
                onClick={() => {
                  const draft = generateEmailDraft(p, orgName);
                  navigator.clipboard?.writeText(draft);
                  showNotif("Email copied to clipboard.");
                }}
                style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Copy Email
              </button>
            </div>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 10px" }}>
              Updates are emailed automatically when the stage changes. The parent's email also carries the symptoms and incident notes; nobody else's does. This copy leaves them out, so it is safe to forward by hand &mdash; but not to an additional contact who has not yet confirmed they want updates.
            </p>
            <pre style={{ fontFamily: "inherit", fontSize: 12, color: "#374151", background: "#f9fafb", borderRadius: 6, padding: 14, whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7, maxHeight: 280, overflowY: "auto" }}>
              {generateEmailDraft(p, orgName)}
            </pre>
            {(p.parent_email || p.staff_email || p.school_coach_email || p.club_coach_email || p.other_contact_1_email || p.other_contact_2_email) && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                {p.parent_email && <div>Parent ({p.parent_name || "—"}): {p.parent_email}</div>}
                {p.staff_email && <div>School Staff: {p.staff_email}</div>}
                {p.school_coach_email && <div>School Coach ({p.school_coach_name || "—"}): {p.school_coach_email}</div>}
                {p.club_coach_email && <div>Club Coach ({p.club_coach_name || "—"}): {p.club_coach_email}</div>}
                {p.other_contact_1_email && <div>Additional Contact 1 ({p.other_contact_1_name || "—"}): {p.other_contact_1_email}</div>}
                {p.other_contact_2_email && <div>Additional Contact 2 ({p.other_contact_2_name || "—"}): {p.other_contact_2_email}</div>}
              </div>
            )}
            {p.parent_acknowledged_at ? (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 10px" }}>
                <span>✅</span>
                <span>
                  {p.age_grade === "Adult" ? "Player" : "Parent"} confirmed reading this on {new Date(p.parent_acknowledged_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at {new Date(p.parent_acknowledged_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  {p.parent_acknowledged_via === "parent_portal" ? " (in the parent portal)" : p.parent_acknowledged_via === "tracking_link" ? " (on their tracking link)" : ""}
                </span>
              </div>
            ) : p.parent_email && !p.is_test ? (
              familyAckState({ sharedAt: p.created_at, acknowledgedAt: null }, { live: p.status === "active" }) === "overdue" ? (
                <div style={{ marginTop: 10, fontSize: 12, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "6px 10px" }}>
                  {p.age_grade === "Adult" ? "The player" : "The parent"} hasn't confirmed reading this after 24 hours.
                  {p.ack_reminder_sent_at ? ` A reminder email was sent on ${formatAckTime(p.ack_reminder_sent_at)}.` : ""} It's worth a phone call to check they've seen it.
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
                  {p.age_grade === "Adult" ? "The player" : "The parent"} hasn't yet confirmed reading this.
                </div>
              )
            ) : null}
            {p.consent_given_at ? (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "6px 10px" }}>
                <span>⚖️</span>
                <span>Parental consent recorded on {new Date(p.consent_given_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} at {new Date(p.consent_given_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} ({p.consent_version || "v1.0"})</span>
              </div>
            ) : !p.is_test ? (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#c0272d", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "6px 10px" }}>
                <span>⚠️</span>
                <span>No parental consent on record — this player was logged before the consent requirement was introduced.</span>
              </div>
            ) : null}
          </div>
          )}

          {/* Symptoms recorded */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 10 }}>Recorded Symptoms</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(Array.isArray(p.symptoms) ? p.symptoms : JSON.parse(p.symptoms || "[]")).map(s => (
                <Badge key={s} color="#c0272d">{s}</Badge>
              ))}
            </div>
            {p.incident_details && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#6b7280", borderTop: "1px solid #f3f4f6", paddingTop: 10 }}>
                <strong>Incident:</strong> {p.incident_details}
              </div>
            )}
            {(p.incident_context || p.incident_mechanism) && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#6b7280", display: "flex", gap: 12, flexWrap: "wrap" }}>
                {p.incident_context && (
                  <span style={{ background: "#f0f4f8", borderRadius: 6, padding: "2px 10px", fontWeight: 600 }}>
                    {({ match: "🏆 Match play", training_contact: "⚡ Contact training", training_non_contact: "🏃 Non-contact training", other: "❓ Other" })[p.incident_context] || p.incident_context}
                  </span>
                )}
                {p.incident_mechanism && (
                  <span style={{ background: "#f0f4f8", borderRadius: 6, padding: "2px 10px", fontWeight: 600 }}>
                    {({ tackle_bc: "🏉 Tackle (BC)", tackle_tackler: "🏉 Tackle (tackler)", ruck_maul: "💥 Ruck/maul", scrum: "🔴 Scrum", collision: "⚡ Collision", fall: "🌍 Fall", other: "❓ Other" })[p.incident_mechanism] || p.incident_mechanism}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Notification delivery log */}
          {(notifHistory[p.id] || []).length > 0 && (
            <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 12 }}>Notification Log</div>
              {(() => {
                // Group events by resend_email_id, collapsing sent/delivered/opened
                // into one row per email so the list doesn't show three separate
                // entries for what is ultimately one notification.
                const byEmail = {};
                for (const e of notifHistory[p.id]) {
                  // Group by resend_email_id when available, collapsing
                  // sent/delivered/opened for one email into one row.
                  // Fall back to the row's own database id when there's
                  // no resend_email_id (e.g. send failed before Resend
                  // returned an id) — this guarantees uniqueness and
                  // prevents multiple null-id rows from incorrectly
                  // merging together.
                  const key = e.resend_email_id || `row-${e.id}`;
                  if (!byEmail[key]) byEmail[key] = { ...e, statuses: [] };
                  byEmail[key].statuses.push(e.event_type);
                }
                return Object.values(byEmail).map((entry, i) => {
                  // Opens are no longer recorded. Older records may still
                  // carry one; an open implies delivery, so it reads as
                  // delivered rather than claiming a parent read it.
                  const isDelivered = entry.statuses.includes("delivered") || entry.statuses.includes("opened");
                  const isBounced = entry.statuses.includes("bounced");
                  const statusIcon = isBounced ? "❌" : isDelivered ? "✅" : "📤";
                  const statusText = isBounced ? "Bounced" : isDelivered ? "Delivered" : "Sent";
                  const statusColor = isBounced ? "#c0272d" : isDelivered ? "#15803d" : "#6b7280";
                  const eventLabel = {
                    initial: "Initial notification",
                    advance: "Stage advance",
                    symptom_return: "Symptom return",
                    cleared: "Protocol complete",
                    removed_early: "Removed early",
                    reminder: "Reminder",
                  }[entry.grtp_event_type] || entry.grtp_event_type;
                  return (
                    <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start", fontSize: 13 }}>
                      <div style={{ fontSize: 14, marginTop: 1 }}>{statusIcon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: "#111827" }}>{eventLabel} → {entry.recipient_email || "unknown"}</div>
                        <div style={{ color: statusColor, fontSize: 12 }}>{statusText} · {new Date(entry.occurred_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} {new Date(entry.occurred_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  );
                });
              })()}
              <button
                onClick={() => api.getPlayerNotifications(p.id).then(events => setNotifHistory(prev => ({ ...prev, [p.id]: events })))}
                style={{ marginTop: 8, background: "none", border: "1px solid #d1d5db", color: "#6b7280", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}
              >
                ↻ Refresh
              </button>
            </div>
          )}

          {/* History */}
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 12 }}>Full Protocol History</div>
            {(p.stageHistory || []).map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: STAGES[h.stage]?.color || "#9ca3af", marginTop: 5, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                    {STAGES[h.stage]?.label}: {STAGES[h.stage]?.name}
                    {h.auto ? <span style={{ marginLeft: 6, fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>auto</span> : null}
                  </div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(h.changed_at)} — {h.notes}</div>
                </div>
              </div>
            ))}
          </div>

          {/* GRTP pathway */}
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 12 }}>Full GRTP Pathway</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {STAGES.map(s => {
                const isActive = s.id === p.current_stage && !p.paused;
                const isDone = s.id < p.current_stage && !p.paused;
                return (
                  <div key={s.id} style={{ border: `1px solid ${isActive ? s.color : "#e5e7eb"}`, borderLeft: `4px solid ${s.color}`, borderRadius: 6, padding: "10px 14px", background: isActive ? s.color + "08" : "transparent", opacity: !isActive && !isDone ? 0.5 : 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 22, height: 22, borderRadius: "50%", background: isDone ? s.color : isActive ? s.color : "#e5e7eb", color: isDone || isActive ? "#fff" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {isDone ? "✓" : stageNumber(s)}
                      </span>
                      <span style={{ fontWeight: 700, fontSize: 13, color: isActive ? s.color : isDone ? "#374151" : "#9ca3af" }}>
                        {s.label}: {s.name}
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "#9ca3af" }}>Day {s.dayStart}{s.dayEnd ? `–${s.dayEnd}` : "+"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Record management */}
          <div style={{ background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#374151", marginBottom: 10 }}>Record Management</div>
            {p.archived ? (
              <>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px" }}>
                  This record is archived and hidden from the active dashboard, but kept on file.
                </p>
                <button onClick={() => handleUnarchive(p)} style={{ background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  ↩ Restore to Active Dashboard
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 10px" }}>
                  {p.is_test
                    ? "This is a test entry. Archiving removes it from the active dashboard. To permanently delete it, use the Admin area in the Archive view."
                    : "Real records can't be permanently deleted from here. Archiving removes it from the active dashboard while keeping the full history on file for safeguarding purposes."}
                </p>
                <button onClick={() => handleDeletePlayer(p)} style={{ background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  📁 Archive Record
                </button>
              </>
            )}
          </div>

        </div>
        {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.title}
            message={confirmAction.message}
            confirmLabel={confirmAction.confirmLabel}
            danger={confirmAction.danger}
            onConfirm={confirmAction.onConfirm}
            onCancel={() => setConfirmAction(null)}
          />
        )}
        {removeEarlyTarget && (
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3000 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "'Inter', system-ui, sans-serif" }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", marginBottom: 8 }}>Remove {removeEarlyTarget.name} from the protocol?</div>
              <p style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6, marginBottom: 16 }}>
                This records that {removeEarlyTarget.name} was cleared before the protocol's 21-day schedule completed — distinct from reaching Day 21 naturally. Parents and staff will be notified, and this cannot easily be undone.
              </p>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                Reason for removal <span style={{ color: "#c0272d" }}>(required)</span>
              </label>
              <textarea
                value={removeEarlyReason}
                onChange={e => setRemoveEarlyReason(e.target.value)}
                autoFocus
                rows={4}
                placeholder="e.g. Assessed and cleared by Dr. Smith on 25/06/2026"
                style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", marginBottom: 18 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={handleRemoveEarly}
                  disabled={removeEarlySubmitting || !removeEarlyReason.trim()}
                  style={{ flex: 1, background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: removeEarlySubmitting || !removeEarlyReason.trim() ? "default" : "pointer", opacity: removeEarlySubmitting || !removeEarlyReason.trim() ? 0.5 : 1 }}
                >
                  {removeEarlySubmitting ? "Removing..." : "Yes, Remove from Protocol"}
                </button>
                <button
                  onClick={() => { setRemoveEarlyTarget(null); setRemoveEarlyReason(""); }}
                  style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </AppShell>
    );
  }

  // ── DASHBOARD ─────────────────────────────────────────────────
  const activeCount = players.filter(p => !p.archived && p.status === "active" && !p.paused).length;
  const pausedCount = players.filter(p => !p.archived && p.paused).length;
  const clearedCount = players.filter(p => !p.archived && p.status === "cleared").length;
  const archivedCount = players.filter(p => p.archived).length;

  // Filter by search query (name, year group, position all match)
  const query = searchQuery.trim().toLowerCase();
  const filteredPlayers = players.filter(p => {
    // Dashboard view: hide archived players entirely.
    // Archive view: show ONLY archived players, nothing else.
    if (showArchived ? !p.archived : p.archived) return false;
    if (filterTeam && (p.team || "") !== filterTeam) return false;
    // Incident dates are stored as YYYY-MM-DD, so a string comparison
    // against the date inputs (also YYYY-MM-DD) orders correctly without
    // parsing either into a Date and inheriting a timezone with it.
    if (filterFrom && (p.incident_date || "") < filterFrom) return false;
    if (filterTo && (p.incident_date || "") > filterTo) return false;
    if (!query) return true;
    return (
      p.name.toLowerCase().includes(query) ||
      (p.age_grade || "").toLowerCase().includes(query) ||
      (p.position || "").toLowerCase().includes(query) ||
      (p.team || "").toLowerCase().includes(query)
    );
  });

  // What the export buttons will actually produce. Kept next to the
  // filter that decides it, because the whole point is that the button
  // cannot describe one thing and download another.
  //
  // The Archive view counts as a filter in its own right. Without this a
  // button reading "all" on a screen showing only archived records would
  // quietly download the live ones too. On the Dashboard with nothing set,
  // "all" still means every record, archived included, as it always has.
  const hasClearableFilters = Boolean(query || filterTeam || filterFrom || filterTo);
  const exportIsFiltered = hasClearableFilters || Boolean(showArchived);

  // Test entries are listed on screen with a badge, but no export has ever
  // contained them and none should — they are not real children. The
  // server drops them too; dropping them here as well is what keeps the
  // number on the button equal to the number of rows in the file.
  const exportablePlayers = filteredPlayers.filter(p => !p.is_test);
  const testEntriesHidden = filteredPlayers.length - exportablePlayers.length;
  const exportDescription = [
    showArchived && "archived records only",
    filterTeam && `team ${filterTeam}`,
    filterFrom && `from ${filterFrom}`,
    filterTo && `to ${filterTo}`,
    query && `matching "${searchQuery.trim()}"`,
  ].filter(Boolean).join(", ");

  // Split into two genuinely distinct groups, rather than mixing every
  // outcome together: players still actively on the protocol, and
  // players whose protocol has ended one way or another (completed the
  // full 21-day schedule, or removed early by medical decision). This
  // only applies on the normal dashboard — the Archive view already has
  // its own separate meaning and isn't split this way.
  // Protocols being run by another organisation for a child who is also
  // registered here. Shared with us, and we are entitled to act on it —
  // so it belongs in the list a coach actually searches, not only in a
  // banner they may have scrolled past. Filtered by the same query, or
  // searching a linked player's name mid-protocol returns nothing at all
  // and reads as "no concussion on record".
  //
  // It never becomes ours: not counted as our own, not in our exports,
  // not editable. See the rows themselves.
  const filteredCrossOrg = (crossOrgProtocols || []).filter(p => {
    if (showArchived) return false;
    if (!query) return true;
    return (
      (p.playerName || "").toLowerCase().includes(query) ||
      (p.ageGrade || "").toLowerCase().includes(query) ||
      (p.organisationName || "").toLowerCase().includes(query)
    );
  });

  const activePlayers = showArchived ? filteredPlayers : filteredPlayers.filter(p => p.status === "active");
  // Completed section: only show players cleared in the last 30 days.
  // Older records are still fully accessible via Roster — they just don't
  // clutter the daily dashboard view. Sorted newest-first so the most
  // recent completions appear at the top.
  const COMPLETED_DAYS_VISIBLE = 30;
  const COMPLETED_PREVIEW_COUNT = 3;
  const allCompletedPlayers = showArchived ? [] : filteredPlayers.filter(p => p.status !== "active");
  const completedPlayers = allCompletedPlayers.filter(p => {
    if (!p.incident_date) return true;
    const daysSince = Math.floor((Date.now() - new Date(p.incident_date).getTime()) / 86400000);
    return daysSince <= COMPLETED_DAYS_VISIBLE + 21; // incident + 21-day protocol + 30 days
  }).sort((a, b) => new Date(b.incident_date) - new Date(a.incident_date));
  const hiddenCompletedCount = allCompletedPlayers.length - completedPlayers.length;

  function groupByAgeGrade(list) {
    const grouped = {};
    list.forEach(p => {
      const key = p.age_grade || "Unspecified Age Grade";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(p);
    });
    const groupKeys = Object.keys(grouped).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ""), 10);
      const numB = parseInt(b.replace(/\D/g, ""), 10);
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;
      return numA - numB;
    });
    return { grouped, groupKeys };
  }

  // On the Archive view, every filtered player is shown together under
  // one grouping, same as before this change — only the normal
  // dashboard gets the new active/completed split.
  const { grouped, groupKeys } = groupByAgeGrade(showArchived ? filteredPlayers : activePlayers);
  const { grouped: completedGrouped, groupKeys: completedGroupKeys } = groupByAgeGrade(completedPlayers);

  function renderGroupedCards(groupsObj, keysArr) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {keysArr.map(groupKey => (
          <div key={groupKey}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
              {groupKey}
              <span style={{ background: "#e5e7eb", color: "#6b7280", borderRadius: 99, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{groupsObj[groupKey].length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {groupsObj[groupKey].map(p => {
                const stage = STAGES[p.current_stage];
                const day = protocolDay(p);
                const daysUntilNext = p.current_stage < 4 ? Math.max(0, STAGES[p.current_stage + 1].dayStart - day) : null;
                return (
                  <div key={p.id} onClick={() => { setSelectedId(p.id); setView("detail"); }}
                    style={{ background: "#fff", border: "1px solid #e5e7eb", borderLeft: `4px solid ${p.archived ? "#9ca3af" : p.status === "removed_early" ? "#6b7280" : p.paused ? "#c9991a" : stage.color}`, borderRadius: 8, padding: "14px 16px", cursor: "pointer", opacity: p.archived ? 0.7 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.07)"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: p.archived ? "#9ca3af" : p.status === "removed_early" ? "#6b7280" : p.paused ? "#c9991a" : stage.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: p.archived || p.status === "removed_early" || p.paused ? 14 : 12, flexShrink: 0 }}>
                          {p.archived ? "📁" : p.status === "removed_early" ? "⤴" : p.paused ? "⏸" : stage.label.replace("Stage ", "")}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", display: "flex", alignItems: "center", gap: 6 }}>
                            {p.name}
                            {p.is_test && <Badge color="#9333ea">Test</Badge>}
                            {p.team && <Badge color="#3b5bdb">{p.team}</Badge>}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {p.position && `${p.position} · `}Day <strong>{day}</strong> · {formatDate(p.incident_date)}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: p.archived ? "#6b7280" : p.status === "removed_early" ? "#6b7280" : p.paused ? "#c9991a" : stage.color }}>
                          {p.archived ? "Archived" : p.status === "removed_early" ? "Removed Early" : p.paused ? "48hr Symptom Rest" : `${stage.label}: ${stage.name}`}
                        </div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>
                          {p.archived ? "Click to view or restore"
                            : p.status === "removed_early" ? (p.removal_reason || "No reason recorded")
                            : p.status === "cleared" ? "Schedule complete — RTP decision needed"
                            : p.paused ? `Resumes ${formatDate(p.pause_until)}`
                            : p.stage5_earliest && new Date(p.stage5_earliest) > new Date() && p.current_stage >= 2
                              ? `${STAGES[3]?.label} available ${formatDate(p.stage5_earliest)}`
                            : daysUntilNext === 0 ? "Advancing soon"
                            : `${daysUntilNext}d until ${STAGES[p.current_stage + 1]?.label || "next stage"}`}
                        </div>
                        {showArchived && isOrgAdmin && p.archived && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePermanentDelete(p); }}
                            style={{ marginTop: 6, background: "#fff", color: "#c0272d", border: "1px solid #c0272d", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                          >
                            🗑 Delete Permanently
                          </button>
                        )}
                      </div>
                    </div>
                    {/* The family's read confirmation, on its own line under
                        the name. Inside the right-hand stage block it moved
                        left or right with the length of the stage name once
                        that block wrapped on a phone. */}
                    {!p.archived && !p.is_test && p.parent_email
                      && familyAckState({ sharedAt: p.created_at, acknowledgedAt: p.parent_acknowledged_at }, { live: p.status === "active" }) && (
                      <div style={{ marginTop: 8, paddingLeft: 46 }}>
                        <AckBadge
                          ack={{ sharedAt: p.created_at, acknowledgedAt: p.parent_acknowledged_at }}
                          live={p.status === "active"}
                          who={p.age_grade === "Adult" ? "Player" : "Parent"} />
                      </div>
                    )}
                    {/* A concussion email that bounced: someone wasn't told.
                        Shown to everyone who can see the player, until the
                        address is fixed or someone has contacted them. */}
                    {!p.archived && Array.isArray(p.emailBounces) && p.emailBounces.length > 0 && (
                      <div style={{ marginTop: 8, paddingLeft: 46 }}>
                        <span style={{ display: "inline-block", maxWidth: "100%", boxSizing: "border-box", whiteSpace: "normal", lineHeight: 1.35, fontSize: 11.5, fontWeight: 700, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, padding: "2px 7px" }}>
                          ✉ Email bounced: {p.emailBounces.map(b => b.role).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <AppShell
      view={view} setView={setView} orgName={orgName}
      isOrgAdmin={isOrgAdmin} pendingRegCount={registrationsWaiting}
      onLogout={handleLogout}
      onOpenRoster={handleOpenRoster} onOpenStaff={handleOpenStaffManagement} onOpenActivity={() => { setView("activity"); loadActivityLog(); }} onOpenSecurity={() => { setView("security"); loadMfaStatus(); }} onOpenRegistrations={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }} onOpenAnalytics={() => { setView("analytics"); loadAnalytics(); }} onOpenDataProtection={handleOpenDataProtection} onOpenMessages={() => { setView("messages"); loadMessageThreads(); }} messagesUnreadCount={messagesUnreadCount}
      pageTitle={showArchived ? "Archived Records" : "Dashboard"}
      pageActions={!showArchived && (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => { setInjuryForm(blankInjuryForm()); setInjuryResult(null); if (!rosterList.length) loadRoster(); setView("new_injury"); }}
            style={ACTION_BTN_SECONDARY}
          >
            + Log an Injury
          </button>
          <button
            onClick={() => { setForm(blankLogConcussionForm()); setView("new"); }}
            style={ACTION_BTN_PRIMARY}
          >
            + Log New Concussion
          </button>
        </div>
      )}
    >
      <div style={{ maxWidth: 820, margin: "0 auto" }}>

        {/* The email service refused to send (a limit, or it was down), so
            updates may not have reached families. Admins only, first on the
            page: the safe response is a phone call, which only a person
            who knows can make. */}
        {isOrgAdmin && emailHealth && emailHealth.refused > 0 && (
          <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderLeft: "4px solid #c0272d", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#991b1b", marginBottom: 4 }}>
              ⚠️ Some emails couldn't be sent
            </div>
            <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.55 }}>
              Since {new Date(emailHealth.firstAt).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}, the email service hasn't sent some of GRTP's emails, so parents, coaches and staff may not have had updates since then. If anything urgent has happened, such as a concussion or a stage change, please contact them directly. This notice stays for 24 hours after the last email that couldn't be sent.
            </div>
          </div>
        )}

        {/* Cross-org alerts — other orgs have logged a concussion for a shared player */}
        {crossOrgAlerts && crossOrgAlerts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {crossOrgAlerts.map(alert => (
              <div key={alert.id} style={{ background: "#fffbeb", border: "1px solid #c9991a", borderRadius: 10, padding: "14px 16px", marginBottom: 8, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#92400e", marginBottom: 2 }}>
                    ⚠️ Shared player protocol alert
                  </div>
                  <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                    {/* "may also be registered" dates from when these alerts were
                        raised on a name match. They are now only ever raised for a
                        link both organisations confirmed against a specific child,
                        so hedging about it understates what we know — and reads as
                        uncertainty about whether it is even the same player. */}
                    <strong>{alert.player_name}</strong> is registered with you and has an active GRAS
                    programme logged by <strong>{alert.source_org_name}</strong> ({alert.source_org_type}).
                    {alert.stageLabel ? ` ${alert.stageLabel} in progress.` : ""} They must not play or train
                    with you until that protocol is complete.
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await api.dismissCrossOrgAlert(alert.id);
                    setCrossOrgAlerts(prev => prev.filter(a => a.id !== alert.id));
                  }}
                  style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 18, flexShrink: 0, padding: "0 4px" }}
                  title="Dismiss"
                >×</button>
              </div>
            ))}
          </div>
        )}

        {/* Players of ours currently on a protocol run by a linked
            organisation. Unlike the alerts above this is not dismissible
            and does not disappear — it reflects live state, so it is
            still here on the Sunday morning when somebody needs it.
            Status only: no incident notes, no symptoms. */}
        {/* The standing "on a protocol elsewhere" panel that used to sit
            here has moved into the player list below, where it is searchable
            and sits in age-grade context with everyone else. Keeping both put
            the same child on screen three times — twice in red — which read as
            three separate problems rather than one. The dismissible alert
            above is a different thing: an event, not a standing state. */}
        {daysLeftInTrial !== null && daysLeftInTrial !== undefined && !showArchived && (
          <div style={{ background: daysLeftInTrial <= 5 ? "#fffbeb" : "#f0f7f3", border: `1px solid ${daysLeftInTrial <= 5 ? "#c9991a" : "#b6d8c5"}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: daysLeftInTrial <= 5 ? "#92400e" : "#1a4a2e" }}>
              {daysLeftInTrial === 0 ? "⚠️ Your free trial ends today" : `🎯 Free trial — ${daysLeftInTrial} day${daysLeftInTrial !== 1 ? "s" : ""} remaining`}
            </div>
            {/* Not in the phone app: no route to buying, per Apple's rules. */}
            {!nativeApp.isNativeApp() && (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Contact <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#1a4a2e", fontWeight: 600 }}>{SUPPORT_EMAIL}</a> to continue after your trial.
              </div>
            )}
          </div>
        )}
        <div style={{ marginBottom: 20 }}>
          <p style={{ color: "#7a95ad", fontSize: 13, margin: 0 }}>
            {showArchived ? "Records removed from the active dashboard, kept on file" : "Stages advance automatically each morning at 07:30"}
          </p>
        </div>

        {/* Auto-advance banners */}
        {newlyAdvanced.map((a, i) => (
          <div key={i} style={{ background: "#1a4a2e", color: "#fff", borderRadius: 8, padding: "12px 16px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span>
              <strong>{a.name}</strong> auto-advanced from <span style={{ color: "#c9991a" }}>{STAGES[a.from]?.label}</span> to{" "}
              <span style={{ color: "#c9991a" }}>{STAGES[a.to]?.label}: {STAGES[a.to]?.name}</span>
              <span style={{ marginLeft: 8, opacity: 0.7, fontSize: 12 }}>— email notifications sent</span>
            </span>
            <button onClick={() => setNewlyAdvanced(prev => prev.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18 }}>×</button>
          </div>
        ))}

        {loading && <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading...</div>}
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 16, color: "#c0272d", marginBottom: 16 }}>{error}</div>}

        {!loading && (
          <>
            {!showArchived && (
              <div className="grtp-stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "Total", value: players.filter(p => !p.archived).length, color: "#6b7280" },
                  // The elsewhere count is shown beside this number rather
                  // than added to it. Adding it would make the figure count
                  // records this organisation does not hold — and disagree
                  // with its own CSV export. Leaving it off entirely was
                  // worse: "Active GRTP 0" sat directly above a do-not-play
                  // warning about a real child.
                  { label: "Active GRTP", value: activeCount, color: "#c0272d",
                    aside: (crossOrgProtocols || []).length > 0 ? `+${crossOrgProtocols.length} elsewhere` : null },
                  { label: "On Rest", value: pausedCount, color: "#c9991a" },
                  { label: "Protocol Complete", value: clearedCount, color: "#1a4a2e" },
                ].map(s => (
                  <div key={s.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}>{s.label}</div>
                    {s.aside && (
                      <div style={{ fontSize: 11, color: "#c0272d", fontWeight: 700, marginTop: 2 }}>{s.aside}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Search */}
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, age grade, team, or position..."
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", marginBottom: 10 }}
            />

            {/* Team and date filters — for pulling a season or a squad out
                for an audit. The export buttons below follow whatever is
                set here. */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              {teamsList.length > 0 && (
                <select
                  value={filterTeam}
                  onChange={e => setFilterTeam(e.target.value)}
                  style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", background: "#fff", color: filterTeam ? "#111827" : "#6b7280", cursor: "pointer" }}
                >
                  <option value="">All teams</option>
                  {teamsList.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              )}
              {/* A pair, side by side with their labels above, at every
                  width. Inline, the labels and boxes wrapped onto separate
                  lines on a phone ("Incident from", a full-width box, "to",
                  another), and an empty iPhone date box collapsed shorter
                  than everything round it. */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flex: "1 1 260px", maxWidth: 380 }}>
                {[["Incident from", filterFrom, setFilterFrom], ["To", filterTo, setFilterTo]].map(([label, value, set]) => (
                  <label key={label} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#6b7280", fontWeight: 600, minWidth: 0 }}>
                    {label}
                    <input type="date" value={value} onChange={e => set(e.target.value)}
                      style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 9px", fontSize: 13, fontFamily: "inherit", background: "#fff", color: value ? "#111827" : "#6b7280", minHeight: 38 }} />
                  </label>
                ))}
              </div>
              {hasClearableFilters && (
                <button
                  onClick={() => { setFilterTeam(""); setFilterFrom(""); setFilterTo(""); setSearchQuery(""); }}
                  style={{ background: "#fff", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 6, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                >
                  Clear filters
                </button>
              )}
            </div>

            {exportIsFiltered && (
              <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                Filtered: showing {filteredPlayers.length} of {players.filter(p => showArchived ? p.archived : !p.archived).length} records ({exportDescription}). The exports below cover {exportablePlayers.length}{testEntriesHidden > 0 ? ` — test ${testEntriesHidden === 1 ? "entry is" : "entries are"} never exported` : ""}.
              </div>
            )}

            {/* Toolbar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <button onClick={() => setShowArchived(v => !v)} style={{ background: showArchived ? "#1a4a2e" : "#fff", color: showArchived ? "#fff" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                {showArchived ? "← Back to Dashboard" : `📁 Archive${archivedCount ? ` (${archivedCount})` : ""}`}
              </button>
              {isOrgAdmin && (
                <button
                  onClick={() => { setView("registrations"); loadPendingRegistrations(); loadLinkRequests(); if (!rosterList.length) loadRoster(); }}
                  style={{ background: registrationsWaiting > 0 ? "#1a4a2e" : "#fff", color: registrationsWaiting > 0 ? "#fff" : "#374151", border: `1px solid ${registrationsWaiting > 0 ? "#1a4a2e" : "#d1d5db"}`, borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", position: "relative" }}
                >
                  📋 Registrations{registrationsWaiting > 0 ? ` (${registrationsWaiting})` : ""}
                </button>
              )}
              {/* The label says exactly what the file will contain. A
                  button reading "CSV" next to a filtered list is how you
                  hand a governing body every parent's email address while
                  believing you sent them one squad. */}
              <button onClick={handleExportCSV} disabled={exportIsFiltered && exportablePlayers.length === 0} style={{ background: "#fff", color: exportIsFiltered && exportablePlayers.length === 0 ? "#9ca3af" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: exportIsFiltered && exportablePlayers.length === 0 ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                📊 CSV{exportIsFiltered ? ` — ${exportablePlayers.length} shown` : " — all"}
              </button>
              <button onClick={handleExportAllPDF} disabled={exportIsFiltered && exportablePlayers.length === 0} style={{ background: "#fff", color: exportIsFiltered && exportablePlayers.length === 0 ? "#9ca3af" : "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: exportIsFiltered && exportablePlayers.length === 0 ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
                📄 PDF{exportIsFiltered ? ` — ${exportablePlayers.length} shown` : " — all"}
              </button>
              <button onClick={handleExport} style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                ⬇ Backup
              </button>
              {showArchived && isOrgAdmin && (
                <div style={{ background: "#fef2f2", color: "#c0272d", border: "1px solid #fca5a5", borderRadius: 6, padding: "9px 14px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                  🔓 Admin tools active
                </div>
              )}
            </div>

            {showArchived && isOrgAdmin && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontSize: 13, color: "#7f1d1d" }}>
                  <strong>Admin mode active.</strong> You can now permanently delete individual records below, or clear everything at once.
                </div>
                <button onClick={() => setShowClearAllConfirm(true)} style={{ background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  🗑 Clear All Data
                </button>
              </div>
            )}

            {/* A protocol at another organisation counts as something to
                show. Without it in this condition, searching a linked
                player mid-protocol fell through to "No matching players
                found" while a do-not-play row was sitting ready to
                render — the single worst answer this search can give. */}
            {filteredPlayers.length === 0 && filteredCrossOrg.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><LogoMark size={48} /></div>
                <div style={{ fontWeight: 600, fontSize: 16, color: "#6b7280", marginBottom: 6 }}>
                  {query ? "No matching players found" : showArchived ? "No archived records" : "No players on the GRAS programme"}
                </div>
                <div style={{ fontSize: 14 }}>{!query && !showArchived && "Use the button above to log a suspected concussion."}</div>
              </div>
            ) : showArchived ? (
              renderGroupedCards(grouped, groupKeys)
            ) : (
              <>
                {/* Another organisation's protocols, in the list rather than
                    only in the banner above — a coach looking up one child
                    searches, and a search that returns nothing reads as "no
                    concussion on record". Deliberately styled as somebody
                    else's: red rail, the managing organisation named on every
                    row, no click target, nothing to edit. */}
                {filteredCrossOrg.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#c0272d", marginBottom: 10, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <span>Managed by another organisation — do not play</span>
                      <span style={{ fontWeight: 400, fontSize: 12.5, color: "#6b7280" }}>
                        shared with you · read-only
                      </span>
                    </div>
                    {filteredCrossOrg.map((p, i) => (
                      <div key={`xo_${i}`} style={{ background: "#fff", border: "1px solid #fca5a5", borderLeft: "4px solid #c0272d", borderRadius: 10, padding: "13px 16px", marginBottom: 8, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
                            {p.playerName}
                            {(p.ageGrade || p.team) && (
                              <span style={{ fontWeight: 500, fontSize: 12.5, color: "#6b7280" }}>
                                {" · "}{[p.ageGrade, p.team].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12.5, color: "#4b5563", lineHeight: 1.6 }}>
                            Managed by <strong>{p.organisationName}</strong> since{" "}
                            {new Date(p.incidentDate).toLocaleDateString("en-GB")}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#c0272d" }}>
                            {STAGES[p.currentStage]?.label || `Stage ${p.currentStage + 1}`}
                          </div>
                          {p.paused && <div style={{ fontSize: 12, color: "#9a6207", fontWeight: 600 }}>On rest</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {activePlayers.length > 0 && renderGroupedCards(grouped, groupKeys)}
                {completedPlayers.length > 0 && (
                  <div style={{ marginTop: activePlayers.length > 0 ? 32 : 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 14, paddingTop: activePlayers.length > 0 ? 20 : 0, borderTop: activePlayers.length > 0 ? "1px solid #e5e7eb" : "none", display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                      <span>
                        Completed / Removed
                        <span style={{ marginLeft: 8, fontWeight: 500, color: "#9ca3af", fontSize: 12 }}>Last 30 days</span>
                      </span>
                      {hiddenCompletedCount > 0 && (
                        <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 400 }}>
                          {hiddenCompletedCount} older record{hiddenCompletedCount !== 1 ? "s" : ""} hidden —{" "}
                          <button onClick={() => setView("roster")} style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline", fontFamily: "inherit" }}>
                            view in Roster
                          </button>
                        </span>
                      )}
                    </div>
                    {(() => {
                      const visibleCompleted = showAllCompleted ? completedPlayers : completedPlayers.slice(0, COMPLETED_PREVIEW_COUNT);
                      const { grouped: cg, groupKeys: cgk } = groupByAgeGrade(visibleCompleted);
                      return (
                        <>
                          {renderGroupedCards(cg, cgk)}
                          {completedPlayers.length > COMPLETED_PREVIEW_COUNT && (
                            <button
                              onClick={() => setShowAllCompleted(v => !v)}
                              style={{ marginTop: 10, background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "#6b7280", cursor: "pointer", width: "100%", fontFamily: "inherit" }}
                            >
                              {showAllCompleted
                                ? "Show fewer"
                                : `Show all ${completedPlayers.length} completed protocols`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {activePlayers.length === 0 && completedPlayers.length === 0 && filteredCrossOrg.length === 0 && (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: "#6b7280" }}>No matching players found</div>
                  </div>
                )}
              </>
            )}

            {/* Recent injuries — a glance, not a management surface.
                Deliberately below the concussion protocols and visibly
                separate from them: these carry no stage, nothing
                advances, and reading them as a protocol would be the
                one dangerous misunderstanding available here. */}
            {!showArchived && (injuries.length > 0 || crossOrgInjuries.length > 0) && (
              <div style={{ marginTop: 24 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1a4a2e", letterSpacing: 0.8, textTransform: "uppercase" }}>
                    Injuries and first aid
                  </div>
                  <button onClick={() => { setInjuryForm(blankInjuryForm()); setInjuryResult(null); if (!rosterList.length) loadRoster(); setView("new_injury"); }}
                    style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                    + Log an injury
                  </button>
                </div>
                <div style={{ fontSize: 12.5, color: "#9ca3af", marginBottom: 10, lineHeight: 1.5 }}>
                  A record of what happened and what was done. Not concussion protocol — nothing here advances on its own.
                </div>
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                  {injuries.slice(0, 10).map(inj => (
                    <div key={inj.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, color: "#111827" }}>
                          {inj.subjectName}
                          {!inj.onRoster && <span style={{ fontSize: 11, color: "#92400e", background: "#fef9ec", border: "1px solid #f0d88a", borderRadius: 4, padding: "1px 6px", marginLeft: 8, whiteSpace: "nowrap", display: "inline-block" }}>not on roster</span>}
                          {inj.isTest && <span style={{ fontSize: 11, color: "#6b21a8", background: "#f5f0ff", border: "1px solid #ddd0f5", borderRadius: 4, padding: "1px 6px", marginLeft: 8, whiteSpace: "nowrap", display: "inline-block" }}>test</span>}
                        </div>
                        <div style={{ fontSize: 12.5, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {[inj.bodyPart, inj.nature, inj.incidentDate && formatDate(inj.incidentDate)].filter(Boolean).join(" · ")}
                          {inj.daysLost !== null && ` · ${inj.daysLost} days lost`}
                        </div>
                      </div>
                      {inj.followupDueAt && !inj.followupAnsweredAt && (
                        <span style={{ fontSize: 11.5, color: "#92400e", background: "#fef9ec", border: "1px solid #f0d88a", borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap" }}>
                          follow-up due
                        </span>
                      )}
                    </div>
                  ))}
                  {crossOrgInjuries.map(inj => (
                    <div key={inj.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: "1px solid #f3f4f6", background: "#fafafa" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, color: "#111827" }}>{inj.subjectName}</div>
                        <div style={{ fontSize: 12.5, color: "#6b7280" }}>
                          {[inj.bodyPart, inj.nature, inj.incidentDate && formatDate(inj.incidentDate)].filter(Boolean).join(" · ")} · shared by {inj.organisationName}
                        </div>
                      </div>
                      <span style={{ fontSize: 11.5, color: "#6b7280", background: "#f3f4f6", borderRadius: 4, padding: "3px 8px", whiteSpace: "nowrap" }}>view only</span>
                    </div>
                  ))}
                  <div style={{ padding: "9px 14px", fontSize: 12.5, color: "#9ca3af", display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span>{injuries.length > 10 ? `Showing the 10 most recent of ${injuries.length}.` : ""}</span>
                    <button onClick={() => setView("first_aid_log")}
                      style={{ background: "none", border: "none", color: "#1a4a2e", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0, whiteSpace: "nowrap" }}>
                      Open the first aid log →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Calendar */}
            {!showArchived && (
              <GRTPCalendar
                players={players}
                onSelectPlayer={(id) => { setSelectedId(id); setView("detail"); }}
              />
            )}

            {/* Protocol reference */}
            {!showArchived && (
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginTop: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#1a4a2e", marginBottom: 10, letterSpacing: 0.8, textTransform: "uppercase" }}>Protocol Timeline</div>
                {STAGES.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 4 }}>
                    <div style={{ width: 60, fontSize: 11, fontWeight: 700, color: s.color, flexShrink: 0 }}>Day {s.dayStart}{s.dayEnd ? `–${s.dayEnd}` : "+"}</div>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                    <div style={{ color: "#374151" }}><strong>{s.label}:</strong> {s.name}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      {notif && <Notif msg={notif.msg} type={notif.type} onDismiss={() => setNotif(null)} />}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
      {showClearAllConfirm && (
        <ConfirmModal
          title="Clear All Data?"
          message="This will permanently delete every player record — active, archived, test, and real — along with their full history. This cannot be undone. There is no backup unless you have already downloaded one."
          confirmLabel="Yes, Delete Everything"
          danger={true}
          onConfirm={handleClearAllData}
          onCancel={() => setShowClearAllConfirm(false)}
        />
      )}
      {removeEarlyTarget && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", marginBottom: 8 }}>Remove {removeEarlyTarget.name} from the protocol?</div>
            <p style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6, marginBottom: 16 }}>
              This records that {removeEarlyTarget.name} was cleared before the protocol's 21-day schedule completed — distinct from reaching Day 21 naturally. Parents and staff will be notified, and this can't easily be undone.
            </p>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
              Reason for removal <span style={{ color: "#c0272d" }}>(required)</span>
            </label>
            <textarea
              value={removeEarlyReason}
              onChange={e => setRemoveEarlyReason(e.target.value)}
              autoFocus
              rows={4}
              placeholder="e.g. Assessed and cleared by Dr. Smith on 25/06/2026"
              style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", resize: "vertical", marginBottom: 18 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleRemoveEarly}
                disabled={removeEarlySubmitting || !removeEarlyReason.trim()}
                style={{ flex: 1, background: "#c0272d", color: "#fff", border: "none", borderRadius: 6, padding: "10px 0", fontSize: 14, fontWeight: 700, cursor: removeEarlySubmitting || !removeEarlyReason.trim() ? "default" : "pointer", opacity: removeEarlySubmitting || !removeEarlyReason.trim() ? 0.5 : 1 }}
              >
                {removeEarlySubmitting ? "Removing..." : "Yes, Remove from Protocol"}
              </button>
              <button
                onClick={() => { setRemoveEarlyTarget(null); setRemoveEarlyReason(""); }}
                style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showTimeoutWarning && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 400, width: "100%", fontFamily: "'Inter', system-ui, sans-serif", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏱</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", marginBottom: 10 }}>Still there?</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 22px" }}>
              For the security of player data, you'll be logged out in 2 minutes due to inactivity.
            </p>
            <button
              onClick={() => resetRef.current && resetRef.current()}
              style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}
            >
              Stay logged in
            </button>
            <button
              onClick={() => handleLogout({ keepDevice: true })}
              style={{ width: "100%", background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: "pointer", padding: "4px 0" }}
            >
              Log out now
            </button>
          </div>
        </div>
      )}
      {/* This sits over the dashboard, which is where the buttons for
          logging an injury or a concussion are. Anything that makes it
          hard to close makes the app unusable rather than merely
          annoying, so: a close control, and clicking the backdrop also
          closes it. Both only hide it for this visit — the two buttons
          below are still the way to answer the question for good. */}
      {showAgeGradePrompt && (
        <div
          onClick={() => setShowAgeGradePrompt(false)}
          style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 3500 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", background: "#fff", borderRadius: 12, padding: 28, maxWidth: 440, width: "100%", fontFamily: "inherit" }}>
            <button
              onClick={() => setShowAgeGradePrompt(false)}
              aria-label="Close"
              style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", color: "#9ca3af", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "4px 8px", fontFamily: "inherit" }}
            >
              ×
            </button>
            <div style={{ fontSize: 24, marginBottom: 12 }}>🏉</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", marginBottom: 10 }}>New season — have you done your rollover?</div>
            <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 22px" }}>
              It looks like you haven't advanced age grades for the {new Date().getFullYear()} season yet.
              Head to <strong>Roster → Season Rollover</strong> to move every player up one age grade before the season kicks off.
            </p>
            <button
              onClick={() => { setShowAgeGradePrompt(false); handleOpenRoster(); }}
              style={{ width: "100%", background: "#1a4a2e", color: "#fff", border: "none", borderRadius: 8, padding: "12px 0", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10, fontFamily: "inherit" }}
            >
              Go to Roster
            </button>
            <button
              onClick={() => { setShowAgeGradePrompt(false); handleAgeGradeDecision("keep"); }}
              disabled={ageGradeDeciding}
              style={{ width: "100%", background: "none", border: "none", color: "#9ca3af", fontSize: 13, cursor: ageGradeDeciding ? "default" : "pointer", padding: "4px 0", fontFamily: "inherit", opacity: ageGradeDeciding ? 0.6 : 1 }}
            >
              Not this season — don't ask again until next September
            </button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
